import _ from 'lodash';
import { ICountry, TCountryCode, countries, getEmojiFlag } from 'countries-list';

const regionNamesInGerman = new Intl.DisplayNames(['de'], { type: 'region' });
const languageNamesInGerman = new Intl.DisplayNames(['de'], {
  type: 'language',
});

/**
 * Supported languages with German display names and flag emojis.
 */
export const allLanguages = [
  {
    code: 'de',
    name: languageNamesInGerman.of('de'),
    label: `${getEmojiFlag('DE')} ${languageNamesInGerman.of('de')}`,
  },
  {
    code: 'en',
    name: languageNamesInGerman.of('en'),
    label: `${getEmojiFlag('GB')} ${languageNamesInGerman.of('en')}`,
  },
  {
    code: 'fr',
    name: languageNamesInGerman.of('fr'),
    label: `${getEmojiFlag('FR')} ${languageNamesInGerman.of('fr')}`,
  },
];
/**
 * Lookup map for languages by language code.
 */
export const allLanguagesByCode: {
  [code: string]: (typeof allLanguages)[0];
} = allLanguages.reduce((agg, c) => ({ ...agg, [c.code]: c }), {});

/**
 * All countries with German display names, native names, and flag emojis.
 */
export const allCountries = Object.keys(countries).map(code => {
  const country: ICountry = (countries as any)[code];

  return {
    code,
    name: regionNamesInGerman.of(code),
    native: country.native,
    label: `${getEmojiFlag(code as TCountryCode)} ${regionNamesInGerman.of(code)}`,
    emoji: getEmojiFlag(code as TCountryCode),
  };
});

/**
 * Lookup map for countries by country code.
 */
export const allCountriesByCode: {
  [code: string]: (typeof allCountries)[0];
} = allCountries.reduce((agg, c) => ({ ...agg, [c.code]: c }), {});

/**
 * Countries sorted with AT, DE, CH first, then alphabetically.
 * Includes a null separator between prioritized and alphabetical sections.
 */
export const allCountriesSorted = [
  allCountries.find(c => c.code === 'AT')!,
  allCountries.find(c => c.code === 'DE')!,
  allCountries.find(c => c.code === 'CH')!,
  null,
  ..._.sortBy(
    allCountries.filter(c => c.code !== 'AT' && c.code !== 'DE' && c.code !== 'CH'),
    c => c.name,
  ),
];

/**
 * Extracts currency name from Intl.NumberFormat output.
 * @param locale - Locale for currency name formatting
 * @param currencyCode - ISO currency code
 * @returns Localized currency name
 */
function _getCurrencyName(locale: string, currencyCode: string) {
  const formattedCurrency = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: 'name',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(0);
  const currencyName = formattedCurrency.replace(/[0,.]/g, '').trim();
  return currencyName || '?';
}

/**
 * Supported currencies with German names, symbols, and flag emojis.
 */
export const allCurrencies = [
  {
    code: 'EUR',
    name: _getCurrencyName('de', 'EUR'),
    label: `🇪🇺 ${_getCurrencyName('de', 'EUR')}`,
    symbol: '€',
  },
  {
    code: 'USD',
    name: _getCurrencyName('de', 'USD'),
    label: `${getEmojiFlag('US')} ${_getCurrencyName('de', 'USD')}`,
    symbol: '$',
  },
  {
    code: 'CHF',
    name: _getCurrencyName('de', 'CHF'),
    label: `${getEmojiFlag('CH')} ${_getCurrencyName('de', 'CHF')}`,
    symbol: 'CHF',
  },
  {
    code: 'ITL',
    name: _getCurrencyName('de', 'ITL'),
    label: `${getEmojiFlag('IT')} ${_getCurrencyName('de', 'ITL')}`,
    symbol: '₤',
  },
  {
    code: 'FRF',
    name: _getCurrencyName('de', 'FRF'),
    label: `${getEmojiFlag('FR')} ${_getCurrencyName('de', 'FRF')}`,
    symbol: '₣',
  },
  {
    code: 'GBP',
    name: _getCurrencyName('de', 'GBP'),
    label: `${getEmojiFlag('GB')} ${_getCurrencyName('de', 'GBP')}`,
    symbol: '£',
  },
];
/**
 * Lookup map for currencies by currency code.
 */
export const allCurrenciesByCode: {
  [code: string]: (typeof allCurrencies)[0];
} = allCurrencies.reduce((agg, c) => ({ ...agg, [c.code]: c }), {});

/**
 * i18next configuration with German as default language and translation resources.
 */
export const languages = {
  lng: 'de',
  fallbackLng: 'de',
  supportedLngs: ['de', 'en'],
  debug: true,
  resources: {
    de: {
      translation: {
        // Sidebar
        'sidebar-tasks': 'Tasks',
        'sidebar-clients': 'Kunden',
        'sidebar-groups': 'Ketten',
        'sidebar-widgetoffers': 'SEM Buchungen',
        'sidebar-listeroffers': 'seminargo Buchungen',
        'sidebar-bericht': 'Reports',
        'sidebar-catalog': 'Leistungskonfiguration',
        'sidebar-products': 'Leistungen',
        'sidebar-product-bundles': 'Leistungspakete',
        'sidebar-pricelist': 'Preisliste',
        'sidebar-pricelists': 'Preislisten',
        'sidebar-availability': 'Verfügbarkeit',
        'sidebar-content': 'Inhaltsverwaltung',
        'sidebar-security': 'Benutzer / Rechte',
        'sidebar-users': 'Benutzer',
        'sidebar-spaces': 'Verwaltungsbereiche',
        'sidebar-text': 'Textbausteine',
        'sidebar-media': 'Medien',
        'sidebar-settings': 'Einstellungen',
        'sidebar-templates': 'Vorlagen',
        'sidebar-hotels': 'Hotels',
        'sidebar-integration': 'Schnittstellen',
        'sidebar-space': 'Corporate Identity',
        'sidebar-help': 'Hilfe',
        'sidebar-search': 'Suche',

        'confirmation-dialog-decline': 'Abbrechen',
        'confirmation-dialog-confirm': 'Fortsetzen',
        'confirmation-dialog-yes': 'Ja',
        'confirmation-dialog-no': 'Nein',
        'information-dialog-confirm': 'Fenster schließen',
        close: 'Schließen',

        // Enums
        'meeting-package-import-value-unresolved': 'Nicht entschieden',
        'meeting-package-import-value-linked-existing': 'Mit Bestandshotel verknüpft',
        'meeting-package-import-value-created-from-source': 'Aus Meeting Package erstellt',
        'meeting-package-import-value-ignored': 'Ignoriert',
        'meeting-package-import-value-discovered': 'Entdeckt',
        'meeting-package-import-value-incomplete': 'Unvollständig',
        'meeting-package-import-value-review-required': 'Manuelle Prüfung erforderlich',
        'meeting-package-import-value-approved-create': 'Neuanlage freigegeben',
        'meeting-package-import-value-importing': 'Import läuft',
        'meeting-package-import-value-imported-pending-provisioning': 'Importiert, Bereitstellung ausstehend',
        'meeting-package-import-value-ready': 'Bereit',
        'meeting-package-import-value-failed-retryable': 'Fehlgeschlagen, wiederholbar',
        'meeting-package-import-value-failed': 'Fehlgeschlagen',
        'meeting-package-import-value-cancelled': 'Abgebrochen',
        'meeting-package-cancel-import': 'Import abbrechen',
        'meeting-package-import-cancellation-requested': 'Abbruch angefordert',
        'meeting-package-import-value-inactive': 'Inaktiv',
        'meeting-package-import-value-running': 'Läuft',
        'meeting-package-import-value-success': 'Erfolgreich',
        'meeting-package-import-value-partial': 'Teilweise erfolgreich',
        'meeting-package-import-value-auto-match': 'Automatische Zuordnung',
        'meeting-package-import-value-manual-match': 'Manuelle Zuordnung',
        'meeting-package-import-value-auto-create': 'Automatische Neuanlage',
        'meeting-package-import-progress-heading': 'Einträge geprüft',
        'meeting-package-import-progress-loading': 'Anzahl der zu prüfenden Einträge wird ermittelt …',
        'meeting-package-import-progress': '{{processed}} / {{total}} Einträge ({{percent}} %)',
        'meeting-package-run-api': 'API-Einträge im Lauf',
        'meeting-package-run-selection-group': 'Prüfung der Einträge',
        'meeting-package-run-outcomes-group': 'Gefundene Hotels: Gesamtzahl und Ergebnisse',
        'meeting-package-run-other-group': 'Weitere Ergebnisse',
        'meeting-package-run-review': 'Prüfung erforderlich',
        'meeting-package-run-retries': 'Zusätzliche Wiederholungen',
        'meeting-package-run-prefiltered': 'Vorab wegen Länderfilter übersprungen',
        'meeting-package-run-excluded': 'Nach Detailprüfung ausgeschlossen',
        'meeting-package-run-discovered': 'Hotels im Länderfilter gefunden',
        'meeting-package-run-updated': 'Bestehende Zuordnungen aktualisiert',
        'meeting-package-run-linked': 'Neu verknüpft',
        'meeting-package-run-created': 'Neu erstellt und bereitgestellt',
        'meeting-package-run-ignored': 'Ignoriert',
        'meeting-package-run-discovered-failed': 'Gefunden, Verarbeitung fehlgeschlagen',
        'meeting-package-run-all-errors': 'Fehler insgesamt',
        'meeting-package-run-not-recorded': 'Nicht erfasst',
        'meeting-package-run-historical': 'Historische Angaben / aus Events rekonstruiert',
        'meeting-package-run-accounting-help':
          '100 % bedeutet: Alle vorgesehenen Einträge wurden geprüft. Gefundene Hotels sind die Gesamtzahl innerhalb des Länderfilters. ' +
          'Aktualisiert, neu verknüpft, neu erstellt, Prüfung erforderlich, ignoriert und Verarbeitung fehlgeschlagen beschreiben deren Ergebnisse ' +
          'und werden nicht zur Gesamtzahl addiert. Vorschläge bei deaktivierter Automatik sind in der Prüfung enthalten. ' +
          'Inaktivierungen und Fehler insgesamt können auch weitere Einträge betreffen. Bei alten Läufen sind fehlende Angaben als „Nicht erfasst“ gekennzeichnet.',
        'meeting-package-match-outcome-filter': 'Matching-Ergebnis',
        'meeting-package-match-outcome-matched': 'Bestehendes Hotel gefunden',
        'meeting-package-match-outcome-new-hotel': 'Kein passendes Hotel / Neuanlage',
        'meeting-package-match-outcome-needs-review': 'Manuelle Prüfung nötig',
        'meeting-package-rows-per-page': 'Hotels pro Seite',
        'meeting-package-auto-create-result-created': 'Tatsächlich neu angelegt',
        'meeting-package-auto-create-result-disabled': 'Nicht angelegt: Auto-Create deaktiviert',
        'meeting-package-auto-match-result-linked': 'Tatsächlich verknüpft',
        'meeting-package-auto-match-result-disabled': 'Nicht verknüpft: Auto-Match deaktiviert',
        'meeting-package-import-value-manual-create': 'Manuelle Neuanlage',
        'meeting-package-import-value-match': 'Zuordnung erwartet',
        'meeting-package-import-value-review': 'Prüfung erwartet',
        'meeting-package-import-value-no-auto-match': 'Keine automatische Zuordnung',
        'meeting-package-import-value-create': 'Neuanlage erwartet',
        'meeting-package-import-value-unknown': 'Unbekannt',
        'meeting-package-import-value-high': 'Hohe Sicherheit',
        'meeting-package-import-value-medium': 'Mittlere Sicherheit',
        'meeting-package-import-value-low': 'Niedrige Sicherheit',
        'meeting-package-start-venue-sync': 'Venue-Abgleich starten',
        'meeting-package-all-countries': 'Alle Länder',
        'meeting-package-import-countries': 'Länder für diesen Import',
        'meeting-package-import-countries-help':
          'Importiert nur Venues der ausgewählten Länder. Bereits ermittelte Länder werden für spätere Läufe wiederverwendet. Beim ersten Lauf werden unbekannte Länder zunächst ermittelt.',
        'meeting-package-address': 'Adresse',
        'meeting-package-administration-area': 'Verwaltungsbereich: {{name}}',
        'meeting-package-all': 'Alle',
        'meeting-package-all-venue-details-loaded': 'Alle angeforderten Venue-Details wurden erfolgreich geladen.',
        'meeting-package-and': 'und',
        'meeting-package-api-config-check-failed': 'API-Konfiguration konnte nicht geprüft werden: {{error}}',
        'meeting-package-api-errors': 'API-Fehler',
        'meeting-package-api-errors-count': 'API-Fehler ({{count}})',
        'meeting-package-api-venue': 'API-Venue',
        'meeting-package-api-venues-loaded-count': '{{count}} API-Venues geladen',
        'meeting-package-auto-create-candidates-count': '{{count}} Auto-Create-Kandidaten',
        'meeting-package-auto-match-candidates-count': '{{count}} Auto-Match-Kandidaten',
        'meeting-package-missing-required-fields': 'Fehlende Pflichtfelder: {{fields}}',
        'meeting-package-review-queue-filter': 'Prüfqueue (inkl. unvollständig)',
        'meeting-package-best-candidate': 'Bester Kandidat',
        'meeting-package-cancel': 'Abbrechen',
        'meeting-package-candidate': 'Kandidat',
        'meeting-package-cases': 'Fälle',
        'meeting-package-close': 'Schließen',
        'meeting-package-compare': 'Vergleichen',
        'meeting-package-comparison': 'Vergleich',
        'meeting-package-config-value-missing-plural': 'fehlen.',
        'meeting-package-config-value-missing-singular': 'fehlt.',
        'meeting-package-conflicts': 'Konflikte',
        'meeting-package-coordinates': 'Koordinaten',
        'meeting-package-correct-auto-matches-count': '{{count}} korrekte Auto-Matches',
        'meeting-package-country': 'Land',
        'meeting-package-country-iso-optional': 'Land (ISO, optional)',
        'meeting-package-created': 'Erstellt',
        'meeting-package-data': 'Meeting-Package-Daten',
        'meeting-package-db-hotel-sample': 'DB-Hotel-Stichprobe',
        'meeting-package-db-hotels-count': '{{count}} DB-Hotels',
        'meeting-package-decision': 'Entscheidung',
        'meeting-package-decision-value': 'Entscheidung {{value}}',
        'meeting-package-decisions-count': 'Entscheidungen ({{count}})',
        'meeting-package-delete': 'Löschen',
        'meeting-package-delete-all-reports-confirm': 'Alle abgeschlossenen {{mode}}-Reports löschen?',
        'meeting-package-delete-completed-reports': 'Abgeschlossene Reports löschen',
        'meeting-package-delete-hotel-and-area': 'Hotel und Verwaltungsbereich löschen',
        'meeting-package-delete-report-confirm': 'Diesen Matching-Dry-Run-Report löschen?',
        'meeting-package-details': 'Details',
        'meeting-package-deviations-count': 'Abweichungen ({{count}})',
        'meeting-package-discovered': 'Entdeckt',
        'meeting-package-discovery-unavailable': 'Meeting Package Discovery ist nicht verfügbar.',
        'meeting-package-dry-run-info':
          'Manueller Read-only-Lauf: Hotels und operative Queue werden nicht verändert. Im Sandbox-Modus werden echte Venue-Daten von der separat konfigurierten Meeting-Package-Sandbox geladen. Gespeichert wird ausschließlich der Report; es gibt keinen Scheduler-Lauf.',
        'meeting-package-dry-run-real-venue-info':
          'Echte Sandbox-API-Venue aus dem Dry Run. Aktionen sind bewusst deaktiviert und verändern keine operative Queue.',
        'meeting-package-dry-run-simulated-case-info':
          'Simulierter Review-Fall aus dem Dry Run. Aktionen sind bewusst deaktiviert und verändern keine operative Queue.',
        'meeting-package-error': 'Fehler',
        'meeting-package-error-details': 'Fehlerdetails',
        'meeting-package-time': 'Zeitpunkt',
        'meeting-package-import-errors-title': 'Fehler im Importlauf #{{runId}}',
        'meeting-package-no-venue-errors': 'Für diesen Lauf sind keine einzelnen Venue-Fehler gespeichert.',
        'meeting-package-import-queued': 'Import angefordert. Wartet auf den Scheduler.',
        'meeting-package-import-value-queued': 'Wartet auf Scheduler',
        'meeting-package-expectation': 'Erwartung',
        'meeting-package-expectation-value': 'Erwartung {{value}}',
        'meeting-package-field': 'Feld',
        'meeting-package-field-comparison': 'Feldvergleich',
        'meeting-package-geo-distance': 'Geo-Distanz',
        'meeting-package-hotel': 'Hotel',
        'meeting-package-hotel-search-placeholder': 'Hotelname oder RefCode',
        'meeting-package-ignore': 'Ignorieren',
        'meeting-package-import-as-new-hotel': 'Als neues Hotel importieren',
        'meeting-package-import-runs': 'Importläufe',
        'meeting-package-import-run-filter': 'Importlauf',
        'meeting-package-import-run-current-status': 'Status und Match-Ergebnis zeigen den aktuellen Stand.',
        'meeting-package-all-import-runs': 'Alle Importläufe',
        'meeting-package-show-inactive': 'Inaktive zusätzlich anzeigen',
        'meeting-package-not-in-source': 'Nicht mehr in der Quelle',
        'meeting-package-older-entry': 'Älterer Eintrag',
        'meeting-package-older-entry-tooltip':
          'Seit Beginn des letzten Importlaufs noch nicht erneut gesehen. Der Lauf kann noch laufen oder fehlgeschlagen sein.',
        'meeting-package-import-title': 'Meeting Package Import',
        'meeting-package-in-sample-count': '{{count}} im Sample',
        'meeting-package-inactive': 'Inaktiv',
        'meeting-package-incorrect-auto-matches': 'Falsche Auto-Matches',
        'meeting-package-incorrect-auto-matches-count': '{{count}} falsche Auto-Matches',
        'meeting-package-last-seen': 'Zuletzt gesehen',
        'meeting-package-limit-seed': 'Limit / Seed',
        'meeting-package-link': 'Verknüpfen',
        'meeting-package-linked': 'Verknüpft',
        'meeting-package-suggested-linked-tooltip':
          'Automatische Verknüpfung deaktiviert: {{count}} Kandidaten erkannt, aber nicht verknüpft. Tatsächlich verknüpft: {{actual}}.',
        'meeting-package-suggested-created-tooltip':
          'Automatische Neuanlage deaktiviert: {{count}} Kandidaten erkannt, aber nicht angelegt. Tatsächlich angelegt: {{actual}}.',
        'meeting-package-linked-with': 'Verknüpft mit {{name}} ({{refCode}})',
        'meeting-package-matching-dry-run': 'Matching Dry Run',
        'meeting-package-max-api-venues': 'Max. API-Venues',
        'meeting-package-mock-cases-count': '{{count}} Mock-Fälle',
        'meeting-package-mock-history': 'Mock-Szenario-Historie',
        'meeting-package-mock-scenario': 'Mock-Szenario',
        'meeting-package-mock-scenarios': 'Mock-Szenarien',
        'meeting-package-mock-venue': 'Mock-Venue',
        'meeting-package-name': 'Name',
        'meeting-package-name-score': 'Namensscore',
        'meeting-package-no-ground-truth': 'keine Ground Truth',
        'meeting-package-no-permission': 'Keine Berechtigung.',
        'meeting-package-no-process-log-entries': 'Noch keine Process-Log-Einträge vorhanden.',
        'meeting-package-note': 'Notiz',
        'meeting-package-old-report-no-review-queue': 'Dieser ältere Report enthält noch keine Review Queue.',
        'meeting-package-open': 'Öffnen',
        'meeting-package-open-hotel': 'Hotel öffnen',
        'meeting-package-or-selected-base-url': 'beziehungsweise die ausgewählte Sandbox-/Production-Base-URL',
        'meeting-package-payload': 'Payload',
        'meeting-package-postcode-city': 'PLZ / Ort',
        'meeting-package-prioritized-deviations': 'Priorisierte Abweichungen',
        'meeting-package-process-log-count': 'Process Log ({{count}})',
        'meeting-package-real-venues-count': '{{count}} echte Venues',
        'meeting-package-reasons': 'Gründe',
        'meeting-package-reevaluate': 'Neu bewerten',
        'meeting-package-refresh': 'Aktualisieren',
        'meeting-package-reset-confirm-text':
          'Die Venue wird anschließend neu gematcht, wieder zur manuellen Prüfung vorgemerkt und kann mit einem vollständig neuen Hotel und Verwaltungsbereich importiert werden. Der Vorgang wird abgelehnt, wenn das Hotel oder der Verwaltungsbereich bereits verwendet wird.',
        'meeting-package-reset-confirm-title': 'Meeting-Package-Import zurücksetzen?',
        'meeting-package-reset-import': 'Import zurücksetzen',
        'meeting-package-reset-warning': 'Das aus Meeting Package angelegte Hotel und sein dedizierter Verwaltungsbereich werden dauerhaft gelöscht.',
        'meeting-package-restart-server-after-config': 'Nach dem Setzen muss der Server neu gestartet werden.',
        'meeting-package-result-from': 'Ergebnis vom {{date}}',
        'meeting-package-retry': 'Erneut versuchen',
        'meeting-package-review-queue': 'Review Queue',
        'meeting-package-review-queue-count': 'Review Queue ({{count}})',
        'meeting-package-reviews-count': '{{count}} Review',
        'meeting-package-sandbox-api': 'Sandbox-API',
        'meeting-package-sandbox-config-missing': 'Sandbox Dry Run ist freigeschaltet, aber Key oder Base-URL fehlen in der Server-Konfiguration.',
        'meeting-package-sandbox-history': 'Sandbox-API-Historie',
        'meeting-package-sandbox-match-decisions': 'Match-Entscheidungen für echte Sandbox-Venues',
        'meeting-package-sandbox-match-decisions-info':
          'Die Entscheidung zeigt, was der Matcher bei aktivierten Auto-Flags vorschlagen würde. Der Dry Run führt keine dieser Aktionen aus.',
        'meeting-package-sandbox-no-ground-truth-info':
          'Das sind echte Sandbox-Payloads, aber keine Ground Truth. Deshalb werden keine Precision, Recall oder „falsche Matches“ behauptet.',
        'meeting-package-sandbox-review-queue-info':
          'Echte Sandbox-Venues, die mit den aktuellen Schwellenwerten manuelles Review benötigen. Sie existieren ausschließlich innerhalb dieses Dry-Run-Reports.',
        'meeting-package-sandbox-review-queue-summary': 'Sandbox Review Queue ({{shown}} von {{total}})',
        'meeting-package-sandbox-venue': 'Sandbox-Venue',
        'meeting-package-scenario': 'Szenario',
        'meeting-package-scenarios': 'Szenarien',
        'meeting-package-search': 'Suche',
        'meeting-package-select': 'Auswählen',
        'meeting-package-select-other-hotel': 'Anderes Hotel auswählen',
        'meeting-package-selected-candidate': 'Ausgewählter Kandidat',
        'meeting-package-show': 'Anzeigen',
        'meeting-package-simulated-review-queue-info':
          'Repräsentative, nach Szenario gemischte Review-Fälle. Sie existieren ausschließlich innerhalb dieses Dry-Run-Reports.',
        'meeting-package-simulated-review-queue-summary': 'Simulierte Review Queue ({{shown}} von {{total}})',
        'meeting-package-source': 'Quelle',
        'meeting-package-start': 'Start',
        'meeting-package-start-matching-dry-run': 'Matching Dry Run starten',
        'meeting-package-start-sandbox-dry-run': 'Sandbox Dry Run starten',
        'meeting-package-status': 'Status',
        'meeting-package-suggested-hotels': 'Vorgeschlagene Hotels',
        'meeting-package-this-hotel': 'diesem Hotel',
        'meeting-package-threshold-calibration': 'Threshold-Kalibrierung',
        'meeting-package-thresholds': 'Thresholds',
        'meeting-package-top-candidate-correct': 'Top-Kandidat korrekt',
        'meeting-package-top-candidates': 'Top-Kandidaten',
        'meeting-package-total-score': 'Gesamtscore',
        'meeting-package-unlink': 'Zuordnung lösen',
        'meeting-package-unlink-confirm-text':
          'Die Meeting-Package-Venue wird von {{name}} getrennt und wieder zur Review Queue hinzugefügt. Das Hotel selbst bleibt vollständig unverändert.',
        'meeting-package-unlink-confirm-title': 'Hotelzuordnung lösen?',
        'meeting-package-auto-create': 'Auto-Create',
        'meeting-package-auto-match': 'Auto-Match',
        'meeting-package-evidence': 'Evidence',
        'meeting-package-false-negatives': 'False Negatives',
        'meeting-package-false-positives': 'False Positives',
        'meeting-package-map-source-venue': 'A: Meeting Package Venue',
        'meeting-package-map-candidate-marker': 'B · {{name}}',
        'meeting-package-map-source-marker': 'A · Meeting Package',
        'meeting-package-pass-rate': 'Pass Rate',
        'meeting-package-precision': 'Precision',
        'meeting-package-recall': 'Recall',
        'meeting-package-review': 'Review',
        'meeting-package-score': 'Score',
        'meeting-package-score-percentiles': 'Score P10 / P50 / P90',
        'meeting-package-score-runner-up': 'Score / Runner-up',
        'meeting-package-seed': 'Seed',
        'meeting-package-source-name': 'Meeting Package',
        'meeting-package-true-positives': 'True Positives',
        'meeting-package-venue': 'Venue',
        'meeting-package-venue-id': 'Venue-ID',
        'meeting-package-evidence-value': 'Evidence {{value}}',
        'meeting-package-pass-rate-value': 'Pass Rate {{value}}',
        'meeting-package-runner-up-value': 'Runner-up {{value}}',
        'meeting-package-score-value': 'Score {{value}}',
        system: 'System',
        'system-section-overview': 'Überwachung',
        'system-section-maintenance': 'Wartung',
        'system-section-cache': 'Cache & Redis',
        'system-section-audit': 'API-Audit',
        'system-section-meeting-package': 'Meeting Package',
        'system-audit-title': 'API-Audit-Log',
        'system-audit-filter-api-type': 'API-Typ',
        'system-audit-filter-service': 'Service',
        'system-audit-filter-status': 'Status',
        'system-audit-filter-user': 'Benutzer',
        'system-audit-filter-operation': 'Operation',
        'system-audit-filter-from': 'Von',
        'system-audit-filter-to': 'Bis',
        'system-audit-filter-apply': 'Filtern',
        'system-audit-col-time': 'Zeit',
        'system-audit-col-type': 'Typ',
        'system-audit-col-service': 'Service',
        'system-audit-col-who': 'Wer',
        'system-audit-col-what': 'Was',
        'system-audit-col-status': 'Status',
        'system-audit-col-version': 'Version',
        'system-audit-empty': 'Keine Audit-Einträge gefunden.',
        'system-audit-total': '{{total}} Einträge',
        'system-audit-details-title': 'Audit-Details',
        'system-audit-details': 'Details',
        'system-audit-all': 'Alle',
        'system-audit-success': 'Erfolg',
        'system-audit-failure': 'Fehler',
        'system-audit-previous': 'Zurück',
        'system-audit-next': 'Weiter',
        'enums-EProductPayment-BYCOUNT': 'Nach Anzahl und Tag',
        'enums-EProductPayment-BYGUEST': 'Pro Gast (einmalig)',
        'enums-EProductPayment-BYGUESTANDDAY': 'Pro Gast und Tag',
        'enums-EProductPayment-BYROOM': 'Pro Zimmer (einmalig)',
        'enums-EProductPayment-BYROOMANDDAY': 'Pro Zimmer und Tag',
        'enums-EFacilityPayment-BYCOUNT': 'Nach Anzahl',
        'enums-EFacilityPayment-BYDAY': 'Pro Tag',
        'enums-EFacilityPayment-BYGUEST': 'Pro Gast (einmalig)',

        'enums-EContentHotelServiceTypeAssignment-REGULAR': 'Ganztags',
        'enums-EContentHotelServiceTypeAssignment-SMALL': 'klein',
        'enums-EContentHotelServiceTypeAssignment-MEDIUM': 'Halbtags',

        'enums-EOfferRoomOccupancy-FULLDAY': 'Ganztägig',
        'enums-EOfferRoomOccupancy-MORNING': 'Vormittag',
        'enums-EOfferRoomOccupancy-AFTERNOON': 'Nachmittag',

        'enums-EOfferRoomSeating-UFORM': 'U-Form',
        'enums-EOfferRoomSeating-THEATER': 'Theater',
        'enums-EOfferRoomSeating-PARLAMENT': 'Parlament',
        'enums-EOfferRoomSeating-CIRCLE': 'Kreis',
        'enums-EOfferRoomSeating-BANKETT': 'Bankett',
        'enums-EOfferRoomSeating-COCKTAIL': 'Cocktail',
        'enums-EOfferRoomSeating-BLOCK': 'Block',
        'enums-EOfferRoomSeating-EMPTY': 'keine Bestuhlung',
        'enums-EOfferRoomSeating-OTHER': 'Sonstige',

        'enums-EPricingWeekDays-MON': 'Montag',
        'enums-EPricingWeekDays-TUE': 'Dienstag',
        'enums-EPricingWeekDays-WED': 'Mittwoch',
        'enums-EPricingWeekDays-THU': 'Donnerstag',
        'enums-EPricingWeekDays-FRI': 'Freitag',
        'enums-EPricingWeekDays-SAT': 'Samstag',
        'enums-EPricingWeekDays-SUN': 'Sonntag',

        'enums-EClientGender-MALE': 'Männlich',
        'enums-EClientGender-FEMALE': 'Weiblich',
        'enums-EClientGender-VARIOUS': 'Divers / keine Angabe',

        'enums-EBundleMode-NONE': 'Kein Preis',
        'enums-EBundleMode-PRODUCTS': 'Summe Einzelleistungen',
        'enums-EBundleMode-PRICE': 'Pauschalpreis',

        'enums-EProductImportMergeMode-CLEANUP': 'Leistungskatalog löschen und neu importieren (VORSICHT!!!)',
        'enums-EProductImportMergeMode-IGNORE': 'Nur neue Leistungen importieren',
        'enums-EProductImportMergeMode-UPDATE': 'Gleichnamige Leistungen überschreiben',

        'enums-EContentProductDetailPosition-HEADER': 'Bezeichnung',
        'enums-EContentProductDetailPosition-HEADER-tooltip': 'Wird als Überschrift der Position in den Angebotszeilen verwendet',
        'enums-EContentProductDetailPosition-HEADER_SHORT': 'Bezeichnung (Kurzform)',
        'enums-EContentProductDetailPosition-HEADER_SHORT-tooltip':
          'Wird als Bezeichnung im SEM Widget verwendet (falls unterschiedlich zur Bezeichnung in den Angebotszeilen)',
        'enums-EContentProductDetailPosition-DETAILS': 'Beschreibungstext',
        'enums-EContentProductDetailPosition-DETAILS-tooltip': 'Verwendung als Beschreibungstext der Angebotszeilen',
        'enums-EContentProductDetailPosition-GROUP': 'Gruppierung (nur für White Label)',
        'enums-EContentProductDetailPosition-GROUP-tooltip': 'Gruppierung (nur für White Label)',

        'enums-EContentEmailTemplate-USER_PASSWORD': 'Passwort-Änderung',
        'enums-EContentEmailTemplate-USER_WELCOME': 'Willkommens-E-Mail an neue Benutzer',
        'enums-EContentEmailTemplate-RESET_PASSWORD': 'Passwort vergessen',
        'enums-EContentEmailTemplate-CLIENT_VERIFICATION': 'Verifizierung Kunden-E-Mail-Adresse (an Kunde)',
        'enums-EContentEmailTemplate-OFFER_REMINDER_HOTEL': 'Erinnerung Reservierung (an Hotel)',
        'enums-EContentEmailTemplate-OFFER_CANCELED_CLIENT': 'Stornierung der Buchung (an Kunde)',
        'enums-EContentEmailTemplate-LISTER_REMINDER_REQUEST_HOTEL': 'Erinnerung Seminaranfrage (an Hotel)',
        'enums-EContentEmailTemplate-LISTER_REMINDER_RESERVATION_HOTEL': 'Erinnerung Reservierungsbestätigung (an Hotel)',
        'enums-EContentEmailTemplate-RESERVATION_REMINDER_HOTEL': 'Erinnerung Reservierungsbestätigung (an Hotel)',
        'enums-EContentEmailTemplate-OFFER_REMINDER_CLIENT': 'Erinnerung Reservierung (an Kunde)',
        'enums-EContentEmailTemplate-OFFER_RECEIVED_CLIENT': 'Reservierungsbestätigung (an Kunde)',
        'enums-EContentEmailTemplate-OFFER_CHANGED_RECEIVED_CLIENT': 'Reservierungsbestätigung geändertes Angebot (an Kunde)',
        'enums-EContentEmailTemplate-BOOKING_RECEIVED_HOTEL': 'Neue Seminaranfrage (an Hotel)',
        'enums-EContentEmailTemplate-BOOKING_RECEIVED_CLIENT': 'Anfrage ist in Bearbeitung',
        'enums-EContentEmailTemplate-BOOKING_REJECTED_OVERFLOW_CLIENT': 'Ablehnung der Verfügbarkeit',
        'enums-EContentEmailTemplate-BOOKING_APPROVED_CLIENT': 'Buchung von Kunde bestätigt',
        'enums-EContentEmailTemplate-BOOKING_REJECTED_CLIENT': 'Buchung von Kunde abgelehnt',
        'enums-EContentEmailTemplate-BOOKING_APPROVED_HOTEL': 'Buchung von Kunde bestätigt',
        'enums-EContentEmailTemplate-BOOKING_REJECTED_HOTEL': 'Buchung von Kunde abgelehnt',
        'enums-EContentEmailTemplate-BOOKING_OVERFLOW_APPROVED_CLIENT': 'Übergabe an seminargo bestätigt',
        'enums-EContentEmailTemplate-BOOKING_OVERFLOW_APPROVED_HOTEL': 'Übergabe an seminargo bestätigt',
        'enums-EContentEmailTemplate-BOOKING_OVERFLOW_REJECTED_CLIENT': 'Übergabe an seminargo abgelehnt',
        'enums-EContentEmailTemplate-BOOKING_OVERFLOW_APPROVED_SEMINARGO': 'Übergabe an seminargo',
        'enums-EContentEmailTemplate-DEPOSIT_REMINDER_HOTEL': 'Erinnerung Deposit-Rechnung',
        'enums-EContentEmailTemplate-DEPOSIT_REMINDER_CHECK_HOTEL': 'Erinnerung Deposit-Eingang',
        'enums-EContentEmailTemplate-LISTER_BOOKING_DIRECT_CLIENT': 'Bestätigung Direktbuchung über Hotellister (an Kunde)',
        'enums-EContentEmailTemplate-LISTER_BOOKING_DIRECT_HOTEL': 'Bestätigung Direktbuchung über Hotellister (an Hotel)',
        'enums-EContentEmailTemplate-LISTER_CLIENT_VERIFICATION': 'Kundenverifzierung über Hotellister (an Kunde)',
        'enums-EContentEmailTemplate-BOOKING_DIRECT_CLIENT': 'Bestätigung Direktbuchung über Whitelabel (an Kunde)',
        'enums-EContentEmailTemplate-BOOKING_DIRECT_HOTEL': 'Bestätigung Direktbuchung über Whitelabel (an Hotel)',
        'enums-EContentEmailTemplate-BOOKING_CHANGED_CLIENT': 'Bestätigung geänderte Reservierung (an Kunde)',

        'enums-EContentOfferTemplateType-OFFER': 'Angebotsvorlage',
        'enums-EContentOfferTemplateType-RESERVATION_CONFIRMATION': 'Reservierungsbestätigung',
        'enums-EContentOfferTemplateType-CANCELLATION_CONFIRMATION': 'Stornierungsbestätigung',
        'enums-EContentOfferTemplateType-PROFORMAINVOICE': 'Proforma-Rechnung',
        'enums-EContentOfferTemplateType-CANCELLATIONINVOICE': 'Storno-Rechnung',
        'enums-EContentOfferTemplateType-COMMISSIONINVOICE': 'Kommissions-Rechnung',
        'enums-EContentOfferTemplateType-CLIENTINVOICE': 'Kunden-Rechnung',
        'enums-EContentOfferTemplateType-COSTCENTRE': 'Kostenstellen-Beleg',

        'enums-EOfferSource-LISTER': 'seminargo',
        'enums-EOfferSource-WIDGET': 'SEM',

        'enums-EOfferStatus-CLIENTVERIFICATION': 'Warten auf E-Mail-Verifizierung',
        'enums-EOfferStatus-ON_HOLD': 'In Wartestellung',
        'enums-EOfferStatus-PENDING': 'In Bearbeitung',
        'enums-EOfferStatus-APPROVED': 'Bestätigt',
        'enums-EOfferStatus-REJECTED': 'Abgelehnt',
        'enums-EOfferStatus-ACTIVE': 'Laufend',
        'enums-EOfferStatus-INVOICE': 'Rechnungslegung',
        'enums-EOfferStatus-CLOSED': 'Abgeschlossen',
        'enums-EOfferStatus-DELETED': 'Gelöscht',

        'enums-EOfferVersionStatus-CLIENTVERIFICATION': 'Warten auf E-Mail-Verifizierung',
        'enums-EOfferVersionStatus-WAITFORHOTELAPPROVAL': 'Hotel-Bestätigung ausstehend',
        'enums-EOfferVersionStatus-WAITFORCLIENTAPPROVAL': 'Kunden-Bestätigung ausstehend',
        'enums-EOfferVersionStatus-WAITFORHOTELRESERVATION': 'Rückbestätigung ausstehend',
        'enums-EOfferVersionStatus-DRAFT': 'Entwurf',
        'enums-EOfferVersionStatus-ON_HOLD': 'In Wartestellung',
        'enums-EOfferVersionStatus-APPROVED': 'Bestätigt',
        'enums-EOfferVersionStatus-HOTEL_OVERWRITTEN': 'Bearbeitet durch Hotel',
        'enums-EOfferVersionStatus-IV_GENERATED': 'Rechnung erstellt',
        'enums-EOfferVersionStatus-REJECTED_CLIENT': 'Abgelehnt durch Kunde',
        'enums-EOfferVersionStatus-REJECTED_HOTEL': 'Abgelehnt durch Hotel',
        'enums-EOfferVersionStatus-REJECTED_HOTEL_WITHDRAWN': 'Vom Hotel zurückgezogen',
        'enums-EOfferVersionStatus-CANCELLED_CLIENT': 'Storniert',
        'enums-EOfferVersionStatus-CANCELLED_CLIENT_PENDING_HOTEL': 'Stornierungsbestätigung des Hotels ausstehend',
        'enums-EOfferVersionStatus-CR_EDITING': 'In Bearbeitung durch Kunde',
        'enums-EOfferVersionStatus-CR_WAITFORHOTELAPPROVAL': 'Hotel-Bestätigung (nach Bearbeitung) ausstehend',
        'offer-cr-waitforhotelapproval-warning':
          "Falls Zusatzkosten (zB Stornokosten) durch die Reservierungsänderung des Kunden entstehen, ergänzen Sie diese mittels 'Zusatzleistung hinzufügen/Sonstiges' in der Buchung. Die Rechnungslegung erfolgt separat.",
        'enums-EOfferVersionStatus-CR_WAITFORHOTELAPPROVAL-RESERVATION': 'Hotel-Bestätigung (nach Reservierungsänderung) ausstehend',
        'enums-EOfferVersionStatus-CR_WAITFORCLIENTAPPROVAL': 'Kunden-Bestätigung (nach Bearbeitung) ausstehend',
        'enums-EOfferVersionStatus-CR_WAITFORCLIENTAPPROVAL-RESERVATION': 'Kundenbestätigung (nach Reservierungsänderung) ausstehend',
        'enums-EOfferVersionStatus-CR_OVERWRITTEN': 'Durch Kunde geändert',
        'enums-EOfferVersionStatus-CIV_WAITFORHOTEL': 'Storno - Hotel-Bestätigung ausstehend',
        'enums-EOfferVersionStatus-IV_WAITFORHOTEL': 'Rechnung - Hotel-Bestätigung ausstehend',
        'enums-EOfferVersionStatus-WAITFORSEMAPPROVAL': 'seminargo-Bestätigung ausstehend',
        'enums-EOfferVersionStatus-IV_WAITFORSEMAPPROVAL': 'Rechnung - seminargo-Bestätigung ausstehend',
        'enums-EOfferVersionStatus-IV_WAITFORCLIENTAPPROVAL': 'Rechnung - Kunden-Bestätigung ausstehend',
        'enums-EOfferVersionStatus-IV_APPROVED': 'Rechnung bestätigt',
        'enums-EOfferVersionStatus-CLOSED': 'Abgeschlossen',
        'enums-EOfferVersionStatus-DELETED': 'Gelöscht',

        'enums-EOfferVersionHistoryStatus-CLIENTVERIFICATION_OPEN': 'Warten auf E-Mail-Verifizierung',
        'enums-EOfferVersionHistoryStatus-CLIENTVERIFICATION_DONE': 'E-Mail-Verifizierung abgeschlossen',
        'enums-EOfferVersionHistoryStatus-CLIENT_REMINDER_SENT': 'Erinnerung an Kunde gesendet',
        'enums-EOfferVersionHistoryStatus-HOTEL_REMINDER_SENT': 'Erinnerung an Hotel gesendet',
        'enums-EOfferVersionHistoryStatus-HOTEL_RESERVATION_REMINDER_SENT': 'Erinnerung an Hotel gesendet',
        'enums-EOfferVersionHistoryStatus-WAITFORCLIENTAPPROVAL': 'Kunden-Bestätigung ausstehend',
        'enums-EOfferVersionHistoryStatus-APPROVED_CLIENT': 'Kunde hat bestätigt',
        'enums-EOfferVersionHistoryStatus-APPROVED_CLIENT_BY_HOTEL': 'Hotel hat für Kunde bestätigt',
        'enums-EOfferVersionHistoryStatus-APPROVED_HOTEL': 'Hotel hat Verfügbarkeit bestätigt',
        'enums-EOfferVersionHistoryStatus-APPROVED_SEMINARGO': 'Von seminargo bestätigt',
        'enums-EOfferVersionHistoryStatus-WAITFORSEMAPPROVAL': 'seminargo-Bestätigung ausstehend',
        'enums-EOfferVersionHistoryStatus-RESERVATION_HOTEL': 'Hotel hat Reservierung bestätigt',
        'enums-EOfferVersionHistoryStatus-REJECTED_CLIENT': 'Kunde hat abgelehnt',
        'enums-EOfferVersionHistoryStatus-REJECTED_HOTEL': 'Hotel hat abgelehnt',
        'enums-EOfferVersionHistoryStatus-DELETED': 'Gelöscht',
        'enums-EOfferVersionHistoryStatus-EDITING': 'Angebot unter Bearbeitung',
        'enums-EOfferVersionHistoryStatus-APPROVED_EDITING': 'Reservierungsbestätigung unter Bearbeitung (nach Bestätigung)',
        'enums-EOfferVersionHistoryStatus-WAITFORHOTELAPPROVAL': 'Hotel-Bestätigung ausstehend',
        'enums-EOfferVersionHistoryStatus-OVERFLOW_SENT': 'Overflow-Benachrichtung versendet',
        'enums-EOfferVersionHistoryStatus-OVERFLOW_APPROVED': 'Kunde hat Overflow bestätigt',
        'enums-EOfferVersionHistoryStatus-OVERFLOW_REJECTED': 'Kunde hat Overflow abgelehnt',
        'enums-EOfferVersionHistoryStatus-DEPOSIT_REMINDER_SENT': 'Erinnerung Deposit-Rechnung an Hotel gesendet',
        'enums-EOfferVersionHistoryStatus-DEPOSIT_CHECK_REMINDER_SENT': 'Erinnerung Deposit-Eingang an Hotel gesendet',
        'enums-EOfferVersionHistoryStatus-INTEGRATION_BOOKING_CREATED': 'PMS-Buchung angelegt',
        'enums-EOfferVersionHistoryStatus-INTEGRATION_BOOKING_CONFIRMED': 'PMS-Buchung bestätigt',
        'enums-EOfferVersionHistoryStatus-INTEGRATION_BOOKING_CANCELED': 'PMS-Buchung storniert',
        'enums-EOfferVersionHistoryStatus-INTEGRATION_BOOKING_DELETED': 'PMS-Buchung gelöscht',
        'enums-EOfferVersionHistoryStatus-CR_EDITING': 'Unter Bearbeitung (durch Kunde)',
        'enums-EOfferVersionHistoryStatus-CR_WAITFORAPPROVAL': 'Hotel-Bestätigung ausstehend',
        'enums-EOfferVersionHistoryStatus-CR_APPROVED': 'Hotel hat bestätigt',
        'enums-EOfferVersionHistoryStatus-CR_APPROVED_AUTO': 'Automatisch bestätigt',
        'enums-EOfferVersionHistoryStatus-CR_REJECTED': 'Hotel hat abgelehnt',
        'enums-EOfferVersionHistoryStatus-CR_APPROVED_CLIENT': 'Kunde hat bestätigt',
        'enums-EOfferVersionHistoryStatus-CR_REJECTED_CLIENT': 'Kunde hat abgelehnt',
        'enums-EOfferVersionHistoryStatus-CR_NOTIFICATION_SENT': 'Änderungsanfrage-Benachrichtigung versendet',
        'enums-EOfferVersionHistoryStatus-CANCELLED_CLIENT': 'Storniert',
        'enums-EOfferVersionHistoryStatus-CANCELLATION_CONFIRMED_BY_HOTEL': 'Stornierung bestätigt',
        'enums-EOfferVersionHistoryStatus-CANCELLATION_PERIOD_STARTING_SOON_CLIENT_SENT': 'Stornierungsfrist beginnt bald an Kunde gesendet',
        'enums-EOfferVersionHistoryStatus-EVENT_STARTING_SOON_HOTEL_SENT': 'Veranstaltung beginnt bald an Hotel gesendet',
        'enums-EOfferVersionHistoryStatus-EVENT_STARTED': 'Veranstaltung hat begonnen',
        'enums-EOfferVersionHistoryStatus-EVENT_ENDED': 'Veranstaltung hat geendet',
        'enums-EOfferVersionHistoryStatus-EVENT_PLANNING_HOTEL_SENT': 'Veranstaltungsplanung an Hotel gesendet',
        'enums-EOfferVersionHistoryStatus-EVENT_MISSINGDETAILS_CLIENT_SENT': 'Seminardetails fehlen an Kunde gesendet',
        'enums-EOfferVersionHistoryStatus-EVENT_MISSINGRESERVATION_CLIENT_SENT': 'Fehlende Reservierung zur Buchung an Kunde gesendet',
        'enums-EOfferVersionHistoryStatus-IV_APPROVED_HOTEL': 'Hotel hat bestätigt',
        'enums-EOfferVersionHistoryStatus-IV_APPROVED_SEM': 'seminargo hat bestätigt',
        'enums-EOfferVersionHistoryStatus-IV_REJECTED_SEM': 'seminargo hat abgelehnt',
        'enums-EOfferVersionHistoryStatus-IV_APPROVED_CLIENT': 'Kunde hat bestätigt',
        'enums-EOfferVersionHistoryStatus-IV_REJECTED_CLIENT': 'Kunde hat abgelehnt',
        'enums-EOfferVersionHistoryStatus-IV_SAP_SENT': 'SAP-Übermittlung erfolreich',
        'enums-EOfferVersionHistoryStatus-IV_SAP_FAILED': 'SAP-Übermittlung fehlgeschlagen',
        'enums-EOfferVersionHistoryStatus-IV_CLOSED_HOTEL': 'Buchung vom Hotel abgeschlossen',
        'enums-EOfferVersionHistoryStatus-PROCESS_FAILURE': 'Prozessfehler',
        'enums-EOfferVersionHistoryStatus-REQUEST_SUBMITTED': 'Anfrage gestellt',
        'enums-EOfferVersionHistoryStatus-REQUEST_DATA_CHANGED': 'Anfragedaten geändert',
        'enums-EOfferVersionHistoryStatus-ATTENDEE_DATA_EDITED': 'Teilnehmerinformationen bearbeitet',
        'enums-EOfferVersionHistoryStatus-BILLING_DATA_EDITED': 'Verrechnungsdetails bearbeitet',
        'enums-EOfferVersionHistoryStatus-EVENT_PLANNING_EDITED': 'Veranstaltungsplanung bearbeitet',
        'enums-EOfferVersionHistoryStatus-SERVICE_COUNT_EDITED': 'Anzahl der Leistungen bearbeitet',
        'enums-EOfferVersionHistoryStatus-IV_EDITED_SEM': 'Rechnung wurde von seminargo bearbeitet',
        'enums-EOfferVersionHistoryStatus-RESPONSIBLE_PERSON_TRANSFERRED': 'Ansprechpartner übertragen',
        'enums-EOfferVersionHistoryStatus-RESPONSIBLE_ACCOUNT_TRANSFERRED': 'Buchung übertragen',
        'enums-EOfferVersionHistoryStatus-CANCELLATION_RULES_EDITED': 'Stornierungsregeln bearbeitet',

        // Boolean Values
        'enums-Boolean-TRUE': 'Ja',
        'enums-Boolean-FALSE': 'Nein',

        // Room Values
        'enums-Room-NONE': 'Kein Raum',
        'enums-Room-A': 'Raum A',
        'enums-Room-B': 'Raum B',
        'enums-Room-C': 'Raum C',
        'enums-Room-D': 'Raum D',
        'enums-Room-E': 'Raum E',
        'enums-Room-F': 'Raum F',
        'enums-Room-G': 'Raum G',
        'enums-Room-H': 'Raum H',
        'enums-Room-SINGLE': 'Personen im Einzelzimmer',
        'enums-Room-DOUBLE': 'Personen im Doppelzimmer',
        'enums-Room-ROOM_NONE': 'Kein Raum',
        'enums-Room-ROOM_SINGLE': 'Personen im Einzelzimmer',
        'enums-Room-ROOM_DOUBLE': 'Personen im Doppelzimmer',

        // Product lookup codes
        'enums-EProductLookupCode-VOLLPENSIONSPAUSCHALE_EZ': 'Vollpensionspauschale Einzelzimmer',
        'enums-EProductLookupCode-VOLLPENSIONSPAUSCHALE_DZ': 'Vollpensionspauschale Doppelzimmer',

        'enums-EOfferVersionContentBlockFormFieldType-TEXT': 'Einzeiliger Text',
        'enums-EOfferVersionContentBlockFormFieldType-TEXTAREA_PLAINTEXT': 'Mehrzeiliger Text',
        'enums-EOfferVersionContentBlockFormFieldType-TEXTAREA_RICHTEXT': 'Formatierter Text',

        'enums-EIntSystem-DUMMY': 'Testdaten (Musterhotel)',
        'enums-EIntSystem-APALEO': 'Apaleo',
        'enums-EIntSystem-ELITEFO': 'Elite Hotelsoftware',
        'enums-EIntSystem-PROTELIDS': 'Protel IDS',
        'enums-EIntSystem-MEETINGPACKAGE': 'Meeting Package',

        'enums-EIntBookingMode-SEM': 'Keine Direktbuchung (Bearbeitung in SEM)',
        'enums-EIntBookingMode-DIRECT_OFFER': 'Direktbuchung als Reservierung (Bearbeitung im PMS)',
        'enums-EIntBookingMode-DIRECT_CONFIRM': 'Direktbuchung mit automatischer Bestätigung',

        'enums-EIntegrationLogStatus-PENDING': 'Offen',
        'enums-EIntegrationLogStatus-RUNNING': 'Läuft',
        'enums-EIntegrationLogStatus-SUCCESS': 'Erfolgreich',
        'enums-EIntegrationLogStatus-FAILED': 'Fehler',
        'enums-EIntegrationLogStatus-SKIPPED': 'Übersprungen',

        'enums-EIntegrationLogTrigger-INIT': 'Erstabgleich',
        'enums-EIntegrationLogTrigger-SCHEDULED': 'Zeitgesteuert',
        'enums-EIntegrationLogTrigger-USER': 'Benutzer',
        'enums-EIntegrationLogTrigger-PMS': 'PMS',
        'enums-EIntegrationLogTrigger-PROCESS': 'Automatisiert',

        'enums-EContentHotelAttribute-CATEGORY': 'Kategorie',
        'enums-EContentHotelAttribute-CATEGORY_NONE': 'keine',
        'enums-EContentHotelAttribute-CATEGORY_ONE_STARS': '1*',
        'enums-EContentHotelAttribute-CATEGORY_TWO_STARS': '2*',
        'enums-EContentHotelAttribute-CATEGORY_THREE_STARS': '3*',
        'enums-EContentHotelAttribute-CATEGORY_FOUR_STARS': '4*',
        'enums-EContentHotelAttribute-CATEGORY_FIVE_STARS': '5*',
        'enums-EContentHotelAttribute-CATEGORY_FIVE_STARS_SUPERIOR': '5*s',
        'enums-EContentHotelAttribute-CATEGORY_FOUR_STARS_SUPERIOR': '4*s',
        'enums-EContentHotelAttribute-CATEGORY_THREE_STARS_SUPERIOR': '3*s',
        'enums-EContentHotelAttribute-LOCATION': 'Lage',
        'enums-EContentHotelAttribute-LOCATION_CITY': 'Stadt',
        'enums-EContentHotelAttribute-LOCATION_COUNTRY': 'Im Grünen',
        'enums-EContentHotelAttribute-ROOM': 'Zimmer',
        'enums-EContentHotelAttribute-ROOM_AIRCONDITIONER': 'Klimaanlage',
        'enums-EContentHotelAttribute-ROOM_BALCONY': 'Balkon',
        'enums-EContentHotelAttribute-ROOM_BARRIER_FREE': 'Barrierefrei',
        'enums-EContentHotelAttribute-ROOM_HAIRDRYER': 'Föhn',
        'enums-EContentHotelAttribute-ROOM_MINIBAR': 'Minibar',
        'enums-EContentHotelAttribute-ROOM_SAFE': 'Safe',
        'enums-EContentHotelAttribute-ROOM_TV': 'TV',
        'enums-EContentHotelAttribute-ROOM_INTERNET_WIFI': 'WLAN',
        'enums-EContentHotelAttribute-ROOM_INTERNET_FIBER': 'Glasfaser',
        'enums-EContentHotelAttribute-ROOM_SMOKING': 'Raucherzimmer',
        'enums-EContentHotelAttribute-ROOM_WORKTABLE': 'Arbeitstisch',
        'enums-EContentHotelAttribute-ROOM_TRAINERROOM': 'Trainerzimmer',
        'enums-EContentHotelAttribute-SEMINAR': 'Seminarraum',
        'enums-EContentHotelAttribute-SEMINAR_ACCESSABLE_BY_CAR': 'Zufahrt mit PKW',
        'enums-EContentHotelAttribute-SEMINAR_AIRCONDITIONER': 'Klimaanlage',
        'enums-EContentHotelAttribute-SEMINAR_DAYLIGHT': 'Tageslicht',
        'enums-EContentHotelAttribute-SEMINAR_INTERNET_WIFI': 'WLAN',
        'enums-EContentHotelAttribute-SEMINAR_INTERNET_FIBER': 'Glasfaser',
        'enums-EContentHotelAttribute-SEMINAR_BREAK_AREA': 'Pausenbereich',
        'enums-EContentHotelAttribute-SEMINAR_GROUPWORK': 'Gruppenarbeit',
        'enums-EContentHotelAttribute-DESIGN': 'Design',
        'enums-EContentHotelAttribute-DESIGN_MODERN': 'Modern',
        'enums-EContentHotelAttribute-DESIGN_BUSINESS': 'Business',
        'enums-EContentHotelAttribute-DESIGN_TRADITIONAL': 'Traditionell',
        'enums-EContentHotelAttribute-DESIGN_RURAL': 'Ländlich',
        'enums-EContentHotelAttribute-ACTIVITY': 'Aktivitäten',
        'enums-EContentHotelAttribute-ACTIVITY_BIKE_RENTAL': 'Fahrradverleih',
        'enums-EContentHotelAttribute-ACTIVITY_GOLF': 'Golf',
        'enums-EContentHotelAttribute-ACTIVITY_MINIGOLF': 'Minigolf',
        'enums-EContentHotelAttribute-ACTIVITY_BOWLING': 'Bowling',
        'enums-EContentHotelAttribute-ACTIVITY_TENNIS': 'Tennis',
        'enums-EContentHotelAttribute-ACTIVITY_RUNNING': 'Laufen',
        'enums-EContentHotelAttribute-ACTIVITY_BILLIARD': 'Billard',
        'enums-EContentHotelAttribute-ACTIVITY_FITNESS': 'Fitnesscenter',
        'enums-EContentHotelAttribute-ACTIVITY_HIGH_ROPES_COURSE': 'Hochseilgarten',
        'enums-EContentHotelAttribute-ACTIVITY_TEAM_COOKING': 'Teamkochen',
        'enums-EContentHotelAttribute-ACTIVITY_RAFT_BUILDING': 'Floßbau',
        'enums-EContentHotelAttribute-ACTIVITY_RAFTING': 'Rafting',
        'enums-EContentHotelAttribute-ACTIVITY_SKI_SLOPE': 'Skipiste',
        'enums-EContentHotelAttribute-ACTIVITY_TOBOGGAN_RUN': 'Rodelbahn',
        'enums-EContentHotelAttribute-CATERING': 'Verpflegung',
        'enums-EContentHotelAttribute-CATERING_LUNCH': 'Mittagsmenü',
        'enums-EContentHotelAttribute-CATERING_DINNER': 'Abendmenü',
        'enums-EContentHotelAttribute-CATERING_BREAKFAST': 'Frühstück',
        'enums-EContentHotelAttribute-CATERING_HALFBOARD': 'Halbpension',
        'enums-EContentHotelAttribute-CATERING_FULLBOARD': 'Vollpension',
        'enums-EContentHotelAttribute-CATERING_OUTDOOR': 'Außengastronomie',
        'enums-EContentHotelAttribute-CATERING_RESTAURANT': 'Restaurant',
        'enums-EContentHotelAttribute-WELLNESS': 'Wellness',
        'enums-EContentHotelAttribute-WELLNESS_INDOOR_POOL': 'Innenpool',
        'enums-EContentHotelAttribute-WELLNESS_OUTDOOR_POOL': 'Außenpool',
        'enums-EContentHotelAttribute-WELLNESS_THERMAL': 'Therme',
        'enums-EContentHotelAttribute-WELLNESS_MASSAGE': 'Massage',
        'enums-EContentHotelAttribute-WELLNESS_WHIRLPOOL': 'Whirlpool',
        'enums-EContentHotelAttribute-WELLNESS_INFRARED_CABIN': 'Infrarotkabine',
        'enums-EContentHotelAttribute-WELLNESS_SAUNA': 'Sauna',
        'enums-EContentHotelAttribute-WELLNESS_STEAM_BATH': 'Dampfbad',
        'enums-EContentHotelAttribute-PARKING_FREE': 'Kostenlose Parkplätze',
        'enums-EContentHotelAttribute-PARKING_PAID': 'Kostenpflichtige Parkplätze',
        'enums-EContentHotelAttribute-HOTELFACILITY': 'Hotelausstattung',
        'enums-EContentHotelAttribute-HOTELFACILITY_24_ROOMSERVICE': '24h Zimmerservice',
        'enums-EContentHotelAttribute-HOTELFACILITY_BARRIER_FREE': 'Barrierefrei',
        'enums-EContentHotelAttribute-HOTELFACILITY_CLOTHES_CLEANING': 'Wäscherei',
        'enums-EContentHotelAttribute-HOTELFACILITY_COPY_FAX': 'Kopieren/Fax',
        'enums-EContentHotelAttribute-HOTELFACILITY_ELECTRIC_CHARGING_STATION': 'Elektroauto-Ladestation',
        'enums-EContentHotelAttribute-HOTELFACILITY_ELEVATOR': 'Fahrstuhl',
        'enums-EContentHotelAttribute-HOTELFACILITY_GREEN_AREA': 'Grünanlage',
        'enums-EContentHotelAttribute-HOTELFACILITY_INTERNET_WIFI': 'WLAN',
        'enums-EContentHotelAttribute-HOTELFACILITY_IT_ROOM': 'IT-Raum',
        'enums-EContentHotelAttribute-HOTELFACILITY_PRESS_ROOM': 'Presse-Raum',
        'enums-EContentHotelAttribute-HOTELFACILITY_PUBLIC_INTERNET': 'Öffentliches Internet',
        'enums-EContentHotelAttribute-HOTELFACILITY_SECRETARY': 'Sekretariat',
        'enums-EContentHotelAttribute-HOTELFACILITY_SHUTTLE': 'Shuttle-Service',
        'enums-EContentHotelAttribute-HOTELFACILITY_SMOKING_AREA': 'Raucherbereich',
        'enums-EContentHotelAttribute-HOTELFACILITY_WARDROBE': 'Garderobe',

        'enums-EContentHotelAttribute-ECOLABEL': 'Umweltzeichen',
        'enums-EContentHotelAttribute-ECOLABEL_AUSTRIAN_ECOLABEL': 'Österreichisches Umweltzeichen',
        'enums-EContentHotelAttribute-ECOLABEL_EU_ECOLABEL': 'EU Ecolabel',
        'enums-EContentHotelAttribute-ECOLABEL_GREEN_KEY': 'Green Key',
        'enums-EContentHotelAttribute-ECOLABEL_GREEN_MEETINGS_AND_EVENTS': 'Green Meetings & Events',

        'enums-EHotelWidgetModeType-SLIM': 'Schmales Widget (nur Button)',
        'enums-EHotelWidgetModeType-EXPANDED': 'Aufgeklapptes Widget',

        'enums-EContentHotelTextType-HOTEL_DESCRIPTION': 'Hotelbeschreibung',
        'enums-EContentHotelTextType-ARRIVAL_CAR': 'Anreise mit dem Auto',
        'enums-EContentHotelTextType-ARRIVAL_TRAIN': 'Anreise mit dem Zug',
        'enums-EContentHotelTextType-ARRIVAL_FLIGHT': 'Anreise mit dem Flugzeug',
        'enums-EContentHotelTextType-HOTEL_MEETING_ROOMS_COUNT': 'Anzahl Seminarräume',
        'enums-EContentHotelTextType-HOTEL_ROOMS_COUNT': 'Anzahl Zimmer',
        'enums-EContentHotelTextType-HOTEL_PERSONS_COUNT': 'Anzahl Personen',
        'enums-EContentHotelTextType-MIG_TECH': 'Technik',
        'enums-EContentHotelTextType-MIG_ROUTE': 'Anfahrt',
        'enums-EContentHotelTextType-MIG_FREETIME': 'Freizeit',
        'enums-EContentHotelTextType-MIG_SR_DESC': 'Seminarraum Beschreibung',
        'enums-EContentHotelTextType-MIG_ROOM': 'Zimmer',
        'enums-EContentHotelTextType-MIG_CULINARY': 'Kulinarik',

        'enums-EOfferCommissionBase-GROSS': 'Bruttobetrag',
        'enums-EOfferCommissionBase-NET': 'Nettobetrag',

        // Enum translations for booking status used on Overflows page
        'enums-EHLBookingStatus-PENDING': 'offen',
        'enums-EHLBookingStatus-APPROVED': 'reserviert',
        'enums-EHLBookingStatus-DELETED': 'abgelehnt',
        'enums-EHLBookingStatus-CANCELLED': 'storniert',

        // Error codes
        'graphqlerror-PERMISSION_DENIED': 'Ihnen fehlen die erforderlichen Berechtigungen',
        'graphqlerror-FOREIGN_KEY_CONSTRAINT_FAILED':
          'Es wurden für den Speichervorgang erforderliche Datensätze nicht gefunden. Möglicherweise wurden von einem anderen Benutzer in der Zwischenzeit Änderungen vorgenommen. Bitte laden Sie das Browser-Fenster neu.',
        'graphqlerror-CONTENT_SPACE_SUCCESSOR': 'Es ist nur die Auswahl eines Eintrags auf einem untergeordneten Verwaltungsbereichs erlaubt',
        'graphqlerror-CONTENT_SPACE_ANCESTOR': 'Es ist nur die Auswahl eines Eintrags auf einem übergeordnetem Verwaltungsbereichs erlaubt',

        // Tasks
        'tasks-page-title': 'Tasks',

        // Dashboard

        'dashboard-offerlist-chart-header': 'Buchungsverlauf',
        'dashboard-offerlist-chart-pending': 'In Bearbeitung',
        'dashboard-offerlist-chart-approved': 'Bestätigt',
        'dashboard-offerlist-chart-rejected': 'Abgelehnt',
        'dashboard-offerlist-chart-week': 'Woche',
        'dashboard-offerlist-chart-month': 'Monat',
        'dashboard-offerlist-chart-halfyear': 'Halbjahr',
        'dashboard-offerlist-chart-year': 'Jahr',
        'dashboard-offerlist-chart-count': 'Anzahl',
        'dashboard-offerlist-chart-price': 'Summe',
        'dashboard-offerlist-chart-space-all': 'Alle Verwaltungsbereiche',
        'dashboard-offerlist-chart-hotels-all': 'Alle Hotels',
        'dashboard-offerlist-chart-tooltip-header': 'Buchungsdatum: {{date}}',
        'dashboard-offerlist-chart-tooltip-count': '{{count}} Buchung(en)',
        'dashboard-offerlist-chart-tooltip-price': '{{price}}',
        'dashboard-offerlist-export': 'Dateiexport Buchungsliste (*.xlsx)',

        // Content Texts

        'content-product-header-position': 'Position',
        'content-product-header-hotel': 'Hotel',
        'content-product-header-language': 'Sprache',
        'content-product-header-text': 'Text',
        'content-product-add': 'Neuen Textbaustein anlegen',
        'graphqlerror-CONTENT_LANGUAGE_UNIQUE':
          'Die Kombination aus Position, Hotel, Sprache und Verwaltungsbereich muss eindeutig sein. Es wurden andere Datensätze gefunden, auf die dieses zutrifft.',

        'content-tabs-header-products': 'Textbausteine',
        'content-tabs-header-medias': 'Mediendateien',
        'content-tabs-header-offertemplates': 'Pdf/Html-Vorlagen',
        'content-tabs-header-hotels': 'Hotels',
        'content-tabs-header-emailtemplates': 'E-Mail-Vorlagen',

        'contentproducts-list-page-title': 'Textbausteine',
        'contentproducts-list-header-sku': 'SKU',
        'contentproducts-list-header-product': 'Leistung',
        'contentproducts-list-header-position': 'Textposition',
        'contentproducts-list-header-hotel': 'Hotel',
        'contentproducts-list-add': 'Textbaustein hinzufügen',

        'contentproduct-sku': 'SKU',
        'contentproduct-position': 'Textposition',
        'contentproduct-hotel': 'Hotel',
        'contentproduct-texts-tab': 'Texte',
        'contentproduct-text-language': 'Sprache',
        'contentproduct-text-text': 'Text',
        'contentproduct-text-add': 'Text hinzufügen',
        'contentproduct-save': 'Textbaustein speichern',
        'contentproduct-saved': 'Textbaustein gespeichert',

        'clients-list-page-title': 'Kunden',
        'clients-tabs-header-clients': 'Ansprechpersonen',
        'clients-tabs-header-companies': 'Firmen',

        'publicclient-save': 'Ansprechpartner speichern',
        'publicclient-saved': 'Ansprechpartner gespeichert',
        'publicclient-phone': 'Telefon',
        'publicclient-mobile': 'Mobil',
        'publicclient-phone-country-code': 'Ländervorwahl Telefon',
        'publicclient-mobile-country-code': 'Ländervorwahl Mobil',
        'publicclient-email': 'E-Mail',
        'publicclient-company': 'Zugewiesene Firma',
        'publicclient-firstname': 'Vorname',
        'publicclient-lastname': 'Nachname',
        'publicclient-title': 'Titel',
        'publicclient-position': 'Position',
        'publicclient-responsibility': 'Verantwortungsbereich',
        'publicclient-main-language': 'Hauptsprache',
        'publicclient-gender': 'Geschlecht',
        'publicclient-gender-male': 'Männlich',
        'publicclient-gender-female': 'Weiblich',
        'publicclient-gender-various': 'Divers',
        'publicclient-street': 'Straße',
        'publicclient-street-number': 'Hausnummer',
        'publicclient-zip': 'PLZ',
        'publicclient-city': 'Ort',
        'publicclient-country': 'Land',
        'publicclient-state': 'Bundesland',
        'publicclient-address-supplement': 'Adresszusatz',
        'publicclient-section-required': 'Pflichtfelder',
        'publicclient-section-optional': 'Optionale Informationen',
        'publicclient-section-contact': 'Kontaktinformationen',
        'publicclient-section-address': 'Adresse',
        'publicclient-section-comments': 'Interne Kommentare',
        'publicclient-section-phone': 'Telefonnummer',
        'publicclient-section-mobile': 'Mobilnummer',
        'publicclient-language-german': 'Deutsch',
        'publicclient-language-english': 'Englisch',
        'publicclient-country-germany': 'Deutschland',
        'publicclient-country-austria': 'Österreich',
        'publicclient-country-switzerland': 'Schweiz',
        'publicclient-country-usa': 'USA',
        'publicclient-country-uk': 'Großbritannien',
        'publicclient-country-france': 'Frankreich',
        'publicclient-no-selection': 'Keine Auswahl',
        'publicclient-is-master': 'Master',
        'publicclient-new-title': 'Neuer Ansprechpartner',
        'publicclient-button-new': 'Neuer Ansprechpartner',
        'publicclient-back-to-company': 'Zurück zur Firma',
        'publicclient-active-contacts': 'Aktive Ansprechpersonen',
        'publicclient-inactive-contacts': 'Inaktive Ansprechpersonen',
        'publicclient-deactivate-title': 'Ansprechpartner deaktivieren',
        'publicclient-deactivate-question':
          'Nach der Deaktivierung kann sich diese Person nicht mehr mit ihrer E-Mail-Adresse anmelden. Sie können den Account jederzeit wieder aktivieren.',
        'publicclient-deactivate-error-pending': 'Deaktivierung nicht möglich: Es liegen noch Vorgänge im Status "Angefragt" vor.',
        'publicclient-deactivate-error-reserved': 'Deaktivierung nicht möglich: Es liegen noch Vorgänge im Status "Reserviert" vor.',
        'publicclient-deactivate-error-invoices': 'Deaktivierung nicht möglich: Es liegen noch Vorgänge im Status "Rechnungen" vor.',
        'publicclient-deactivated-success': 'Ansprechpartner wurde erfolgreich deaktiviert.',
        'publicclient-reactivate-title': 'Ansprechpartner wiederherstellen',
        'publicclient-reactivate-question': 'Möchten Sie diesen Ansprechpartner wirklich wiederherstellen?',
        'publicclient-reactivated-success': 'Ansprechpartner wurde erfolgreich wiederhergestellt.',
        'publicclient-blocked-dialog-title': 'Ansprechperson kann nicht deaktiviert werden',
        'publicclient-blocked-dialog-message':
          'Diese Ansprechperson hat noch aktive Buchungen und kann daher nicht inaktiv gesetzt werden. Bitte löschen Sie zuerst alle Buchungen oder übertragen Sie diese auf eine andere Ansprechperson.',
        'publicclient-blocked-bookings-title': 'Betroffene Buchungen:',
        'publicclient-blocked-booking-id': 'Booking ID',
        'publicclient-blocked-dialog-close': 'Zurück',

        'publicclient-sudo': 'Als dieser Kunde anmelden',
        'publicclient-confirm-sudo': 'Sie melden sich in seminargo als dieser Kunde an. Fortsetzen ?',
        'publicclient-confirm-sudo-title': 'Anmeldung als Kunde',
        'publicclient-sudo-failed': 'Anmeldung als Kunde fehlgeschlagen',
        'publicclient-address-type': 'Adresstyp',
        'publicclient-use-company-address': 'Firmenadresse verwenden',
        'publicclient-use-own-address': 'Eigene Adresse verwenden',
        'publicclient-company-address-preview': 'Firmenadresse',

        'publiccompany-save': 'Firma speichern',
        'publiccompany-saved': 'Firma gespeichert',
        'publiccompany-is-premium': 'Premium-Kunde',
        'publiccompany-name': 'Firmenname',
        'publiccompany-address': 'Adresse',
        'publiccompany-zip': 'PLZ',
        'publiccompany-city': 'Ort',
        'publiccompany-country': 'Land',
        'publiccompany-show-prices-in-net': 'Preise in Netto anzeigen',
        'publiccompany-list-header-address': 'Adresse',
        'publiccompany-list-header-clients-count': 'Anzahl Ansprechpersonen',
        'publiccompany-list-header-premium': 'Premium-Kunde',
        'publiccompany-list-header-customer-type': 'Kundenart',
        'publiccompany-list-header-id': 'ID',
        'publiccompany-list-header-email': 'E-Mail',
        'publiccompany-list-header-contacts-count': 'Anzahl Ansprechpersonen',
        'publiccompany-list-header-status': 'Status',

        'booking-offers-header-hotel': 'Hotel',
        'booking-offers-header-status': 'Status',

        'bookings-list-header-refcode': 'BID',
        'bookings-list-header-title': 'Titel',
        'bookings-list-header-company': 'Firma',
        'bookings-list-header-client': 'Kunde',
        'bookings-list-header-hotel': 'Hotel',
        'bookings-list-header-assignee': 'Verantwortlich',
        'booking-info-assign-to-me': 'Buchung an mich zuweisen',
        'booking-info-assign-employee': 'Mitarbeiter zuweisen',
        'booking-info-change-assignee': 'Verantwortlichen ändern',
        'booking-info-select-employee': 'Mitarbeiter auswählen',
        'booking-info-assignee-unassigned': 'Nicht zugewiesen',
        'booking-info-assignee': 'Verantwortlich',
        'booking-page-header': 'Buchung',
        'booking-info-status': 'Status',
        'booking-info-bid': 'BID',
        'booking-info-bid-copy': 'BID kopieren',
        'booking-info-bid-copied': 'BID kopiert!',
        'booking-info-title': 'Titel',
        'booking-info-company': 'Firma',
        'booking-info-contact': 'Ansprechperson',
        'booking-info-login-as-client': 'Als Kunde anmelden',
        'booking-hotels-section-title': 'Hotelübersicht',
        'booking-todos-section-title': 'ToDos',
        'booking-comments-section-title': 'Interne Notizen',
        'booking-comment-add': 'Kommentar hinzufügen',
        'booking-comment-edit': 'Kommentar bearbeiten',
        'booking-comment-delete': 'Kommentar löschen',
        'booking-comment-delete-confirm': 'Möchten Sie diesen Kommentar wirklich löschen?',
        'booking-comment-delete-success': 'Kommentar erfolgreich gelöscht',
        'booking-comment-save-success': 'Kommentar erfolgreich gespeichert',
        'booking-comment-created-at': 'Erstellt am',
        'booking-comment-created-by': 'Ersteller',
        'booking-comment-description': 'Beschreibung',
        'booking-comment-description-helper': 'Mindestens 10 Zeichen',
        'booking-comment-validation-min': 'Beschreibung muss mindestens 10 Zeichen lang sein',
        'booking-comment-no-comments': 'Noch keine Notizen vorhanden',
        'booking-comments-error-loading': 'Fehler beim Laden der Notizen',
        'booking-not-found': 'Buchung nicht gefunden',

        'search-quicksearch-label': 'Schnellsuche',

        'publiccompany-list-header-name': 'Firmenname',
        'publiccompany-new-title': 'Neue Firma',
        'publiccompany-main-language': 'Hauptsprache',
        'publiccompany-language-german': 'Deutsch',
        'publiccompany-language-english': 'Englisch',
        'publiccompany-section-basic': 'Grundinformationen',
        'publiccompany-section-address': 'Adresse',
        'publiccompany-section-contact': 'Kontakt',
        'publiccompany-section-business': 'Geschäftsinformationen',
        'publiccompany-section-comments': 'Interne Kommentare',
        'publiccompany-comment-add': 'Kommentar hinzufügen',
        'publiccompany-comment-edit': 'Kommentar bearbeiten',
        'publiccompany-comment-delete': 'Kommentar löschen',
        'publiccompany-comment-delete-confirm': 'Möchten Sie diesen Kommentar wirklich löschen?',
        'publiccompany-comment-delete-success': 'Kommentar erfolgreich gelöscht',
        'publiccompany-comment-save-success': 'Kommentar erfolgreich gespeichert',
        'publiccompany-comment-description': 'Beschreibung',
        'publiccompany-comment-description-helper': 'Mindestens 10 Zeichen erforderlich',
        'publiccompany-comment-validation-min': 'Beschreibung muss mindestens 10 Zeichen haben',
        'publiccompany-comment-booking-relevant': 'Buchungsrelevant',
        'publiccompany-comment-booking-relevant-helper': 'Kennzeichnet den Kommentar als wichtig für Buchungen',
        'publiccompany-comment-no-comments': 'Keine Kommentare vorhanden',
        'publiccompany-comments-error-loading': 'Fehler beim Laden der Kommentare',
        'publiccompany-street': 'Straße',
        'publiccompany-street-number': 'Hausnummer',
        'publiccompany-state': 'Bundesland',
        'publiccompany-address-supplement': 'Zusatz',
        'publiccompany-section-phone': 'Telefonnummer',
        'publiccompany-phone-country-code': 'Landesvorwahl',
        'publiccompany-phone-number': 'Nummer',
        'publiccompany-email': 'Mailadresse',
        'publiccompany-industry': 'Branche',
        'publiccompany-website': 'URL',
        'publiccompany-key-account-manager': 'Key Account Manager',
        'publiccompany-no-selection': 'Keine Auswahl',
        'publiccompany-premium': 'Premium',
        'publiccompany-show-prices-net': 'Preise netto anzeigen',
        'publiccompany-privacy-policy-accepted': 'Datenschutzbestimmungen akzeptiert',
        'publiccompany-button-save': 'Speichern',
        'publiccompany-button-new': 'Neue Firma anlegen',
        'publiccompany-button-back': 'Zurück zu Firmen',
        'publiccompany-created': 'Firma erfolgreich angelegt',
        'publiccompany-updated': 'Firma erfolgreich gespeichert',
        'publiccompany-status-active': 'Aktiv',
        'publiccompany-status-inactive': 'Inaktiv',
        'publiccompany-deactivate-button': 'Firma deaktivieren',
        'publiccompany-deactivate-title': 'Firma deaktivieren',
        'publiccompany-deactivate-question':
          'Durch die Deaktivierung der Firma werden alle zugehörigen Ansprechpersonen deaktiviert, diese können sich dann nicht mehr mit ihrer E-Mail-Adresse anmelden. Sie können die Firma jederzeit wieder aktivieren.',
        'publiccompany-deactivated-success': 'Firma wurde erfolgreich deaktiviert.',
        'publiccompany-reactivate-button': 'Firma reaktivieren',
        'publiccompany-reactivate-title': 'Firma reaktivieren',
        'publiccompany-reactivate-question':
          'Mit diesem Schritt wird die Firma wieder aktiviert, alle vorhandenen Ansprechpersonen bleiben deaktiviert und müssen bei Bedarf manuell aktiviert werden.',
        'publiccompany-reactivated-success': 'Firma wurde erfolgreich reaktiviert.',
        'publiccompany-blocked-dialog-title': 'Firma kann nicht deaktiviert werden',
        'publiccompany-blocked-dialog-message':
          'Ansprechpersonen in dieser Firma haben noch aktive Buchungen und können nicht deaktiviert werden. Bitte löschen Sie alle offenen Buchungen.',
        'publiccompany-blocked-contact': 'Ansprechperson',
        'publiccompany-inactive-contacts-warning':
          'Diese Firma ist inaktiv. Es können keine neuen Ansprechpersonen angelegt oder bestehende Ansprechpersonen reaktiviert werden.',
        'publiccompany-inactive-contact-create-blocked': 'Für inaktive Firmen können keine neuen Ansprechpersonen angelegt werden.',
        'publiccompany-inactive-contact-action-blocked': 'Ansprechpersonen können nur bei aktiven Firmen reaktiviert werden.',
        'publiccompany-tab-basic-data': 'Basisdaten',
        'publiccompany-tab-contracts': 'Verträge',
        'publiccompany-tab-contacts': 'Ansprechpartner',
        'publiccompany-tab-billing': 'Verrechnung',
        'publiccompany-coming-soon-contracts': 'Coming soon - Verträge werden hier angezeigt',
        'publiccompany-coming-soon-billing': 'Coming soon - Verrechnung wird hier angezeigt',
        'publiccompany-contacts-name': 'Name',
        'publiccompany-contacts-email': 'E-Mail',
        'publiccompany-contacts-phone': 'Telefon',
        'publiccompany-contacts-premium': 'Premium',
        'publiccompany-country-germany': 'Deutschland',
        'publiccompany-country-austria': 'Österreich',
        'publiccompany-country-switzerland': 'Schweiz',
        'publiccompany-country-usa': 'USA/Kanada',
        'publiccompany-country-uk': 'UK',
        'publiccompany-country-france': 'Frankreich',
        'clients-list-header-name': 'Name',
        'clients-list-header-company': 'Firma',
        'clients-list-header-email': 'E-Mail',
        'clients-list-header-phone': 'Telefon',
        'clients-list-header-premium': 'Premium-Kunde',
        'clients-list-header-master': 'Master',

        'search-tabs-header-clients': 'Ansprechpersonen',
        'search-tabs-header-companies': 'Firmen',
        'search-tabs-header-bookings': 'Buchungen',
        'search-tabs-header-hotels': 'Hotels',
        'search-min-length-error': 'Suchbegriff muss mindestens {{count}} Zeichen haben',
        'search-companies-contact-person-singular': 'Ansprechperson',
        'search-companies-contact-person-plural': 'Ansprechpersonen',

        'hotels-list-page-title': 'Hotels',
        'hotels-list-header-name': 'Name',
        'hotels-list-header-businesscountry': 'Land',
        'hotels-list-header-refcode': 'Referenzcode',
        'hotels-list-header-integration': 'PMS-Verknüpfung',
        'hotels-list-header-lister': 'seminargo',
        'hotels-list-header-widget': 'SEM',
        'hotels-list-header-address': 'Adresse',
        'hotels-list-header-email': 'E-Mail',
        'hotels-list-lister-url': 'Öffnen in seminargo',
        'hotels-list-test-url': 'SEM testen',
        'hotels-list-test-url-no-rooms': 'Ohne Zimmerbuchung',
        'hotels-list-add': 'Hotel anlegen',
        'hotel-selection-search-placeholder': 'Hotel oder Bereich suchen...',

        active: 'Aktiv',
        inactive: 'Inaktiv',

        'hotel-name': 'Hotelname',
        'hotel-refcode': 'Referenzcode',
        'hotel-extrefcode': 'Externer Referenzcode',
        'hotel-ispartner': 'seminargo Partnerhotel',
        'hotel-details': 'Details',
        'hotel-businessname': 'Offizieller Firmenname',
        'hotel-businessaddress1': 'Straße',
        'hotel-businessaddress2': 'Adresszusatz 1',
        'hotel-businessaddress3': 'Adresszusatz 2',
        'hotel-businessaddress4': 'Geschäftsadresse 4',
        'hotel-businesscountry': 'Land',
        'hotel-businesszip': 'PLZ',
        'hotel-businesscity': 'Stadt',
        'hotel-businessstate': 'Bundesland',
        'hotel-businessemail': 'Geschäftliche E-Mail-Adresse',
        'hotel-businessphone': 'Geschäftliche Telefonnummer',
        'hotel-businessurl': 'Geschäftliche Webseite',

        'hotel-contact-firstname': 'Vorname',
        'hotel-contact-lastname': 'Nachname',
        'hotel-contact-title': 'Titel',
        'hotel-contact-job': 'Position',
        'hotel-contact-gender': 'Geschlecht',
        'hotel-contact-phone': 'Telefon',
        'hotel-contact-email': 'Email',
        'hotel-contact-maincontact': 'Hauptkontakt',
        'hotel-contact-techcontact': 'Technischer Kontakt',
        'hotel-contact-bookingcontact': 'Buchungs-Kontakt',
        'hotel-contact-add': 'Ansprechpartner hinzufügen',
        'hotel-contact-type': 'Kontakttyp',

        'hotel-different-invoice-address': 'abweichende Rechnungsadresse',
        'hotel-invoiceaddress1': 'Adresszusatz 1',
        'hotel-invoiceaddress2': 'Adresszusatz 2',
        'hotel-invoiceaddress3': 'Geschäftsadresse 3',
        'hotel-invoiceaddress4': 'Straße',
        'hotel-comments-section-title': 'Interne Kommentare',
        'hotel-comment-add': 'Kommentar hinzufügen',
        'hotel-comment-edit': 'Kommentar bearbeiten',
        'hotel-comment-delete': 'Kommentar löschen',
        'hotel-comment-delete-confirm': 'Möchten Sie diesen Kommentar wirklich löschen?',
        'hotel-comment-delete-success': 'Kommentar erfolgreich gelöscht',
        'hotel-comment-save-success': 'Kommentar erfolgreich gespeichert',
        'hotel-comment-description': 'Beschreibung',
        'hotel-comment-description-helper': 'Mindestens 10 Zeichen erforderlich',
        'hotel-comment-validation-min': 'Beschreibung muss mindestens 10 Zeichen haben',
        'hotel-comment-no-comments': 'Noch keine Kommentare vorhanden',
        'hotel-comments-error-loading': 'Fehler beim Laden der Kommentare',
        'hotel-comment-created-at': 'Erstellt am',
        'hotel-comment-creator': 'Ersteller',
        'hotel-comment-description-label': 'Beschreibung',
        'hotel-comment-actions': 'Aktionen',

        'enums-EContentHotelContactType-MAIN_CONTACT': 'Hauptkontakt',
        'enums-EContentHotelContactType-MANAGEMENT': 'Management',
        'enums-EContentHotelContactType-BANQUET_DEPARTMENT': 'Bankettabteilung',
        'enums-EContentHotelContactType-RESERVATION': 'Reservierung',
        'enums-EContentHotelContactType-SALES': 'Sales',
        'enums-EContentHotelContactType-ACCOUNTING': 'Buchhaltung',

        'hotel-offertemplate': 'Angebotsvorlage',
        'hotel-offerexpirationdays': 'Angebotslaufzeit (Tage)',
        'hotel-enableavailabilitycheck': 'Verfügbarkeitsprüfung aktivieren',

        'hotel-reminder-days-section': 'Zeitfenster Konfiguration',
        'hotel-offernotapprovedreminderclient': 'Versende Erinnerung an Kunden (nach Tagen)',
        'hotel-offernotapprovedreminderhotel': 'Versende Erinnerung an Hotel (nach Tage)',
        'hotel-depositreminderhotel': 'Deposit-Rechnung Erinnerung (Tage vor Beginn)',
        'hotel-depositremindercheckhotel': 'Deposit-Eingang Erinnerung (nach Tage)',

        'hotel-reminder-receiver-section': 'Haupt-E-Mail-Adresse für Benachrichtigungen',
        'hotel-notificationemail': 'E-Mail-Adresse für Benachrichtigungen *',
        'hotel-notificationemail-helper':
          'Die E-Mail erhält alle System-Reminder für dieses Hotel. Diese Einstellung ist verpflichtend, damit keine wichtigen Benachrichtigungen verloren gehen.',

        'hotel-reminder-type-section': 'Zusätzliche E-Mail-Empfänger pro Erinnerungs-Typ',
        'hotel-reminder-type-section-helper':
          'Hier können Sie festlegen, welche zusätzlichen E-Mail-Adressen bestimmte Benachrichtigungs-Typen erhalten sollen.',

        'hotel-reminder-type': 'Erinnerungs-Typ',
        'hotel-reminder-description': 'Beschreibung',
        'hotel-reminder-receiver': 'Zusätzliche Empfänger',
        'hotel-reminder-edit-header': 'E-Mail-Empfänger für "{{type}}" bearbeiten',
        'hotel-reminder-edit-header-helper':
          'Fügen Sie zusätzliche E-Mail-Adressen hinzu, die diese Benachrichtigung erhalten sollen. Die Haupt-Email-Adresse {{notificationEmail}} erhält diese Benachrichtigung immer.',
        'hotel-reminder-edit-add-header': 'Neue E-Mail-Adresse hinzufügen',
        'hotel-remidner-edit-receivers': 'Empfänger-Email-Adressen',

        'hotel-reminder-type-LISTER_NONBINDING_REQUEST_HOTEL': 'Anfrage eingelangt',
        'hotel-reminder-type-LISTER_REMINDER_REQUEST_HOTEL': 'Erinnerung bei unbearbeiteter Anfrage',
        'hotel-reminder-type-LISTER_REJECTED_OFFER_HOTEL': 'Anfrage durch Buchenden abgelehnt',
        'hotel-reminder-type-LISTER_RESERVATION_HOTEL': 'Reservierung',
        'hotel-reminder-type-LISTER_CR_HOTEL': 'Änderung',
        'hotel-reminder-type-CLASSIC_CR_HOTEL': 'Reservierungsänderung (Classic)',
        'hotel-reminder-type-LISTER_TITLE_CHANGE_HOTEL': 'Änderung Seminartitel',
        'hotel-reminder-type-LISTER_BOOKING_INFORMATION_HOTEL': 'Checkliste',
        'hotel-reminder-type-LISTER_BOOKING_REMINDER_START_SOON_HOTEL': 'Erinnerung',
        'hotel-reminder-type-LISTER_BOOKING_REMINDER_END_TODAY_HOTEL': 'Rechnungsabschluss',
        'hotel-reminder-type-LISTER_BOOKING_CANCELLATION_HOTEL': 'Storno',
        'hotel-reminder-type-LISTER_AVAILABILITY_APPROVED_CLIENT': 'Angebot bestätigt durch das Hotel',
        'hotel-reminder-type-LISTER_RESERVATION_APPROVED_CLIENT': 'Reservierung rückbestätigt durch das Hotel',

        'hotel-reminder-type-LISTER_INVOICE_HOTEL': 'Ausgangsrechnungen',
        'hotel-reminder-type-LISTER_CREDIT_NOTE_HOTEL': 'Gutschriften',

        'hotel-reminder-description-LISTER_NONBINDING_REQUEST_HOTEL': 'Wird bei einer neuen Anfrage versendet',
        'hotel-reminder-description-LISTER_REMINDER_REQUEST_HOTEL': 'Wird versendet, wenn eine Anfrage nicht beantwortet wurde nach 5h',
        'hotel-reminder-description-LISTER_REJECTED_OFFER_HOTEL': 'Wird bei Ablehnung einer Anfrage durch den Buchenden versendet',
        'hotel-reminder-description-LISTER_RESERVATION_HOTEL': 'Wird bei Reservierung durch den Buchenden versendet',
        'hotel-reminder-description-LISTER_CR_HOTEL': 'Wird versendet, wenn der Buchende Änderungen an der Reservierung vornimmt.',
        'hotel-reminder-description-CLASSIC_CR_HOTEL': 'Wird versendet, wenn ein Classic-Kunde eine Reservierungsänderung anfragt.',
        'hotel-reminder-description-LISTER_TITLE_CHANGE_HOTEL': 'Wird versendet, wenn der Buchende den Seminartitel anpasst',
        'hotel-reminder-description-LISTER_BOOKING_INFORMATION_HOTEL': 'Enthält die Checkliste für das Seminar',
        'hotel-reminder-description-LISTER_BOOKING_REMINDER_START_SOON_HOTEL': 'Erinnert an den zeitnahen Beginn der Veranstaltung',
        'hotel-reminder-description-LISTER_BOOKING_REMINDER_END_TODAY_HOTEL':
          'Wird versendet, wenn die Buchung beendet ist und offiziell abgeschlossen werden soll.',
        'hotel-reminder-description-LISTER_BOOKING_CANCELLATION_HOTEL': 'Wird bei Stornierung der Buchung versendet.',
        'hotel-reminder-description-LISTER_AVAILABILITY_APPROVED_CLIENT': 'Wird an den Kunden gesendet, wenn das Hotel das Angebot bestätigt hat.',
        'hotel-reminder-description-LISTER_RESERVATION_APPROVED_CLIENT':
          'Wird an den Kunden gesendet, wenn das Hotel die Reservierung rückbestätigt hat.',

        'hotel-reminder-description-LISTER_INVOICE_HOTEL': 'Wird für den Versand von Ausgangsrechnungen verwendet.',
        'hotel-reminder-description-LISTER_CREDIT_NOTE_HOTEL': 'Wird für den Versand von Gutschriften (Rechnungen im Namen des Hotels) verwendet.',
        'hotel-cancellation-details-tab': 'Stornobedingungen',
        'hotel-cancellation-details-partner':
          'Als seminargo-Vertragspartner können Sie eigene Stornobedingungen festlegen, wenn mehr als 50 % der Zimmerkapazität des Hotels oder über 30 Teilnehmer gebucht werden. Ansonsten gelten die Standard-Stornobedingungen von seminargo.',
        'hotel-cancellation-details-nopartner':
          'Sie sind kein seminargo-Vertragspartner und können somit Ihre Stornobedingungen frei festlegen. Sollten keine eigenen Stornobedingungen festgelegt werden, kommen die Standard-Stornobedingungen von seminargo zu tragen. Diese können von Ihnen bei der Angebotslegung angepasst werden.',
        'hotel-cancellation-details-defaultrules-premiumclient': 'Standard-Stornobedingungen für seminargo-Premiumkunden',
        'hotel-cancellation-details-defaultrules-defaultclient': 'Standard-Stornobedingungen für seminargo-Vermittlungskunden',
        'hotel-cancellation-details-rule': 'Regel',
        'hotel-cancellation-details-cancellation-days': 'Stornofrist (Tage vor Beginn)',
        'hotel-cancellation-details-cancellation-capacity': 'Auslastung (von - bis in %)',
        'hotel-cancellation-details-cancellation-overnight': 'Übernachtungsgäste (von - bis)',
        'hotel-cancellation-details-cancellation-totalguests': 'Seminargäste (von - bis)',
        'hotel-cancellation-category-overnight': 'Übernachtungsgäste',
        'hotel-cancellation-category-seminar': 'Seminargäste',
        'hotel-cancellation-details-tolerance': 'Toleranz (in %)',
        'hotel-cancellation-details-rate': 'Stornorate (in %)',
        'hotel-cancellation-agreed-waiver': 'Vereinbarter Stornoverzicht (in %)',
        'hotel-cancellation-agreed-waiver-info': 'Der vereinbarte Stornoverzicht wird im Falle einer Kundenstornierung kostenfrei gutgeschrieben.',
        'hotel-cancellation-details-add': 'Stornobedingung hinzufügen',
        'hotel-cancellation-details-add-defaults': 'Standard-Stornobedingungen übernehmen und anpassen',

        'hotel-iban': 'IBAN',
        'hotel-uid': 'UID',
        'hotel-bic': 'BIC',

        'hotel-lister-settings': 'seminargo Einstellungen',
        'hotel-showinlister': 'Im öffentlichen seminargo Verzeichnis anzeigen',
        'hotel-lister-servicetypes': 'Verfügbare Leistungspakete',
        'hotel-lister-servicetype-add': 'Leistungspaket festlegen',

        'hotel-rating': 'Bewertung',
        'hotel-slug': 'SEO-Kürzel (URL)',
        'hotel-location-latitude': 'Koordinaten (Breitengrad)',
        'hotel-location-longitude': 'Koordinaten (Längengrad)',
        'hotel-location-fetch-coords': 'Koordinaten von Adresse automatisch ermitteln',
        'hotel-location-openmap': 'In Karte öffnen',

        'hotel-settings-location-tab': 'Standort',
        'hotel-settings-texts-tab': 'Textbausteine',
        'hotel-settings-texts-type': 'Typ',
        'hotel-settings-texts-details': 'Text',
        'hotel-settings-texts-add': 'Textbaustein hinzufügen',
        'hotel-settings-texts-language': 'Sprache',

        'hotel-settings-meeting-rooms-tab': 'Seminarräume',
        'hotel-settings-meeting-rooms-add': 'Seminarraum hinzufügen',
        'hotel-settings-meeting-rooms-name': 'Name',
        'hotel-settings-meeting-rooms-daylight': 'Tageslicht',
        'hotel-settings-meeting-rooms-airconditioning': 'Klimaanlage',
        'hotel-settings-meeting-rooms-area': 'Fläche',
        'hotel-settings-meeting-rooms-capacityUForm': 'U-Form',
        'hotel-settings-meeting-rooms-capacityTheater': 'Theater',
        'hotel-settings-meeting-rooms-capacityParlament': 'Parlament',
        'hotel-settings-meeting-rooms-capacityCircle': 'Sesselkreis',
        'hotel-settings-meeting-rooms-capacityBankett': 'Bankett',
        'hotel-settings-meeting-rooms-capacityCocktail': 'Cocktail',
        'hotel-settings-meeting-rooms-capacityBlock': 'Block',

        'hotel-settings-capacities-tab': 'Kapazitäten',
        'hotel-settings-meeting-rooms': 'Seminarräume',
        'hotel-settings-capacities-maxCapacityRooms': 'Maximale Anzahl Zimmer',
        'hotel-settings-capacities-maxCapacityPeople': 'Maximale Anzahl Personen',

        'hotel-settings-attributes-tab': 'Attribute',
        'hotel-settings-attributes-parking': 'Parkplatz',
        'hotel-settings-attributes-parking-free': 'Kostenlose Parkplätze',
        'hotel-settings-attributes-parking-free-spaces': 'Anzahl kostenloser Parkplätze',
        'hotel-settings-attributes-parking-paid': 'Kostenpflichtige Parkplätze',
        'hotel-settings-attributes-parking-paid-spaces': 'Anzahl kostenpflichtiger Parkplätze',
        'hotel-settings-cancellation-tab': 'Stornobedingungen',

        'hotel-settings-media-tab': 'Medien-Auswahl',
        'hotel-settings-medias-name': 'Name',
        'hotel-settings-medias-description': 'Beschreibung',
        'hotel-settings-medias-add': 'Medium hinzufügen',

        'hotel-settings-agb-tab': 'AGB',
        'hotel-settings-agb-title': 'Allgemeine Geschäftsbedingungen (AGB)',
        'hotel-settings-agb-filename': 'Dateiname',
        'hotel-settings-agb-size': 'Dateigröße',
        'hotel-settings-agb-upload-date': 'Hochgeladen am',
        'hotel-settings-agb-uploader': 'Hochgeladen durch',
        'hotel-settings-agb-download': 'Herunterladen',
        'hotel-settings-agb-upload': 'AGB hochladen',
        'hotel-settings-agb-drop-pdf': 'Ziehen Sie hier PDF-Datei hierher',
        'hotel-settings-agb-or': 'oder',
        'hotel-settings-agb-upload-select-file': 'Datei auswählen',
        'hotel-settings-agb-upload-hint': 'Nur PDF-Dateien + Maximale Dateigröße: 10MB',
        'hotel-settings-agb-uploading': 'Wird hochgeladen...',
        'hotel-settings-agb-no-file': 'Noch keine AGB vorhanden',
        'hotel-settings-agb-pdf-required': 'Bitte wählen Sie eine PDF-Datei aus',
        'hotel-settings-agb-upload-success': 'AGB erfolgreich hochgeladen',
        'hotel-settings-agb-upload-failed': 'Fehler beim Hochladen der AGB',
        'hotel-settings-agb-file-read-failed': 'Fehler beim Lesen der Datei',
        'hotel-settings-agb-download-failed': 'Fehler beim Herunterladen der AGB',

        'hotel-settings-commission-tab': 'Provisionssätze',
        'hotel-commission-rates-info': 'Die hier eingetragenen Werte überschreiben die Basiseinstellungen der Provision für dieses Hotel.',
        'hotel-commission-customer-type': 'Kundentyp',
        'hotel-commission-base': 'Berechnungsbasis',
        'hotel-commission-percentage': 'Provisionshöhe (%)',
        'hotel-commission-valid-from': 'Gültig ab',
        'hotel-commission-remark': 'Anmerkung',
        'hotel-commission-add': 'Provisionssatz hinzufügen',
        'hotel-commission-dialog-title': 'Provisionssatz bearbeiten',
        'hotel-commission-save': 'Speichern',
        'hotel-commission-saved': 'Provisionssatz erfolgreich gespeichert',
        'hotel-commission-deleted': 'Provisionssatz erfolgreich gelöscht',
        'hotel-commission-error-duplicate': 'Ein Provisionssatz mit dem gleichen Kundentyp und Gültigkeitsdatum existiert bereits',
        'hotel-commission-validation-past-date': 'Das Gültigkeitsdatum kann nicht in der Vergangenheit liegen',

        'hotel-lister-status-READY': 'Vollständig konfiguriert',
        'hotel-lister-status-INACTIVE': 'Deaktiviert',
        'hotel-lister-status-NO_TAX_RATES': 'Steuersätze wurden das Land nicht festgelegt',
        'hotel-lister-status-NO_OFFER_TEMPLATE': 'Keine Angebotsvorlage zugeordnet',
        'hotel-lister-status-NO_SERVICE_TYPES': 'Keine veröffentlichte Leistungen zugeordnet',
        'hotel-lister-status-NO_PRICELIST': 'Keine veröffentlichte Preisliste zugeordnet',
        'hotel-lister-status-SERVICE_TYPE_INCOMPLETE': 'Das Leistungspaket entspricht nicht den seminargo-Anforderungen',
        'hotel-lister-status-NO_ASSIGNMENT': 'Es müssen alle Leistungspakete zugeordnet werden (Small / Medium / Regular)',
        'hotel-lister-status-PRICELIST_INCOMPLETE': 'Preisliste unvollständig',

        'hotel-widget-status-READY': 'Vollständig konfiguriert',
        'hotel-widget-status-INACTIVE': 'Deaktiviert',
        'hotel-widget-status-NO_TAX_RATES': 'Steuersätze wurden das Land nicht festgelegt',
        'hotel-widget-status-NO_OFFER_TEMPLATE': 'Keine Angebotsvorlage zugeordnet',
        'hotel-widget-status-NO_SERVICE_TYPES': 'Keine veröffentlichte Leistungen zugeordnet',
        'hotel-widget-status-NO_PRICELIST': 'Keine veröffentlichte Preisliste zugeordnet',
        'hotel-widget-status-SERVICE_TYPE_INCOMPLETE': 'Das Leistungspaket entspricht nicht den SEM-Anforderungen',
        'hotel-widget-status-PRICELIST_INCOMPLETE': 'Preisliste unvollständig',

        'hotel-showinwidget': 'Als SEM Widget anzeigen',

        'hotel-widget-settings': 'SEM-Widget Einstellungen',
        'hotel-widget-code': 'Code Snippet zum Einbinden in die Webseite',
        'hotel-widget-advanced-options': 'Erweiterte Optionen (Event Tracking)',
        'hotel-widget-event-tracking-title': 'Event Tracking für das SEM-Widget',
        'hotel-widget-event-tracking-description':
          'Das Widget feuert Events bei wichtigen Interaktionen (Tab-Wechsel, Klicks, Buchungsformular, E-Mail-Verifikation). Diese können auf der Webseite abonniert werden, um sie z.B. an Google Analytics, GTM oder eigene Analytics-Systeme weiterzugeben.',
        'hotel-widget-event-tracking-snippet-label': 'Beispiel-Integration',
        'hotel-widget-event-tracking-events-label': 'Verfügbare Events',
        'hotel-widget-event-tracking-copy': 'Code kopieren',
        'hotel-widget-event-tracking-copied': 'Code in die Zwischenablage kopiert',
        'hotel-widget-display-mode': 'Widget-Anzeigemodus',
        'hotel-widgeturl': 'White Label URL',
        'hotel-widget-preview': 'Vorschau',
        'hotel-widgeturl-helper':
          'Die URL, unter der das White Label Widget erreichbar ist. Links in E-Mails verweisen auf die URL anstatt auf die URL von SEM.',
        'hotel-widget-no-rooms': 'Ohne Zimmerbuchung',
        'hotel-widget-no-rooms-helper': 'Wenn aktiviert, wird die Zimmerbuchung im White Label Widget nicht angezeigt.',
        'hotel-widget-privacy-url': 'Datenschutz URL',
        'hotel-widget-privacy-url-helper': 'Wenn angegeben, wird ein Link zum Datenschutz im White Label Widget verlinkt.',
        'hotel-widget-terms-url': 'AGB URL',
        'hotel-widget-terms-url-helper': 'Wenn angegeben, wird ein Link zu den AGB im White Label Widget verlinkt.',
        'hotel-widget-color': 'Widget Primärfarbe',
        'hotel-widget-color-helper': 'Die Primärfarbe wird für die Buttons im White Label Widget verwendet.',
        'hotel-widget-color-enabled': 'Setze Widget Primärfarbe',
        'hotel-widget-slim-mode': 'Nur als Button anzeigen (für schmale Bereiche)',
        'hotel-widget-slim-mode-helper': 'Wenn aktiviert, wird das Widget nie aufgeklappt.',
        'hotel-widget-expanded-mode': 'Aufgeklapptes Widget (wenn es sich von der Browserbreite ausgeht)',
        'hotel-widget-expanded-mode-helper': 'Wenn aktiviert, wird das Widget schon beim ersten Erscheinen aufgeklappt.',
        'hotel-servicetypes-widget': 'Verfügbare Leistungspakete',

        'hotel-save': 'Hotel-Einstellungen speichern',
        'hotel-validation-error': 'Speichern nicht möglich. Bitte Pflichtfelder in folgenden Tabs ausfüllen: {{tabs}}',
        'hotel-updated': 'Hotel-Einstellungen gespeichert',
        'hotel-updated-by-another-user': 'Das Hotel wurde von einem anderen Benutzer geändert. Bitte laden Sie die Seite neu.',
        'hotel-lister-slug-required': 'Bitte zuerst einen Slug eingeben.',
        'hotel-wordpress-url-not-found': 'Für diesen Slug wurde keine Wordpress-URL gefunden.',
        'generic-error-unknown': 'Unbekannter Fehler',
        'hotel-created': 'Hotel angelegt',

        'hotel-confirm-delete':
          'Das Hotel sowie dafür erstellte Textbausteine werden unwiderruflich gelöscht und können nicht wiederhergestellt werden.',
        'hotel-confirm-delete-title': 'Hotel löschen',
        'hotel-delete': 'Hotel löschen',
        'hotel-deleted': 'Hotel wurde gelöscht',
        'graphqlerror-HOTEL_DELETE_NOT_POSSIBLE_STILL_ASSIGNED': 'Das Hotel {{name}} kann nicht gelöscht werden, da es noch in Verwendung ist.',
        'graphqlerror-HOTEL_SLUG_ALREADY_EXISTS': 'Das SEO-Kürzel {{- slug}} wird bereits von einem anderen Hotel verwendet',
        'hotel-groups-assignment': 'Kettenzuordnung',
        'hotel-groups-assignment-helper': 'Wählen Sie die Ketten aus, zu denen dieses Hotel gehören soll.',
        'hotel-groups-select': 'Ketten auswählen',
        'hotel-groups-updated': 'Kettenzuordnung wurde aktualisiert',
        'hotel-groups-none-available': 'Keine Ketten verfügbar',
        'hotel-groups-none-assigned': 'Keine Ketten zugeordnet',
        'groups-available': 'Verfügbare Ketten',
        'groups-assigned': 'Zugewiesene Ketten',
        'no-results': 'Keine Ergebnisse',
        'generic-of': 'von',
        'main-filter-search-placeholder': 'Hotel oder Bereich suchen...',
        'sidebar-bookings': 'Buchungen',
        'sidebar-overflows': 'Overflows',
        'sidebar-administration': 'Administration',
        'version-label-branch': 'Branch-Version',
        'version-label-build-date': 'Build-Date',
        'version-label-commit-hash': 'Commit-Hash',
        'main-load-spaces-hotels-error': 'Hotels/Bereiche konnten nicht geladen werden. Bitte Seite neu laden. Details in der Browser-Konsole:',
        'contracts-title': 'Verträge',
        'contracts-title-single': 'Vertrag',
        'contracts-create-new': 'Neuen Vertrag anlegen',
        'contracts-header-type': 'Typ',
        'contracts-header-validity': 'Gültigkeit',
        'contracts-header-interval': 'Intervall',
        'contracts-header-amount': 'Betrag',
        'contracts-header-status': 'Status',
        'contracts-header-next-invoice': 'Nächste Rechnung',
        'contracts-header-created-at': 'Erstellt am',
        'contracts-header-actions': 'Aktionen',
        'contracts-empty': 'Keine Verträge vorhanden',
        'contracts-delete-title': 'Vertrag löschen',
        'contracts-delete-confirm': 'Möchten Sie diesen Vertrag wirklich löschen?',
        'contracts-delete-success': 'Vertrag erfolgreich gelöscht',
        'contracts-update-success': 'Vertrag erfolgreich aktualisiert',
        'contracts-create-success': 'Vertrag erfolgreich erstellt',
        'contracts-invoice-create-success': 'Rechnung erfolgreich erstellt',
        'contracts-document-upload-success': 'Dokument erfolgreich hochgeladen',
        'contracts-document-delete-success': 'Dokument erfolgreich gelöscht',
        'contracts-not-found': 'Vertrag nicht gefunden',
        'contracts-details': 'Vertragsdetails',
        'contracts-field-type': 'Vertragstyp',
        'contracts-field-valid-from': 'Gültig von',
        'contracts-field-valid-to': 'Gültig bis',
        'contracts-field-interval': 'Rechnungsintervall',
        'contracts-field-amount': 'Betrag (EUR)',
        'contracts-field-description': 'Beschreibung',
        'contracts-status-sap': 'Status & SAP-Integration',
        'contracts-no-invoice-planned': 'Keine geplant',
        'contracts-sap-status': 'SAP-Status',
        'contracts-sap-last-sync': 'Letzte SAP-Synchronisierung',
        'contracts-sap-error': 'SAP-Fehler',
        'contracts-upload-pdf': 'PDF hochladen',
        'contracts-no-documents': 'Keine Dokumente vorhanden',
        'contracts-invoice-history': 'Rechnungshistorie',
        'contracts-create-manual-invoice': 'Manuelle Rechnung erstellen',
        'contracts-invoice-date': 'Rechnungsdatum',
        'contracts-triggered-at': 'Ausgelöst am',
        'contracts-trigger-auto': 'Automatisch',
        'contracts-trigger-manual': 'Manuell',
        'contracts-trigger-on-create': 'Bei Erstellung',
        'contracts-no-invoices': 'Keine Rechnungen vorhanden',
        filename: 'Dateiname',
        size: 'Größe',
        'uploaded-at': 'Hochgeladen am',
        'updated-at': 'Zuletzt aktualisiert',
        trigger: 'Trigger',
        error: 'Fehler',
        documents: 'Dokumente',
        uploading: 'Hochladen...',
        download: 'Herunterladen',
        create: 'Erstellen',
        new: 'Neu',
        action: 'Aktion',
        actions: 'Aktionen',
        'accounting-area-vat-id-invalid': 'Ungültiges UID-Format (z.B. ATU12345678)',
        add: 'Hinzufügen',
        added: 'Hinzugefügt',
        remove: 'Entfernen',
        edit: 'Bearbeiten',
        cancel: 'Abbrechen',
        apply: 'Übernehmen',
        confirm: 'Bestätigen',
        restore: 'Wiederherstellen',
        loading: 'Laden...',
        'common-search-placeholder': 'Suchen...',
        'error-boundary-title': 'Es ist ein Fehler aufgetreten',
        'error-boundary-description': 'Bitte laden Sie die Seite neu oder kontaktieren Sie den Support.',
        'error-boundary-reload': 'Seite neu laden',
        'reports-statistics-section-hotel': 'Hotel',
        'reports-statistics-section-customer': 'Kunde',
        'contracts-agreement-type-symposion-hotel': 'Symposion Hotel Vereinbarung',
        'contracts-agreement-type-partner': 'Partner-Vereinbarung',
        'contracts-agreement-type-none': 'keine Vereinbarung',
        'contact-subjects-title': 'Kontaktrelevante Betreffe',
        'contact-subjects-manage-title': 'Kontaktrelevante Betreffe verwalten',
        'contact-subjects-add': 'Neuer Betreff',
        'contact-subjects-info':
          'Hier können Sie die verfügbaren kontaktrelevanten Betreffe für Kommentare verwalten. Deaktivierte Betreffe werden nicht mehr in neuen Kommentaren angezeigt, bleiben aber in bestehenden Kommentaren erhalten.',
        'contact-subjects-empty': 'Keine Betreffe gefunden',
        'contact-subjects-edit': 'Betreff bearbeiten',
        'contact-subjects-create': 'Neuen Betreff erstellen',
        'contact-subjects-name-required': 'Name *',
        'contact-subjects-name-placeholder': 'z.B. Premium Vertrag',
        'contact-subjects-validation-name-required': 'Name ist erforderlich',
        'contact-subjects-validation-name-min': 'Name muss mindestens 3 Zeichen lang sein',
        'contact-subjects-validation-name-duplicate': 'Ein Betreff mit diesem Namen existiert bereits',
        'contact-subjects-updated': 'Betreff erfolgreich aktualisiert',
        'contact-subjects-created': 'Betreff erfolgreich erstellt',
        'contact-subjects-deactivate-confirm': 'Möchten Sie den Betreff "{{name}}" wirklich deaktivieren?',
        'contact-subjects-deactivated': 'Betreff erfolgreich deaktiviert',
        'comment-dialog-title-edit': 'Kommentar bearbeiten',
        'comment-dialog-created-at': 'Erstellt am',
        'comment-dialog-created-by': 'Erstellt von',
        'comment-dialog-current-user': 'Aktueller Benutzer',
        'comment-dialog-unknown-user': 'Unbekannt',
        'comment-dialog-booking-relevant': 'Kommentar ist buchungsrelevant',
        'comment-dialog-contact-relevant': 'Kommentar ist kontaktrelevant',
        'comment-dialog-contact-type': 'Kontaktart',
        'comment-dialog-subject': 'Betreff',
        'comment-dialog-subject-required': 'Betreff *',
        'comment-dialog-no-subject': 'Kein Betreff',
        'comment-dialog-subjects-refresh': 'Betreffe aktualisieren',
        'comment-dialog-subjects-manage': 'Betreffe verwalten',
        'comment-dialog-contact-person': 'Ansprechperson',
        'comment-dialog-no-contact-person': 'Keine Ansprechperson',
        'comment-dialog-description-required': 'Kommentar *',
        'comment-dialog-description-placeholder': 'Geben Sie hier Ihren Kommentar ein...',
        'comment-dialog-validation-description-required': 'Kommentar ist erforderlich',
        'comment-dialog-validation-subject-required': 'Betreff ist erforderlich',
        'comment-contact-type-personal': 'Persönlich',
        'comment-contact-type-email': 'Per E-Mail',
        'comment-contact-type-phone': 'Telefonisch',
        'comment-contact-type-info': 'Info',
        'comment-edited-at': 'Bearbeitet',
        'comment-booking-relevant': 'Buchungsrelevant',
        'comment-booking': 'Buchung',
        'comment-contact-relevant': 'Kontaktrelevant',
        'comment-relevance': 'Relevanz',
        'booking-comments-booking-relevant-count': '{{count}} buchungsrelevante Kommentare',
        'booking-comments-company-note-tooltip': 'Firmen-Notiz (buchungsrelevant)',
        'offer-documents-title': 'Zusätzliche Dokumente',
        'offer-documents-upload-pdf': 'PDF Dokument hochladen',
        'offer-documents-upload-hint': 'Nur PDF-Dateien sind erlaubt. Dokumente werden in Bestätigungs-E-Mails mitgesendet.',
        'offer-documents-uploaded-at': 'Hochgeladen am',
        'offer-documents-only-pdf': 'Nur PDF-Dateien sind erlaubt',
        'offer-documents-load-error': 'Fehler beim Laden der Dokumente',
        'offer-documents-upload-success': 'Dokument erfolgreich hochgeladen',
        'offer-documents-delete-success': 'Dokument erfolgreich gelöscht',
        'offer-documents-empty-with-upload': 'Noch keine Dokumente hochgeladen. Klicken Sie auf "PDF Dokument hochladen" um Dokumente hinzuzufügen.',
        'offer-documents-empty-readonly': 'Keine zusätzlichen Dokumente für dieses Angebot.',
        'admin-offer-documents-loading': 'Dokumente werden geladen...',
        'admin-offer-documents-load-error': 'Fehler beim Laden der Dokumente',
        'admin-offer-documents-empty': 'Keine Dokumente vom Kunden hochgeladen.',
        'admin-offer-documents-title': 'Dokumente zum Seminar',
        'admin-offer-documents-uploaded-at': 'Hochgeladen am',
        'publicclient-resend-invitation': 'Einladung erneut senden',
        'usergroup-title': 'User Gruppen',
        'usergroup-no-active-contacts': 'Keine aktiven Ansprechpersonen für diese Firma vorhanden.',
        'contracts-only-pdf-allowed': 'Nur PDF-Dateien sind erlaubt',
        'contracts-download-url-missing': 'Keine Download-URL verfügbar',
        'contracts-hotel-id-missing': 'Hotel-ID fehlt für die Vertragserstellung',
        'contracts-invoice-date-required': 'Bitte wählen Sie ein Rechnungsdatum aus',
        'contracts-interval-ONCE': 'Einmalig',
        'contracts-interval-M1': 'Monatlich',
        'contracts-interval-M3': 'Quartalsweise',
        'contracts-interval-M6': 'Halbjährlich',
        'contracts-interval-M12': 'Jährlich',
        'contracts-sap-PENDING': 'Ausstehend',
        'contracts-sap-SENT': 'Gesendet',
        'contracts-sap-FAILED': 'Fehlgeschlagen',
        'contracts-type-SYMPOSION_HOTEL_OFFER': 'Symposion Hotel Angebotslegung',
        'contracts-type-SYMPOSION_HOTEL_CONTRACT': 'Symposion Hotel Vertrag',
        'contracts-type-PARTNER_HOTEL_CONTRACT': 'Partner Hotel Vertrag',
        'contracts-type-FRIEND_HOTEL_CONTRACT': 'Friend Hotel Vertrag',
        'contracts-type-FRIEND_LOCATION_CONTRACT': 'Friend Location Vertrag',
        'contracts-type-CATALOG_REGISTRATION': 'Kataloganmeldung',
        'contracts-type-NEWSLETTER': 'Newsletter',
        'contracts-type-HOTEL_OF_THE_WEEK': 'Hotel der Woche',
        'contracts-type-DIGITAL_SALES_BOOSTER': 'Digital Sales Booster',
        'contracts-type-DIGITAL_SALES_BOOSTER_SETUP_FEE': 'Digital Sales Booster Set-Up Fee',
        'contracts-type-SEM_BUSINESS': 'SEM Business',
        'contracts-type-SEM_SETUP_FEE': 'SEM Set-Up Fee',
        'contracts-type-SEM_SERVICE_FEE': 'SEM Servicegebühr',
        'contracts-type-CATALOG_RIGHT_SIDE': 'Katalog rechte Seite',
        'contracts-type-CATALOG_LOGO': 'Katalog Logo',
        'contracts-type-CATALOG_TRANSLATION': 'Katalog Übersetzung',
        'contracts-type-CATALOG_SECOND_CHANGE': 'Katalog 2. Änderung',
        'contracts-type-CATALOG_QR': 'Katalog QR',
        'contracts-type-MEETING_PACKAGE_IMPORT': 'Meeting Package Import',
        'contracts-type-BASIC_AGREEMENT': 'Basic-Vereinbarung',
        'contracts-type-POP_UP': 'Pop-Up',
        'contracts-type-TEASER': 'Teaser',
        'company-contract-type-PREMIUM_CUSTOMER_CONTRACT': 'Premium Kunden Vertrag',
        'company-contract-type-SPONSORING_CATALOG': 'Sponsoring Katalog',
        'company-contract-type-SPONSORING_INDUSTRY_MEETING': 'Sponsoring Branchentreff',
        'company-contract-title-new': 'Neuer Firmenvertrag',
        'company-contract-title-edit': 'Firmenvertrag bearbeiten',
        'company-contract-title-view': 'Firmenvertrag anzeigen',
        'system-cache-flushed-rebuilding': 'Cache geleert, Neuaufbau läuft im Hintergrund...',
        'system-cache-flushed': 'Cache erfolgreich geleert',
        'system-widget-form-reset': 'Widget-Formularzustand erfolgreich zurückgesetzt',
        'system-cache-rebuild-cancelled': 'Cache-Aufbau abgebrochen',
        'system-seed-products-running': 'Leistungen & Preislisten werden importiert...',
        'system-import-cancelled': 'Import abgebrochen',
        'system-seed-texts-running': 'Fehlende Produkttexte werden ergänzt...',
        'system-text-import-cancelled': 'Produkttext-Import abgebrochen',
        'system-debug-hotel-or-ref-required': 'Bitte Hotel-ID oder RefCode eingeben',
        'system-debug-pattern-required': 'Bitte Pattern eingeben',
        'system-debug-key-required': 'Bitte Key eingeben',
        'system-widget-reset-title': 'Lister Widget zurücksetzen',
        'system-widget-reset-confirm': 'Möchten Sie wirklich alle Widget-Formularzustände zurücksetzen?',
        'system-cache-clear-title': 'Cache leeren',
        'system-cache-clear-confirm': 'Möchten Sie wirklich den gesamten Cache leeren? Diese Aktion kann nicht rückgängig gemacht werden.',
        'system-cache-rebuild-after-clear': 'Cache nach dem Leeren neu aufbauen',
        'system-cache-rebuild-hint': 'Der Neuaufbau läuft im Hintergrund. Der Fortschritt wird oben angezeigt.',
        'system-cache-status-title': 'Cache Status',
        'system-storage-source': 'Gelesen aus',
        'system-storage-in-redis': 'In Redis',
        'system-storage-in-db': 'In DB',
        'system-storage-db-updated': 'DB aktualisiert',
        'system-storage-no-ttl': 'ohne Ablauf',
        'system-storage-not-db-backed': 'nicht DB-gestützt',
        'system-storage-source-REDIS': 'Redis',
        'system-storage-source-DB': 'Datenbank',
        'system-storage-source-NONE': 'Nicht vorhanden',
        'system-cache-rebuilding': 'Cache wird neu aufgebaut...',
        'system-cache-please-wait': 'Bitte warten Sie, bis der Vorgang abgeschlossen ist.',
        'system-main-data': 'Hauptdaten',
        'system-hotels': 'Hotels',
        'system-calculators': 'Preislisten (Calculators)',
        'system-products': 'Leistungen (Products)',
        'system-pricelist-validation': 'Preislisten-Validierung',
        'system-lister-validation': 'Lister-Validierung',
        'system-widget-validation': 'Widget-Validierung',
        'system-status-title': 'System Status',
        'system-unlimited': 'unbegrenzt',
        connections: 'Verbindungen',
        memory: 'Speicher',
        type: 'Typ',
        default: 'Standard',
        saving: 'Speichere...',
        'system-admin-title': 'System-Administration',
        'administration-settings-title': 'Einstellungen',
        'administration-offer-seminargo-approval': 'Angebots-Überprüfung durch seminargo',
        'administration-offer-seminargo-approval-description':
          'Wenn aktiviert, müssen Hotelangebote von seminargo bestätigt werden, bevor sie an den Kunden weitergeleitet werden.',
        'administration-offer-seminargo-approval-disable-warning':
          'Alle Angebote mit dem Status "seminargo-Bestätigung ausstehend" werden automatisch freigegeben.',
        'administration-offer-seminargo-approval-updated': 'Einstellung wurde aktualisiert.',
        'system-redis-cache': 'Redis Cache',
        'system-redis-cache-description': 'Löscht alle Daten aus dem Redis Cache (FLUSHALL). Diese Aktion kann nicht rückgängig gemacht werden.',
        'system-lister-widget': 'Lister Widget',
        'system-lister-widget-description':
          'Setzt den Formularzustand aller Lister Widgets zurück. Alle Nutzer müssen ihre Suchkriterien neu eingeben.',
        'system-services-pricelists': 'Leistungen & Preislisten',
        'system-seed-products-description':
          'Importiert EPILO Leistungen (Products, Facilities, Bundles, Service Types) und erstellt EPILO Preislisten für alle Hotels. Bestehende Produktdetails werden nicht gelöscht.',
        'system-import-running': 'Import läuft...',
        'system-processing-spaces': 'Verarbeite Verwaltungsbereiche ({{count}})',
        'system-spaces-completed': 'Spaces abgeschlossen',
        'system-services-pricelists-import': 'Leistungen & Preislisten importieren',
        'system-missing-producttexts': 'Produkttexte ergänzen (EPILO)',
        'system-seed-texts-description':
          'Ergänzt fehlende Standardtexte (HEADER/DETAILS) für EPILO-Produkte, -Bundles, -Ausstattungen und -Leistungspakete. Bestehende Texte werden nicht überschrieben.',
        'system-text-import-running': 'Produkttext-Import läuft...',
        'system-missing-producttexts-fill': 'Fehlende Produkttexte ergänzen',
        'system-cache-debug-tool': 'Cache Debug Tool',
        'system-cache-debug-description':
          'Zeigt die gespeicherten Daten für ein Hotel an und woher sie kommen: Redis oder Datenbank (DB-Store). Es wird nichts neu berechnet.',
        'system-debug-hotel-or-ref': 'Hotel ID oder RefCode',
        'system-debug-hotel-or-ref-placeholder': 'z.B. 123 oder HOTEL-XYZ',
        'system-debug-max-bytes': 'Max Bytes',
        'system-cache-fetch': 'Cache abrufen',
        'system-lister-status': 'Lister Status',
        'system-widget-status': 'Widget Status',
        'system-hotel-data': 'Hotel Data',
        'system-redis-key-scan': 'Redis Key Scan',
        'system-redis-key-scan-description': 'Durchsucht Redis-Keys per Pattern (SCAN). Unterstützt Wildcards wie * und ?.',
        'system-pattern': 'Pattern',
        'system-pattern-placeholder': 'z.B. HOTEL_DATA_* oder QUICKPRICE_*',
        'system-limit': 'Limit',
        'system-max-keys': 'Max Keys',
        'system-cursor-optional': 'Cursor (optional)',
        'system-cursor-start-placeholder': '0 für Start',
        'system-scan-keys': 'Scan Keys',
        'system-scan-results': 'Scan Results',
        'system-found-keys': 'Found: {{count}} keys',
        'system-cursor': 'Cursor',
        'system-complete': 'complete',
        'system-no-keys-found': 'Keine Keys gefunden',
        'system-continue-scan': 'Weiter scannen',
        'system-redis-key-lookup': 'Redis Key Lookup',
        'system-redis-key-lookup-description': 'Zeigt den Wert eines Redis-Keys an (nur String-Typen werden vollständig angezeigt).',
        'system-key': 'Key',
        'system-key-placeholder': 'z.B. HOTEL_DATA_123',
        'system-lookup-key': 'Lookup Key',
        'system-key-exists-no-value': 'Key existiert, aber Wert konnte nicht geladen werden',
        'system-key-not-found': 'Key nicht gefunden',
        'system-seed-products-confirm': 'Möchten Sie wirklich EPILO Leistungen und Preislisten für alle Spaces importieren?',
        'system-action-imports': 'Diese Aktion importiert:',
        'system-import-item-products': 'EPILO Produkte',
        'system-import-item-facilities': 'EPILO Ausstattungen (Facilities)',
        'system-import-item-bundles': 'EPILO Sammelleistungen (Bundles)',
        'system-import-item-servicetypes': 'EPILO Leistungspakete (Service Types)',
        'system-import-item-pricelists': 'EPILO Preislisten für alle Hotels',
        'system-import-item-assignments': 'Service Type Zuweisungen (Hotel ↔ Leistungspaket)',
        'system-seed-products-hint': 'Bestehende Produktdetails werden nicht gelöscht. Der Fortschritt wird während des Imports angezeigt.',
        'system-restart-ignore-processed-spaces': 'Von vorne beginnen (ignoriert bereits verarbeitete Spaces)',
        'system-seed-texts-confirm': 'Möchten Sie wirklich fehlende EPILO Produkttexte (HEADER/DETAILS) für alle Spaces ergänzen?',
        'system-action-fills-for': 'Diese Aktion ergänzt nur fehlende Texte für:',
        'system-seed-texts-hint': 'Bereits vorhandene Produkttexte bleiben unverändert.',
        'offer-editor-change-servicetype-title': 'Leistungspaket ändern',
        'integration-log-sync-property': 'Property',
        'integration-log-sync-availability': 'Availability',
        'integration-log-sync-products': 'Products',
        'integration-log-sync-prices': 'Prices',
        'integration-log-sync-booking': 'Booking',
        'integration-log-sync-delete-booking': 'Delete Booking',
        'integration-log-sync-confirm-booking': 'Confirm Booking',
        'hotel-meetingroom-capacity-max-tooltip': 'max. Personenanzahl',
        'accounting-area-new': 'Neuer Buchungskreis',
        'accounting-area-edit': 'Buchungskreis bearbeiten',
        'accounting-area-basic-data': 'Grunddaten',
        'accounting-area-company-name-required': 'Firmenname *',
        'accounting-area-attention': 'Zuhanden',
        'accounting-area-vat-id-required': 'UID Nummer *',
        'accounting-area-address': 'Adresse',
        'accounting-area-country-required': 'Land *',
        'accounting-area-zip-required': 'PLZ *',
        'accounting-area-city-required': 'Ort *',
        'accounting-area-street-required': 'Straße *',
        'accounting-area-address-addition': 'Adresszusatz',
        'accounting-area-invoice-email-required': 'E-Mail für Rechnungsversand *',
        'accounting-area-email-recipients': 'Email-Empfänger',
        'accounting-area-email-recipient-add': 'E-Mail-Empfänger hinzufügen',
        'accounting-area-set-default': 'Als Standard setzen',
        'accounting-area-created': 'Buchungskreis erstellt.',
        'accounting-area-saved': 'Buchungskreis gespeichert.',
        'accounting-area-save-error': 'Fehler beim Speichern: {{message}}',
        'accounting-area-default-set': 'Standard-Buchungskreis gesetzt.',
        'accounting-area-default-error': 'Fehler: {{message}}',
        'accounting-area-cannot-delete-default': 'Der Standard-Buchungskreis kann nicht gelöscht werden.',
        'accounting-area-delete-confirm': 'Buchungskreis wirklich löschen?',
        'accounting-area-deleted': 'Buchungskreis gelöscht.',
        'accounting-area-delete-error': 'Fehler beim Löschen: {{message}}',
        'contracts-document-delete-confirm': 'Möchten Sie dieses Dokument wirklich löschen?',
        'system-service-backoffice-api': 'Backoffice API',
        'system-service-public-api': 'Öffentliche API',
        'system-service-scheduler': 'Scheduler',
        offline: 'offline',
        'system-heap-max': 'Heap / Max',
        cpu: 'CPU',
        uptime: 'Uptime',
        bytes: 'Bytes',
        'system-pricelist-lister': 'Pricelist (LISTER)',
        'system-pricelist-widget': 'Pricelist (WIDGET)',
        'system-cached': 'CACHED',
        'system-not-cached': 'NOT CACHED',
        'system-truncated': 'TRUNCATED',
        'system-not-in-cache': 'Nicht im Cache',
        'system-exists': 'EXISTS',
        'system-not-found': 'NOT FOUND',
        'system-ttl': 'TTL',
        unknown: 'unknown',

        'hotel-settings-tab': 'Stammdaten',
        'hotel-cancellation-tab': 'Stornobedingungen',
        'hotel-offers-tab': 'Angebote',
        'hotel-notifications-tab': 'Benachrichtigungen',
        'hotel-contacts-tab': 'Ansprechpartner',
        'hotel-lister-tab': 'seminargo-Einstellungen',
        'hotel-widget-tab': 'SEM-Einstellungen',
        'hotel-groups-tab': 'Ketten',
        'hotel-contracts-tab': 'Vereinbarungen',
        'hotel-integrations-tab': 'Integrationen',
        'hotel-mp-enable-service': 'Dienst aktivieren',
        'hotel-mp-description':
          'Dieser Dienst ruft automatisch alle verfügbaren Informationen (Hotelname, Tagungsräume, E-Mail-Adresse) von Meeting Package ab.',
        'hotel-mp-property-identifier': 'Property-Kennung',
        'hotel-mp-property-identifier-hint': 'Geben Sie Ihre eindeutige MeetingPackage-ID ein, um die Synchronisierung zu starten.',
        'hotel-mp-validated-hotel-name': 'Validierter Hotelname',
        'hotel-mp-no-hotel-found': 'Kein Hotel gefunden',
        'hotel-mp-validating': 'Wird geprüft...',
        'hotel-mp-enter-valid-identifier': 'Geben Sie eine gültige Property-Kennung ein, um den Dienst zu aktivieren.',
        'hotel-mp-enabled': 'Meeting Package Integration aktiviert',
        'hotel-mp-disabled': 'Meeting Package Integration deaktiviert',
        'hotel-mp-synced': 'Meeting Package Daten wurden synchronisiert',
        'hotel-mp-sync-now': 'Jetzt synchronisieren',
        'hotel-mp-fields-readonly':
          'Diese Felder werden von Meeting Package verwaltet. Deaktivieren Sie die MeetingPackage-Integration, um sie manuell zu bearbeiten.',
        'hotel-mp-open-integrations': 'Zu Integrationen',
        'hotel-mp-active-info': 'Meeting Package ist aktiv. Daten werden automatisch von Meeting Package synchronisiert.',
        'hotel-mp-need-assistance': 'Benötigen Sie Unterstützung?',
        'hotel-mp-view-documentation': 'Zum FAQ',
        'hotel-mp-contact-support': 'Seminargo kontaktieren',

        'hotel-sem-widget-settings': 'SEM-Widget Einstellungen',
        'hotel-sem-offer-settings': 'SEM-Angebote',

        'availability-hotels-list-page-title': 'Verfügbarkeit',
        'availability-list-page-title': 'Verfügbarkeit',
        'availability-header-hotel': 'Hotel',
        'availability-header-product': 'Leistung',

        'contentmedias-list-page-title': 'Mediendateien',
        'contentmedias-list-header-name': 'Dateiname',
        'contentmedias-list-header-mimetype': 'Mime-Type',
        'contentmedias-list-header-updatedat': 'Letzte Änderung',
        'contentmedias-export-run': 'Export',
        'contentmedias-import-run': 'Import',
        'contentmedias-import-ready': 'Mediendateien wurden importiert',
        'contentmedias-select-area-first':
          'Bitte wählen Sie zuerst einen konkreten Verwaltungsbereich aus, um Mediendateien anzuzeigen oder hochzuladen.',

        'contentmedia-confirm-delete': 'Die Datei wird unwiderruflich gelöscht und kann nicht wiederhergestellt werden.',
        'contentmedia-confirm-delete-title': 'Datei löschen',
        'contentmedia-deleted': 'Datei wurde gelöscht',
        'graphqlerror-MEDIA_DELETE_NOT_POSSIBLE_STILL_ASSIGNED': 'Die Datei {{name}} kann nicht gelöscht werden, da sie noch in Verwendung ist.',
        'graphqlerror-MEDIA_DELETE_WRONG_AREA': 'Die Datei {{name}} gehört zu {{area}} und kann nicht von hier aus gelöscht werden.',
        'graphqlerror-MEDIA_DELETE_INVALID_CONTEXT': 'Löschen ist nur aus SEM oder Seminargo möglich.',

        'emailtemplates-list-page-title': 'E-Mail-Vorlagen',
        'emailtemplates-list-header-usage': 'Verwendung',
        'emailtemplates-list-header-subject': 'Betreff',
        'emailtemplates-list-header-language': 'Sprache',
        'emailtemplates-list-header-receiver': 'Empfänger',
        'emailtemplates-list-header-updatedat': 'Letzte Änderung',
        'emailtemplates-list-add': 'E-Mail-Vorlage anlegen',
        'emailtemplates-export-run': 'Export',
        'emailtemplates-import-run': 'Import',
        'emailtemplates-import-ready': 'E-Mail-Vorlagen wurden importiert',
        'emailtemplates-list-receiver-client': 'Kunde',
        'emailtemplates-list-receiver-hotel': 'Hotel',
        'emailtemplates-list-receiver-seminargo': 'seminargo',
        'emailtemplates-list-receiver-user': 'SEM-Benutzer',
        'emailtemplates-list-warning-new-templates':
          'Achtung: Es gibt neue E-Mail-Vorlagen. Wenn du eine gewisse Vorlage inhaltlich ändern möchtest, aber nicht findest, dann musst du diese neu erstellen.',
        'emailtemplates-list-warning-missing-templates':
          'Folgende Vorlagen werden als Standardvorlagen vom System geholt, anstatt von der unten angeführten Liste',

        'emailtemplate-usage': 'Verwendung',
        'emailtemplate-subject': 'Betreff',
        'emailtemplate-language': 'Sprache',
        'emailtemplate-body': 'Inhalt',
        'emailtemplate-medias': 'Mediendateien',
        'emailtemplate-save': 'E-Mail-Vorlage speichern',
        'emailtemplate-updated': 'E-Mail-Vorlage gespeichert',
        'emailtemplate-created': 'E-Mail-Vorlage angelegt',
        'emailtemplate-preview': 'Vorschau anzeigen',
        'emailtemplate-preview-created': 'Vorschau wird angezeigt',
        'emailtemplate-confirm-delete': 'Die E-Mail-Vorlage wird gelöscht und kann nicht mehr verwendet werden.',
        'emailtemplate-confirm-delete-title': 'E-Mail-Vorlage löschen',
        'emailtemplate-delete': 'E-Mail-Vorlage löschen',
        'emailtemplate-deleted': 'E-Mail-Vorlage gelöscht',
        'emailtemplate-copy': 'E-Mail-Vorlage duplizieren',
        'emailtemplate-copied': 'E-Mail-Vorlage wurde dupliziert',

        'offer-actions-more': 'Weitere Aktionen',
        'offertemplates-list-page-title': 'PDF/HTML-Vorlagen',
        'offertemplates-list-header-name': 'Bezeichnung',
        'offertemplates-list-header-title': 'Dokumententitel',
        'offertemplates-list-header-type': 'Vorlagen-Typ',
        'offertemplates-list-header-updatedat': 'Letzte Änderung',
        'offertemplates-list-header-ispublished': 'Veröffentlicht',
        'offertemplates-list-add': 'Vorlage anlegen',
        'offertemplates-export-run': 'Export',
        'offertemplates-import-run': 'Import',
        'offertemplates-import-ready': 'Vorlagen wurden importiert',

        'offertemplate-data-tab': 'Basis-Einstellungen',
        'offertemplate-template-tab': 'HTML Layout',
        'offertemplate-name': 'Bezeichnung',
        'offertemplate-ispublished': 'Veröffentlicht',
        'offertemplate-includeemptylineitems': 'Leere Angebotszeilen übernehmen',

        'offertemplate-section-offer': 'Anfragedaten',
        'offertemplate-section-info': 'Allgemeine Informationen',
        'offertemplate-section-input': 'Eingegangene Kundenanfrage',
        'offertemplate-startdate': 'Startdatum Seminar',
        'offertemplate-offerdate': 'Angebotsdatum',
        'offertemplate-version': 'Kommentar',
        'offertemplate-section-servicetype': 'Leistungspaket',
        'offertemplate-service-type': 'Leistungspaket auswählen',
        'offertemplate-service-type-change-confirmation':
          'Beim Ändern des Leistungspakets werden Änderungen an den Leistungen rückgängig gemacht. Wollen Sie fortfahren?',
        'offertemplate-section-document': 'Dokumenteneinstellungen',
        'offertemplate-title': 'Dokumententitel',
        'offertemplate-filename': 'Dateiname (ohne Endung)',
        'offertemplate-section-header': 'Kopf- und Fußzeilen',
        'offertemplate-header': 'Vorlage Kopfzeile',
        'offertemplate-footer': 'Vorlage Fußzeile',
        'offertemplate-section-page': 'Seiteneinstellungen',
        'offertemplate-marginTop': 'Seitenabstand oben (px)',
        'offertemplate-marginBottom': 'Seitenabstand unten (px)',
        'offertemplate-marginLeft': 'Seitenabstand links (px)',
        'offertemplate-marginRight': 'Seitenabstand rechts (px)',
        'offertemplate-section-css': 'CSS Stylesheet',
        'offertemplate-type': 'Vorlagen-Typ',
        'offertemplate-css': 'CSS Stylesheet',
        'offertemplate-save': 'Vorlage speichern',
        'offertemplate-updated': 'Vorlage gespeichert',
        'offertemplate-created': 'Vorlage angelegt',
        'offertemplate-confirm-delete': 'Die Vorlage wird gelöscht und kann nicht mehr verwendet werden.',
        'offertemplate-confirm-delete-title': 'Vorlage löschen',
        'offertemplate-delete': 'Vorlage löschen',
        'offertemplate-deleted': 'Vorlage gelöscht',
        'graphqlerror-OFFERTEMPLATE_DELETE_NOT_POSSIBLE_STILL_ASSIGNED':
          'Die Vorlage {{name}} kann nicht gelöscht werden, da sie noch in Verwendung ist.',

        'offertemplate-copy': 'Vorlage duplizieren',
        'offertemplate-default-name': 'Preisübersicht',
        'offertemplate-copied': 'Vorlage wurde dupliziert',

        'offertemplate-section-client': 'Kunden- und Angebotsdaten',
        'offertemplate-section-client-company': 'Firmenadresse',
        'offertemplate-section-client-billing': 'Rechnungsadresse',
        'offertemplate-client-company-firstname': 'Vorname',
        'offertemplate-client-company-lastname': 'Nachname',
        'offertemplate-client-company-company': 'Firma',
        'offertemplate-client-company-address': 'Adresse',
        'offertemplate-client-company-zip': 'PLZ',
        'offertemplate-client-company-city': 'Ort',
        'offertemplate-client-company-country': 'Land',
        'offertemplate-client-phone': 'Telefon',
        'offertemplate-client-email': 'E-Mail',
        'offertemplate-client-billing-company': 'Firma',
        'offertemplate-client-billing-firstname': 'Vorname',
        'offertemplate-client-billing-lastname': 'Nachname',
        'offertemplate-client-billing-address': 'Adresse',
        'offertemplate-client-billing-zip': 'PLZ',
        'offertemplate-client-billing-city': 'Ort',
        'offertemplate-client-billing-country': 'Land',

        'offertemplate-additionalinfo1': 'Zusatzinfo 1',
        'offertemplate-additionalinfo2': 'Zusatzinfo 2',
        'offertemplate-additionalinfo3': 'Zusatzinfo 3',

        'offer-personal-message': 'Ihre persönliche Nachricht an den Kunden',

        'offertemplate-layout': 'HTML Inhalt',
        'offerblock-plainhtml-header': 'Textblock',
        'offerblock-plainhtml-hint': 'Statischer Text (HTML)',
        'offerblock-plainhtml-html': 'HTML Inhalt',
        'offerblock-lineitems-header': 'Angebotszeilen (pro Tag)',
        'offerblock-lineitems-header-day': 'Angebotszeilen',
        'offerblock-cancellationitems-header': 'Stornozeilen (pro Phase)',
        'offerblock-cancellationitems-header-phase': 'Stornophase {{rate}} (bis {{daysToEvent}} Tage)',
        'offerblock-lineitems-hint': 'Dynamischer Text (HTML)',
        'offerblock-lineitems-html': 'HTML Vorlage',
        'offerblock-inputdays-header': 'Leistungskonfiguration (pro Tag)',
        'offerblock-inputdays-header-day': 'Leistungskonfiguration',
        'offerblock-inputdays-hint': 'Dynamischer Text (HTML)',
        'offerblock-inputdays-html': 'HTML Vorlage',
        'offerblock-lineitems-data-tab': 'Angebotszeilen',
        'offerblock-lineitems-template-tab': 'HTML Vorlage',
        'offerblock-lineitem-header': 'Überschrift',
        'offerblock-lineitem-details': 'Beschreibungstext',
        'offer-individual-seating': 'individuelle Bestuhlung',
        'offerblock-lineitem-count': 'Anzahl',
        'offerblock-lineitem-priceitem-header': 'Zusammensetzung Einzelleistung',
        'offerblock-lineitem-priceitem': 'Einzelleistung Preis',
        'offerblock-lineitem-priceitem-net': 'Einzelleistung (Netto)',
        'offerblock-lineitem-priceitem-gross': 'Einzelleistung (Brutto)',
        'offerblock-lineitem-pricenet': 'Gesamtleistung (Netto)',
        'offerblock-lineitem-pricegross': 'Gesamtleistung (Brutto)',
        'offerblock-lineitem-delete': 'Angebotszeile Löschen',
        'offerblock-lineitem-copyprices': 'Preis für alle Tage übernehmen',
        'offerblock-lineitem-components': 'Steuerklassen',
        'offerblock-formfield-append': 'Eingabefeld hinzufügen',
        'offerblock-formfield-delete': 'Eingabefeld löschen',
        'offerblock-formfield-type': 'Feldtyp',
        'offerblock-formfield-name': 'Feldname',
        'offerblock-formfield-label': 'Bezeichnung',
        'offerblock-formfield-placeholder': 'Platzhalter',
        'offerblock-formfield-options': 'Einstellungen',
        'offerblock-formfield-helptext': 'Hilfetext',
        'offerblock-formfield-defaultvalue': 'Standardwert',
        'offerblock-formfield-required': 'Pflichtfeld',
        'offerblock-formfield-usage': 'Verwendung als: ',
        'offerblock-customform-header': 'Texteingabeblock',
        'offerblock-customform-hint': '',
        'offerblock-customform-html': 'HTML Vorlage',
        'offerblock-customform-data-tab': 'Eingabefelder',
        'offerblock-customform-template-tab': 'HTML Vorlage',
        'offerblock-formfield-type-textarea-richtext': 'Formatierter Text',
        'offerblock-formfield-type-textarea-plaintext': 'Mehrzeiliger Text',
        'offerblock-formfield-type-text': 'Einzeiliger Text',

        'offerblock-lineitem-addsku': 'Zusatzleistung hinzufügen (Optional)',
        'offerblock-lineitem-addfree': 'Neue Zusatzleistung (Freitext)',
        'offerblock-lineitem-other': 'Sonstiges',
        'offerblock-special-requests-info': 'beispielsweise für allfällige Sonderwünsche des Buchenden, wie ein zusätzliches Fingerfood etc.',
        'offerblock-show-special-requests': 'Sonderwünsche anzeigen',
        'offerblock-no-special-requests': 'diese Anfrage hat keine Sonderwünsche',
        'offerblock-delete': 'Gesamten Block Löschen',
        'offerblock-description': 'Inhaltsblock-Beschreibung',
        'offerblock-customform-add': 'Texteingabeblock',
        'offerblock-lineitems-add': 'Angebotszeilen',
        'offerblock-cancellationitems-add': 'Stornozeilen',
        'offerblock-plainhtml-add': 'Textblock',
        'offerblock-inputdays-add': 'Leistungskonfiguration',
        'offerblock-contentblock-add': 'Inhaltsblock hinzufügen',

        'offertemplate-showhtml': 'Angebotsvorschau',
        'offertemplate-advanced': 'Erweiterte Bearbeitung',

        'offertemplate-add-previousday': 'Vortagesanreise hinzufügen',
        'offertemplate-add-day': 'Zusätzlichen Tag hinzufügen',
        'offerblock-delete-day': 'Tag löschen',

        'offertemplate-showchangerequest': 'Nur angefragte Leistungsänderungen anzeigen',
        'offertemplate-totals-displaynet': 'Anzeige der Preise in Netto',
        'offertemplate-totals-displaygross': 'Anzeige der Preise in Brutto',
        'offertemplate-totals-displaytooltip': 'Ob Brutto/Netto-Preise angezeigt werden, hängt von der verwendeten Preisliste ab.',
        'offertemplate-section-totals': 'Preisübersicht',
        'offertemplate-totals-net': 'Netto',
        'offertemplate-totals-tax': 'USt %',
        'offertemplate-totals-gross': 'Brutto',
        'offertemplate-totals-taxrate': 'Steuer',
        'offertemplate-totals-total': 'Gesamt',
        'offertemplate-totals-incomplete': 'Bitte Preisinformationen vervollständigen',

        'offertemplate-totals-display-mode-net': 'Netto',
        'offertemplate-totals-display-mode-gross': 'Brutto',

        'offerversion-saved': 'Angebotsversion wurde gespeichert',

        // Prices

        'pricelists-tabs-header-pricelists': 'Preislisten',
        'pricelists-tabs-header-taxtypes': 'Steuerklassen',
        'pricelists-tabs-header-import': 'Dateiimport',

        'taxtypes-list-page-title': 'Steuerklassen',
        'taxtypes-list-header-name': 'Steuerklasse',
        'taxtypes-list-header-sequence': 'Sortierreihenfolge',
        'taxtypes-list-add': 'Neue Steuerklasse anlegen',
        'taxtype-name': 'Bezeichnung',
        'taxtype-sequence': 'Sortierreihenfolge',
        'taxtype-rates-tab': 'Steuersätze',
        'taxtype-rates': 'Keine Steuersätze angelegt',
        'taxtype-rate-name': 'Bezeichnung',
        'taxtype-rate-rate': 'Steuersatz',
        'taxtype-rate-country': 'Land',
        'taxtype-rate-isreversecharge': 'Reverse Charge',
        'taxtype-rate-validfrom': 'Gültig von',
        'taxtype-rate-validto': 'Gültig bis',
        'taxtype-rate-add': 'Neuen Steuersatz anlegen',
        'taxtype-save': 'Steuerklasse speichern',
        'taxtype-saved': 'Steuerklasse gespeichert',
        'taxtype-saved-bg-refresh': 'Steuerklasse gespeichert. Preise werden im Hintergrund aktualisiert.',

        'taxtype-refresh-progress': 'Aktualisiere Preise & Texte…',
        'taxtype-refresh-cancel': 'Abbrechen',
        'taxtype-refresh-cancelled': 'Refresh abgebrochen',
        'taxtype-refresh-hotels': 'Hotels',
        'taxtype-refresh-spaces': 'Verwaltungsbereiche',
        'taxtype-refresh-current-space': 'Aktueller Verwaltungsbereich',
        'taxtype-refresh-starting': 'Refresh wird gestartet…',
        'taxtype-refresh-idle': 'Kein Refresh aktiv.',
        'taxtype-refresh-confirm-title': 'Preise & Texte aktualisieren',
        'taxtype-refresh-confirm-message-global':
          'Die Preise und Texte werden für alle Verwaltungsbereiche und Hotels neu berechnet. Möchten Sie fortfahren?',
        'taxtype-refresh-confirm-message-space':
          'Die Preise und Texte werden für die Hotels dieses Verwaltungsbereichs neu berechnet. Möchten Sie fortfahren?',

        'taxtype-delete': 'Steuerklasse löschen',
        'taxtype-deleted': 'Steuerklasse gelöscht',
        'taxtype-confirm-delete-title': 'Steuerklasse löschen',
        'taxtype-confirm-delete': 'Soll die Steuerklasse inklusive Steuersätzen wirklich gelöscht werden ?',
        'graphqlerror-TAXTYPE_DELETE_NOT_POSSIBLE_STILL_ASSIGNED':
          'Die Steuerklasse {{name}} kann nicht gelöscht werden, da sie noch in Verwendung ist.',

        'pricelists-list-page-title': 'Preislisten',
        'pricelists-list-published': 'Veröffentlichte Preislisten',
        'pricelists-list-notpublished': 'Nicht veröffentliche Preislisten',
        'pricelists-list-header-name': 'Preisliste',
        'pricelists-list-header-refcode': 'Referenzcode',
        'pricelists-list-header-version': 'Version',
        'pricelists-list-header-validity': 'Gültigkeitszeitraum',
        'pricelists-list-header-ispublished': 'Veröffentlicht',
        'pricelists-list-header-isdefault': 'Standard',
        'pricelists-list-header-isvalid': 'Vollständig',
        'pricelists-list-header-hotel': 'Hotel',
        'pricelists-list-add': 'Preisliste anlegen',
        'pricelists-list-import': 'Preisblatt importieren',

        'pricelist-name': 'Bezeichnung',
        'pricelist-refcode': 'Referenzcode',
        'pricelist-version': 'Version',
        'pricelist-isdefault': 'Standard-Preisliste (für alle Hotels)',
        'pricelist-ispublished': 'Veröffentlicht',
        'pricelist-ispricesbrut': 'Preise sind Bruttopreise',
        'pricelist-currency': 'Währung',
        'pricelist-hotel': 'Hotel',
        'pricelist-hotel-none': 'Kein Hotel',
        'pricelist-title-for-hotel': 'Preisliste für Hotel {{hotel}}',
        'pricelist-title-for-hotel-with-type': '{{type}} Preisliste für Hotel {{hotel}}',
        'pricelist-forlister': 'Verwendung in seminargo',
        'pricelist-forwidget': 'Verwendung im SEM-Widget',
        'pricelist-includemainroominpackage': '(Haupt-)Raummiete in Pauschalen inkludiert',
        'pricelist-includemainroominpackage-description': 'Der erste automatisch gewählte Seminarraum wird mit 0€ berechnet',
        'pricelist-validity': 'Gültigkeitszeiträume',
        'pricelist-validfrom': 'Gültig von',
        'pricelist-validto': 'Gültig bis',
        'pricelist-validity-add': 'Gültigkeitszeitraum hinzufügen',

        'pricelist-validation-tab': 'Preisliste unvollständig',
        'pricelist-validation-products': '{{productCount}} Einzelleistung(en) ohne Preis',
        'pricelist-validation-productcomponents': '{{productCount}} Einzelleistung(en) ohne Steuerklassen',
        'pricelist-validation-products-without-base-price': 'Einzelleistungen ohne Basispreis',
        'pricelist-validation-products-without-price-components': 'Einzelleistungen ohne Steuerklassen',
        'pricelist-validation-bundles': '{{bundleCount}} Sammelleistung(en) ohne Preis',
        'pricelist-validation-bundlecomponents': '{{bundleCount}} Sammelleistung(en) ohne Steuerklassen',
        'pricelist-validation-bundles-without-base-price': 'Sammelleistungen ohne Basispreis',
        'pricelist-validation-bundles-without-price-components': 'Sammelleistungen ohne Steuerklassen',
        'pricelist-validation-facilities': '{{facilityCount}} Ausstattung(en) ohne Preis',
        'pricelist-validation-facilitycomponents': '{{facilityCount}} Ausstattung(en) ohne Steuerklassen',
        'pricelist-validation-facilities-without-base-price': 'Ausstattungen ohne Basispreis',
        'pricelist-validation-facilities-without-price-components': 'Ausstattungen ohne Steuerklassen',
        'graphqlerror-PRICELIST_PUBLISH_NOT_POSSIBLE':
          'Die Preisliste {{name}} kann nicht veröffentlicht werden, da nicht für alle Leistungen Preise angegeben sind.',

        'pricelist-selector-page-title': 'Preisregel',
        'pricelist-selectors-tab': 'Preisregeln',
        'pricelist-selector-name': 'Bezeichnung',
        'pricelist-selector-baseprice': 'Basispreis',
        'pricelist-selector-minguestcount': 'Min. Gäste',
        'pricelist-selector-maxguestcount': 'Max. Gäste',
        'pricelist-selector-minitemcount': 'Min. Anzahl',
        'pricelist-selector-maxitemcount': 'Max. Anzahl',
        'pricelist-selector-validity': 'Gültigkeitszeitraum',
        'pricelist-selector-weekdays': 'Wochentage',
        'pricelist-selector-add': 'Neue Preisregel anlegen',

        'pricelist-rules-tab': 'Berechnungsregeln',
        'pricelist-parent': 'Basispreise für Berechnungen übernehmen von',
        'pricelist-rules-rule': 'Berechnungsregel',
        'pricelist-rules-rate': 'Aufschlag / Prozentsatz',
        'pricelist-rules-rounding': 'Rundung',
        'pricelist-rules-item': 'Gültig für Produkt / Austattung',
        'pricelist-rules-all': 'Alle Preise',
        'pricelist-rules-add-empty': 'Alle Preise abändern',
        'pricelist-rules-add-product': 'Produkt-Preis abändern',
        'pricelist-rules-add-bundle': 'Sammelprodukt-Preis abändern',
        'pricelist-rules-add-facility': 'Austattungs-Preis abändern',

        'enums-EPricingRuleType-FIXED': 'Fixbetrag aufschlagen',
        'enums-EPricingRuleType-OVERWRITE': 'Preis überschreiben',
        'enums-EPricingRuleType-PERCENTAGE': 'Prozentsatz aufschlagen',
        'enums-EPricingRoundingType-NONE': 'Nicht runden',
        'enums-EPricingRoundingType-CEIL': 'Aufrunden',
        'enums-EPricingRoundingType-FLOOR': 'Abrunden',

        'pricelist-save': 'Preisliste speichern',
        'pricelist-saved': 'Preisliste gespeichert',

        'pricelist-delete': 'Preisliste löschen',
        'pricelist-deleted': 'Preisliste gelöscht',
        'pricelist-confirm-delete-title': 'Preisliste löschen',
        'pricelist-confirm-delete': 'Soll die Preisliste inklusive Preisen wirklich gelöscht werden ?',
        'graphqlerror-PRICELIST_DELETE_NOT_POSSIBLE_STILL_ASSIGNED':
          'Die Preisliste {{name}} kann nicht gelöscht werden, da sie durch Angebote in Verwendung ist.',
        'graphqlerror-PRICELIST_DELETE_NOT_POSSIBLE_STILL_CHILDREN':
          'Die Preisliste {{name}} kann nicht gelöscht werden, da andere Preislisten abhängig sind.',

        'pricelist-confirm-copy-title': 'Preisliste duplizieren',
        'pricelist-space': 'Verwaltungsbereich',
        'pricelist-copy': 'Preisliste duplizieren',
        'pricelist-copied': 'Preisliste wurde dupliziert',

        'selector-backlink': 'Zurück zur Preisliste',
        'selector-name': 'Bezeichnung',
        'selector-name-required': 'Bezeichnung ist ein Pflichtfeld',
        'selector-isbaseprice': 'Basispreis',
        'selector-minguestcount': 'Min. Gäste',
        'selector-minguestcount-helpertext': 'Bezieht sich auf die Anzahl der gebuchten Pauschalen',
        'selector-maxguestcount': 'Max. Gäste',
        'selector-maxguestcount-helpertext': 'Bezieht sich auf die Anzahl der gebuchten Pauschalen',
        'selector-minitemcount': 'Min. Anzahl',
        'selector-minitemcount-helpertext': 'Bezieht sich auf die Anzahl der gebuchten Leistungen',
        'selector-maxitemcount': 'Max. Anzahl',
        'selector-maxitemcount-helpertext': 'Bezieht sich auf die Anzahl der gebuchten Leistungen',
        'selector-validfrom': 'Gültig von',
        'selector-validfrom-before-validto': 'Muss vor Gültig bis liegen',
        'selector-validto': 'Gültig bis',
        'selector-validto-after-validfrom': 'Muss nach Gültig von liegen',
        'selector-validity-no-overlap': 'Zeiträume dürfen sich nicht überschneiden',
        'selector-customer': 'Kunde',
        'selector-customer-all': 'Alle Kunden',
        'selector-weekdays': 'Wochentage',
        'selector-guestcount': 'Gästeanzahl',
        'selector-itemcount': 'Anzahl',
        'selector-validity': 'Gültigkeit',
        'selector-list-title': 'Preisregeln',
        'no-price-selectors': 'Keine Preisregeln vorhanden',

        'selector-products-tab': 'Preise Einzelleistungen',
        'selector-bundles-tab': 'Preise Sammelleistungen',
        'selector-facilities-tab': 'Preise Ausstattung & Technik',
        'selector-multitax-price': 'Steueranteil',
        'selector-product-price': 'Preis',
        'selector-bundle-price': 'Preis',
        'selector-bundle-pricefromproduct': 'Summe aus Einzelleistungen berechnen',
        'selector-facility-price': 'Preis',
        'selector-price-label-premium': 'Premium-Preis',
        'selector-price-label-classic-bar': 'BAR-Preis (Classic)',
        'selector-price-total': 'Gesamt',
        'selector-price-net': 'Netto',
        'selector-price-gross': 'Brutto',
        'selector-price-on-request': 'auf Anfrage',
        'selector-save': 'Preise speichern',
        'selector-saved': 'Preise gespeichert',

        'selector-delete': 'Preisregel löschen',
        'selector-deleted': 'Preisregel gelöscht',
        'selector-confirm-delete-title': 'Preisregel löschen',
        'selector-confirm-delete': 'Soll die Preisregel inklusive Preisen wirklich gelöscht werden ?',
        'graphqlerror-SELECTOR_DELETE_NOT_POSSIBLE':
          'Die Preisregel {{name}} kann nicht gelöscht werden, da die Preisliste dadurch unvollständig sein würde.',

        'selector-copy': 'Preisregel duplizieren',
        'selector-copied': 'Preisregel wurde dupliziert',

        'prices-import-page-title': 'Dateiimport',
        'prices-import-name': 'Name',
        'prices-import-version': 'Version',
        'prices-import-file': 'Preisliste Datei (*.json, *.xlsx)',
        'prices-import-default-name': 'Preisliste',
        'prices-import-run': 'Dateiimport starten',
        'prices-import-ready': 'Dateiimport abgeschlossen',
        'prices-import-result-header': 'Ergebnis',
        'prices-import-result-pricelist': 'Preisliste',
        'prices-import-result-version': 'Version',
        'prices-import-result-complete': 'Vollständig',
        'prices-import-result-productnoprice': 'Einzelleistungen ohne Preis',
        'prices-import-result-productnocomponents': 'Einzelleistungen ohne Steuerklassen',
        'prices-import-result-bundlesnoprice': 'Sammelleistungen ohne Preis',
        'prices-import-result-bundlesnocomponents': 'Sammelleistungen ohne Steuerklassen',
        'prices-import-result-facilitiesnoprice': 'Ausstattung ohne Preis',
        'prices-import-result-facilitiesnocomponents': 'Ausstattung ohne Steuerklassen',
        'prices-import-start': 'Zurück',
        'graphqlerror-IMPORT_PRICES_FAILED': 'Dateiimport fehlgeschlagen, bitte Dateiinhalt überprüfen: {{- err}}.',

        'prices-export-run': 'Dateiexport starten',
        'prices-export-ready': 'Dateiexport abgeschlossen',

        // Products

        'productprices-pricelist': 'Preisliste',
        'productprices-selector': 'Preisregel',
        'productprices-ispublished': 'Veröffentlicht',
        'productprices-version': 'Version',
        'productprices-price': 'Preis',

        'products-tabs-header-products': 'Einzelleistungen',
        'products-tabs-header-bundles': 'Sammelleistungen',
        'products-tabs-header-facilities': 'Ausstattung & Technik',
        'products-tabs-header-servicetypes': 'Leistungspakete',
        'products-tabs-header-import': 'Dateiimport',

        'products-list-page-title': 'Einzelleistungen',
        'products-list-header-product': 'Leistung',
        'products-list-header-sku': 'SKU',
        'products-list-header-recurring': 'Verrechnung',
        'products-list-header-isaddon': 'Zusatzleistung',
        'products-list-header-isdeduction': 'Abschlag',
        'products-list-add': 'Leistung hinzufügen',
        'products-list-import': 'Leistungs-Liste importieren',

        'product-name': 'Bezeichnung',
        'product-sku': 'SKU',
        'product-sku-not-unique': 'SKU "{{sku}}" bereits vergeben',
        'product-lookupcode': 'SEM-Referenzcode',
        'product-extrefcode': 'Externer Referenzcode',
        'product-recurring': 'Verrechnung',
        'product-issystem': 'seminargo-Basisleistung',
        'product-isdeduction': 'Verrechnung als Abschlag',
        'product-isaddon': 'Optionale Zusatzleistung',
        'product-components': 'Steuerklasse(n)',
        'product-component-taxtype': 'Steuerklasse',
        'product-components-not-100': 'Steuerklassenanteile müssen sich auf 100% summieren',
        'product-prices-tab': 'Preislisten',
        'product-bundles-tab': 'Sammelleistungen',
        'product-bundles-bundle': 'Sammelleistung',
        'product-bundles-included': 'Anzahl inkludiert',
        'product-text-tab': 'Textbausteine',
        'product-availability-tab': 'Verfügbarkeit',
        'product-save': 'Leistung speichern',
        'product-save-text': 'Textbausteine speichern',
        'product-saved': 'Leistung gespeichert',
        'product-delete': 'Leistung löschen',
        'product-deleted': 'Leistung gelöscht',
        'product-confirm-delete-title': 'Leistung löschen',
        'product-confirm-delete': 'Soll die Leistung inklusive Preisen wirklich gelöscht werden ?',
        'product-confirm-copy-title': 'In welchen Verwaltungsbereich soll die Leistung dupliziert werden?',
        'product-copy-space': 'Verwaltungsbereich',
        'graphqlerror-PRODUCT_DELETE_NOT_POSSIBLE_STILL_ASSIGNED':
          'Die Leistung {{name}} kann nicht gelöscht werden, da es durch Sammelleistungen und/oder Leistungspakete in Verwendung ist.',

        'product-copy': 'Leistung duplizieren',
        'product-copied': 'Leistung wurde dupliziert',

        'productbundles-list-page-title': 'Sammelleistungen',
        'productbundles-list-header-bundle': 'Leistung',
        'productbundles-list-header-sku': 'SKU',
        'productbundles-list-header-recurring': 'Verrechnung',
        'productbundles-list-add': 'Sammelleistung hinzufügen',

        'productbundle-name': 'Bezeichnung',
        'productbundle-sku': 'SKU',
        'productbundle-sku-not-unique': 'SKU "{{sku}}" bereits vergeben',
        'productbundle-recurring': 'Verrechnung',
        'productbundle-lookupcode': 'SEM-Referenzcode',
        'productbundle-extrefcode': 'Externer Referenzcode',
        'productbundle-issystem': 'seminargo-Basisleistung',
        'productbundle-components': 'Steuerklasse(n)',
        'productbundle-items-tab': 'Einzelleistungsanteile',
        'productbundle-item-product': 'Einzelleistung',
        'productbundle-item-includedcount': 'Inkludierte Einheiten',
        'productbundle-item-isoptional': 'Optional',
        'productbundle-item-add': 'Zusätzlicher Leistungsanteil',
        'productbundle-prices-tab': 'Preislisten',
        'productbundle-text-tab': 'Textbausteine',
        'productbundle-availability-tab': 'Verfügbarkeit',
        'productbundle-save': 'Sammelleistung speichern',
        'productbundle-save-text': 'Textbausteine speichern',
        'productbundle-saved': 'Sammelleistung gespeichert',
        'productbundle-delete': 'Sammelleistung löschen',
        'productbundle-deleted': 'Sammelleistung gelöscht',
        'productbundle-confirm-delete-title': 'Sammelleistung löschen',
        'productbundle-confirm-delete': 'Soll die Sammelleistung inklusive Preisen wirklich gelöscht werden ?',
        'graphqlerror-BUNDLE_DELETE_NOT_POSSIBLE_STILL_ASSIGNED':
          'Das Sammelleistung {{name}} kann nicht gelöscht werden, da es durch Leistungspakete in Verwendung ist.',
        'productbundle-confirm-copy-title': 'In welchen Verwaltungsbereich soll die Sammelleistung dupliziert werden?',
        'productbundle-copy-space': 'Verwaltungsbereich',
        'productbundle-copy': 'Sammelleistung duplizieren',
        'productbundle-copied': 'Sammelleistung wurde dupliziert',
        'productbundle-item-product-not-found': 'Produkt nicht gefunden',
        'productbundle-item-remove-missing': 'Fehlendes Produkt entfernen',
        'productbundle-products-load-error': 'Fehler beim Laden der Produkte',

        'facilities-list-page-title': 'Ausstattung & Technik',
        'facilities-list-header-facility': 'Ausstattung',
        'facilities-list-header-sku': 'SKU',
        'facilities-list-header-recurring': 'Verrechnung',
        'facilities-list-add': 'Ausstattung hinzufügen',
        'facilities-list-header-isaddon': 'Zusatzaustattung',

        'facility-name': 'Bezeichnung',
        'facility-sku': 'SKU',
        'facility-sku-not-unique': 'SKU "{{sku}}" bereits vergeben',
        'facility-lookupcode': 'SEM-Referenzcode',
        'facility-extrefcode': 'Externer Referenzcode',
        'facility-recurring': 'Verrechnung',
        'facility-isaddon': 'Optionale Zusatzausstattung',
        'facility-issystem': 'seminargo-Basisausstattung',
        'facility-components': 'Steuerklasse(n)',
        'facility-prices-tab': 'Preislisten',
        'facility-text-tab': 'Textbausteine',
        'facility-availability-tab': 'Verfügbarkeit',
        'facility-save': 'Ausstattung speichern',
        'facility-save-text': 'Textbausteine speichern',
        'facility-saved': 'Ausstattung gespeichert',
        'facility-delete': 'Ausstattung löschen',
        'facility-deleted': 'Ausstattung gelöscht',
        'facility-confirm-delete-title': 'Ausstattung löschen',
        'facility-confirm-delete': 'Soll die Ausstattung inklusive Preisen wirklich gelöscht werden ?',
        'graphqlerror-FACILITY_DELETE_NOT_POSSIBLE_STILL_ASSIGNED':
          'Die Ausstattung {{name}} kann nicht gelöscht werden, da es durch Leistungspakete in Verwendung ist.',
        'facility-confirm-copy-title': 'In welchen Verwaltungsbereich soll die Ausstattung dupliziert werden?',
        'facility-copy-space': 'Verwaltungsbereich',
        'facility-copy': 'Ausstattung duplizieren',
        'facility-copied': 'Ausstattung wurde dupliziert',

        'servicetypes-list-page-title': 'Leistungspakete',
        'servicetypes-list-header-type': 'Leistungspaket',
        'servicetypes-list-header-sku': 'SKU',
        'servicetypes-list-header-sequence': 'Sortierreihenfolge',
        'servicetypes-list-header-ispublished': 'Veröffentlicht',
        'servicetypes-list-add': 'Leistungspaket hinzufügen',

        'servicetype-name': 'Bezeichnung',
        'servicetype-sku': 'SKU',
        'servicetype-sku-not-unique': 'SKU "{{sku}}" bereits vergeben',
        'servicetype-ispublished': 'Veröffentlicht',
        'servicetype-issystem': 'seminargo-Basispaket',
        'servicetype-sequence': 'Sortierreihenfolge',
        'servicetype-hotel': 'Hotel',
        'servicetype-hotel-empty': 'Keine Auswahl (Für alle Hotels verfügbar)',
        'servicetype-pricelist': 'Preisliste',
        'servicetype-pricelist-empty': 'Keine Auswahl (Standard-Preisliste verwenden)',

        'servicetype-validation-tab': 'Leistungspaket unvollständig',
        'servicetype-validation-hint':
          'Das Leistungspaket muss den von seminargo vorgeschriebenen Standards entsprechen, um in seminargo und/oder im SEM Widget gebucht werden zu können.',
        'servicetype-validation-products': '{{productCount}} fehlende Leistung(en)',
        'servicetype-validation-facilities': '{{facilityCount}} fehlende Ausstattung(en)',
        'graphqlerror-SERVICETYPE_PUBLISH_NOT_POSSIBLE':
          'Das Leistungspaket {{name}} kann nicht veröffentlicht werden, da es nicht den von seminargo vorgegebenen Standards entspricht',

        'servicetype-products-tab': 'Inkludierte Leistungen',
        'servicetype-products-product': 'Leistung',
        'servicetype-products-isfordayvisitor': 'Für Tagesgäste',
        'servicetype-products-isforovernight': 'Für Übernachtungsgäste',
        'servicetype-products-isforroom': 'Nur Übernachtung',
        'servicetype-products-isfordeparture': 'Für Abreisegäste',
        'servicetype-products-isforsemidayvisitor': 'Für Halbtagesgäste',
        'servicetype-products-isforsemidayovernight': 'Für Halbtages-Übernachtungsgäste',
        'servicetype-products-isforsemidaydeparture': 'Für Halbtages-Abreisegäste',
        'servicetype-products-isforprevday': 'Für Vortagsanreise',
        'servicetype-products-isfornextday': 'Für Verlängerungsnacht',
        'servicetype-product-add': 'Einzelleistung hinzufügen',
        'servicetype-productbundle-add': 'Sammelleistung hinzufügen',
        'servicetype-product-or-bundle': 'Keine Einzelleistung oder Sammelleistung ausgewählt',
        'servicetype-products-or-facilities': 'Keine Leistungen oder Ausstattung ausgewählt',
        'servicetype-usage-not-selected': 'Keine Verrechnung ausgewählt',
        'servicetype-facilities-tab': 'Inkludierte Ausstattung & Technik',
        'servicetype-facility-name': 'Ausstattung',
        'servicetype-facility-includedcount': 'Inkludierte Anzahl',
        'servicetype-facility-isforday': 'Für Ganztagesbelegung',
        'servicetype-facility-isforsemiday': 'Für Halbtagesbelegung',
        'servicetype-facility-add': 'Ausstattung hinzufügen',
        'servicetype-addons-tab': 'Optionale Leistungen',
        'servicetype-addons-facilities': 'Optionale Ausstattung & Technik',
        'servicetype-addons-products': 'Optionale Zusatzleistungen',
        'servicetype-addonservices-helper':
          'Diese Leistungen können optional dazugebucht werden. Sie können festlegen, ab welcher Anzahl die zugebuchte Leistung vom Hotel nochmal bestätigt werden muss, wenn die Zubuchung nach bereits bestätigter Reservierung erfolgt (zustimmungspflichtig). seminargo-Basisleistungen (Austattung, Übernachtung, ...) können immer dazugebucht werden, erfordern aber ebenfalls eine nochmalige Bestätigung (wenn hier angegeben).',
        'servicetype-addonservices-product': 'Leistung',
        'servicetype-addonservices-addproduct': 'Einzelleistung festlegen',
        'servicetype-addonservices-facility': 'Ausstattung',
        'servicetype-addonservices-addfacility': 'Ausstattung festlegen',
        'servicetype-addonservices-approvallimit': 'Zustimmungspflichtig ab Anzahl / Tag (Leerlassen für nicht zustimmungspflichtige Leistung)',
        'servicetype-addonservices-selectable': 'Auswählbar',
        'servicetype-text-tab': 'Textbausteine',
        'servicetype-save': 'Leistungspaket speichern',
        'servicetype-save-text': 'Textbausteine speichern',
        'servicetype-contact-office': 'Für Anpassungen wende dich an office@seminargo.com',
        'servicetype-saved': 'Leistungspaket gespeichert',
        'servicetype-delete': 'Leistungspaket löschen',
        'servicetype-deleted': 'Leistungspaket gelöscht',
        'servicetype-confirm-delete-title': 'Leistungspaket löschen',
        'servicetype-confirm-delete': 'Soll das Leistungspaket wirklich gelöscht werden ?',
        'servicetype-copy': 'Leistungspaket duplizieren',
        'servicetype-confirm-copy-title': 'In welchen Verwaltungsbereich soll das Leistungspaket dupliziert werden?',
        'servicetype-copy-space': 'Verwaltungsbereich',
        'servicetype-copied': 'Leistungspaket wurde dupliziert',

        'products-import-page-title': 'Dateiimport',
        'products-import-mode': 'Import-Modus',
        'products-import-file': 'Leistungeskatalog Datei (*.json)',
        'products-import-run': 'Dateiimport starten',
        'products-import-ready': 'Dateiimport abgeschlossen',
        'products-import-result-header': 'Ergebnis',
        'products-import-result-products': '{{productCount}} Einzelleistungen importiert',
        'products-import-result-bundles': '{{bundleCount}} Sammelleistungen importiert',
        'products-import-result-facilities': '{{facilityCount}} Ausstattungen importiert',
        'products-import-result-servicetypes': '{{serviceTypeCount}} Leistungspakete importiert',
        'products-import-start': 'Zurück',
        'graphqlerror-IMPORT_PRODUCTS_FAILED': 'Dateiimport fehlgeschlagen, bitte Dateiinhalt überprüfen: {{- err}}.',

        'products-export-run': 'Dateiexport starten',
        'products-export-ready': 'Dateiexport abgeschlossen',

        // Offers
        'offers-list-seminargo-confirmation': 'Von seminargo zu bestätigende Abrechnungen',
        'offers-list-offer-seminargo-confirmation': 'Von seminargo zu bestätigende Angebote',
        'offers-list-offer-seminargo-confirmation-hotel': 'Angebotsprüfung',
        'offers-list-open': 'Zu bearbeiten',
        'offers-list-hotel-editing': 'Entwürfe in Bearbeitung',
        'offers-list-approved': 'Bestätigt',
        'offers-list-rejected': 'Abgelehnt',
        'offers-list-reservations': 'Reservierungen',
        'offers-list-invoice-review': 'Rechnungsprüfung',
        'offers-list-active': 'Laufend',
        'offers-list-offers': 'Angebote',
        'offers-list-invoice': 'Rechnungslegung',
        'offers-list-closed': 'Abgeschlossen',
        'offers-list-archive': 'Archiv',

        'offers-list-invoice-waitforsemapproval': 'Von seminargo zu bestätigende Abrechnungen ({{count}})',
        'offers-list-invoice-waitforhotelapproval': 'Rechnungslegung ({{count}})',
        'offers-list-invoice-waitforinvoiceapproval': 'Vom Kunden zu bestätigende Abrechnungen ({{count}})',
        'offers-list-open-waitforhotelapproval': 'Anfragen ({{count}})',
        'offers-list-open-waitforhotelreservation': 'Rückbestätigungen ({{count}})',
        'offers-list-open-waitforhotelcrapproval': 'Änderungen ({{count}})',
        'offers-list-open-waitforclientapproval': 'Angebote ({{count}})',

        'offers-list-page-title-WIDGET': 'SEM Buchungen',
        'offers-list-page-title-LISTER': 'seminargo Buchungen',
        'offers-list-header-createdat': 'Eingelangt',
        'offers-list-header-from-to': 'Anreise/Abreise',
        'offers-list-header-refcode': 'Buchungs-ID',
        'offers-list-header-offer': 'Angebot',
        'offers-list-header-title': 'Titel der Veranstaltung',
        'offers-list-header-sku': 'SKU',
        'offers-list-header-hotel': 'Hotel',
        'offers-list-header-client': 'Kunde',
        'offers-list-header-email': 'E-Mail',
        'offers-list-header-version': 'Version',
        'offers-list-header-status': 'Status',
        'offers-list-header-updatedat': 'Letzte Änderung',
        'offers-list-header-delete-reason': 'Grund der Archivierung',
        'offers-list-create-new': 'Neues Angebot erstellen',
        'offer-duplicate-tooltip': 'Duplizieren',
        'offer-duplicate-title': '{{refCode}} - {{company}} duplizieren',
        'offer-duplicate-confirm-text':
          'Bist du sicher, dass du die Buchung {{refCode}} vom Kunden {{company}} duplizieren möchtest? Es wird ein neues Angebot erstellt. Der Kunde bekommt noch keine Benachrichtigung.',
        'offer-duplicate-startdate-label': 'Startdatum',
        'offer-duplicate-startdate-warning': 'Bitte wähle das Startdatum für das neue Angebot. Die Dauer und Vortag-Leistungen bleiben unverändert.',
        'offer-duplicate-startdate-past-error': 'Startdatum darf nicht in der Vergangenheit liegen',
        'offer-duplicate-confirm': 'Angebot erstellen',
        'offer-duplicate-success': 'Angebot {{refCode}} erstellt',
        'offers-list-combined-view': 'Buchungen aus SEM und seminargo anzeigen',
        'offers-debug-copied': 'Debug-Info in die Zwischenablage kopiert',
        'offers-debug-copy-sentry': 'Kopieren + Sentry',
        // Overflows page
        'offers-overflows-search-label': 'Suche',
        'offers-overflows-search-button': 'Suchen',
        'offers-overflows-header-refcode': 'Buchungs-ID',
        'offers-overflows-header-first-hotel-name': 'Hotel (zuerst gemeldet)',
        'offers-overflows-header-first-hotel-id': 'Hotel-ID (zuerst gemeldet)',
        'offers-overflows-header-first-notification': 'Zeitstempel Meldung',
        'offers-overflows-header-start': 'Seminardatum Start',
        'offers-overflows-header-end': 'Seminardatum Ende',
        'offers-overflows-header-status': 'Status',
        'offers-overflows-header-booker': 'Buchender',
        'offers-overflows-header-overnights': 'Übernachtungen',
        'offers-overflows-header-actions': 'Aktionen',
        'offers-overflows-export-download': 'Excel-Report herunterladen',
        'offers-overflows-export-failed': 'Export fehlgeschlagen',
        'offers-list-edit': 'Bearbeiten',
        'offers-list-view': 'Öffnen',
        'offers-list-show': 'Öffnen',
        'offers-list-delete': 'Archivieren',
        'offers-list-deleted': 'Archiviert',

        'offer-process-create': 'Angebot erzeugen',
        'offer-create-header': 'Neues Angebot',
        'offer-create-step1-header': '1. Hotelauswahl',
        'offer-create-step2-header': '2. Eingabe der Kundendaten',
        'offer-create-step3-header': '3. Eingabe des Leistungszeitraums',
        'offer-create-step4-header': '4. Auswahl des Leistungspakets',
        'offer-create-step5-header': '5. Eingabe der Leistungskonfiguration',
        'offer-create-preday-header': 'Vortag',
        'offer-create-day-header': 'Tag',
        'offer-create-hotel': 'Hotel',
        'offer-create-client-firstname': 'Vorname',
        'offer-create-client-lastname': 'Nachname',
        'offer-create-client-email': 'E-Mail',
        'offer-create-client-phone': 'Telefonnummer',
        'offer-create-client-company': 'Firmenname',
        'offer-create-client-address': 'Adresse',
        'offer-create-client-city': 'Stadt',
        'offer-create-client-zip': 'PLZ',
        'offer-create-client-country': 'Land',
        'offer-create-input-startDate': 'Anreise',
        'offer-create-input-endDate': 'Abreise',
        'offer-create-service-type': 'Leistungspaket',
        'offer-create-input-prevdayGuests': 'Vortagsgäste',
        'offer-create-input-days-dayGuests': 'Tagesgäste',
        'offer-create-input-days-overnightGuests': 'Übernachtungsgäste',
        'offer-create-input-day-noguests': 'Gästeanzahl ist ein Pflichtfeld',
        'offer-create-input-days-occupancy-roomcount': '# Räume',
        'offer-create-input-days-occupancy': 'Belegung',
        'offer-create-input-days-seating': 'Bestuhlung',
        'offer-create-input-days-facility-name': 'Ausstattung',
        'offer-create-input-days-facility-recurring': 'Anzahl / Bezahlung',
        'offer-create-input-days-facility-sku': 'SKU',
        'offer-create-input-days-facility-count': 'Anzahl',
        'offer-create-input-days-facility-count-bycount': 'per Anzahl',
        'offer-create-input-days-facility-count-byguest': 'per Gast',
        'offer-create-input-days-facility-count-byday': 'per Tag',
        'offer-create-input-days-product-name': 'Leistung',
        'offer-create-input-days-product-recurring': 'Anzahl / Bezahlung',
        'offer-create-input-days-product-sku': 'SKU',
        'offer-create-input-days-product-count': 'Anzahl',
        'offer-create-input-days-product-count-bycount': 'per Anzahl',
        'offer-create-input-days-product-count-byguest': 'per Gast',
        'offer-create-input-days-product-count-byguestandday': 'per Gast und Tag',
        'offer-create-pricelist-date-hint': 'Nur Daten mit gültig hinterlegter Preisliste können ausgewählt werden.',
        'offer-create-guests-summary': 'Gäste ({{overnightGuests}} Übernachtungsgäste / {{dayGuests}} Tagesgäste)',
        'offer-create-rooms-header': 'Räume: {{count}}',
        'offernew-create': 'Angebot erzeugen',
        'offernew-create-text': 'Bist du sicher, dass du dieses Angebot erzeugen möchtest?',
        'offernew-status-created': 'Angebot wurde erzeugt',
        'offernew-validation-error': 'Bitte Pflichtfelder ausfüllen',
        'offer-approvecr-tab': 'Änderungswunsch zur Reservierung',
        'offer-input-tab': 'Eingegangene Kundenanfrage',
        'offer-editor-tab': 'Angebotsansicht',
        'offer-confirmation-editor-tab': 'Reservierungsbestätigung',
        'offer-editor-tab-change': 'Bearbeitung',
        'offer-integration-tab': 'PMS Verlauf',
        'offer-advancededitor-tab': 'Expertenmodus',
        'offer-history-tab': 'Verlauf',
        'offer-seminargo-contact': 'seminargo Ansprechperson',
        'offer-seminargo-contact-office': 'seminargo Office',
        'offer-history-booking-tab': 'Änderungen',
        'offer-history-email-tab': 'Emails',
        'offer-history-view': 'Angebotsversion anzeigen',
        'offer-versions-header-usertype': 'User',
        'offer-history-usertype-customer': 'Kunde',
        'offer-history-usertype-sudo': 'seminargo (als Kunde)',
        'offer-history-usertype-seminargo': 'seminargo',
        'offer-history-usertype-hotel': 'Hotel',
        'offer-history-usertype-system': 'System',
        'offer-history-user-link-access': 'link access',

        'offer-history-details-title': 'Verlaufsdetails',
        'offer-history-details-action': 'Aktion',
        'offer-history-details-no-action': 'Keine Aktion verfügbar',
        'offer-history-details-extra-info': 'Zusätzliche Informationen',
        'offer-history-details-timestamp': 'Zeitpunkt',
        'offer-history-details-status': 'Status',
        'offer-history-details-user': 'Benutzer',
        'offer-history-details-no-user': 'Kein Benutzer',
        'offer-history-details-usertype': 'Benutzertyp',
        'offer-history-details-prev': 'Zurück',
        'offer-history-details-next': 'Weiter',
        'offer-history-addon-name': 'Dienstleistung',
        'offer-history-previous-count': 'Bisherige Anzahl',
        'offer-history-new-count': 'Neue Anzahl',
        'offer-history-change-status': 'Änderungsstatus',
        'offer-addon-change-removed': 'Entfernt',
        'offer-addon-change-increased': 'Erhöht',
        'offer-addon-change-decreased': 'Reduziert',
        'offer-addon-change-unchanged': 'Unverändert',
        'offer-history-changed-by': 'Geändert von {{user}} am {{date}}',
        'offer-history-unknown-user': 'Unbekannter Benutzer',
        'offer-reminder-email-sent': 'Erinnerungsmail erfolgreich versendet!',
        'offer-reminder-email-send': 'Erinnerungsmail senden',
        'graphqlerror-NO_PRICELIST': 'Keine Preisliste gefunden',
        'graphqlerror-BAD_PRICELIST': 'Konfigurationsfehler in der Preisliste',
        'graphqlerror-BAD_PRODUCT': 'Produkt nicht gefunden',

        'offer-event-tab': 'Veranstaltungs-Checkliste',
        'offer-event-tab-download': 'Checkliste herunterladen',
        'offer-tasks-tab': 'Tasks',
        'offer-event-mealtimes': 'Verpflegungszeiten',
        'offer-event-mealtimes-day': 'Tag',
        'offer-event-mealtimes-ek': 'EK',
        'offer-event-mealtimes-kp1': 'KP1',
        'offer-event-mealtimes-me': 'ME',
        'offer-event-mealtimes-kp2': 'KP2',
        'offer-event-mealtimes-ae': 'AE',
        'offer-event-mealtimes-ez': 'EZ',
        'offer-event-mealtimes-dz': 'DZ',
        'offer-event-rooms': 'Seminarräume',
        'offer-event-rooms-number': 'Raum {{roomNumber}}',
        'offer-event-rooms-duration': 'Dauer',
        'offer-event-rooms-seating': 'Bestuhlung',
        'offer-event-rooms-guests': 'Teilnehmeranzahl',
        'offer-event-rooms-label': 'Beschilderung',
        'offer-event-rooms-facilities': 'Technik',
        'offer-event-attendances': 'Teilnehmer',
        'offer-event-attendances-ek': 'EK',
        'offer-event-attendances-kp1': 'KP1',
        'offer-event-attendances-me': 'ME',
        'offer-event-attendances-kp2': 'KP2',
        'offer-event-attendances-ae': 'AE',
        'offer-event-attendances-ez': 'EZ',
        'offer-event-attendances-dz': 'DZ',
        'offer-event-attendances-coach': 'Coach',
        'offer-event-attendances-dpservice': 'SZ Service',
        'offer-event-attendances-dproom': 'SZ Übernachtung',
        'offer-event-attendances-dpextra': 'SZ Extras',
        'offer-event-attendances-dpparking': 'SZ Parken',
        'offer-event-comments': 'Zusatzinformationen',
        'offer-event-comments-accomodation': 'Infotext Verpflegung',
        'offer-event-comments-payment': 'Infotext Verrechnung',
        'offer-event-comments-rooms': 'Infotext Seminarraum',
        'offer-event-comments-other': 'Infotext Sonstiges',

        'offer-process-eventstarted': 'Seminar wurde begonnen',
        'offer-process-eventended': 'Seminar wurde beendet',

        'offer-history-email-sent': 'E-Mail wurde versendet',
        'offer-history-email-failed': 'E-Mail wurde nicht versendet',
        'offer-history-email-subject': 'Betreff',
        'offer-history-email-from': 'Absender',
        'offer-history-email-to': 'Empfänger',
        'offer-history-email-err': 'Fehlermeldung',
        'customer-message-for-hotel': 'Kundennachricht',
        'customer-message-readonly': 'Diese Nachricht wurde vom Kunden bereitgestellt und kann nicht bearbeitet werden.',
        'customer-comment-dialog-title': 'Kundenkommentar',
        'customer-comment-dialog-intro': 'Der Kunde hat folgende Anmerkung hinterlassen:',
        'customer-comment-dialog-acknowledge': 'Ich habe den Hinweis zur Kenntnis genommen und werde ihn bei der Angebotserstellung berücksichtigen.',
        'customer-comment-dialog-start-editing': 'Bearbeitung starten',
        'customer-request-file-download': 'Herunterladen',

        'offer-versions-header-createdat': 'Zeitpunkt',
        'offer-versions-header-version': 'Versionskommentar',
        'offer-versions-header-status': 'Status',
        'offer-versions-header-user': 'Benutzer',
        'offer-email-history-header-status': 'Status',
        'offer-email-history-header-details': 'Details',

        'offer-info-seminargo-included':
          'Beim ersten Seminarraum sind 1x Beamer & Leinwand / Flatscreen, 1x Flipchart, 1x Pinnwand als Ausstattung inkludiert.',

        'offer-info-header-commission': 'Provision',
        'offer-info-text-commission-net':
          'Bei Zustandekommen einer Reservierung werden {{commissionPercentage}} Kommission auf die Nettosumme zzgl. Ust berechnet.',
        'offer-info-text-commission-gross':
          'Bei Zustandekommen einer Reservierung werden {{commissionPercentage}} Kommission auf die Bruttosumme zzgl. Ust berechnet.',
        'offer-info-commission-calculating': 'Berechne voraussichtliche Provision...',
        'offer-info-commission-error': 'Fehler bei der Berechnung der Provision',
        'offer-info-commission-retry': 'Erneut berechnen',
        'offer-info-commission-calculated': 'Voraussichtliche Provision: {{amount}}',
        'offer-info-commission-calculate': 'Voraussichtliche Provision berechnen',
        'offer-info-edit-commission': 'Hier bearbeiten',
        'offer-info-dialog-commission-title': 'Provision bearbeiten',
        'offer-info-commission-validation-error': 'Provision ist ungültig. Bitte Prozentwert und Berechnungsbasis prüfen.',
        'offer-info-commission-saved': 'Provision wurde gespeichert',

        'offer-info-header-cancellation': 'Stornobedingungen',
        'offer-info-text-cancellation': 'Bei dieser Veranstaltung gelten folgende Stornobedingungen:',
        'offer-info-text-cancellation-rule': 'Ab {{daysToEvent}} Tagen vor Veranstaltungsbeginn fallen {{rate}} Stornokosten an.',
        'offer-info-text-cancellation-rule-waive': 'Es werden {{toleranceRate}} vereinbarter Stornoverzicht berücksichtigt.',
        'offer-info-edit-cancellation': 'Hier bearbeiten',
        'offer-info-dialog-cancellation-title': 'Stornobedingungen bearbeiten',
        'offer-info-cancellation-rule-add': 'Stornobedingung hinzufügen',
        'offer-info-cancellation-rule-days': 'Stornofrist (Tage vor Veranstaltungsbeginn)',
        'offer-info-cancellation-rule-rate': 'Stornorate (in %)',
        'offer-info-cancellation-rule-tolerance': 'Toleranz (in %)',
        'offer-info-cancellation-agreed-waiver': 'Vereinbarter Stornoverzicht (in %)',
        'offer-info-cancellation-agreed-waiver-tooltip':
          'Der vereinbarte Stornoverzicht wird im Falle einer Kundenstornierung kostenfrei gutgeschrieben.',
        'offer-info-cancellation-validation-error':
          'Stornobedingungen sind ungültig. Jede Regel muss unterschiedliche Tage haben und eine Rate zwischen 0% und 100%.',
        'offer-info-cancellation-validation-hint': 'Jede Regel muss unterschiedliche Tage haben und eine Rate zwischen 0% und 100%.',
        'offer-info-cancellation-rules-saved': 'Stornobedingungen wurden gespeichert',

        'offer-info-header-downpayment': 'Anzahlung',
        'offer-info-text-downpayment-empty': 'Hier können Sie Ihre Anzahlungsmodalitäten hinterlegen.',
        'offer-info-text-downpayment-text': 'Anzahlungshöhe {{downpaymentAmount}} mit dem Fälligkeitsdatum {{downpaymentDueDate}}',
        'offer-info-edit-downpayment': 'Hier bearbeiten',
        'offer-info-dialog-downpayment-title': 'Anzahlungsmodalitäten bearbeiten',
        'offer-info-dialog-guests': 'Anzahl Teilnehmer',
        'offer-info-dialog-rooms': 'Anzahl Zimmer',
        'offer-info-dialog-total-amount': 'Gesamtsumme',
        'offer-info-downpayment-calculation-type': 'Eingabe in',
        'offer-info-downpayment-fixed-amount': 'Betrag (€)',
        'offer-info-downpayment-percentage-amount': 'Prozent (%)',
        'offer-info-downpayment-percentage': 'Höhe Anzahlung',
        'offer-info-downpayment-amount': 'Summe Anzahlung',
        'offer-info-downpayment-due-date': 'Fälligkeitsdatum',
        'offer-info-downpayment-remark': 'Sonstige Informationen zur Anzahlung',

        'offer-info-downpayment-validation-error':
          'Anzahlungsmodalitäten sind ungültig. Es muss ein Fälligkeitsdatum und eine Summe angegeben werden.',
        'offer-info-downpayment-saved': 'Anzahlungsmodalitäten wurden gespeichert',

        'offer-input-header-company-data': 'Firmendaten',
        'offer-input-header-billing-data': 'Rechnungsdaten',
        'offer-input-premium-customer-title': 'Premium Kunde',
        'offer-input-premium-customer-note': 'Betreuung nach der Reservierung und Rechnungslegung erfolgt für das Hotel durch seminargo.',
        'offer-input-classic-customer-title': 'Classic Kunde',
        'offer-input-classic-customer-note': 'Betreuung ab der Reservierung und Rechnungslegung erfolgt durch das Hotel.',
        'offer-input-not-available': 'Nicht vorhanden',
        'offer-input-header-name': 'Name',
        'offer-input-header-email': 'E-Mail',
        'offer-input-header-phone': 'Telefonnummer',
        'offer-input-header-address': 'Adresse',
        'offer-input-header-company': 'Firmenname',
        'offer-input-header-start': 'Start',
        'offer-input-header-end': 'Ende',
        'offer-input-header-duration': 'Zeitraum',
        'offer-input-header-servicetype': 'Leistungspaket',
        'offer-input-header-comment': 'Kommentar',
        'offer-input-header-preday': 'Vortag',
        'offer-input-header-day': 'Tag',
        'offer-input-header-guests': 'Gäste',
        'offer-input-header-overnightguests': 'Zimmer',
        'offer-input-header-overnightguests-ez': 'Einzelzimmer',
        'offer-input-header-overnightguests-dz': 'Doppelzimmer',
        'selector-lister-combinations-vollpension-ez': 'Einzelzimmerkombination',
        'selector-lister-combinations-vollpension-ez-subtitle': 'Einzelzimmer + Ganztagespauschale + Abendessen',
        'selector-lister-combinations-vollpension-dz': 'Doppelzimmerkombination (pro Person)',
        'selector-lister-combinations-vollpension-dz-subtitle': 'Doppelzimmer + Ganztagespauschale + Abendessen',
        'selector-lister-combinations-description':
          'Diese Preise werden verwendet, wenn Einzelzimmer oder Doppelzimmer zusammen mit Ganztagespauschale und Abendessen gebucht werden. In diesem Fall ersetzen sie die separaten Zimmer-, Pauschale- und Abendessen-Positionen.',
        'selector-lister-combinations-no-products':
          'Keine Vollpensions-Produkte für diese Preisliste gefunden. Erstellen Sie entsprechende Produkte mit den Lookup-Codes VOLLPENSIONSPAUSCHALE_EZ und VOLLPENSIONSPAUSCHALE_DZ.',
        'offer-input-header-rooms': 'Räume',
        'offer-input-header-addons': 'Zusatzleistungen',
        'offer-input-no-addons': 'Keine Zusatzleistungen',

        'offer-input-line-sku': 'SKU',
        'offer-input-line-header': 'Bezeichnung',
        'offer-input-line-count1': 'Reservierung',
        'offer-input-line-count2': 'Wunsch',
        'offer-input-line-diff': 'Abweichung',
        'offer-input-changed-startdate': 'Änderung des Seminarbeginns von {{fromStartDate}} auf {{toStartDate}}.',

        'offer-occupancy-header-room': 'Raumnummer',
        'offer-occupancy-header-occupancy': 'Dauer',
        'offer-occupancy-header-seating': 'Bestuhlung',
        'offer-occupancy-header-participants': 'Teilnehmer',
        'offer-occupancy-header-requirements': 'Anforderungen',
        'offer-occupancy-header-technology': 'Technik',
        'offer-occupancy-air': 'Klimaanlage',
        'offer-occupancy-daylight': 'Tageslicht',
        'offer-room-persons': 'Personen',
        'offer-room-guaranteed-size': 'Garantierte Raumgröße',
        'offer-room-min-size-hint': '{{size}}m² mindestens erforderlich',
        'offer-room-acknowledge': 'Ich bestätige, dass der angebotene Raum alle oben genannten Anforderungen erfüllt.',
        'offer-room-size-required': 'Garantierte Raumgröße ist erforderlich',
        'offer-room-size-min': 'Mindestens {{size}} m²',
        'offer-room-confirm-required': 'Bitte bestätigen',
        'offer-room-acknowledge-required': 'Bitte die Anforderungen bestätigen',

        'offer-addons-header-name': 'Extra',
        'offer-addons-header-count': 'Anzahl',

        'offer-process-approve-offer': 'Angebot bestätigen',
        'offer-process-approve-reservation': 'Reservierung bestätigen',
        'offer-process-approve-cr': 'Änderungswunsch bestätigen',
        'offer-process-approve-as-client': 'Angebot bestätigen (als Kunde)',
        'offer-process-withdraw-approval': 'Angebot zurückziehen',

        'offer-process-approve-change': '* Angebot bestätigen',
        'offer-process-approve-reservation-change': '* Änderungen bestätigen',
        'offer-process-approve-cr-change': 'Ergänzten Änderungswunsch bestätigen',
        'offer-process-approve-cr-no-changes': 'Änderungswunsch genehmigt',
        'offer-process-approve-approved-change': 'Geänderte Reservierungsbestätigung speichern und senden',
        'offer-process-approve-as-client-change': 'Ergänztes Angebot bestätigen (als Kunde)',

        'offerconfirm-approve-offer-title': 'Angebot bestätigen',
        'offerconfirm-approve-offer-change-title': 'Änderungen bestätigen',
        'offerconfirm-approve-reservation-title': 'Reservierung bestätigen',
        'offerconfirm-approve-approved-title': 'Reservierungsbestätigung abändern',
        'offerconfirm-approve-line-items-overview': 'Folgende Leistungen werden an den Kunden übermittelt',
        'offerconfirm-approve-offer-question': 'Soll das Angebot wirklich bestätigt werden?',
        'offerconfirm-approve-offer-save-prices':
          'Sollen die Preise als Referenz gespeichert werden? Sie können die Preise vor jeder Buchung anpassen.',
        'offerconfirm-approve-reservation-question': 'Soll die Reservierung wirklich bestätigt werden?',
        'offerconfirm-approve-cr-question': 'Soll der Änderungswunsch des Kunden wirklich bestätigt werden?',
        'offerconfirm-approve-as-client-question':
          'Sie sind im Begriff das Angebot im Namen des Kunden zu bestätigen. Drücken Sie nur auf Fortsetzen, wenn dies so mit dem Kunden vereinbart wurde.',

        'offerconfirm-approve-question-change': 'Soll das ergänzte Angebot wirklich bestätigt werden?',
        'offerconfirm-approve-cr-question-change': 'Soll der abgeänderte Änderungswunsch des Kunden wirklich bestätigt werden?',
        'offerconfirm-approve-approved-question-change': 'Soll die Reservierungsbestätigung wirklich abgeändert werden?',
        'offerconfirm-approve-as-client-question-change':
          'Sie sind im Begriff das ergänzte Angebot im Namen des Kunden zu bestätigen. Drücken Sie nur auf Fortsetzen, wenn dies so mit dem Kunden vereinbart wurde.',

        'offer-confirm-agb': 'Ich bestätige die AGB von seminargo',
        'offer-confirm-agb-link': 'AGB ansehen',
        'offer-confirm-cancellation': 'Ich bestätige die geltenden Stornobedingungen',
        'offer-confirm-cancellation-link': 'Stornobedingungen ansehen',
        'offer-confirm-commission': 'Ich bestätige die Provisionsvereinbarung',
        'offer-confirm-commission-link': 'Provisionsvereinbarung ansehen',
        'offer-confirm-payment-premium': 'Das Zahlungsziel des Kunden beträgt 30 Tage nach Rechnungsfreigabe.',

        'offer-process-reject-offer': 'Angebot ablehnen',
        'offer-process-reject-reservation': 'Reservierung ablehnen',
        'offer-process-reject-cr': 'Änderungswunsch ablehnen',
        'offer-process-reject-cr-reservation': 'Änderungen ablehnen',
        'offer-process-edit': 'Angebot ergänzen',
        'offer-confirmation-process-edit': 'Reservierungsbestätigung ergänzen',
        'offer-approvecr-edit': 'Änderungswunsch ergänzen',
        'offer-process-edit-cancel': 'Änderung abbrechen',
        'offer-process-download': 'ANGEBOT HERUNTERLADEN',
        'offer-confirmation-process-download': 'Reservierungsbestätigung herunterladen',
        'offer-cancellation-confirmation-process-download': 'Stornierungsbestätigung herunterladen',
        'offer-save-changes': 'für später speichern (als Entwurf)',
        'offer-save-changes-epilo': 'ENTWURF FÜR SPÄTER SPEICHERN',
        'offer-save-and-send': 'Angebot speichern und senden',
        'offer-save-and-send-success': 'Angebot gespeichert und gesendet',
        'offer-confirmation-save-changes': 'Reservierungsbestätigung für später speichern',
        'offer-process-delete': 'Angebot löschen',
        'offer-process-reset': 'Entwurf löschen',
        'offer-process-reset-title': 'Entwurf löschen',
        'offer-process-reset-text': 'Soll der Entwurf wirklich gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden.',
        'offer-process-reset-success': 'Entwurf wurde gelöscht',
        'offer-status-approved': 'Buchung bestätigt',
        'offer-status-rejected': 'Buchung abgelehnt',

        'offer-process-extrefcode': 'Externer Referenzcode',
        'offer-process-extrefdata': 'Externe Referenzdaten',
        'offer-process-openbooking': 'Im PMS öffnen',
        'offer-process-openbooking-nohref': 'Öffnen aus SEM nicht möglich, bitte direkt im PMS öffnen',
        'offer-process-syncbooking': 'Neu übertragen',
        'offer-process-syncbooking-success': 'Neue PMS-Buchung erstellt',
        'offer-process-confirmbooking': 'Im PMS bestätigen',
        'offer-process-confirmbooking-success': 'PMS-Buchung bestätigt',
        'offer-process-deletebooking': 'Im PMS stornieren',
        'offer-process-deletebooking-success': 'PMS-Buchung storniert',

        'offerconfirm-delete-title': 'Angebot archivieren',
        'offerconfirm-delete-text': 'Soll das Angebot wirklich archiviert werden?',
        'offerconfirm-reject-title': 'Angebot ablehnen',
        'offerconfirm-reject-reason': 'Grund der Ablehnung (optional) - dieser wird dem Kunden mitgeteilt',
        'offerconfirm-reject-text': 'Soll das Angebot wirklich abgelehnt werden?',
        'offerconfirm-rejectcr-title': 'Änderungswunsch ablehnen',
        'offerconfirm-rejectcr-text': 'Soll der Änderungswunsch wirklich abgelehnt werden?',
        'offerconfirm-rejectcr-reservation-title': 'Änderungen ablehnen',
        'offerconfirm-rejectcr-reservation-text': 'Sollen die Änderungen wirklich abgelehnt werden?',
        'offerconfirm-delete-reason': 'Grund der Archivierung',
        'offerconfirm-delete-success': 'Angebot wurde archiviert',
        'offerconfirm-approve-success': 'Angebot wurde bestätigt',
        'offerconfirm-reject-success': 'Angebot wurde abgelehnt',

        'offer-reload-title': 'Änderungen an der Buchung',
        'offer-reload-text': 'Es wurden im Hintergrund Änderungen an der Buchung vorgenommen. Sie werden zur Buchungsübersicht zurückgeleitet.',
        'offer-guest-access-denied':
          'Zugriff verweigert. Bitte prüfen Sie den Link oder melden Sie sich mit einem berechtigten Konto an (ggf. abmelden).',
        'offer-guest-banner': 'Gastzugriff über Link — Sie bearbeiten dieses Angebot ohne vollständige Anmeldung.',
        'offer-guest-sign-in': 'Anmelden',
        'offer-guest-user-type': 'Gast',
        'offer-guest-success-approved': 'Vielen Dank für Ihr Angebot!',
        'offer-guest-success-draft': 'Entwurf wurde gespeichert!',
        'offer-guest-success-declined': 'Die Anfrage wurde abgelehnt!',
        'offer-guest-success-ok': 'OK',

        'offer-cancel-button': 'Reservierung stornieren',
        'offer-confirm-cancellation-button': 'Storno bestätigen',
        'offer-confirm-cancellation-question-title': 'Stornierung bestätigen',
        'offer-confirm-cancellation-question': 'Sind Sie sicher, dass Sie die Stornierung bestätigen möchten?',
        'offer-confirm-cancellation-success': 'Stornierung wurde bestätigt',
        'offer-cancel-editor-tab': 'Stornierungsbestätigungansicht',
        'offer-cancel-sem-button-approve': 'Geänderte Stornierungsbestätigung speichern und senden',
        'offer-cancel-sem-button-cancel': 'Storno abbrechen',

        'rejection-dialog-title': 'Angebot ablehnen',
        'rejection-dialog-subtitle': 'Bitte wählen Sie hier den Grund für die Ablehnung aus:',
        'rejection-dialog-cancel': 'Abbrechen',
        'rejection-dialog-confirm': 'Ablehnen',

        'rejection-reason-NOT_ENOUGH_TIME': 'nicht genügend Zimmer vorhanden',
        'rejection-reason-NOT_ENOUGH_SEMINAR_ROOMS': 'nicht genügend Seminarräume vorhanden',
        'rejection-reason-REQUEST_NOT_FITTING': 'vorgegebene AGB/Stornobedingungen passen nicht',
        'rejection-reason-REQUESTED_TIME_PERIOD_NOT_AVAILABLE': 'Anfrage außerhalb des buchbaren Zeitraums',
        'rejection-reason-OTHER': 'Sonstiges',
        'rejection-reason-custom-label': 'Bitte Grund angeben',
        'rejection-reason-custom-min-chars': 'Zeichen minimum',

        'rejection-error-no-reason': 'Bitte wählen Sie einen Grund aus',
        'rejection-error-no-text': 'Bitte geben Sie einen Grund ein',
        'rejection-error-min-length': 'Der Grund muss mindestens 5 Zeichen lang sein',

        // Invoices

        'invoices-list-page-title-LISTER': 'Rechnungsfreigabe',
        'invoices-list-page-title-WIDGET': 'Rechnungsfreigabe',

        'cancellation-invoice-status-approved': 'Die Stornierung wurde vom Hotel bestätigt',
        'cancellation-invoice-status-rejected': 'Die Stornierung wurde vom Hotel abgelehnt',
        'cancellation-invoice-confirm-title': 'Stornierung bestätigen',
        'cancellation-invoice-confirm-question': 'Soll die Stornierung bestätigt werden ? Diese Aktion kann nicht rückgängig gemacht werden.',
        'cancellation-invoice-confirm-question-title': 'Stornierung bestätigen',

        'invoices-confirm-title': 'Rechnung für seminargo freigeben',
        'invoices-confirm-question': 'Soll die Rechnung für seminargo freigegeben werden? Sie können danach keine Änderungen mehr durchführen.',
        'invoices-confirm-question-force': 'Alle Rechnungspositionen und Rechnungsinformationen ohne Überprüfung bestätigen',
        'invoices-confirm-question-title': 'Rechnung für seminargo freigeben',
        'invoice-status-approved': 'Die Rechnung wurde für seminargo freigegeben',

        'invoices-cancel-title': 'Buchung abschließen',
        'invoices-cancel-question': 'Soll die Rechnung storniert und die Buchung abgeschlossen werden? Es wird keine Rechnung versendet.',
        'invoices-cancel-question-title': 'Buchung abschließen',
        'invoice-status-cancelled': 'Die Buchung wurde abgeschlossen',

        'invoices-semconfirm-title': 'Rechnung für Kunden freigeben',
        'invoices-semconfirm-question': 'Soll die Rechnung für den Kunden freigegeben werden?',
        'invoices-semconfirm-question-title': 'Rechnung für Kunden freigeben',
        'invoice-status-semconfirmed': 'Die Rechnung wurde für den Kunden freigegeben',
        'invoices-erpconfirm-title': 'Rechnung an ERP überleiten',
        'invoices-erpconfirm-question': 'Soll die Rechnung für Überleitung ans ERP freigegeben werden?',
        'invoices-erpconfirm-question-title': 'Rechnung für Überleitung ans ERP freigeben',
        'invoice-status-erpconfirmed': 'Die Rechnung wurde für Überleitung ans ERP freigegeben',
        'invoices-semreject-title': 'Änderungen vom Hotel einfordern',
        'invoices-semreject-question': 'Soll die Rechnung wieder dem Hotel zur Bestätigung zugewiesen werden ?',
        'invoices-semreject-question-title': 'Änderungen vom Hotel einfordern',
        'invoice-status-semrejected': 'Die Rechnung wurde dem Hotel für Änderungen zugewiesen',
        'invoices-generate-client-invoices': 'Belegvorschau generieren',

        'invoice-client-rejected': 'Die Rechnung wurde vom Kunden beanstandet',
        'invoice-client-rejected-reason': 'Grund der Beanstandung: ',

        'invoices-download-proforma-invoice': 'Proforma Kundenrechnung',
        'invoices-generate-client-invoices-split-costcentres': 'Aufteilung auf diese Kostenstellen',
        'invoices-generate-client-invoices-no-costcentres': 'Keine Kostenstellen-Aufteilung',

        'invoice-noshows-tab': 'No-Shows',
        'invoice-noshows-participant': 'Teilnehmer',
        'invoice-noshows-empty': 'Für diese Veranstaltung sind keine Teilnehmer erfasst.',
        'invoice-noshow-present': 'Anwesend',
        'invoice-noshow-noshow': 'No-Show',
        'invoice-lineitems-tab': 'Rechnungspositionen',
        'invoice-booked-services-readonly-title': 'Ursprünglich angefragte und gebuchte Leistungen',
        'invoice-booked-services-readonly-subtitle': 'Nur zur Ansicht: Vergleich der erfassten Leistungen mit den Rechnungspositionen.',
        'invoice-booked-services-current-title': 'Aktuell erfasste Leistungen',
        'invoice-extras-tab': 'Zusatzleistungen',
        'invoice-extras-info': 'Leistungen, die während der Veranstaltung zusätzlich konsumiert wurden und nicht vor Ort gezahlt wurden',
        'invoice-media-tab': 'Rechnungsabschluss',
        'invoice-info-tab': 'Rechnungsinformationen',
        'invoice-info-lineitems-tab': 'Rechnungsübersicht',
        'invoice-generated-tab': 'Belegvorschau',
        'invoice-tasks-tab': 'Tasks',

        'invoice-media-upload-header': 'Dokumente hochladen',
        'invoice-media-upload-description': 'Bitte laden Sie Ihre Endrechnung zur Veranstaltung und die zugehörigen Belege hoch.',
        'invoice-media-upload-header-seminargo': 'Dokumente zur Veranstaltung',
        'invoice-media-upload-invoice': 'Endrechnung hochladen',
        'invoice-media-upload-extra': 'Zugehörige Belege hochladen',
        'invoice-media-upload-helper': 'Unterstützte Dateiformate: Bilddateien (PNG, JPG, JPEG), PDF-Dateien. Maximal 10MB pro Datei.',
        'invoice-media-upload-helper-size': 'Die Datei ist zu groß. Maximal 10MB pro Datei.',
        'invoice-media-type-HOTEL_INVOICE': 'Endrechnung',
        'invoice-media-type-HOTEL_EXTRA': 'Beleg',

        'invoice-commission-base': 'Provisionsbasis',
        'invoice-commission-percentage': 'Provisionshöhe',
        'invoice-commission-base-amount': 'Berechnungsbasis',
        'invoice-commission-base-amount-net': 'Nettobetrag exkl. MWSt',
        'invoice-commission-base-amount-gross': 'Bruttobetrag inkl. MWSt',
        'invoice-commmission-info-text-net':
          'Wir erlauben uns lt. Vertrag eine Provision in Höhe von {{commissionPercentage}} ({{commissionAmountNet}}) auf die vereinbarte Nettosumme ({{commissionBaseAmount}}) zzgl. gesetzlicher Ust in Rechnung zu stellen.',
        'invoice-commmission-info-text-gross':
          'Wir erlauben uns lt. Vertrag eine Provision in Höhe von {{commissionPercentage}} ({{commissionAmountNet}}) auf die vereinbarte Bruttosumme ({{commissionBaseAmount}}) zzgl. gesetzlicher Ust in Rechnung zu stellen.',

        'invoice-err-noprice': 'Kein Preis angegeben',

        'invoice-lineitem-day': 'Datum',
        'invoice-lineitem-count': 'Anzahl',
        'invoice-lineitem-header': 'Position',
        'invoice-lineitem-details': 'Beschreibungstext',
        'invoice-lineitem-sku': 'SKU',
        'invoice-lineitem-price-gross': 'Einzelpreis (Brutto)',
        'invoice-lineitem-price-net': 'Einzelpreis (Netto)',
        'invoice-lineitem-suffix-gross': '(Brutto)',
        'invoice-lineitem-suffix-net': '(Netto)',
        'invoice-lineitem-gross': 'Brutto',
        'invoice-lineitem-net': 'Netto',
        'invoice-lineitem-attendance-show': 'Anwesend',
        'invoice-lineitem-attendance-directpay': 'Selbstzahler',
        'invoice-day-accepted': 'Ich bestätige die Leistungen für diesen Tag',
        'invoice-attendance-reuse-show-question': 'Soll die Abwesenheit für diese Teilnehmerin/diesen Teilnehmer für alle Tage übernommen werden?',
        'invoice-attendance-reuse-show-title': 'Übernehmen',

        'invoice-extras-selectsku': '+ Hinzufügen',
        'invoice-extras-selectsku-products': 'Hotelleistungen',
        'invoice-extras-selectsku-bundles': 'Pauschalen',
        'invoice-extras-selectsku-facilities': 'Technik & Ausstattung',
        'invoice-extras-upload-show': 'Beleg anzeigen',
        'invoice-extras-upload-receipt': 'Beleg hochladen',

        'invoice-cancellation-agreedwaive': 'Vereinbarter Stornoverzicht (exkl. Ust)',
        'invoice-cancellation-manualwaive': 'Manueller Stornoverzicht (exkl. Ust)',
        'invoice-cancellation-manualwaive-min-error': 'Der Betrag darf nicht negativ sein',
        'invoice-cancellation-manualwaive-max-error': 'Der Betrag darf die Rechnungssumme nicht übersteigen',
        'invoice-cancellation-full-waive-button': 'Kompletter Stornoverzicht',
        'invoice-cancellation-fees-and-waiver': 'Stornokosten & kostenfreie Abweichung',
        'invoice-cancellation-fees': 'Stornokosten',
        'invoice-cancellation-waiver': 'Kostenfreie Abweichung',
        'invoice-cancellation-waiver-sum': 'Summe',
        'invoice-cancellation-waiver-price-on-request': 'Preis auf Anfrage',
        'invoice-cancellation-waiver-fees-empty': 'Keine Stornokosten-Aufschlüsselung vorhanden.',
        'invoice-cancellation-waiver-phase': 'Stornophase · bis {{days}} Tage ({{rate}})',
        'invoice-cancellation-waiver-cancelled': 'Stornierte Leistungen',
        'invoice-cancellation-waiver-added': 'Hinzugefügte Leistungen',
        'invoice-cancellation-waiver-basis': 'Stornobasis',
        'invoice-cancellation-waiver-phase-costs': 'Stornokosten',
        'invoice-cancellation-waiver-free-period': 'Änderungen außerhalb der Stornophasen',
        'invoice-cancellation-waiver-tooltip-date': 'Erstmals bestätigt am {{date}}',
        'invoice-cancellation-waiver-tooltip-basis': 'Höchste bestätigte Nettosumme: {{amount}}',
        'invoice-cancellation-waiver-tooltip-rate': '{{rate}} davon: {{amount}}',
        'invoice-cancellation-waiver-ask': 'Möchten Sie auf Stornokosten für den Kunden verzichten?',
        'invoice-cancellation-waiver-yes-complete': 'Ja, vollständig verzichten',
        'invoice-cancellation-waiver-partial': 'Teilweise verzichten',
        'invoice-cancellation-waiver-no-full': 'Nein, vollständigen Betrag einfordern',
        'invoice-cancellation-waiver-charged-amount': 'Eingeforderter Betrag',
        'invoice-cancellation-waiver-amount-to-invoice': 'Verrechneter Betrag',
        'invoice-cancellation-waiver-charged-max-error': 'Der Betrag darf die Summe nicht übersteigen',

        'invoice-sender-name': 'Firmenname',
        'invoice-sender-address1': 'Adresse Zeile 1',
        'invoice-sender-address2': 'Adresse Zeile 2',
        'invoice-sender-address3': 'Adresse Zeile 3',
        'invoice-sender-address4': 'Adresse Zeile 4',
        'invoice-sender-zip': 'PLZ',
        'invoice-sender-city': 'Ort',
        'invoice-sender-country': 'Land',
        'invoice-sender-billing-uid': 'UID',
        'invoice-sender-email': 'E-Mail-Adresse',
        'invoice-sender-phone': 'Telefonnummer',
        'invoice-sender-contact': 'Kontaktname',
        'invoice-sender-comment': 'Rechnungskommentar',
        'invoice-sender-accepted': 'Ich bestätige die Rechnungsstelleradresse',
        'invoice-sender-accepted-commission-net':
          'Ich bestätige hiermit die Richtigkeit und Vollständigkeit aller Angaben. Mit der Freigabe wird die Rechnung zur weiteren Bearbeitung und Verrechnung der Provision laut Vertrag in der Höhe von {{commissionPercentage}} ({{commissionAmount}}) auf die vereinbarte Nettosumme ({{baseAmount}}) zzgl. gesetzlicher USt an seminargo übergeben.',
        'invoice-sender-accepted-commission-gross':
          'Ich bestätige hiermit die Richtigkeit und Vollständigkeit aller Angaben. Mit der Freigabe wird die Rechnung zur weiteren Bearbeitung und Verrechnung der Provision laut Vertrag in der Höhe von {{commissionPercentage}} ({{commissionAmount}}) auf die vereinbarte Bruttosumme ({{baseAmount}}) zzgl. gesetzlicher USt an seminargo übergeben.',
        'invoice-sender-accepted-commission-agb': 'AGB',

        'invoice-invoice-edit-title': 'Verrechnungsdetail Extrabelege/Leistungen anpassen',
        'invoice-invoice-edit-commission': 'Provision',
        'invoice-invoice-edit-costcentre': 'Kostenstelle',
        'invoice-invoice-edit-accountingarea': 'Buchungskreis',
        'invoice-invoice-edit-invoicingrule': 'Verteilung',

        'invoice-totals': 'Preisübersicht',
        'invoice-totals-net': 'Summe Netto',
        'invoice-totals-taxes': 'Summe Steuer',
        'invoice-totals-gross': 'Summe Brutto',

        // Security

        'list-header-space': 'Verwaltungsbereich',
        'field-space': 'Verwaltungsbereich',
        'space-seminargo': 'seminargo',

        'security-tabs-header-users': 'Benutzer',
        'security-tabs-header-spaces': 'Verwaltungsbereiche',

        'users-list-page-title': 'Benutzer',
        'users-list-header-name': 'Name',
        'users-list-header-username': 'Benutzername',
        'users-list-header-email': 'E-Mail-Adresse',
        'users-list-header-isadmin': 'Administratorzugriff',
        'users-list-header-islocked': 'Gesperrt',
        'users-list-header-lastseen': 'Zuletzt gesehen',
        'users-list-header-memberships': 'Weitere Verwaltungsbereiche',
        'users-list-add': 'Benutzer anlegen',

        'user-name': 'Name',
        'user-username': 'Benutzername',
        'user-email': 'E-Mail-Adresse',
        'user-isadmin': 'Administratorzugriff',
        'user-islocked': 'Benutzer ist gesperrt',

        'user-memberships-tab': 'Weitere Verwaltungsbereiche',
        'user-membership-add': 'Weiteren Verwaltungsbereich hinzufügen',
        'user-membership-isadmin': 'Administratorzugriff',
        'user-membership-space': 'Verwaltungsbereich',

        'user-create': 'Benutzer anlegen und Passwort versenden',
        'user-created': 'Benutzer wurde angelegt, das Passwort wurde an die E-Mail-Adresse versendet',
        'user-update': 'Benutzer speichern',
        'user-updated': 'Benutzer gespeichert',
        'user-confirm-islocked': 'Der Benutzer wird sich nicht (mehr) anmelden können - sind Sie sicher ?',
        'user-confirm-islocked-title': 'Benutzer sperren',
        'user-reset': 'Passwort zurücksetzen',
        'user-resetted': 'Das Passwort wurde zurückgesetzt und an die E-Mail-Adresse versendet',
        'user-confirm-reset': 'Das Passwort wird auf einen zufälligen Wert gesetzt und an die E-Mail-Adresse versendet.',
        'user-confirm-reset-title': 'Passwort zurücksetzen',
        'user-reset-password-description':
          'Geben Sie ein neues Passwort für den Benutzer ein. Standardmäßig wird ein zufälliges Passwort generiert, das Sie bei Bedarf überschreiben können.',
        'user-new-password': 'Neues Passwort',
        'user-generate-new-password': 'Neues Passwort generieren',
        'user-reset-password-confirm': 'Passwort setzen',
        'user-password-reset-email-sent': 'E-Mail mit Passwort-Reset-Link wurde versendet',
        'user-set-password': 'Passwort setzen',
        'user-set-password-title': 'Passwort direkt setzen',
        'user-password-set-success': 'Das Passwort wurde erfolgreich gesetzt',
        'user-password-changed': 'Das Passwort wurde erfolgreich geändert',
        'user-sudo': 'Als dieser Benutzer anmelden',
        'user-confirm-sudo':
          'Der aktuelle Benutzer wird auf diesen Benutzer umgestellt. Sie müssen sich danach wieder neu anmelden, um als Root-Benutzer zu arbeiten.',
        'user-confirm-sudo-title': 'Anmeldung als anderer Benutzer',
        'user-sudo-failed': 'Anmeldung als anderer Benutzer fehlgeschlagen',
        'graphqlerror-USER_USERNAME_UNIQUE': 'Der Benutzername {{username}} ist bereits in Verwendung.',
        'graphqlerror-USER_PASSWORD_TOO_SHORT': 'Das gewählte Passwort ist zu kurz (mindestens {{minLength}} Zeichen erforderlich).',

        'spaces-list-page-title': 'Verwaltungsbereiche',
        'spaces-list-header-name': 'Name',
        'spaces-list-header-country': 'Land',
        'spaces-list-header-users': 'Benutzer',
        'spaces-list-header-license': 'SEM-Abo',
        'spaces-list-add': 'Verwaltungsbereich provisionieren',

        'space-name': 'Name',
        'space-country': 'Land',
        'space-parentspace': 'Übergeordneter Verwaltungsbereich',
        'space-license': 'SEM-Abo',
        'space-update': 'Verwaltungsbereich speichern',
        'space-updated': 'Verwaltungsbereich gespeichert',
        'space-confirm-update': 'Beim Ändern des Verwaltungsbereichs können sich Änderungen in der Berechtigungsstruktur ergeben.',
        'space-confirm-update-title': 'Warnung - Ändern des Verwaltungsbereichs',
        'space-delete': 'Verwaltungsbereich löschen',
        'space-deleted': 'Verwaltungsbereich gelöscht',
        'space-confirm-delete-title': 'Verwaltungsbereich löschen',
        'space-confirm-delete':
          'Soll der Verwaltungsbereich „{{name}}“ wirklich gelöscht werden? Das Löschen ist nur möglich, wenn er nicht mehr verwendet wird.',
        'space-confirm-delete-double': 'Ich bestätige, dass der Verwaltungsbereich unwiderruflich gelöscht werden soll.',
        'graphqlerror-SPACE_DELETE_NOT_POSSIBLE_STILL_ASSIGNED':
          'Der Verwaltungsbereich „{{name}}“ kann nicht gelöscht werden, solange ihm noch Daten, Benutzer oder untergeordnete Bereiche zugeordnet sind.',
        'graphqlerror-SPACE_DELETE_PUBLIC_NOT_ALLOWED': 'Der öffentliche Verwaltungsbereich kann nicht gelöscht werden.',
        'space-importoffertemplates': 'Standard-Angebotsvorlagen übernehmen',
        'space-offertemplatesimported': 'Standard-Angebotsvorlagen wurden übernommen',
        'space-confirm-importoffertemplates': 'Sollen die Standard-Angebotsvorlagen (Angebot, Reservierungsbestätigung, Storno) importiert werden?',
        'space-confirm-importoffertemplates-title': 'Angebotsvorlagen importieren',
        'space-importemailtemplates': 'Standard-E-Mail-Vorlagen übernehmen',
        'space-emailtemplatesimported': 'Standard-E-Mail-Vorlagen wurden übernommen',
        'space-confirm-importemailtemplates': 'Bestehende E-Mail-Vorlagen werden überschrieben, eventuelle Änderungen daran gehen verloren.',
        'space-confirm-importemailtemplates-title': 'Warnung - Standard-E-Mail-Vorlagen übernehmen',
        'space-importproducts': 'Leistungspalette importieren',
        'space-productsimported': 'Leistungspalette importiert',
        'space-confirm-importproducts': 'Sollen die SEM-Produkte für diesen Space importiert werden?',
        'space-confirm-importproducts-title': 'Leistungspalette importieren',
        'space-importpricelists': 'Preislisten erstellen',
        'space-pricelistscreated': 'Preislisten erstellt',
        'space-confirm-importpricelists': 'Sollen SEM-Preislisten für alle Hotels erstellt werden?',
        'space-confirm-importpricelists-title': 'Preislisten erstellen',
        'space-users': 'Benutzer',

        'space-settings-ci-logofilename-enabled': 'Logo setzen / überschreiben',
        'space-settings-ci-logofilename': 'Logo',
        'space-settings-ci-logofilename-helper': 'Das Logo kann in den Email-Vorlagen verwendet werden. Platzhalter: {{{ciSettings.logoUrl}}}',
        'space-settings-ci-primarycolor': 'Primärfarbe',
        'space-settings-ci-primarycolor-helper':
          'Die Primärfarbe kann in den Email-Vorlagen verwendet werden. Platzhalter: {{{ciSettings.primaryColor}}}',
        'space-settings-ci-primarycolor-enabled': 'Primärfarbe setzen / überschreiben',
        'space-settings-ci-signature': 'Signatur',
        'space-settings-ci-signature-helper': 'Die Signatur kann in den Email-Vorlagen verwendet werden. Platzhalter: {{{ciSettings.signature}}}',
        'space-settings-ci-signature-enabled': 'Signatur setzen / überschreiben',
        'space-settings-ci-tab': 'Corporate Identity',
        'space-settings-ci-save': 'CI-Einstellungen speichern',
        'space-settings-ci-saved': 'CI-Einstellungen gespeichert',
        'space-settings-ci-page-title': 'Corporate Identity',
        'space-settings-ci-inherit-title': 'Einstellungen für den Verwaltungsbereich "{{spaceName}}"',
        'space-settings-ci-inherit-description':
          'Einstellungen werden vom übergeordneten Verwaltungsbereich übernommen. Diese können hier überschrieben werden.',

        'newspace-page-title': 'Verwaltungsbereich provisionieren',
        'newspace-spaceName': 'Verwaltungsbereich',
        'newspace-spaceCountry': 'Land',
        'newspace-parentspace': 'Übergeordneter Verwaltungsbereich',
        'newspace-hotelName': 'Hotelname',
        'newspace-hotelRefCode': 'Hotel Referenzcode',
        'newspace-import-file': 'Hotel Logo (*.png)',
        'newspace-userid': 'Administratorrechte für Benutzer',
        'newspace-newuser': 'Neuen Benutzer anlegen',
        'newspace-userName': 'Name des Benutzers',
        'newspace-userUsername': 'Benutzername',
        'newspace-userEmail': 'E-Mail-Adresse',
        'newspace-userPassword': 'Passwort setzen',
        'newspace-license': 'SEM-Abo',
        'newspace-importDefaultEmailTemplates': 'Standard-E-Mail-Vorlagen einrichten',
        'newspace-importDefaultOfferTemplates': 'Standard-Angebotsvorlagen einrichten',
        'newspace-importDefaultProducts': 'Standard-Leistungspalette einrichten',
        'newspace-importDefaultPriceslist': 'Standard-Preislisten einrichten',
        'newspace-save': 'Verwaltungsbereich provisionieren',
        'newspace-ready': 'Verwaltungsbereich wurde provisioniert und kann nun verwendet werden',
        'newspace-result-email': 'Anmeldung am System mit E-Mail-Adresse (wenn einmalig verwendet)',
        'newspace-result-username': 'Anmeldung am System mit Benutzername',
        'newspace-result-password': 'Anmeldung mit Initial-Passwort',

        'integration-tabs-header-secrets': 'API-Schlüssel',
        'integration-tabs-header-hotels': 'PMS-Verknüpfungen',
        'integration-secrets-list-page-title': 'Schnittstellen',
        'integration-secrets-list-header-name': 'Name',
        'integration-secrets-list-header-system': 'Fremdsystem',
        'integration-secrets-list-header-key': 'API-Schlüssel',
        'integration-secrets-list-add': 'Neuer API-Schlüssel',

        'integration-secret-name': 'Name',
        'integration-secret-system': 'Fremdsystem',
        'integration-apaleo-key': 'Apaleo Client Id',
        'integration-apaleo-secretchange': 'Apaleo Client Secret ändern',
        'integration-apaleo-secret': 'Apaleo Client Secret',
        'integration-elitefo-secret': 'Authentifizierungsschlüssel (von Elitesoftware)',
        'integration-elitefo-secretchange': 'Authentifizierungsschlüssel ändern',
        'integration-elitefo-htuser': 'HTTP-Basic Auth User (festlegen)',
        'integration-elitefo-htpasswd': 'HTTP-Basic Auth Passwort (festlegen)',
        'integration-protelids-pushurl': 'Push URL',
        'integration-protelids-user': 'Auth User (festlegen)',
        'integration-protelids-passwd': 'Auth Passwort (festlegen)',
        'integration-meetingpackage-key': 'Meeting Package API Key',
        'integration-meetingpackage-keychange': 'Meeting Package API Key (ändern)',

        'integration-secret-save': 'API-Schlüssel speichern',
        'integration-secret-saved': 'API-Schlüssel gespeichert',
        'integration-secret-confirm-delete-title': 'API-Schlüssel löschen',
        'integration-secret-confirm-delete': 'Soll der API-Schlüssel wirklich gelöscht werden ? Die Verknüpfung mit dem PMS wird daraufhin entfernt.',
        'integration-secret-deleted': 'API-Schlüssel gelöscht',
        'integration-secret-delete': 'API-Schlüssel löschen',

        'integration-secret-test': 'Verbindungstest',
        'integration-secret-test-title': 'Verbindungstest',
        'integration-secret-test-empty': 'Die Verbindung ist technisch funktionstüchtig, aber die Liste der Hotel-Standorts ist leer.',
        'integration-secret-test-result': 'Die Verbindung ist technisch funktionstüchtig. Es wurde eine Liste von Hotel-Standorten ausgelesen:',

        'integration-hotels-list-header-hotel': 'Hotel',
        'integration-hotels-list-header-system': 'Fremdsystem',
        'integration-hotels-list-header-name': 'API-Schlüssel',
        'integration-hotels-list-add': 'Neue PMS-Verknüpfung',

        'integration-hotels-list-page-title': 'PMS-Verknüpfungen',
        'integration-hotel-secret': 'API-Schlüssel',
        'integration-hotel-pmsproperty': 'PMS Hotel',
        'integration-hotel-new': 'Neues SEM Hotel anlegen und verknüpfen',
        'integration-hotel-hotel': 'Verknüpftes Hotel',
        'integration-hotel-extrefcode': 'Externer Referenzcode',
        'integration-hotel-hotel-select': 'Bereits angelegtes SEM Hotel auswählen',
        'integration-hotel-hotel-help':
          'Eine eventuell vorhandene Verknüpfung mit einem anderen SEM Hotel im gleichen Verwaltungsbereich wird gelöst',
        'integration-hotel-space-help':
          'Eine eventuell vorhandene Verknüpfung mit einem anderen SEM Hotel im gleichen Verwaltungsbereich wird gelöst',
        'integration-hotel-pms-product-name': 'PMS-Produkt',
        'integration-hotel-pms-facility-name': 'PMS-Ausstattung',
        'integration-hotel-pms-pricelist': 'PMS-Preisliste',
        'integration-hotel-pms-include': '',

        'integration-hotel-syncproperties': 'Synchronisierung der Hotelstammdaten',
        'integration-hotel-syncproducts': 'Synchronisierung des Produktkatalogs',
        'integration-hotel-syncprices': 'Synchronisierung der Preislisten',
        'integration-hotel-syncpricesdays': 'Anzahl der Tage',
        'integration-hotel-syncavailability': 'Synchronisierung der Verfügbarkeiten',
        'integration-hotel-syncavailabilitydays': 'Anzahl der Tage',
        'integration-hotel-directbooking': 'Direktbuchungen',

        'integration-hotel-log-tab': 'Datenabgleich',
        'integration-hotel-log-status': 'Status',
        'integration-hotel-log-action': 'Aktion(en)',
        'integration-hotel-log-trigger': 'Auslöser',
        'integration-hotel-log-offer': 'Buchung',
        'integration-hotel-log-startdate': 'Beginn',
        'integration-hotel-log-enddate': 'Ende',
        'integration-hotel-log-showresult': 'Protokoll anzeigen',
        'integration-hotel-log-showresult-title': 'Protokoll',

        'integration-hotel-create': 'PMS-Verknüpfung anlegen',
        'integration-hotel-created': 'PMS-Verknüfpung wurde angelegt',
        'integration-hotel-save': 'PMS-Verknüpfung speichern',
        'integration-hotel-saved': 'PMS-Verknüpfung gespeichert',
        'integration-hotel-sync': 'Datenabgleich starten',
        'integration-hotel-sync-triggered': 'Datenabgleich gestartet',

        'integration-hotel-confirm-delete-title': 'PMS-Verknüpfung löschen',
        'integration-hotel-confirm-delete': 'Soll die PMS-Verknüpfung mit dem Hotel wirklich gelöscht werden ?',
        'integration-hotel-deleted': 'PMS-Verknüpfung gelöscht',
        'integration-hotel-delete': 'PMS-Verknüpfung löschen',

        'integration-log-result-empty': 'Kein Ergebnis',
        'integration-sync-col-name': 'Name',
        'integration-sync-col-ref-code': 'Ref-Code',
        'integration-sync-col-sku': 'SKU',
        'integration-sync-col-external-ref-code': 'Externer Ref-Code',
        'integration-sync-col-added': 'Hinzugefügt',
        'integration-sync-col-updated': 'Aktualisiert',
        'integration-sync-col-success': 'Erfolg',
        'integration-sync-col-error': 'Fehler',
        'integration-sync-section-property': 'Stammdaten',
        'integration-sync-section-product': 'Leistung',
        'integration-sync-section-facility': 'Ausstattung',
        'integration-sync-section-availability': 'Verfügbarkeit',
        'integration-sync-section-price': 'Preisliste',

        'integration-pms-elitefo-setup-title': 'Einrichtung in Elite Hotel Software',
        'integration-pms-protelids-setup-title': 'Einrichtung in Protel IDS Software',
        'integration-pms-sem-endpoint-label': 'SEM-Endpoint:',
        'integration-pms-elitefo-http-basic-point': 'HTTP-Basic-Authentifizierung wie im API-Schlüssel angegeben',
        'integration-pms-protel-auth-point': 'Authentifizierung wie im API-Schlüssel angegeben',
        'integration-pms-requestor-id-label': 'RequestorID:',
        'integration-pms-hotel-code-label': 'HotelCode:',
        'integration-pms-protel-property-code-label': 'Property Code:',
        'integration-pms-term-reference-code': 'Referenzcode',
        'integration-pms-elitefo-category-before': 'Kategorie: entspricht der ',
        'integration-pms-elitefo-category-after': ' der jeweiligen SEM-Leistungen',
        'integration-pms-elitefo-board-before': 'Verpflegung: entspricht der ',
        'integration-pms-elitefo-board-after': ' der jeweiligen SEM-Leistungen',
        'integration-pms-elitefo-arracode-before': 'Arracode: entspricht dem ',
        'integration-pms-elitefo-arracode-after': ' der SEM-Preisliste',
        'integration-pms-protel-roomcode-before': 'Room Code: entspricht der ',
        'integration-pms-protel-roomcode-after': ' der jeweiligen SEM-Leistungen',
        'integration-pms-protel-ratecode-before': 'Rate Code: entspricht dem ',
        'integration-pms-protel-ratecode-after': ' der SEM-Preisliste',

        // Generic / Reusable

        'generic-new': 'Neu',
        'generic-no-option': 'Keine Auswahlmöglichkeit vorhanden',
        'generic-selection-empty': 'Keine Auswahl',
        'generic-unsaved-changes': 'Deine Änderungen werden nicht gespeichert. Fortfahren ?',

        // Login / Signin
        'login-title': 'Anmeldung bei seminargo',
        'login-email': 'E-Mail oder Benutzername',
        'login-password': 'Passwort',
        'login-login': 'Anmelden',
        'login-ready': 'Angemeldet',
        'login-failed': 'Anmeldung fehlgeschlagen, bitte Passwort überprüfen',
        'login-no-account': 'Noch kein Konto? Registrieren',
        'login-forgot-password': 'Passwort vergessen?',
        'login-forgot-send': 'Passwort zurücksetzen',
        'login-forgot-ready':
          'Falls ein passender Benutzer gefunden werden konnte, erhalten Sie eine E-Mail mit einer Anleitung zum Zurücksetzen des Passworts.',
        'login-reset-title': 'Neues Passwort',
        'login-reset': 'Neues Passwort setzen',
        'login-reset-ready': 'Das Passwort wurde zurückgesetzt. Sie können sich jetzt beim seminargo Backoffice mit dem neuen Passwort anmelden.',
        'login-reset-failed': 'Das Passwort konnte nicht zurückgesetzt werden',

        // Maintenance Mode
        'maintenance-title': 'Wartungsarbeiten',
        'maintenance-message': 'Das System befindet sich derzeit in Wartung. Bitte versuchen Sie es später erneut.',

        'my-account': 'Mein Konto',
        logout: 'Abmelden',

        'main-filter-select': 'Hotel/Verwaltungsbereich auswählen',
        'main-filter-select-hotel': 'Hotel auswählen',
        'main-filter-empty': 'Alle anzeigen',
        'main-usermenu-account': 'Mein Konto',
        'main-usermenu-logout': 'Abmelden',

        'me-page-title': 'Mein Konto',
        'me-password-header': 'Passwort ändern',
        'me-password-oldpassword': 'Aktuelles Passwort',
        'me-password-newpassword1': 'Neues Passwort',
        'me-password-newpassword2': 'Neues Passwort Wiederholen',
        'me-password-not-matching': 'Die Passwörter stimmen nicht überein',
        'me-confirm-password':
          'Das neue Passwort wird sofort aktiv, Sie werden zur Anmeldeseite weitergeleitet und das aktuelle Passwort kann nicht mehr zur Anmeldung verwendet werden',
        'me-confirm-password-title': 'Passwort ändern',
        'me-password': 'Passwort ändern',
        'me-password-changed': 'Das Passwort wurde geändert',
        'me-password-notchanged': 'Das Passwort wurde nicht geändert, bitte überprüfen Sie das aktuelle Passwort',

        'media-selection-dialog-title': 'Medienauswahl',
        'media-selection-dialog-add-selected': 'Ausgewählte hinzufügen',
        'contentmedia-name': 'Name',
        'contentmedia-flag-logo': 'Logo',
        'contentmedia-flag-offer': 'Angebot',
        'contentmedia-flag-outdoorView': 'Außenansicht',
        'contentmedia-flag-restaurant': 'Restaurant',
        'contentmedia-flag-room': 'Zimmer',
        'contentmedia-flag-seminarRoom': 'Seminarraum',
        'contentmedia-flag-homepage': 'Homepage',
        'contentmedia-type': 'Typ',

        refresh: 'Aktualisieren',

        // Error page
        'error-generic': 'Ein unerwarteter Fehler ist aufgetreten.',
        'error-team-notified': 'Unser Team wurde automatisch darüber informiert.',
        'error-details-button': 'Fehlerdetails anzeigen',
        'error-details-title': 'Technische Details',
        'error-details-close': 'Schließen',
        'error-event-id': 'Fehler-ID für Support:',
        'error-refresh': 'Seite neu laden',
        'error-back-to-start': 'Zurück zur Startseite',

        'sem-version-dialog-title': 'Versionsinformation',

        // Invoicing Rules
        'enums-EAMSplitInvoicingRule-ATTENDANT': 'Per Teilnehmer',
        'enums-EAMSplitInvoicingRule-MAIN': 'Hauptrechnung',
        'enums-EAMSplitInvoicingRule-SPLIT': 'Aufteilen',

        // Change Categories
        'enums-ChangeCategory-ATTENDEE': 'Teilnehmer',
        'enums-ChangeCategory-EVENT': 'Veranstaltung',
        'enums-ChangeCategory-PLANNING': 'Planung',
        'enums-ChangeCategory-BILLING': 'Rechnungsstellung',
        'enums-ChangeCategory-SERVICE': 'Leistung',
        'enums-ChangeCategory-ROOM': 'Raum',
        'enums-ChangeCategory-DAY': 'Tag',

        // Accounting Area Types
        'enums-EAccountingAreaType-NONE': 'Kein',
        'enums-EAccountingAreaType-MAIN': 'Hauptkostenstelle',
        'enums-EAccountingAreaType-BY_ATTENDANT': 'Per Teilnehmer',
        'enums-EAccountingAreaType-BY_ATTENDANT_BY_DAY': 'Per Teilnehmer und Tag',

        // Cost Centre Types
        'enums-ECostCentreType-NONE': 'Kein',
        'enums-ECostCentreType-MAIN': 'Hauptkostenstelle',
        'enums-ECostCentreType-BY_ATTENDANT': 'Per Teilnehmer',
        'enums-ECostCentreType-BY_ATTENDANT_BY_DAY': 'Per Teilnehmer und Tag',

        // Entity Types for History
        'enums-EOfferHistoryEntityType-EVENT': 'Veranstaltung',
        'enums-EOfferHistoryEntityType-EVENT_DAY': 'Veranstaltungstag',
        'enums-EOfferHistoryEntityType-EVENT_DAY_ROOM': 'Raum',
        'enums-EOfferHistoryEntityType-ATTENDANCE': 'Teilnahme',
        'enums-EOfferHistoryEntityType-ATTENDANCE_DAY': 'Teilnahmetag',
        'enums-EOfferHistoryEntityType-ATTENDEE_PROFILE': 'Teilnehmerprofil',
        'enums-EOfferHistoryEntityType-ACCOUNTING_AREA': 'Buchungskreis',
        'enums-EOfferHistoryEntityType-COST_CENTRE': 'Kostenstelle',
        'enums-EOfferHistoryEntityType-SERVICE': 'Leistung',
        'enums-EOfferHistoryEntityType-OFFER_VERSION': 'Angebotsversion',
        'enums-EOfferHistoryEntityType-OFFER': 'Angebot',

        // Change Types
        'enums-ChangeType-ADDED': 'Hinzugefügt',
        'enums-ChangeType-REMOVED': 'Entfernt',
        'enums-ChangeType-CHANGED': 'Geändert',
        'enums-ChangeType-UNCHANGED': 'Unverändert',

        // Neue Email History Keys
        'offer-history-email-resend': 'Erneut senden',
        'offer-history-email-resending': 'Wird gesendet...',
        'offer-history-email-resend-success': 'E-Mail wurde erfolgreich erneut gesendet: {{subject}}',
        'offer-history-email-resend-success-generic': 'E-Mail "{{subject}}" wurde erfolgreich erneut gesendet',
        'offer-history-email-resend-error': 'Fehler beim erneuten Senden der E-Mail',
        'offer-history-email-resend-confirm-title': 'E-Mail erneut senden',
        'offer-history-email-resend-confirm-message': 'Möchten Sie diese E-Mail wirklich erneut senden?',
        'offer-history-email-resend-confirm-hotel': 'Hotel',
        'offer-history-email-resend-confirm-subject': 'Betreff',
        'offer-history-email-resend-confirm-recipients': 'Empfänger',
        'offer-history-email-resend-confirm-warning': 'Diese Aktion sendet die E-Mail mit der ursprünglichen Vorlage erneut.',
        'offer-history-email-resend-confirm': 'Erneut senden',
        'offer-history-email-resend-cancel': 'Abbrechen',
        'offer-history-email-cc-title': 'Alternative Empfänger (CC):',
        'offer-history-email-cc-placeholder': 'E-Mail-Adresse hinzufügen',
        'offer-history-email-invalid-address': 'Ungültige E-Mail-Adresse',
        'offer-history-email-max-cc': 'Maximal 5 zusätzliche E-Mail-Adressen erlaubt',
        'offer-history-email-max-addresses': 'Maximal 5 E-Mail-Adressen erlaubt',
        'offer-history-email-duplicate-cc': 'Diese E-Mail-Adresse wurde bereits hinzugefügt',
        'offer-history-email-unknown-error': 'Unbekannter Fehler',
        'offer-history-email-recipients-count-preview': '{{count}} Empfänger: {{recipientType}}: {{emailAddress}} ({{sourceName}}){{suffix}}',
        'offer-history-email-more-recipients': '{{count}} weitere Empfänger',
        'offer-history-email-hotel-with-email': 'Hotel ({{email}})',
        'offer-history-transfer-from-to': 'Buchung wurde von {{oldClientName}}{{oldCompanyName}} an {{newClientName}}{{newCompanyName}} übertragen',
        'offer-history-transfer-to': 'Buchung wurde an {{newClientName}}{{newCompanyName}} übertragen',
        'offer-history-transfer-to-new-contact': 'Buchung wurde an neuen Ansprechpartner übertragen',
        'offer-history-transfer-reason': 'Grund: {{reason}}',
        'offer-history-template-booking-received-client': 'Buchungsbestätigung an Kunde',
        'offer-history-template-booking-received-hotel': 'Buchungsanfrage an Hotel',
        'offer-history-template-booking-approved-client': 'Buchung bestätigt - Kunde',
        'offer-history-template-booking-approved-hotel': 'Buchung bestätigt - Hotel',
        'offer-history-template-booking-rejected-client': 'Buchung abgelehnt - Kunde',
        'offer-history-template-booking-rejected-hotel': 'Buchung abgelehnt - Hotel',
        'offer-history-template-offer-received-client': 'Angebot erhalten - Kunde',
        'offer-history-template-offer-reminder-client': 'Erinnerung Angebot - Kunde',
        'offer-history-template-offer-reminder-hotel': 'Erinnerung Angebot - Hotel',
        'offer-history-template-client-verification': 'E-Mail Verifizierung',
        'offer-history-template-lister-nonbinding-request-hotel': 'Unverbindliche Anfrage - Hotel',
        'offer-history-template-lister-booking-direct-client': 'Direktbuchung - Kunde',
        'offer-history-template-lister-booking-direct-hotel': 'Direktbuchung - Hotel',
        'offer-history-template-lister-booking-cancellation-hotel': 'Buchung storniert über Hotellister (an Hotel)',
        'offer-history-template-lister-reservation-hotel': 'Reservierung über Hotellister (an Hotel)',
        'offer-history-template-lister-reservation-approved-client': 'Reservierung bestätigt über Hotellister (an Kunde)',
        'offer-history-template-lister-booking-reminder-start-soon-hotel': 'Erinnerung Veranstaltungsbeginn bald über Hotellister (an Hotel)',
        'offer-history-template-lister-event-planning-hotel': 'Veranstaltungsplanung über Hotellister (an Hotel)',
        'offer-history-template-lister-missing-data-hotel': 'Fehlende Daten über Hotellister (an Hotel)',
        'offer-history-template-lister-missing-reservation-client': 'Fehlende Reservierung über Hotellister (an Kunde)',
        hotel: 'Hotel',
        email: 'E-Mail',
        'offer-email-history-header-zeitpunkt': 'Zeitpunkt',
        'offer-email-history-header-sender': 'Sender',
        'offer-email-history-header-alternative-recipients': 'Alternative Empfänger',
        'offer-email-history-header-description': 'Beschreibung',
        'offer-history-email-preview': 'Vorschau',
        'offer-history-email-preview-title': 'E-Mail Vorschau',
        'offer-history-email-template-details': 'E-Mail Template Details',
        'offer-history-email-system': 'System',
        'offer-history-email-recipients': 'Empfänger',
        'offer-history-email-body': 'E-Mail Inhalt',
        'offer-history-email-attachments': 'Anhänge',
        'offer-history-email-content': 'Inhalt',
        'offer-history-email-no-content': 'Kein Inhalt verfügbar',

        // Email History Table Headers
        'offer-email-history-header-user': 'Benutzer',
        'offer-email-history-header-template': 'Template',
        'offer-email-history-header-subject': 'Betreff',
        'offer-email-history-header-recipients': 'Empfänger',
        'offer-email-history-header-actions': 'Aktionen',

        // Email History Table Content
        'offer-email-history-sent': 'Versendet',
        'offer-email-history-failed': 'Fehlgeschlagen',
        'offer-email-history-no-recipients': 'Keine Empfänger',
        'offer-email-history-unknown': 'Unbekannt',
        'offer-email-history-no-enhanced-data': 'Keine erweiterten Daten verfügbar',
        'offer-email-history-error-details': 'Fehler-Details',
        'offer-email-history-no-subject': 'Kein Betreff',

        // Email Template Details
        'offer-email-history-template': 'Template',
        'offer-email-history-language': 'Sprache',
        'offer-email-history-from': 'Von',
        'offer-email-history-reply-to': 'Antwort an',
        'offer-email-history-user': 'Benutzer',
        'offer-email-history-sent-at': 'Gesendet um',

        // Recipients Table
        'offer-email-history-recipient-type': 'Typ',
        'offer-email-history-recipient-email': 'E-Mail',
        'offer-email-history-recipient-source': 'Quelle',
        'offer-email-history-recipient-delivery-status': 'Zustellstatus',

        // Attachments Table
        'offer-email-history-attachment-filename': 'Dateiname',
        'offer-email-history-attachment-type': 'Typ',
        'offer-email-history-attachment-reference': 'Referenz',
        'offer-email-history-offer-version': 'Angebotsversion',
        'offer-email-history-event': 'Veranstaltung',
        'offer-email-history-custom-file': 'Benutzerdefinierte Datei',

        // History Changes
        'offer-history-changes': 'Änderungen',
        'offer-history-general': 'Allgemein',
        'offer-history-day': 'Tag',
        'offer-history-entry': 'Eintrag',
        'offer-history-before': 'Vorher',
        'offer-history-after': 'Nachher',
        'offer-history-change': 'Änderung',
        'offer-history-type': 'Typ',

        // Change Types
        'offer-history-added': 'Hinzugefügt',
        'offer-history-removed': 'Entfernt',
        'offer-history-changed': 'Geändert',
        'offer-history-unchanged': 'Unverändert',

        // Additional Info
        // Fehlende LISTER Email Template Übersetzungen
        'enums-EContentEmailTemplate-LISTER_NONBINDING_REQUEST_HOTEL': 'Unverbindliche Anfrage über Hotellister (an Hotel)',
        'enums-EContentEmailTemplate-LISTER_REJECTED_OFFER_HOTEL': 'Abgelehnte Verfügbarkeit über Hotellister (an Hotel)',
        'enums-EContentEmailTemplate-LISTER_CR_HOTEL': 'Änderungswunsch über Hotellister (an Hotel)',
        'enums-EContentEmailTemplate-LISTER_CR_APPROVED_CLIENT': 'Änderungswunsch bestätigt über Hotellister (an Kunde)',
        'enums-EContentEmailTemplate-LISTER_CR_REJECTED_CLIENT': 'Änderungswunsch abgelehnt über Hotellister (an Kunde)',
        'enums-EContentEmailTemplate-LISTER_CR_APPROVED_HOTEL': 'Änderungswunsch bestätigt über Hotellister (an Hotel)',
        'enums-EContentEmailTemplate-LISTER_CR_REJECTED_HOTEL': 'Änderungswunsch abgelehnt über Hotellister (an Hotel)',
        'enums-EContentEmailTemplate-CLASSIC_CR_HOTEL': 'Reservierungsänderung angefragt (Classic, an Hotel)',
        'enums-EContentEmailTemplate-CLASSIC_CR_APPROVED_CLIENT': 'Reservierungsänderung bestätigt (Classic, an Kunde)',
        'enums-EContentEmailTemplate-CLASSIC_CR_REJECTED_CLIENT': 'Reservierungsänderung abgelehnt (Classic, an Kunde)',
        'enums-EContentEmailTemplate-CLASSIC_CR_APPROVED_HOTEL': 'Reservierungsänderung akzeptiert (Classic, an Hotel)',
        'enums-EContentEmailTemplate-CLASSIC_CR_RECONFIRMED_CLIENT': 'Neue Reservierungsbestätigung (Classic, an Kunde)',
        'enums-EContentEmailTemplate-CLASSIC_CR_REJECTED_HOTEL': 'Reservierungsänderung abgelehnt (Classic, an Hotel)',
        'enums-EContentEmailTemplate-LISTER_AVAILABILITY_APPROVED_CLIENT': 'Verfügbarkeit bestätigt über Hotellister (an Kunde)',
        'enums-EContentEmailTemplate-LISTER_RESERVATION_APPROVED_CLIENT': 'Reservierung bestätigt über Hotellister (an Kunde)',
        'enums-EContentEmailTemplate-LISTER_RESERVATION_HOTEL': 'Reservierung über Hotellister (an Hotel)',
        'enums-EContentEmailTemplate-LISTER_RESERVATION_REJECTED_CLIENT': 'Reservierung abgelehnt über Hotellister (an Kunde)',
        'enums-EContentEmailTemplate-LISTER_AVAILABILITY_REJECTED_CLIENT': 'Verfügbarkeit abgelehnt über Hotellister (an Kunde)',
        'enums-EContentEmailTemplate-LISTER_BOOKING_CANCELLATION_CLIENT': 'Buchung storniert über Hotellister (an Kunde)',
        'enums-EContentEmailTemplate-LISTER_BOOKING_CANCELLATION_HOTEL': 'Buchung storniert über Hotellister (an Hotel)',
        'enums-EContentEmailTemplate-LISTER_EVENT_PLANNING_HOTEL': 'Veranstaltungsplanung über Hotellister (an Hotel)',
        'enums-EContentEmailTemplate-LISTER_BOOKING_REMINDER_START_TODAY_HOTEL': 'Erinnerung Veranstaltungsbeginn heute über Hotellister (an Hotel)',
        'enums-EContentEmailTemplate-LISTER_BOOKING_REMINDER_END_TODAY_HOTEL': 'Erinnerung Veranstaltungsende heute über Hotellister (an Hotel)',
        'enums-EContentEmailTemplate-LISTER_BOOKING_REMINDER_START_SOON_HOTEL': 'Erinnerung Veranstaltungsbeginn bald über Hotellister (an Hotel)',
        'enums-EContentEmailTemplate-LISTER_BOOKING_INFORMATION_HOTEL': 'Buchungsinformationen über Hotellister (an Hotel)',
        'enums-EContentEmailTemplate-LISTER_BOOKING_CANCELLATION_CONFIRM_HOTEL': 'Stornierungsbestätigung über Hotellister (an Hotel)',
        'enums-EContentEmailTemplate-LISTER_AVAILABILITY_WITHDRAW_CLIENT': 'Verfügbarkeit zurückgezogen über Hotellister (an Kunde)',
        'enums-EContentEmailTemplate-LISTER_CANCELLATION_PERIOD_START_CLIENT': 'Stornierungsfrist beginnt über Hotellister (an Kunde)',
        'enums-EContentEmailTemplate-LISTER_CANCELLATION_REQUEST_APPROVED_CLIENT': 'Stornierungsanfrage bestätigt über Hotellister (an Kunde)',
        'enums-EContentEmailTemplate-LISTER_INVOICE_RELEASE_REMINDER_CLIENT': 'Erinnerung Rechnungsfreigabe über Hotellister (an Kunde)',
        'enums-EContentEmailTemplate-LISTER_MISSING_DATA_HOTEL': 'Fehlende Daten über Hotellister (an Hotel)',
        'enums-EContentEmailTemplate-LISTER_MISSING_RESERVATION_CLIENT': 'Fehlende Reservierung über Hotellister (an Kunde)',
        'enums-EContentEmailTemplate-LISTER_MISSING_SEMINARDETAILS_CLIENT': 'Fehlende Seminardetails über Hotellister (an Kunde)',
        'enums-EContentEmailTemplate-LISTER_POS_REMINDER_CLIENT': 'Erinnerung POS über Hotellister (an Kunde)',
        'enums-EContentEmailTemplate-LISTER_RELEASE_REPRESENTATION_HOTEL': 'Freigabe Vertretung über Hotellister (an Hotel)',
        'enums-EContentEmailTemplate-LISTER_RESET_PASSWORD': 'Passwort zurücksetzen über Hotellister',
        'enums-EContentEmailTemplate-LISTER_TITLE_CHANGE_HOTEL': 'Titeländerung über Hotellister (an Hotel)',
        'enums-EContentEmailTemplate-LISTER_INVOICE_HOTEL': 'Ausgangsrechnungen',
        'enums-EContentEmailTemplate-LISTER_CREDIT_NOTE_HOTEL': 'Gutschriften',
        'enums-EContentEmailTemplate-COMPANY_INVITATION': 'Firmen-Einladung',

        // Email Recipient Source Übersetzungen
        'enums-EEmailRecipientSource-HOTEL_BUSINESS_EMAIL': 'Hotel Geschäfts-E-Mail',
        'enums-EEmailRecipientSource-HOTEL_NOTIFICATION_EMAIL': 'Hotel Benachrichtigungs-E-Mail',
        'enums-EEmailRecipientSource-HOTEL_CONTACT_RESERVATION': 'Hotel Kontakt (Reservierung)',
        'enums-EEmailRecipientSource-HOTEL_CONTACT_MANAGEMENT': 'Hotel Kontakt (Management)',
        'enums-EEmailRecipientSource-HOTEL_CONTACT_BANQUET': 'Hotel Kontakt (Bankett)',
        'enums-EEmailRecipientSource-HOTEL_CONTACT_SALES': 'Hotel Kontakt (Vertrieb)',
        'enums-EEmailRecipientSource-HOTEL_CONTACT_ACCOUNTING': 'Hotel Kontakt (Buchhaltung)',
        'enums-EEmailRecipientSource-CLIENT_EMAIL': 'Kunde',
        'enums-EEmailRecipientSource-SEMINARGO_TEAM': 'seminargo Team',
        'enums-EEmailRecipientSource-MANUAL_ENTRY': 'Manueller Eintrag',
        'enums-EEmailRecipientSource-USER_EMAIL': 'Benutzer',

        // Email Delivery Status Übersetzungen
        'enums-EEmailDeliveryStatus-PENDING': 'Ausstehend',
        'enums-EEmailDeliveryStatus-SUCCESS': 'Zugestellt',
        'enums-EEmailDeliveryStatus-ERROR': 'Fehlgeschlagen',

        // Groups
        'groups-title': 'Ketten',
        'groups-create': 'Neue Kette',
        'groups-edit': 'Kette bearbeiten',
        'groups-search': 'Nach Kettenname suchen',
        'groups-name': 'Kettenname',
        'groups-hotel-count': 'Anzahl Hotels',
        'groups-assigned-hotels': 'Zugeordnete Hotels',
        'groups-created-success': 'Kette wurde erfolgreich erstellt',
        'groups-updated-success': 'Kette wurde erfolgreich aktualisiert',
        'groups-deleted-success': 'Kette wurde erfolgreich gelöscht',
        'groups-delete-confirm-title': 'Kette löschen',
        'groups-delete-confirm-message':
          'Sind Sie sicher, dass Sie die Kette "{{name}}" löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
        back: 'Zurück',
        save: 'Speichern',
        delete: 'Löschen',
        search: 'Suchen',
        'created-at': 'Erstellt am',
        'created-by': 'Erstellt von',
        'validation-required': 'Dieses Feld ist erforderlich',

        // User Groups
        'usergroup-add-button': 'User Gruppe hinzufügen',
        'usergroup-dialog-title': 'User Gruppe erstellen',
        'usergroup-dialog-edit-title': 'User Gruppe bearbeiten',
        'usergroup-name-label': 'Name der User Gruppe',
        'usergroup-name-placeholder': 'Bitte tragen Sie hier den Namen der User Gruppe ein (max. 30 Zeichen)',
        'usergroup-button-create': 'User Gruppe erstellen',
        'usergroup-button-update': 'User Gruppe speichern',
        'usergroup-button-cancel': 'Abbrechen',
        'usergroup-list-name': 'Name',
        'usergroup-list-members-count': 'Anzahl Mitglieder',
        'usergroup-list-actions': 'Aktionen',
        'usergroup-delete-confirm-title': 'User Gruppe löschen',
        'usergroup-delete-confirm-message':
          'Sind Sie sicher, dass Sie die User Gruppe "{{name}}" löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
        'usergroup-created-success': 'User Gruppe wurde erfolgreich erstellt',
        'usergroup-updated-success': 'User Gruppe wurde erfolgreich aktualisiert',
        'usergroup-deleted-success': 'User Gruppe wurde erfolgreich gelöscht',
        'usergroup-members-updated-success': 'Mitglieder der User Gruppe wurden erfolgreich aktualisiert',
        'usergroup-error-name-required': 'Name ist erforderlich',
        'usergroup-error-name-too-long': 'Name darf maximal 30 Zeichen lang sein',
        'usergroup-error-name-exists': 'Eine User Gruppe mit diesem Namen existiert bereits',
        'usergroup-members-title': 'Mitglieder verwalten',
        'usergroup-members-description': 'Wählen Sie die Ansprechpersonen aus, die zu dieser User Gruppe gehören sollen:',
        'usergroup-no-groups': 'Keine User Gruppen vorhanden',

        'selector-lister-amenities': 'Ausstattung',
        'selector-lister-beamer': 'Beamer & Leinwand / Flatscreen',
        'selector-lister-flipchart': 'Flipchart',
        'selector-lister-pinnwand': 'Pinnwand',
        'selector-lister-moderation': 'Moderationskoffer',
        'selector-lister-board-small': 'Pauschale Klein',
        'selector-lister-board-small-subtitle': '1x Kaffeepause',
        'selector-lister-board-medium': 'Pauschale Halbtags',
        'selector-lister-board-medium-subtitle': '1x Kaffeepause, 1x Mittagessen',
        'selector-lister-board-regular': 'Pauschale Ganztags',
        'selector-lister-board-regular-subtitle': '2x Kaffeepause, 1x Mittagessen',
        'selector-lister-board-welcome-coffee': 'Empfangskaffee',
        'selector-lister-board-coffee-break': 'Kaffeepause',
        'selector-lister-board-lunch': 'Mittagessen',
        'selector-lister-board-dinner': 'Abendessen',
        'selector-lister-combinations-optional': 'Optional',
        'selector-lister-meetingrooms': 'Seminarräume',
        'selector-lister-meetingrooms-missing': 'Keine Seminarräume mit Ausstattung verknüpft.',
        'selector-lister-meetingrooms-included': 'inkludiert:',
        'selector-lister-meetingrooms-equipment-beamer': '1x Beamer & Leinwand / Flatscreen',
        'selector-lister-meetingrooms-equipment-flipchart': '1x Flipchart',
        'selector-lister-meetingrooms-equipment-pinnwand': '1x Pinnwand',
        'selector-lister-open-hotel-settings': 'Hotel-Einstellungen öffnen',
        'selector-lister-rooms': 'Zimmer',
        'selector-lister-board': 'Verpflegung',
        'selector-lister-combinations': 'Kombinationen',
        'selector-lister-room-single': 'Einzelzimmer',
        'selector-lister-room-double': 'Doppelzimmer (pro Person)',
        'selector-lister-open-product': 'Produkt öffnen',
        'selector-lister-open-bundle': 'Paket öffnen',
        'selector-lister-open-facility': 'Ausstattung öffnen',
        'selector-lister-single-services': 'Einzelleistungen',
        'selector-baseprice-info':
          'Die Basispreisliste ist immer gültig, wenn keine andere Preisliste definiert ist. Sie unterliegt keinen Einschränkungen zu Gültigkeitszeitraum, Wochentagen, Teilnehmerzahl oder Kundentyp.',

        'offer-document-delete-confirm-question': 'Sind Sie sicher, dass Sie dieses Dokument löschen möchten?',
        'offer-document-delete-confirm-title': 'Dokument löschen',

        // Bericht
        'bericht-tab-earlybird': 'Fruehbucher',
        'bericht-tab-statistics': 'Buchungsstatistik',
        'bericht-tab-requests': 'Anfragen/Buchungen',
        'bericht-tab-hotel-contracts': 'Hotel-Vereinbarungen',
        'bericht-tab-contact-overview': 'Kontaktübersicht',
        'filter-date-from': 'Von (Datum)',
        'filter-date-to': 'Bis (Datum)',
        'filter-employee': 'Mitarbeiter',
        'filter-company-country': 'Firmenland',
        'filter-company-search-placeholder': 'Firmenname eingeben',
        'filter-contact-subject': 'Betreff',
        'filter-contact-type': 'Kontaktart',
        'bericht-tab-contact-persons': 'Kontaktpersonen',
        'filter-contact-person-country': 'Land (Kontaktperson)',
        'filter-newsletter': 'Newsletter',
        'filter-contact-is-active': 'Status Kontaktperson',
        'filter-contact-created-from': 'Kontakt erstellt von (Datum)',
        'filter-contact-created-to': 'Kontakt erstellt bis (Datum)',
        'filter-booking-date-from': 'Seminardatum von',
        'filter-booking-date-to': 'Seminardatum bis',
        'filter-contact-subjects': 'Betreff(e)',
        'filter-hotel-group': 'Hotelkette',
        'filter-from': 'Anfragedatum von',
        'filter-to': 'Anfragedatum bis',
        'filter-offer-source': 'Angebotsquelle',
        'filter-offer-source-all': 'Alle',
        'filter-status': 'Status',
        'filter-offer-state': 'Anfragestatus',
        'filter-offer-version-state': 'Detailstatus',
        'filter-booking-state': 'Buchungstatus',
        'filter-status-all': 'Alle',
        'filter-seminar-from': 'Seminardatum von',
        'filter-seminar-to': 'Seminardatum bis',
        'filter-hotel': 'Hotel',
        'filter-hotel-chain': 'Hotelkette',
        'filter-agreement-type': 'Vereinbarungstyp',
        'filter-agreement-no-contract': 'Kein Vertrag',
        'filter-contract-types': 'Vereinbarungstyp(en)',
        'filter-invoice-interval': 'Rechnungsintervall',
        'filter-invoice-interval-all': 'Alle',
        'filter-contract-valid-from': 'Gültig von',
        'filter-contract-valid-to': 'Gültig bis',
        'filter-only-active-contracts': 'Nur aktive Vereinbarungen',
        'filter-include-inactive-contracts': 'inaktive Vereinbarungen miteinbeziehen',
        'filter-show-in-lister': 'Hotellister',
        'filter-show-in-lister-yes': 'Im Hotellister angezeigt',
        'filter-show-in-lister-no': 'Im Hotellister nicht angezeigt',
        'filter-state': 'Hotelbundesland',
        'filter-company': 'Firma',
        'filter-customer-country': 'Land Kunde',
        'filter-contact-person': 'Ansprechpartner',
        'filter-generate-report': 'Report erstellen',
        'filter-reset': 'Alle Filter zurücksetzen',
        'filter-cancellation': 'Storno',
        'filter-cancellation-yes': 'Ja',
        'filter-cancellation-no': 'Nein',
        'filter-country-hotel': 'Hotelland',
        'filter-customer-type': 'Kundentyp',
        'filter-customer-type-premium': 'Premium',
        'filter-customer-type-classic': 'Classic',
        'filter-inquiry-status-type': 'Anfrage Status',
        'filter-status-offer': 'Anfragestatus',
        'filter-status-offer-version': 'Detailstatus',
        'filter-status-booking': 'Buchungstatus',
        'filter-hotel-group-select': 'Hotelkette(n) auswählen',
        'filter-hotel-search-placeholder': 'Hotel suchen...',
        'filter-contact-search-placeholder': 'Ansprechpartner suchen...',
        'filter-enter-min-3': 'Mindestens 3 Zeichen eingeben',
        'filter-hotels-not-found': 'Keine Hotels gefunden',
        'filter-companies-not-found': 'Keine Firmen gefunden',
        'filter-contacts-not-found': 'Keine Ansprechpartner gefunden',
        'filter-customer': 'Kunde',
        'filter-date': 'Datum',
        'filter-country': 'Land',
        'report-loading': 'Bericht wird geladen...',
        'report-load-error': 'Fehler beim Laden des Reports. Bitte versuchen Sie es erneut.',
        'report-no-export-data': 'Keine Daten zum Exportieren. Bitte generieren Sie zuerst einen Report.',
        'report-export-failed': 'Export fehlgeschlagen: {{message}}',
        'download-csv': 'CSV herunterladen',
        'download-csv-report': 'CSV Report herunterladen',
        'earlybird-select-date': 'Bitte wählen Sie ein Datum aus.',
        'earlybird-report-title': 'Early Bird Report',
        'earlybird-date-day': 'Datum (Tag)',
        'earlybird-date-week': 'Datum (Woche)',
        'earlybird-date-month': 'Datum (Monat)',
        'earlybird-date-year': 'Datum (Jahr)',
        'earlybird-metric': 'Metrik',
        'earlybird-period-day': 'Tag',
        'earlybird-period-week': 'Woche',
        'earlybird-period-month': 'Monat',
        'earlybird-period-year': 'Jahr',
        'report-contact-persons-found': 'Kontaktperson(en) gefunden',
        'contact-firstname': 'Vorname',
        'contact-lastname': 'Nachname',
        'contact-title': 'Titel',
        'contact-street': 'Straße',
        'contact-street-number': 'Nr.',
        'contact-zip': 'PLZ',
        'contact-city': 'Ort',
        'contact-country': 'Land',
        'contact-state': 'Bundesland',
        'contact-supplement': 'Zusatz',
        'contact-email': 'E-Mail',
        'contact-phone': 'Telefon',
        'contact-mobile': 'Mobil',
        'contact-subjects': 'Betreff(e)',
        'contact-net-inquiries': 'Nettoanfragen',
        'contact-gross-inquiries': 'Bruttoanfragen',
        'contact-reservations': 'Reservierungen',
        'contact-overnights': 'Übernachtungen',
        'contact-participants': 'Teilnehmer',
        'contact-person': 'Kontaktperson',
        'contact-timestamp': 'Zeitstempel',
        'contact-subject': 'Betreff',
        'contact-employee': 'Mitarbeiter',
        'contact-type': 'Kontaktart',
        'report-filter-company-min-3': 'Firma (mind. 3 Zeichen)',
        'report-filter-hotels-min-3': 'Hotel(s) (mind. 3 Zeichen)',
        'report-contract-none': 'Kein Vertrag',
        'statistics-request-date': 'Anfragedatum',
        'statistics-booking-date': 'Buchungsdatum',
        'statistics-year': 'Jahr',
        'statistics-month': 'Anfragemonat',
        'statistics-booking-month-range': 'Buchungsmonat',
        'statistics-no-seminar-start': '— (kein Datum)',
        'statistics-hotel': 'Hotel',
        'statistics-source': 'Quelle',
        'statistics-requests': 'Anfragen',
        'statistics-reservations': 'Reservierungen',
        'statistics-rejected-hotel': 'Abl. Hotel',
        'statistics-rejected-client': 'Abl. Kunde',
        'statistics-overnights': 'Nächtigungen',
        'statistics-participants': 'Teilnehmer',
        'statistics-generate': 'Statistik generieren',
        'statistics-loading': 'Statistik wird geladen...',
        'statistics-results': 'Statistik Ergebnisse',
        'statistics-entries-found': 'Einträge gefunden',
        'statistics-total-sum': 'GESAMTSUMME',
        'statistics-sum': 'Summe',
        'statistics-rows-per-page': 'Einträge pro Seite:',
        'table-pagination-first-page': 'Erste Seite',
        'table-pagination-last-page': 'Letzte Seite',
        'table-pagination-next-page': 'Nächste Seite',
        'table-pagination-previous-page': 'Vorherige Seite',
        'reports-page-tabs-aria': 'Berichte Untertabs',
        'selector-price-tabs-aria': 'Preislisten-Selektor Tabs',
        'system-status-redis-heading': 'Redis',
        'system-status-mysql-heading': 'MySQL',
        'system-mysql-buffer-pool': 'Buffer Pool',
        'statistics-more-than': 'mehr als',
        'tasks-settings-groups': 'Bereiche',
        'tasks-settings-categories': 'Kategorien',
        'tasks-settings-subjects': 'Betreffe',
        'tasks-settings-groups-title': 'Task Einstellungen / Bereiche',
        'tasks-settings-categories-title': 'Task Einstellungen / Kategorien',
        'tasks-settings-subjects-title': 'Task Einstellungen / Betreffe',
        'tasks-table-status': 'Status',
        'tasks-status-disabled': 'Deaktiviert',
        'tasks-status-active': 'Aktiv',
        'tasks-settings-add-group': 'Bereich hinzufügen',
        'tasks-settings-add-category': 'Kategorie hinzufügen',
        'tasks-settings-add-subject': 'Betreff hinzufügen',
        'tasks-filter-title': 'Filter',
        'tasks-filter-saved': 'Filter wurde erfolgreich gespeichert',
        'tasks-filter-apply': 'Filter anwenden',
        'tasks-field-status': 'Status',
        'tasks-status-open': 'Offen',
        'tasks-status-done': 'Erledigt',
        'tasks-field-due-from': 'Fällig ab',
        'tasks-field-due-to': 'Fällig bis',
        'tasks-field-assignee': 'Zugewiesen an',
        'tasks-empty-all': '<Alle>',
        'tasks-edit-title': 'Task bearbeiten',
        'tasks-create-title': 'Task erstellen',
        'tasks-saved': 'Task wurde erfolgreich gespeichert',
        'tasks-marked-done': 'Task wurde auf erledigt gesetzt',
        'tasks-reopened': 'Task wurde wieder geöffnet',
        'tasks-mark-undone': 'Nicht erledigt',
        'tasks-field-assignment': 'Zuweisung',
        'tasks-empty-none': '<keine>',
        'tasks-field-due-date': 'Fälligkeitsdatum',
        'tasks-field-subject': 'Betreff',
        'tasks-field-description': 'Beschreibung',
        'tasks-field-booking': 'Buchung',
        'tasks-field-booking-id': 'Buchung ID',
        'tasks-current-booking': 'Aktuelle Buchung',
        'tasks-comment': 'Kommentar',
        'tasks-created-at': 'Erstellt am',
        'tasks-created-by': 'Erstellt von',
        'tasks-comment-updated': 'Kommentar wurde erfolgreich aktualisiert',
        'tasks-comment-delete-question': 'Sind Sie sicher, dass Sie diesen Kommentar löschen möchten?',
        'tasks-comment-delete-title': 'Kommentar löschen',
        'tasks-comment-deleted': 'Kommentar wurde erfolgreich gelöscht',
        'tasks-new-comment': 'Neuer Kommentar',
        'tasks-comment-saved': 'Kommentar wurde erfolgreich gespeichert',
        'tasks-table-subject': 'Betreff',
        'tasks-table-category': 'Kategorie',
        'tasks-table-due-date': 'Fälligkeit',
        'tasks-table-assignee': 'Zugewiesen an',
        'tasks-table-created-by': 'Erstellt von',
        'tasks-table-hotel': 'Hotel',
        'tasks-table-company': 'Firma',
        'tasks-table-contact': 'Ansprechperson',
        'tasks-table-booking': 'Buchung',
        'tasks-system': 'System',
        'tasks-for-booking': 'Tasks für Buchung',
        'tasks-create-button': 'Neuen Task erstellen',
        'tasks-quickfilter-mine': 'Nur Meine',
        'tasks-quickfilter-all': 'Alle',
        'tasks-quickfilter-unassigned': 'Nicht zugewiesen',
        'tasks-filter-button': 'Filter',
        'tasks-filter-button-with-count': 'Filter ({{count}})',
        'modal-offers': 'Angebote',
        'modal-bookings': 'Buchungen',
        'modal-load-error': 'Fehler beim Laden von',
        'modal-offers-found': 'Angebot(e) gefunden',
        'modal-bookings-found': 'Buchung(en) gefunden',
        'modal-offer-id': 'Angebot ID',
        'modal-booking-id': 'Buchung ID',
        'modal-ref-code': 'Ref / Code',
        'modal-company': 'Firma',
        'modal-request-date': 'Anfragedatum',
        'modal-start-date': 'Startdatum',
        'modal-end-date': 'Enddatum',
        'modal-backoffice-employee': 'Backoffice Mitarbeiter',
        'modal-cancellation': 'Storno',
        'modal-canceled-by': 'Storniert von',
        'modal-type': 'Typ',
        'modal-type-seminar-overnight': 'Seminar mit Nächtigung',
        'modal-type-day-seminar': 'Tagesseminar (ohne Nächtigung)',
        'table-header-hotel-name': 'Hotelname',
        'table-header-zip': 'PLZ',
        'table-header-state': 'Bundesland',
        'table-header-country': 'Land',
        'table-header-hotel-groups': 'Hotelkette',
        'table-header-contract-valid-from': 'Vereinbarungsgültigkeit von',
        'table-header-contract-valid-to': 'Vereinbarungsgültigkeit bis',
        'table-header-invoice-interval': 'Rechnungsintervall',
        'table-header-contract-amount': 'Vereinbarungs-Betrag',
        'table-header-show-in-lister': 'Im Hotellister',
        'export-contracts-to-csv': 'Als CSV exportieren',

        'sync-prices-button': 'Preise aus aktueller Preisliste übernehmen',
        'sync-prices-dialog-title': 'Preise aus aktueller Preisliste übernehmen',
        'sync-prices-dialog-description': 'Wählen Sie, wie die Preise aus der aktuell gültigen Preisliste übernommen werden sollen.',
        'sync-prices-dialog-fill-empty': 'Nur leere Preise befüllen (bestehende Preise bleiben erhalten)',
        'sync-prices-dialog-overwrite': 'Alle Preise überschreiben (auch bereits eingetragene Preise)',
        'tasks-group-updated': 'Bereich aktualisiert',
        'tasks-group-created': 'Bereich erstellt',
        'tasks-category-updated': 'Kategorie aktualisiert',
        'tasks-category-created': 'Kategorie erstellt',
        'tasks-subject-updated': 'Betreff aktualisiert',
        'tasks-subject-created': 'Betreff erstellt',
        'tasks-field-name': 'Name',
        'tasks-field-area': 'Bereich',
        'tasks-field-category': 'Kategorie',
        comments: 'Kommentare',
        'tasks-field-description-template': 'Beschreibungsvorlage',
        'tasks-field-disabled': 'Deaktiviert',
        'tasks-field-system-relevant': 'Systemrelevant',
        'clients-search-placeholder': 'Firmenname, E-Mail, Stadt...',
        'spaces-search-placeholder': 'Name, Land, Benutzer, Lizenz...',
        'users-search-placeholder': 'Name, Username, E-Mail, Space...',
        'publicclient-status-master': 'Master',
        'publicclient-status-invitation-pending': 'Bestätigung ausstehend',
        'publicclient-status-inactive': 'Inaktiv',
        'space-has-epilo': 'seminargo aktiviert',
        'newspace-section-management': 'Verwaltungsbereich',
        'newspace-section-hotel': 'Hotel',
        'newspace-section-user': 'Benutzer',
        'hotel-agb-title': 'Hotel AGB',
        'hotel-agb-filename': 'Dateiname',
        'hotel-agb-filesize': 'Dateigröße',
        'hotel-agb-uploaded-at': 'Hochgeladen',
        'hotel-agb-uploaded-by': 'Hochgeladen von',
        'hotel-agb-download': 'AGB herunterladen',
        'hotel-agb-empty-info': 'Noch keine AGB-Datei vorhanden. Bitte laden Sie eine PDF-Datei hoch.',
        'hotel-document-title': 'Standard-Angebotsanhänge',
        'hotel-document-subtitle':
          'Hinterlege hier Dokumente, die bei jedem Angebot mitsendbar sein sollen. Du kannst deine Auswahl pro Anfrage anpassen.',
        'hotel-document-displayname-placeholder': 'z.B. Rahmenprogramme',
        'hotel-document-upload-hint': 'Nur PDF-Dateien • Max. 10 MB • Max. 10 Dokumente',
        'hotel-document-pdf-required': 'Bitte wählen Sie eine PDF-Datei aus',
        'hotel-document-max-count': 'Maximale Anzahl an Dokumenten (10) erreicht',
        'hotel-document-max-size': 'Die Datei überschreitet die maximale Größe von 10 MB',
        'hotel-document-upload-success': 'Dokument erfolgreich hochgeladen',
        'hotel-document-upload-failed': 'Fehler beim Hochladen des Dokuments',
        'hotel-document-delete-success': 'Dokument erfolgreich gelöscht',
        'hotel-document-approved-tooltip': 'Durch seminargo-Backoffice freigegeben',
        'hotel-document-pending-tooltip': 'Warten auf Freigabe durch seminargo-Backoffice',
        'hotel-document-approve-title': 'Dokument freigeben?',
        'hotel-document-approve-question': 'Möchtest du wirklich "{{title}}" freigeben?',
        'hotel-document-approve': 'Freigeben',
        'hotel-document-delete': 'Löschen',
        'hotel-document-cancel': 'Abbrechen',
        'hotel-document-approved-success': 'Dokument freigegeben',
        'hotel-document-empty-info': 'Noch keine Standard-Angebotsanhänge vorhanden.',
        'offer-standard-documents-title': 'Standard-Angebotsanhänge',
        'offer-standard-documents-empty': 'Keine freigegebenen Standarddokumente für dieses Hotel.',
        'offer-standard-document-size-exceeded':
          'Die Gesamtgröße aller Anhänge darf 10 MB nicht überschreiten. Bitte wählen Sie ein Standard-Dokument ab oder löschen Sie ein zusätzliches Dokument.',
        'offer-document-upload-hint': 'Max. 10 MB Gesamtgröße aller Anhänge',
        'validation-facilities-without-tax-rates': 'Einzelleistungen ohne Steuerklassen',
        'validation-products-without-tax-rates': 'Produkte ohne Steuerklassen',
        'validation-bundles-without-tax-rates': 'Bundles ohne Steuerklassen',
        'validation-missing-products': 'Fehlende Produkte',
        'validation-missing-services': 'Fehlende Leistungen',
        'validation-invalid-products': 'Ungültige Produkte',
        'validation-invalid-services': 'Ungültige Leistungen',
        'validation-no-data': 'Keine Validierungsdaten',
        // PDF import
        'graphqlerror-PDF_IMPORT_DISABLED': 'Der PDF-Import ist nicht aktiviert.',
        'pdf-import-target-hotel': 'Hotel der bestehenden Buchung.',
        'pdf-import-case-both-version': 'ergeben zwei neue Versionen: zuerst das Angebot, dann die Reservierung.',
        'pdf-import-case-confirmation-version': 'wird als neue Reservierungsversion angelegt; die Buchung wird bestätigt.',
        'pdf-import-case-offer-version': 'wird als neue Angebotsversion der Buchung angelegt.',
        'graphqlerror-PDF_IMPORT_ALREADY_IMPORTED': 'Dieses PDF wurde bereits in die Buchung {{refCode}} importiert.',
        'graphqlerror-PDF_IMPORT_GROUP_TARGET': 'Alle Dokumente müssen derselben Buchung zugeordnet sein.',
        'graphqlerror-PDF_IMPORT_TARGET_CLOSED': 'Die Buchung {{refCode}} ist abgeschlossen und nimmt keine neue Version an.',
        'graphqlerror-PDF_IMPORT_TARGET_NOT_FOUND': 'Die Buchung wurde nicht gefunden oder ist nicht zugänglich.',
        'pdf-import-create-version': 'Als neue Version anlegen',
        'pdf-import-create-version-reservation': 'Reservierung als neue Version anlegen',
        'pdf-import-create-versions': '{{count}} neue Versionen anlegen',
        'pdf-import-info-version':
          'Es wird eine neue Angebotsversion in Buchung {{refCode}} angelegt. Es werden keine Nachrichten an Hotel oder Kunde versendet.',
        'pdf-import-info-version-reservation':
          'Es wird eine neue Version in Buchung {{refCode}} angelegt; die letzte neue Version ist die bestätigte Reservierung, die Buchung wird bestätigt. Es werden keine Nachrichten an Hotel oder Kunde versendet.',
        'pdf-import-list-target': 'für {{refCode}}',
        'pdf-import-offer-button': 'PDF importieren',
        'pdf-import-step-version': 'Version anlegen',
        'pdf-import-success-version': 'Die neue Version wurde in Buchung {{refCode}} angelegt.',
        'pdf-import-success-version_other': 'Die {{count}} neuen Versionen wurden in Buchung {{refCode}} angelegt.',
        'pdf-import-target-closed': 'Die Buchung ist abgeschlossen und nimmt keine neue Version an.',
        'pdf-import-target-customer': 'Der Kunde kommt aus der Buchung und wird beim Anlegen der neuen Version nicht geändert.',
        'pdf-import-target-number': 'Buchungsnummer der bestehenden Buchung.',
        'pdf-import-target-open': 'Buchung öffnen',
        'pdf-import-target-review-text':
          'Nach dem Anlegen wird dieses Dokument als neue Version an die {{count}} bestehenden Versionen angehängt. Hotel, Kunde und Buchungsnummer kommen aus der Buchung; bestehende Versionen bleiben unverändert.',
        'pdf-import-target-title': 'Neue Version für Buchung {{refCode}}',
        'pdf-import-target-upload-text':
          'Hochgeladene PDFs werden nach der Prüfung als neue Version dieser Buchung angelegt: ein Angebot als Angebotsversion, eine Buchungsbestätigung als Reservierung. Hotel, Kunde und Buchungsnummer bleiben erhalten.',
        'graphqlerror-PDF_IMPORT_AUTH': 'PDF-Import erfordert eine angemeldete Backoffice-Sitzung.',
        'graphqlerror-PDF_IMPORT_BOOKING_TAKEN':
          'Die Buchung {{refCode}} existiert bereits. Dieses PDF kann nicht importiert werden; lösche zuerst die bestehende Buchung, wenn sie ersetzt werden soll.',
        'graphqlerror-PDF_IMPORT_CHANGED': 'Der Import wurde zwischenzeitlich geändert. Bitte neu laden.',
        'graphqlerror-PDF_IMPORT_COMPANY_NOT_FOUND': 'Unternehmen wurde nicht gefunden.',
        'graphqlerror-PDF_IMPORT_FILE_INVALID': 'Bitte eine gültige PDF-Datei bis 20 MB hochladen.',
        'graphqlerror-PDF_IMPORT_FILE_SIZE': 'PDF darf höchstens 20 MB groß sein.',
        'graphqlerror-PDF_IMPORT_GROUP_HOTEL': 'Alle Dokumente einer Buchung müssen demselben Hotel zugeordnet sein.',
        'graphqlerror-PDF_IMPORT_GROUP_IMPORTED':
          'Ein Dokument dieser Buchung wurde bereits importiert. Bitte das Dokument aus der gemeinsamen Buchung lösen.',
        'graphqlerror-PDF_IMPORT_GROUP_INVALID': 'Ungültige Upload-Gruppe.',
        'graphqlerror-PDF_IMPORT_GROUP_LEAVE': 'Importierte Dokumente können nicht mehr gelöst werden.',
        'graphqlerror-PDF_IMPORT_GROUP_NUMBERS': 'Die Dokumente der Buchung haben unterschiedliche Buchungsnummern ({{numbers}}).',
        'graphqlerror-PDF_IMPORT_GROUP_PENDING': 'Alle Dokumente der Buchung müssen geprüft sein: {{- files}}',
        'graphqlerror-PDF_IMPORT_GROUP_SIZE': 'Höchstens {{max}} PDFs können gemeinsam als eine Buchung importiert werden.',
        'graphqlerror-PDF_IMPORT_HOTEL_FIRST': 'Zuerst ein Hotel zuordnen.',
        'graphqlerror-PDF_IMPORT_HOTEL_NOT_FOUND': 'Hotel wurde nicht gefunden oder ist nicht zugänglich.',
        'graphqlerror-PDF_IMPORT_INVALID': '{{- details}}',
        'graphqlerror-PDF_IMPORT_NOT_FOUND': 'PDF-Import wurde nicht gefunden.',
        'graphqlerror-PDF_IMPORT_PREVIEW_LIMIT': 'PDF überschreitet das Vorschaulimit.',
        'graphqlerror-PDF_IMPORT_RETRY_STATE': 'Nur fehlgeschlagene oder noch nicht importierte Reviews können neu aufbereitet werden.',
        'graphqlerror-PDF_IMPORT_TEMPLATE_OFFER': 'Angebotsvorlage für den Import fehlt.',
        'graphqlerror-PDF_IMPORT_TEMPLATE_RESERVATION': 'Vorlage für Reservierungsbestätigungen fehlt.',
        'graphqlerror-PDF_IMPORT_TOTAL_DIFFERENCE':
          'Die Gesamtsumme ({{offer}} €) weicht von der Summe im PDF ({{printed}} €) ab. Bitte die Positionen prüfen oder die Abweichung bestätigen.',
        'graphqlerror-PDF_IMPORT_TOTAL_DIFFERENCE_GROUP':
          'Die Gesamtsumme weicht in {{- files}} von der Summe im PDF ab. Bitte die Positionen prüfen oder die Abweichung bestätigen.',
        'pdf-import-additionalTerms': 'Weitere Vereinbarungen',
        'pdf-import-addon-ADDON_COFFEE': 'Kaffee',
        'pdf-import-addon-ADDON_COFFEEBREAK': 'Kaffeepause',
        'pdf-import-addon-ADDON_DINNER': 'Abendessen',
        'pdf-import-addon-ADDON_LUNCH': 'Mittagessen',
        'pdf-import-addon-ADDON_MEALDRINK': 'Getränke zum Essen',
        'pdf-import-addon-ADDON_SEMINARDRINK': 'Seminargetränke',
        'pdf-import-addon-VISITOR_TAX': 'Ortstaxe',
        'pdf-import-addon-add': 'Zusatzleistung ergänzen',
        'pdf-import-addon-description': 'Zusatzleistung laut PDF',
        'pdf-import-addon-remove': 'Zusatzleistung entfernen',
        'pdf-import-addon-type': 'Art der Zusatzleistung',
        'pdf-import-addons': 'Zusatzleistungen',
        'pdf-import-all-uploads': 'Alle Uploads',
        'pdf-import-apply': 'Übernehmen',
        'pdf-import-basis-gross': 'brutto',
        'pdf-import-basis-net': 'netto',
        'pdf-import-billing': 'Rechnungsdaten',
        'pdf-import-billing-address': 'Rechnungsadresse',
        'pdf-import-billing-city': 'Rechnungsort',
        'pdf-import-billing-company': 'Rechnungsfirma',
        'pdf-import-billing-country': 'Rechnungsland',
        'pdf-import-billing-firstname': 'Vorname Rechnung',
        'pdf-import-billing-hint': 'Nur ergänzen, wenn im PDF gesonderte Rechnungsdaten angegeben sind.',
        'pdf-import-billing-lastname': 'Nachname Rechnung',
        'pdf-import-billing-zip': 'Rechnungs-PLZ',
        'pdf-import-booking-all-ready': 'alle Dokumente zur Prüfung bereit',
        'pdf-import-booking-created': 'angelegt',
        'pdf-import-booking-documents': 'Buchung mit {{count}} Dokumenten',
        'pdf-import-booking-number': 'Buchungsnummer',
        'pdf-import-booking-number-empty': 'Leer lassen, wenn das PDF keine Buchungsnummer nennt.',
        'pdf-import-booking-number-placeholder': 'z. B. BWTBXB',
        'pdf-import-booking-number-taken': 'Diese Buchungsnummer ist bereits vergeben.',
        'pdf-import-booking-number-taken-bar': 'Buchungsnummer {{refCode}} ist bereits vergeben.',
        'pdf-import-booking-number-used': 'Die Buchung erhält diese Nummer.',
        'pdf-import-booking-taken-chip': 'Buchung existiert bereits',
        'pdf-import-busy': 'Wird bearbeitet …',
        'pdf-import-button': 'PDF importieren',
        'pdf-import-cancellationTerms': 'Stornobedingungen',
        'pdf-import-card-deposit': 'Anzahlung {{amount}}',
        'pdf-import-card-gross': '{{amount}} brutto',
        'pdf-import-card-no-customer': 'Kein Kunde erkannt',
        'pdf-import-card-no-deposit': 'Keine Anzahlung',
        'pdf-import-card-no-period': 'Zeitraum fehlt',
        'pdf-import-card-no-tiers': 'Keine Stornostaffel – es gelten die Hotelregeln',
        'pdf-import-card-no-title': 'Ohne Titel',
        'pdf-import-card-no-total': 'Summe fehlt',
        'pdf-import-card-participants': '{{count}} Teilnehmer',
        'pdf-import-card-participants-unknown': 'Teilnehmerzahl unbekannt',
        'pdf-import-card-printed': 'Summe im PDF: {{amount}}',
        'pdf-import-card-services': '{{count}} Leistungen',
        'pdf-import-card-services_one': '{{count}} Leistung',
        'pdf-import-card-tiers': '{{count}} Stornostufen',
        'pdf-import-card-tiers_one': '{{count}} Stornostufe',
        'pdf-import-card-valid-until': 'Gültig bis {{date}}',
        'pdf-import-case-both-text': 'ergeben eine Buchung mit Angebots- und Reservierungsversion.',
        'pdf-import-case-both-title': 'Angebot und Buchungsbestätigung zusammen',
        'pdf-import-case-confirmation-text': 'wird als bestätigte Reservierung angelegt.',
        'pdf-import-case-confirmation-title': 'Eine Buchungsbestätigung',
        'pdf-import-case-offer-text': 'wird als Angebotsentwurf angelegt.',
        'pdf-import-case-offer-title': 'Ein Angebot',
        'pdf-import-category-ABREISEPAUSCHALE': 'Abreisepauschale',
        'pdf-import-category-ADDON_COFFEE': 'Kaffee',
        'pdf-import-category-ADDON_COFFEEBREAK': 'Kaffeepause',
        'pdf-import-category-ADDON_DINNER': 'Abendessen',
        'pdf-import-category-ADDON_LUNCH': 'Mittagessen',
        'pdf-import-category-ADDON_MEALDRINK': 'Getränke zum Essen',
        'pdf-import-category-ADDON_SEMINARDRINK': 'Seminargetränke',
        'pdf-import-category-BEAMER': 'Beamer',
        'pdf-import-category-CAM': 'Kamera',
        'pdf-import-category-CD': 'CD/Audio',
        'pdf-import-category-FLIPCHART': 'Flipchart',
        'pdf-import-category-GROUPROOM': 'Gruppenraum',
        'pdf-import-category-HALBTAGESPAUSCHALE': 'Halbtagespauschale',
        'pdf-import-category-LEINWAND': 'Leinwand',
        'pdf-import-category-MODERATION': 'Moderationskoffer',
        'pdf-import-category-PINNWAND': 'Pinnwand',
        'pdf-import-category-ROOM_DZ': 'Übernachtung Doppelzimmer',
        'pdf-import-category-ROOM_EZ': 'Übernachtung Einzelzimmer',
        'pdf-import-category-SEMINARPAUSCHALE_DZ': 'Seminarpauschale DZ',
        'pdf-import-category-SEMINARPAUSCHALE_EZ': 'Seminarpauschale EZ',
        'pdf-import-category-SEMINARROOM_FULLDAY': 'Seminarraum ganztags',
        'pdf-import-category-SEMINARROOM_SEMIDAY': 'Seminarraum halbtags',
        'pdf-import-category-TAGESPAUSCHALE': 'Tagespauschale',
        'pdf-import-category-TV': 'TV',
        'pdf-import-category-VISITOR_TAX': 'Ortstaxe',
        'pdf-import-category-VOLLPENSIONSPAUSCHALE_DZ': 'Vollpensionspauschale DZ',
        'pdf-import-category-VOLLPENSIONSPAUSCHALE_EZ': 'Vollpensionspauschale EZ',
        'pdf-import-category-VORTAGSANREISE_DZ': 'Vortagsanreise DZ',
        'pdf-import-category-VORTAGSANREISE_EZ': 'Vortagsanreise EZ',
        'pdf-import-change': 'Ändern',
        'pdf-import-check-details': 'Angaben prüfen',
        'pdf-import-checks-text': 'Diese Angaben brauchen wir, bevor angelegt werden kann. Ein Klick führt zum passenden Bereich.',
        'pdf-import-checks-title': 'Bitte noch prüfen ({{count}})',
        'pdf-import-chip-blocked': 'Nicht importierbar',
        'pdf-import-chip-draft': 'Als Entwurf erstellt',
        'pdf-import-chip-ready': 'Bereit zum Anlegen',
        'pdf-import-chip-reservation': 'Als Reservierung angelegt',
        'pdf-import-chip-version': 'Als Version der Buchung angelegt',
        'pdf-import-choose-files': 'PDFs auswählen',
        'pdf-import-client-address': 'Adresse',
        'pdf-import-client-city': 'Ort',
        'pdf-import-client-company': 'Firma',
        'pdf-import-client-country': 'Land',
        'pdf-import-client-email': 'E-Mail',
        'pdf-import-client-firstname': 'Vorname',
        'pdf-import-client-lastname': 'Nachname',
        'pdf-import-client-phone': 'Telefon',
        'pdf-import-client-zip': 'PLZ',
        'pdf-import-col-customer': 'Kunde',
        'pdf-import-col-document': 'Dokument',
        'pdf-import-col-hotel': 'Hotel',
        'pdf-import-col-period': 'Zeitraum',
        'pdf-import-col-status': 'Status',
        'pdf-import-col-total': 'Summe',
        'pdf-import-conflict-deleted-text':
          'Beim Anlegen erhält die neue Buchung die Nummer {{refCode}}; die gelöschte Buchung wird zu {{refCode}}-{{offerId}} umbenannt.',
        'pdf-import-conflict-deleted-title': 'Gelöschte Buchung {{refCode}} vorhanden',
        'pdf-import-conflict-open': 'Bestehende Buchung öffnen',
        'pdf-import-conflict-text':
          'Dieses PDF kann nicht importiert werden. Soll die bestehende Buchung ersetzt werden, lösche sie zuerst und öffne diesen Import dann erneut.',
        'pdf-import-conflict-title': 'Buchung {{refCode}} existiert bereits – Import nicht möglich',
        'pdf-import-country-placeholder': 'Land auswählen',
        'pdf-import-create-booking': 'Buchung mit {{count}} Versionen anlegen',
        'pdf-import-create-offer': 'Angebot als Entwurf erstellen',
        'pdf-import-create-reservation': 'Reservierung anlegen',
        'pdf-import-currency': 'Währung',
        'pdf-import-customer-company': 'Kunde im Firmenverzeichnis (optional)',
        'pdf-import-customer-company-helper': 'Suche eine vorhandene Firma oder prüfe die Angaben darunter.',
        'pdf-import-customer-existing': 'Bestehenden Kunden übernehmen',
        'pdf-import-customer-existing-helper': 'Übernimmt Kontakt, E-Mail, Adresse und Rechnungsdaten. Vorschläge passend zur Firma aus dem PDF.',
        'pdf-import-customers-none': 'Keine passenden Kunden gefunden',
        'pdf-import-day-outside': 'Dieser Tag liegt außerhalb des Veranstaltungszeitraums. Zeitraum korrigieren oder den Tag ausdrücklich entfernen.',
        'pdf-import-day-remove': 'Tag entfernen',
        'pdf-import-day-summary': 'Gäste: {{guests}} · Einzelzimmer: {{single}} · Doppelzimmer: {{double}}',
        'pdf-import-deposit': 'Anzahlung',
        'pdf-import-deposit-due': 'Anzahlung fällig am',
        'pdf-import-details': 'Details',
        'pdf-import-difference-confirm': 'Ich habe die Positionen geprüft und übernehme die abweichende Summe.',
        'pdf-import-difference-others': 'Auch in anderen Dokumenten der Buchung weicht die Summe vom PDF ab: {{- files}}.',
        'pdf-import-difference-text':
          'Die Gesamtsumme des Angebots ({{offer}} €) weicht von der Summe im PDF ({{printed}} €) ab. Häufige Ursachen: fehlende Nächte oder Positionen, Aufschläge wie die Ortstaxe, die laut PDF nicht enthalten sind.',
        'pdf-import-disabled': 'Der PDF-Import ist hier noch nicht verfügbar.',
        'pdf-import-document-BOOKING_CONFIRMATION': 'Reservierung',
        'pdf-import-document-OFFER': 'Angebot',
        'pdf-import-document-UNKNOWN': 'Dokumentart offen',
        'pdf-import-document-type': 'Dokumentart',
        'pdf-import-document-type-confirmation': 'Buchungs-/Reservierungsbestätigung (wird als Reservierung angelegt)',
        'pdf-import-document-type-offer': 'Hotelangebot',
        'pdf-import-document-type-unknown': 'Bitte bestätigen',
        'pdf-import-documentDate': 'Datum des PDFs',
        'pdf-import-double-rooms': 'Doppelzimmer',
        'pdf-import-download-raw': 'Erkennungsdaten herunterladen (JSON)',
        'pdf-import-download-review': 'Geprüfte Daten herunterladen (JSON)',
        'pdf-import-drop-here': 'PDFs hierher ziehen',
        'pdf-import-drop-limits':
          'Bis zu {{max}} PDFs gleichzeitig, je höchstens 20 MB. Du prüfst die erkannten Angaben vor dem Anlegen; es werden keine Nachrichten an Hotel oder Kunde versendet.',
        'pdf-import-empty-imported': 'Noch nichts angelegt.',
        'pdf-import-empty-none': 'Noch keine PDFs hochgeladen.',
        'pdf-import-empty-open': 'Alles erledigt – keine offenen Uploads.',
        'pdf-import-error-action': 'Aktion fehlgeschlagen.',
        'pdf-import-error-too-large': 'Das PDF ist für die automatische Erkennung zu umfangreich. Bitte teile es in kleinere Dateien.',
        'pdf-import-error-unavailable':
          'Die automatische Verarbeitung ist gerade nicht verfügbar. Bitte versuche es erneut oder wende dich an den Support.',
        'pdf-import-event-requirements': 'Zimmer, Räume und weitere Wünsche',
        'pdf-import-event-title': 'Titel',
        'pdf-import-external-reference': 'Angebots- oder Buchungsnummer',
        'pdf-import-failed-default': 'Die Angaben konnten nicht gelesen werden.',
        'pdf-import-failed-hint': 'Falls nötig, lade die Datei noch einmal hoch.',
        'pdf-import-filter-all': 'Alle ({{count}})',
        'pdf-import-filter-imported': 'Angelegt ({{count}})',
        'pdf-import-filter-open': 'Offen ({{count}})',
        'pdf-import-from-pdf': 'Angaben aus dem PDF',
        'pdf-import-gross': 'Brutto',
        'pdf-import-gross-prices': 'Bruttopreise',
        'pdf-import-group-hotel': 'Alle Dokumente der Buchung müssen demselben Hotel zugeordnet sein.',
        'pdf-import-group-leave': 'Dieses Dokument einzeln importieren',
        'pdf-import-group-leave-hint':
          'Löst dieses Dokument aus der gemeinsamen Buchung; es wird dann als eigenes Angebot bzw. eigene Reservierung angelegt.',
        'pdf-import-group-numbers': 'Die Dokumente der Buchung haben unterschiedliche Buchungsnummern.',
        'pdf-import-group-open': '{{count}} Angaben offen',
        'pdf-import-group-open-checks': 'In anderen Dokumenten der Buchung sind noch Angaben zu prüfen.',
        'pdf-import-group-pending': 'Es werden noch nicht alle Dokumente der Buchung gelesen oder geprüft.',
        'pdf-import-group-text':
          'Die Dokumente werden gemeinsam als Versionen einer Buchung angelegt, in dieser Reihenfolge. Prüfe jedes Dokument; die Buchung kann anschließend aus jedem der Dokumente angelegt werden.',
        'pdf-import-group-title': 'Gemeinsame Buchung ({{count}} Dokumente)',
        'pdf-import-guests': 'Gäste',
        'pdf-import-hotel-conflicts': 'Abweichungen zum PDF: {{- conflicts}}. Bitte Hotelzuordnung prüfen.',
        'pdf-import-hotel-differs': 'Angaben weichen vom PDF ab – bitte prüfen',
        'pdf-import-hotel-helper': 'Das erkannte Hotel ist vorausgewählt. Du kannst nach einem anderen Hotel oder Ort suchen.',
        'pdf-import-hotel-label': 'Welches Hotel gehört zu diesem Angebot?',
        'pdf-import-hotel-matches': 'Passt zu den Angaben im PDF',
        'pdf-import-hotels-loading': 'Hotels werden geladen …',
        'pdf-import-hotels-matching': 'Passende Hotels aus dem PDF',
        'pdf-import-hotels-none': 'Keine passenden Hotels gefunden',
        'pdf-import-hotels-other': 'Weitere Hotels',
        'pdf-import-imported-details': 'Übernommene Angaben',
        'pdf-import-info-booking':
          'Es wird eine Buchung mit {{count}} Versionen angelegt. Es werden keine Nachrichten an Hotel oder Kunde versendet.',
        'pdf-import-info-booking-reservation':
          'Es wird eine Buchung mit {{count}} Versionen angelegt; die letzte Version ist die bestätigte Reservierung. Es werden keine Nachrichten an Hotel oder Kunde versendet.',
        'pdf-import-info-offer': 'Es wird ein Entwurf erstellt. Es werden keine Nachrichten versendet und keine Buchungen ausgelöst.',
        'pdf-import-info-reservation':
          'Es wird eine bestätigte Reservierung angelegt (Reservierungsbestätigung). Es werden keine Nachrichten an Hotel oder Kunde versendet.',
        'pdf-import-issue-country': 'Bitte wähle das Land des Kunden aus.',
        'pdf-import-issue-email': 'Bitte ergänze die E-Mail-Adresse der Kontaktperson.',
        'pdf-import-issue-hotelMissing': 'Bitte wähle das passende Hotel aus.',
        'pdf-import-issue-priceListMissing': 'Für dieses Hotel fehlt noch die Einrichtung zur Angebotserstellung. Bitte wende dich an den Support.',
        'pdf-import-issue-taxMissing': 'Bitte prüfe den Steuersatz. Fehlende Beträge berechnen wir anschließend.',
        'pdf-import-item': 'Position {{n}}',
        'pdf-import-language-de': 'Deutsch',
        'pdf-import-language-en': 'Englisch',
        'pdf-import-line-add': 'Position ergänzen',
        'pdf-import-line-category': 'Leistungsart',
        'pdf-import-line-category-helper': 'Hilft bei der Zuordnung zum Hotelkatalog und zur Steuer.',
        'pdf-import-line-date': 'Leistungsdatum',
        'pdf-import-line-description': 'Leistung',
        'pdf-import-line-notes': 'Details / enthaltene Leistungen',
        'pdf-import-line-remove': 'Position entfernen',
        'pdf-import-line-tax': 'Steuersatz zuordnen',
        'pdf-import-line-taxAmount': 'Steuerbetrag',
        'pdf-import-line-totalGross': 'Brutto gesamt',
        'pdf-import-line-totalNet': 'Netto gesamt',
        'pdf-import-line-unit': 'Einheit',
        'pdf-import-line-unitPrice': 'Einzelpreis',
        'pdf-import-lines-details': 'Einzelne Leistungen genauer bearbeiten',
        'pdf-import-mixed-catalog': 'Gemischt laut Hotelkatalog',
        'pdf-import-mixed-summary': '{{- item}}: gemischte Steuer {{- shares}} laut Hotelkatalog.',
        'pdf-import-net': 'Netto',
        'pdf-import-net-prices': 'Nettopreise',
        'pdf-import-no': 'Nein',
        'pdf-import-no-addons': 'Keine Zusatzleistungen (bestätigt)',
        'pdf-import-no-rooms': 'Keine Räume (bestätigt)',
        'pdf-import-no-services': 'Noch keine Leistungen erkannt.',
        'pdf-import-not-assigned': 'Nicht zugeordnet',
        'pdf-import-not-in-pdf': 'Im PDF nicht angegeben',
        'pdf-import-offer-language': 'Angebotssprache',
        'pdf-import-open': 'Öffnen',
        'pdf-import-open-booking': 'Buchung öffnen',
        'pdf-import-open-offer': 'Angebot öffnen',
        'pdf-import-open-pdf-first': 'Bitte öffne zuerst ein PDF.',
        'pdf-import-open-points': 'Noch {{count}} Punkte offen',
        'pdf-import-open-points-show': 'Noch {{count}} Punkte offen – anzeigen',
        'pdf-import-open-points-show_one': 'Noch {{count}} Punkt offen – anzeigen',
        'pdf-import-open-points_one': 'Noch {{count}} Punkt offen',
        'pdf-import-opened': 'geöffnet',
        'pdf-import-paymentTerms': 'Zahlungsbedingungen',
        'pdf-import-please-choose': 'Bitte auswählen',
        'pdf-import-preview-download': 'Original herunterladen',
        'pdf-import-preview-draw-failed': 'Die PDF-Vorschau konnte nicht gezeichnet werden.',
        'pdf-import-preview-fit-width': 'An Seitenbreite anpassen',
        'pdf-import-preview-fullscreen': 'Vollbild',
        'pdf-import-preview-fullscreen-close': 'Vollbild schließen',
        'pdf-import-preview-fullscreen-close-hint': 'Vollbild schließen (Esc)',
        'pdf-import-preview-fullscreen-label': 'PDF-Vollbild: {{- filename}}',
        'pdf-import-preview-hint': 'Vergleiche die erkannten Angaben mit dem Original. Über die Vorschau kannst du das PDF auch groß öffnen.',
        'pdf-import-preview-load-failed': 'PDF konnte nicht geladen werden.',
        'pdf-import-preview-loading': 'PDF wird geladen',
        'pdf-import-preview-page': 'Seite {{page}} / {{pages}}',
        'pdf-import-preview-page-drawing': 'Seite {{page}} wird gezeichnet',
        'pdf-import-preview-page-failed': 'PDF-Seite konnte nicht angezeigt werden.',
        'pdf-import-preview-page-label': 'PDF, Seite {{page}} von {{pages}}',
        'pdf-import-preview-preparing': 'PDF wird vorbereitet',
        'pdf-import-preview-reload': 'Erneut laden',
        'pdf-import-preview-scroll-hint': 'Zum Blättern im PDF scrollen',
        'pdf-import-preview-zoom-in': 'Vergrößern',
        'pdf-import-preview-zoom-out': 'Verkleinern',
        'pdf-import-price-basis-all': 'Preisbasis für alle Positionen',
        'pdf-import-price-basis-hint':
          'Brutto enthält die Steuer, Netto nicht. Fehlt die Angabe im PDF, nehmen wir Brutto an. Beim Umschalten werden die Summen neu berechnet.',
        'pdf-import-price-basis-line': 'Preisbasis Position {{n}}',
        'pdf-import-prices-are': 'Die Preise im PDF sind',
        'pdf-import-prices-kept': 'Die Preise aus deinem PDF bleiben erhalten. Passende Steuersätze werden automatisch ergänzt.',
        'pdf-import-processing': 'Die Verarbeitung läuft im Hintergrund. Du kannst diese Seite später wieder öffnen.',
        'pdf-import-quantity': 'Menge',
        'pdf-import-raw-hide': 'Erkennungsdaten ausblenden',
        'pdf-import-raw-show': 'Erkennungsdaten anzeigen',
        'pdf-import-reading': 'Wird gelesen',
        'pdf-import-recognition-notes': 'Hinweise zur Erkennung',
        'pdf-import-remove': 'Entfernen',
        'pdf-import-request-comment': 'Kommentar / weitere Anforderungen',
        'pdf-import-reread': 'Angaben erneut aus PDF lesen',
        'pdf-import-reread-hint': 'Liest die Angaben neu ein und ersetzt die bisher geprüften Daten.',
        'pdf-import-review': 'Prüfen',
        'pdf-import-room': 'Raum {{n}}',
        'pdf-import-room-add': 'Raum ergänzen',
        'pdf-import-room-airConditioning': 'Klimaanlage',
        'pdf-import-room-beamers': 'Beamer',
        'pdf-import-room-daylight': 'Tageslicht',
        'pdf-import-room-flipcharts': 'Flipcharts',
        'pdf-import-room-from': 'Von',
        'pdf-import-room-minArea': 'Mindestfläche (m²)',
        'pdf-import-room-name': 'Raumname',
        'pdf-import-room-number': 'Raumnummer',
        'pdf-import-room-participants': 'Teilnehmer',
        'pdf-import-room-pinboards': 'Pinnwände',
        'pdf-import-room-remove': 'Raum entfernen',
        'pdf-import-room-to': 'Bis',
        'pdf-import-rooms': 'Räume',
        'pdf-import-save': 'Änderungen speichern',
        'pdf-import-save-failed': 'Die Änderungen konnten nicht gespeichert werden. Bitte versuche es erneut.',
        'pdf-import-seating': 'Bestuhlung',
        'pdf-import-seating-BANKETT': 'Bankett',
        'pdf-import-seating-BLOCK': 'Block',
        'pdf-import-seating-CIRCLE': 'Sesselkreis',
        'pdf-import-seating-COCKTAIL': 'Cocktail',
        'pdf-import-seating-EMPTY': 'Ohne Bestuhlung',
        'pdf-import-seating-PARLAMENT': 'Parlament',
        'pdf-import-seating-THEATER': 'Theater',
        'pdf-import-seating-UFORM': 'U-Form',
        'pdf-import-section-customer': 'Kunde',
        'pdf-import-section-event': 'Veranstaltung',
        'pdf-import-section-header': 'Hotel & Dokument',
        'pdf-import-section-overview': 'Übersicht',
        'pdf-import-section-services': 'Leistungen',
        'pdf-import-section-terms': 'Bedingungen',
        'pdf-import-single-rooms': 'Einzelzimmer',
        'pdf-import-split-apply-all': 'Für alle {{count}} Positionen „{{- item}}“ übernehmen',
        'pdf-import-split-check-first': 'Zuerst Menge und Einzelpreis prüfen.',
        'pdf-import-split-intro': '{{- item}}: gemischte Steuer laut Hotelkatalog. Anteil je Einheit ({{basis}}) eintragen',
        'pdf-import-split-rest': 'Rest {{rate}} %:',
        'pdf-import-split-share': 'davon {{rate}} % in €',
        'pdf-import-split-single-instead': 'Stattdessen einen Steuersatz wählen',
        'pdf-import-split-too-high': 'Die Anteile dürfen zusammen nicht über dem Einzelpreis liegen.',
        'pdf-import-split-unit': ', Einzelpreis {{price}} €',
        'pdf-import-status-FAILED': 'Verarbeitung fehlgeschlagen',
        'pdf-import-status-IMPORTED': 'Angebot erstellt',
        'pdf-import-status-IMPORTING': 'Angebot wird angelegt',
        'pdf-import-status-PROCESSING': 'PDF wird gelesen',
        'pdf-import-status-QUEUED': 'Wird gleich gelesen',
        'pdf-import-status-REVIEW': 'Zur Prüfung bereit',
        'pdf-import-step-booking': 'Buchung anlegen',
        'pdf-import-step-offer': 'Angebot anlegen',
        'pdf-import-step-review': 'Angaben prüfen',
        'pdf-import-step-upload': 'PDF hochladen',
        'pdf-import-subtitle': 'Hotelangebote und Buchungsbestätigungen aus PDFs übernehmen.',
        'pdf-import-success-booking': 'Die Buchung wurde mit {{count}} Versionen angelegt ({{- documents}}).',
        'pdf-import-success-offer': 'Dein Angebot wurde als Entwurf erstellt. Du kannst es jetzt öffnen und weiterbearbeiten.',
        'pdf-import-success-reservation': 'Die Reservierung wurde als bestätigte Reservierung angelegt. Du kannst sie jetzt öffnen.',
        'pdf-import-sum-totalGross': 'Brutto-Gesamtsumme',
        'pdf-import-sum-totalNet': 'Netto-Gesamtsumme',
        'pdf-import-sum-totalTax': 'Steuer-Gesamtsumme',
        'pdf-import-tax-change-hint': 'Bei Bedarf kannst du den Steuersatz ändern.',
        'pdf-import-tax-choose': 'Steuersatz auswählen',
        'pdf-import-tax-choose-hint': 'Bitte wähle den Steuersatz aus. Die Beträge werden danach berechnet.',
        'pdf-import-tax-for': 'Steuer: {{- item}}',
        'pdf-import-tax-mixed-option': 'Gemischt: {{low}} % + {{high}} %',
        'pdf-import-tax-open': 'Steuersatz offen',
        'pdf-import-tax-recheck': 'Steuersätze erneut prüfen',
        'pdf-import-tax-summary': '{{- item}}: Steuersatz {{rate}} %.',
        'pdf-import-tax-unclear': 'Bei einzelnen Leistungen ist die Steuer noch nicht eindeutig. Bitte wähle dort den passenden Steuersatz aus.',
        'pdf-import-technical': 'Weitere Optionen und technische Details',
        'pdf-import-terms-title': 'Storno, Zahlung und weitere Vereinbarungen',
        'pdf-import-tier-add': 'Stornostufe ergänzen',
        'pdf-import-tier-days': 'Ab Tage vor Veranstaltung',
        'pdf-import-tier-percent': 'Gebühr %',
        'pdf-import-tier-wording': 'Wortlaut im PDF',
        'pdf-import-tiers': 'Stornostaffel',
        'pdf-import-tiers-hint':
          'Wird als Stornoregel am Angebot gespeichert und ersetzt dort die Standardregeln des Hotels. Ohne Staffel gelten die Hotelregeln.',
        'pdf-import-title': 'PDF-Import',
        'pdf-import-to-offers': 'Zur Angebotsliste',
        'pdf-import-totals': 'Summen',
        'pdf-import-unit-price-is': 'Der Einzelpreis ist',
        'pdf-import-unknown': 'Unbekannt',
        'pdf-import-upload-only-pdf': 'Bitte PDF-Dateien hochladen.',
        'pdf-import-upload-read-failed': 'Datei konnte nicht gelesen werden.',
        'pdf-import-uploads': 'Uploads',
        'pdf-import-validUntil': 'Gültig bis laut PDF',
        'pdf-import-version': 'Version {{n}}',
        'pdf-import-yes': 'Ja',
      },
    },
  },
};

/**
 * BO English UI layer:
 * - Uses existing German resource as base (complete coverage)
 * - Overrides important navigation/action labels in English
 * - Keeps missing keys readable while incremental translations are added
 */
const enTranslationOverrides: Record<string, string> = {
  'enums-EOfferRoomSeating-OTHER': 'Other',
  'offer-individual-seating': 'individual seating',
  'offer-email-history-recipient-delivery-status': 'Delivery status',
  'enums-EEmailDeliveryStatus-PENDING': 'Pending',
  'enums-EEmailDeliveryStatus-SUCCESS': 'Delivered',
  'enums-EEmailDeliveryStatus-ERROR': 'Failed',
  'meeting-package-import-value-unresolved': 'Unresolved',
  'meeting-package-import-value-linked-existing': 'Linked to existing hotel',
  'meeting-package-import-value-created-from-source': 'Created from Meeting Package',
  'meeting-package-import-value-ignored': 'Ignored',
  'meeting-package-import-value-discovered': 'Discovered',
  'meeting-package-import-value-incomplete': 'Incomplete',
  'meeting-package-import-value-review-required': 'Manual review required',
  'meeting-package-import-value-approved-create': 'Creation approved',
  'meeting-package-import-value-importing': 'Importing',
  'meeting-package-import-value-imported-pending-provisioning': 'Imported, provisioning pending',
  'meeting-package-import-value-ready': 'Ready',
  'meeting-package-import-value-failed-retryable': 'Failed, retry available',
  'meeting-package-import-value-failed': 'Failed',
  'meeting-package-import-value-cancelled': 'Cancelled',
  'meeting-package-cancel-import': 'Cancel import',
  'meeting-package-import-cancellation-requested': 'Cancellation requested',
  'meeting-package-import-value-inactive': 'Inactive',
  'meeting-package-import-value-running': 'Running',
  'meeting-package-import-value-success': 'Successful',
  'meeting-package-import-value-partial': 'Partially successful',
  'meeting-package-import-value-auto-match': 'Automatic match',
  'meeting-package-import-value-manual-match': 'Manual match',
  'meeting-package-import-value-auto-create': 'Automatic creation',
  'meeting-package-import-progress-heading': 'Entries checked',
  'meeting-package-import-progress-loading': 'Determining the number of entries to check…',
  'meeting-package-import-progress': '{{processed}} / {{total}} entries ({{percent}}%)',
  'meeting-package-run-api': 'API entries in this run',
  'meeting-package-run-selection-group': 'Entry checks',
  'meeting-package-run-outcomes-group': 'Hotels found: total and outcomes',
  'meeting-package-run-other-group': 'Other results',
  'meeting-package-run-review': 'Review required',
  'meeting-package-run-retries': 'Additional retries',
  'meeting-package-run-prefiltered': 'Skipped by country prefilter',
  'meeting-package-run-excluded': 'Excluded after checking details',
  'meeting-package-run-discovered': 'Hotels found within country selection',
  'meeting-package-run-updated': 'Existing assignments updated',
  'meeting-package-run-linked': 'Newly linked',
  'meeting-package-run-created': 'Newly created and provisioned',
  'meeting-package-run-ignored': 'Ignored',
  'meeting-package-run-discovered-failed': 'Found, processing failed',
  'meeting-package-run-all-errors': 'Total errors',
  'meeting-package-run-not-recorded': 'Not recorded',
  'meeting-package-run-historical': 'Historical figures / reconstructed from events',
  'meeting-package-run-accounting-help':
    '100% means all scheduled entries were checked. Hotels found is the total within the country selection. ' +
    'Updated, newly linked, newly created, review required, ignored and processing failed describe their outcomes ' +
    'and must not be added to that total. Suggestions with automation disabled are included in review. ' +
    'Inactive entries and total errors may include additional entries. Missing historical figures are marked “Not recorded”.',
  'meeting-package-match-outcome-filter': 'Matching result',
  'meeting-package-match-outcome-matched': 'Existing hotel found',
  'meeting-package-match-outcome-new-hotel': 'No matching hotel / creation',
  'meeting-package-match-outcome-needs-review': 'Manual review needed',
  'meeting-package-rows-per-page': 'Hotels per page',
  'meeting-package-auto-create-result-created': 'Hotel created',
  'meeting-package-auto-create-result-disabled': 'Not created: auto-create disabled',
  'meeting-package-auto-match-result-linked': 'Hotel linked',
  'meeting-package-auto-match-result-disabled': 'Not linked: auto-match disabled',
  'meeting-package-import-value-manual-create': 'Manual creation',
  'meeting-package-import-value-match': 'Match expected',
  'meeting-package-import-value-review': 'Review expected',
  'meeting-package-import-value-no-auto-match': 'No automatic match',
  'meeting-package-import-value-create': 'Creation expected',
  'meeting-package-import-value-unknown': 'Unknown',
  'meeting-package-import-value-high': 'High confidence',
  'meeting-package-import-value-medium': 'Medium confidence',
  'meeting-package-import-value-low': 'Low confidence',
  'meeting-package-start-venue-sync': 'Start venue sync',
  'meeting-package-all-countries': 'All countries',
  'meeting-package-import-countries': 'Countries for this import',
  'meeting-package-import-countries-help':
    'Imports venues only from the selected countries. Known countries are reused in later runs. The first run identifies countries that are not yet known.',
  'meeting-package-address': 'Address',
  'meeting-package-administration-area': 'Administration area: {{name}}',
  'meeting-package-all': 'All',
  'meeting-package-all-venue-details-loaded': 'All requested venue details were loaded successfully.',
  'meeting-package-and': 'and',
  'meeting-package-api-config-check-failed': 'The API configuration could not be checked: {{error}}',
  'meeting-package-api-errors': 'API errors',
  'meeting-package-api-errors-count': 'API errors ({{count}})',
  'meeting-package-api-venue': 'API venue',
  'meeting-package-api-venues-loaded-count': '{{count}} API venues loaded',
  'meeting-package-auto-create-candidates-count': '{{count}} auto-create candidates',
  'meeting-package-auto-match-candidates-count': '{{count}} auto-match candidates',
  'meeting-package-missing-required-fields': 'Missing required fields: {{fields}}',
  'meeting-package-review-queue-filter': 'Review queue (including incomplete)',
  'meeting-package-best-candidate': 'Best candidate',
  'meeting-package-cancel': 'Cancel',
  'meeting-package-candidate': 'Candidate',
  'meeting-package-cases': 'Cases',
  'meeting-package-close': 'Close',
  'meeting-package-compare': 'Compare',
  'meeting-package-comparison': 'Comparison',
  'meeting-package-config-value-missing-plural': 'are missing.',
  'meeting-package-config-value-missing-singular': 'is missing.',
  'meeting-package-conflicts': 'Conflicts',
  'meeting-package-coordinates': 'Coordinates',
  'meeting-package-correct-auto-matches-count': '{{count}} correct auto-matches',
  'meeting-package-country': 'Country',
  'meeting-package-country-iso-optional': 'Country (ISO, optional)',
  'meeting-package-created': 'Created',
  'meeting-package-data': 'Meeting Package data',
  'meeting-package-db-hotel-sample': 'Database hotel sample',
  'meeting-package-db-hotels-count': '{{count}} database hotels',
  'meeting-package-decision': 'Decision',
  'meeting-package-decision-value': 'Decision {{value}}',
  'meeting-package-decisions-count': 'Decisions ({{count}})',
  'meeting-package-delete': 'Delete',
  'meeting-package-delete-all-reports-confirm': 'Delete all completed {{mode}} reports?',
  'meeting-package-delete-completed-reports': 'Delete completed reports',
  'meeting-package-delete-hotel-and-area': 'Delete hotel and administration area',
  'meeting-package-delete-report-confirm': 'Delete this matching dry-run report?',
  'meeting-package-details': 'Details',
  'meeting-package-deviations-count': 'Deviations ({{count}})',
  'meeting-package-discovered': 'Discovered',
  'meeting-package-discovery-unavailable': 'Meeting Package Discovery is unavailable.',
  'meeting-package-dry-run-info':
    'Manual read-only run: Hotels and the operational queue are not changed. In sandbox mode, real venue data is loaded from the separately configured Meeting Package sandbox. Only the report is stored; no scheduler run is created.',
  'meeting-package-dry-run-real-venue-info':
    'Real sandbox API venue from the dry run. Actions are intentionally disabled and do not change the operational queue.',
  'meeting-package-dry-run-simulated-case-info':
    'Simulated review case from the dry run. Actions are intentionally disabled and do not change the operational queue.',
  'meeting-package-error': 'Error',
  'meeting-package-error-details': 'Error details',
  'meeting-package-time': 'Time',
  'meeting-package-import-errors-title': 'Errors in import run #{{runId}}',
  'meeting-package-no-venue-errors': 'No individual venue errors are stored for this run.',
  'meeting-package-import-queued': 'Import requested. Waiting for the scheduler.',
  'meeting-package-import-value-queued': 'Waiting for scheduler',
  'meeting-package-expectation': 'Expectation',
  'meeting-package-expectation-value': 'Expectation {{value}}',
  'meeting-package-field': 'Field',
  'meeting-package-field-comparison': 'Field comparison',
  'meeting-package-geo-distance': 'Geographic distance',
  'meeting-package-hotel': 'Hotel',
  'meeting-package-hotel-search-placeholder': 'Hotel name or reference code',
  'meeting-package-ignore': 'Ignore',
  'meeting-package-import-as-new-hotel': 'Import as new hotel',
  'meeting-package-import-runs': 'Import runs',
  'meeting-package-import-run-filter': 'Import run',
  'meeting-package-import-run-current-status': 'Status and match result show the current state.',
  'meeting-package-all-import-runs': 'All import runs',
  'meeting-package-show-inactive': 'Include inactive hotels',
  'meeting-package-not-in-source': 'No longer in source',
  'meeting-package-older-entry': 'Older entry',
  'meeting-package-older-entry-tooltip': 'Not seen again since the latest import run started. That run may still be active or may have failed.',
  'meeting-package-import-title': 'Meeting Package Import',
  'meeting-package-in-sample-count': '{{count}} in sample',
  'meeting-package-inactive': 'Inactive',
  'meeting-package-incorrect-auto-matches': 'Incorrect auto-matches',
  'meeting-package-incorrect-auto-matches-count': '{{count}} incorrect auto-matches',
  'meeting-package-last-seen': 'Last seen',
  'meeting-package-limit-seed': 'Limit / seed',
  'meeting-package-link': 'Link',
  'meeting-package-linked': 'Linked',
  'meeting-package-suggested-linked-tooltip': 'Automatic linking disabled: {{count}} candidates found but not linked. Actually linked: {{actual}}.',
  'meeting-package-suggested-created-tooltip':
    'Automatic creation disabled: {{count}} candidates found but not created. Actually created: {{actual}}.',
  'meeting-package-linked-with': 'Linked to {{name}} ({{refCode}})',
  'meeting-package-matching-dry-run': 'Matching dry run',
  'meeting-package-max-api-venues': 'Max. API venues',
  'meeting-package-mock-cases-count': '{{count}} mock cases',
  'meeting-package-mock-history': 'Mock scenario history',
  'meeting-package-mock-scenario': 'Mock scenario',
  'meeting-package-mock-scenarios': 'Mock scenarios',
  'meeting-package-mock-venue': 'Mock venue',
  'meeting-package-name': 'Name',
  'meeting-package-name-score': 'Name score',
  'meeting-package-no-ground-truth': 'no ground truth',
  'meeting-package-no-permission': 'No permission.',
  'meeting-package-no-process-log-entries': 'No process log entries yet.',
  'meeting-package-note': 'Note',
  'meeting-package-old-report-no-review-queue': 'This older report does not contain a review queue.',
  'meeting-package-open': 'Open',
  'meeting-package-open-hotel': 'Open hotel',
  'meeting-package-or-selected-base-url': 'or the selected sandbox/production base URL',
  'meeting-package-payload': 'Payload',
  'meeting-package-postcode-city': 'Postcode / city',
  'meeting-package-prioritized-deviations': 'Prioritized deviations',
  'meeting-package-process-log-count': 'Process log ({{count}})',
  'meeting-package-real-venues-count': '{{count}} real venues',
  'meeting-package-reasons': 'Reasons',
  'meeting-package-reevaluate': 'Re-evaluate',
  'meeting-package-refresh': 'Refresh',
  'meeting-package-reset-confirm-text':
    'The venue is then matched again, queued for manual review, and can be imported with a completely new hotel and administration area. The operation is rejected if the hotel or administration area is already in use.',
  'meeting-package-reset-confirm-title': 'Reset Meeting Package import?',
  'meeting-package-reset-import': 'Reset import',
  'meeting-package-reset-warning': 'The hotel created from Meeting Package and its dedicated administration area will be permanently deleted.',
  'meeting-package-restart-server-after-config': 'The server must be restarted after the configuration is set.',
  'meeting-package-result-from': 'Result from {{date}}',
  'meeting-package-retry': 'Retry',
  'meeting-package-review-queue': 'Review queue',
  'meeting-package-review-queue-count': 'Review queue ({{count}})',
  'meeting-package-reviews-count': '{{count}} reviews',
  'meeting-package-sandbox-api': 'Sandbox API',
  'meeting-package-sandbox-config-missing': 'The sandbox dry run is enabled, but the key or base URL is missing from the server configuration.',
  'meeting-package-sandbox-history': 'Sandbox API history',
  'meeting-package-sandbox-match-decisions': 'Match decisions for real sandbox venues',
  'meeting-package-sandbox-match-decisions-info':
    'The decision shows what the matcher would suggest with automatic flags enabled. The dry run does not perform any of these actions.',
  'meeting-package-sandbox-no-ground-truth-info':
    'These are real sandbox payloads, but there is no ground truth. Therefore, no claims are made about precision, recall, or incorrect matches.',
  'meeting-package-sandbox-review-queue-info':
    'Real sandbox venues that require manual review with the current thresholds. They exist only within this dry-run report.',
  'meeting-package-sandbox-review-queue-summary': 'Sandbox review queue ({{shown}} of {{total}})',
  'meeting-package-sandbox-venue': 'Sandbox venue',
  'meeting-package-scenario': 'Scenario',
  'meeting-package-scenarios': 'Scenarios',
  'meeting-package-search': 'Search',
  'meeting-package-select': 'Select',
  'meeting-package-select-other-hotel': 'Select another hotel',
  'meeting-package-selected-candidate': 'Selected candidate',
  'meeting-package-show': 'Show',
  'meeting-package-simulated-review-queue-info': 'Representative review cases mixed by scenario. They exist only within this dry-run report.',
  'meeting-package-simulated-review-queue-summary': 'Simulated review queue ({{shown}} of {{total}})',
  'meeting-package-source': 'Source',
  'meeting-package-start': 'Start',
  'meeting-package-start-matching-dry-run': 'Start matching dry run',
  'meeting-package-start-sandbox-dry-run': 'Start sandbox dry run',
  'meeting-package-status': 'Status',
  'meeting-package-suggested-hotels': 'Suggested hotels',
  'meeting-package-this-hotel': 'this hotel',
  'meeting-package-threshold-calibration': 'Threshold calibration',
  'meeting-package-thresholds': 'Thresholds',
  'meeting-package-top-candidate-correct': 'Top candidate correct',
  'meeting-package-top-candidates': 'Top candidates',
  'meeting-package-total-score': 'Total score',
  'meeting-package-unlink': 'Unlink',
  'meeting-package-unlink-confirm-text':
    'The Meeting Package venue will be unlinked from {{name}} and added back to the review queue. The hotel itself remains completely unchanged.',
  'meeting-package-unlink-confirm-title': 'Unlink hotel?',
  'meeting-package-auto-create': 'Auto-create',
  'meeting-package-auto-match': 'Auto-match',
  'meeting-package-evidence': 'Evidence',
  'meeting-package-false-negatives': 'False negatives',
  'meeting-package-false-positives': 'False positives',
  'meeting-package-map-source-venue': 'A: Meeting Package venue',
  'meeting-package-map-candidate-marker': 'B · {{name}}',
  'meeting-package-map-source-marker': 'A · Meeting Package',
  'meeting-package-pass-rate': 'Pass rate',
  'meeting-package-precision': 'Precision',
  'meeting-package-recall': 'Recall',
  'meeting-package-review': 'Review',
  'meeting-package-score': 'Score',
  'meeting-package-score-percentiles': 'Score P10 / P50 / P90',
  'meeting-package-score-runner-up': 'Score / runner-up',
  'meeting-package-seed': 'Seed',
  'meeting-package-source-name': 'Meeting Package',
  'meeting-package-true-positives': 'True positives',
  'meeting-package-venue': 'Venue',
  'meeting-package-venue-id': 'Venue ID',
  'meeting-package-evidence-value': 'Evidence {{value}}',
  'meeting-package-pass-rate-value': 'Pass rate {{value}}',
  'meeting-package-runner-up-value': 'Runner-up {{value}}',
  'meeting-package-score-value': 'Score {{value}}',
  system: 'System',
  'system-section-overview': 'Overview',
  'system-section-maintenance': 'Maintenance',
  'system-section-cache': 'Cache & Redis',
  'system-section-audit': 'API Audit',
  'system-section-meeting-package': 'Meeting Package',
  'system-audit-title': 'API audit log',
  'system-audit-filter-api-type': 'API type',
  'system-audit-filter-service': 'Service',
  'system-audit-filter-status': 'Status',
  'system-audit-filter-user': 'User',
  'system-audit-filter-operation': 'Operation',
  'system-audit-filter-from': 'From',
  'system-audit-filter-to': 'To',
  'system-audit-filter-apply': 'Filter',
  'system-audit-col-time': 'Time',
  'system-audit-col-type': 'Type',
  'system-audit-col-service': 'Service',
  'system-audit-col-who': 'Who',
  'system-audit-col-what': 'What',
  'system-audit-col-status': 'Status',
  'system-audit-col-version': 'Version',
  'system-audit-empty': 'No audit entries found.',
  'system-audit-total': '{{total}} entries',
  'system-audit-details-title': 'Audit details',
  'system-audit-details': 'Details',
  'system-audit-all': 'All',
  'system-audit-success': 'Success',
  'system-audit-failure': 'Failure',
  'system-audit-previous': 'Previous',
  'system-audit-next': 'Next',
  'language-button-label': 'Language',
  'language-select-title': 'Select language',
  'confirmation-dialog-decline': 'Cancel',
  'confirmation-dialog-confirm': 'Continue',
  'information-dialog-confirm': 'Close window',
  'offer-reload-title': 'Changes to the booking',
  'offer-reload-text': 'Changes were made to the booking in the background. You will be redirected to the booking overview.',
  'offer-guest-access-denied': 'Access denied. Check the link or sign in with an authorized account (log out if needed).',
  'offer-guest-banner': 'Guest access via link — you can work on this offer without a full sign-in.',
  'offer-guest-sign-in': 'Sign in',
  'offer-guest-user-type': 'GUEST',
  'offer-guest-success-approved': 'Thank you for your offer!',
  'offer-guest-success-draft': 'Draft has been saved!',
  'offer-guest-success-declined': 'The inquiry has been declined!',
  'offer-guest-success-ok': 'OK',
  'sidebar-clients': 'Clients',
  'sidebar-groups': 'Chains',
  'sidebar-widgetoffers': 'SEM bookings',
  'sidebar-listeroffers': 'seminargo bookings',
  'sidebar-bericht': 'Reports',
  'sidebar-catalog': 'Service configuration',
  'sidebar-products': 'Services',
  'sidebar-product-bundles': 'Service packages',
  'sidebar-pricelist': 'Price list',
  'sidebar-pricelists': 'Price lists',
  'sidebar-availability': 'Availability',
  'availability-list-page-title': 'Availability',
  'pricelists-list-page-title': 'Price lists',
  'selector-products-tab': 'Service prices',
  'selector-bundles-tab': 'Package prices',
  'selector-facilities-tab': 'Facility & technology prices',
  'products-tabs-header-products': 'Services',
  'products-tabs-header-bundles': 'Packages',
  'products-tabs-header-facilities': 'Facility & technology',
  'products-tabs-header-servicetypes': 'Service packages',
  'products-tabs-header-import': 'File import',
  'products-list-page-title': 'Services',
  'products-list-header-product': 'Service',
  'products-list-header-sku': 'SKU',
  'products-list-header-recurring': 'Billing',
  'products-list-header-isaddon': 'Additional service',
  'products-list-header-isdeduction': 'Deduction',
  'product-prices-tab': 'Price lists',
  'product-bundles-tab': 'Packages',
  'product-bundles-bundle': 'Package',
  'product-bundles-included': 'Included quantity',
  'product-text-tab': 'Text modules',
  'product-availability-tab': 'Availability',
  'productbundles-list-page-title': 'Packages',
  'productbundles-list-header-bundle': 'Service',
  'productbundles-list-header-sku': 'SKU',
  'productbundles-list-header-recurring': 'Billing',
  'productbundle-items-tab': 'Service items',
  'productbundle-item-product': 'Service',
  'productbundle-prices-tab': 'Price lists',
  'productbundle-text-tab': 'Text modules',
  'productbundle-availability-tab': 'Availability',
  'facilities-list-page-title': 'Facility & technology',
  'facilities-list-header-facility': 'Facility',
  'facilities-list-header-sku': 'SKU',
  'facilities-list-header-recurring': 'Billing',
  'facilities-list-header-isaddon': 'Additional facility',
  'facility-prices-tab': 'Price lists',
  'facility-text-tab': 'Text modules',
  'facility-availability-tab': 'Availability',
  'servicetypes-list-page-title': 'Service packages',
  'servicetypes-list-header-type': 'Service package',
  'servicetypes-list-header-sku': 'SKU',
  'servicetypes-list-header-sequence': 'Sort order',
  'servicetypes-list-header-ispublished': 'Published',
  'servicetype-validation-tab': 'Service package incomplete',
  'servicetype-validation-hint': 'The service package has missing entries. Save after completing all required assignments.',
  'servicetype-validation-products': '{{productCount}} missing service(s)',
  'servicetype-validation-facilities': '{{facilityCount}} missing facility/facilities',
  'servicetype-products-tab': 'Included services',
  'servicetype-products-product': 'Service',
  'servicetype-products-isfordayvisitor': 'For day guests',
  'servicetype-products-isforovernight': 'For overnight guests',
  'servicetype-products-isforroom': 'Overnight only',
  'servicetype-products-isfordeparture': 'For departure guests',
  'servicetype-products-isforsemidayvisitor': 'For half-day guests',
  'servicetype-products-isforsemidayovernight': 'For half-day overnight guests',
  'servicetype-products-isforsemidaydeparture': 'For half-day departure guests',
  'servicetype-products-isforprevday': 'For previous-day arrival',
  'servicetype-products-isfornextday': 'For extension night',
  'servicetype-product-add': 'Add individual service',
  'servicetype-productbundle-add': 'Add bundled service',
  'servicetype-product-or-bundle': 'No individual or bundled service selected',
  'servicetype-products-or-facilities': 'No service or facility selected',
  'servicetype-usage-not-selected': 'No usage selected',
  'servicetype-facilities-tab': 'Included facility & technology',
  'servicetype-facility-name': 'Facility',
  'servicetype-facility-includedcount': 'Included quantity',
  'servicetype-facility-isforday': 'For full-day occupancy',
  'servicetype-facility-isforsemiday': 'For half-day occupancy',
  'servicetype-facility-add': 'Add facility',
  'servicetype-addons-tab': 'Optional services',
  'servicetype-addons-facilities': 'Optional facility & technology',
  'servicetype-addons-products': 'Optional additional services',
  'servicetype-addonservices-helper':
    'These services can be optionally added. You can define from which quantity/day a hotel confirmation is required when add-ons occur after an already confirmed reservation.',
  'servicetype-addonservices-product': 'Service',
  'servicetype-addonservices-addproduct': 'Select individual service',
  'servicetype-addonservices-facility': 'Facility',
  'servicetype-addonservices-addfacility': 'Select facility',
  'servicetype-addonservices-approvallimit': 'Confirmation required from quantity/day (leave empty if no confirmation is required)',
  'servicetype-addonservices-selectable': 'Selectable',
  'servicetype-text-tab': 'Text modules',
  'sidebar-content': 'Content management',
  'sidebar-security': 'Users / Permissions',
  'sidebar-users': 'Users',
  'sidebar-spaces': 'Administrative areas',
  'sidebar-text': 'Text blocks',
  'sidebar-media': 'Media',
  'sidebar-settings': 'Settings',
  'sidebar-templates': 'Templates',
  'sidebar-hotels': 'Hotels',
  'sidebar-integration': 'Integrations',
  'sidebar-space': 'Corporate identity',
  'sidebar-help': 'Help',
  'sidebar-search': 'Search',
  search: 'Search',
  actions: 'Actions',
  'created-at': 'Created at',
  'accounting-area-vat-id-invalid': 'Invalid VAT ID format (e.g. ATU12345678)',
  'clients-list-page-title': 'Clients',
  'search-tabs-header-companies': 'Companies',
  'search-tabs-header-clients': 'Contacts',
  'search-tabs-header-bookings': 'Bookings',
  'search-tabs-header-hotels': 'Hotels',
  'publiccompany-list-header-name': 'Company name',
  'publiccompany-list-header-customer-type': 'Customer type',
  'publiccompany-list-header-id': 'ID',
  'publiccompany-list-header-address': 'Address',
  'publiccompany-list-header-email': 'Email',
  'publiccompany-list-header-contacts-count': 'Number of contacts',
  'publiccompany-list-header-status': 'Status',
  'publiccompany-button-new': 'Create new company',
  'publiccompany-list-header-premium': 'Premium customer',
  'publiccompany-list-header-clients-count': 'Number of contacts',
  'clients-list-header-name': 'Name',
  'clients-list-header-email': 'Email',
  'clients-list-header-phone': 'Phone',
  'clients-list-header-company': 'Company',
  'clients-list-header-premium': 'Premium customer',
  'bookings-list-header-refcode': 'BID',
  'bookings-list-header-title': 'Title',
  'bookings-list-header-company': 'Company',
  'bookings-list-header-client': 'Customer',
  'bookings-list-header-hotel': 'Hotel',
  'bookings-list-header-assignee': 'Assignee',
  'hotels-list-header-name': 'Name',
  'hotels-list-header-businesscountry': 'Country',
  'hotels-list-header-email': 'Email',
  'main-filter-select': 'Select hotel/administrative area',
  'main-filter-select-hotel': 'Select hotel',
  'main-filter-empty': 'Show all',
  'filter-generate-report': 'Generate report',
  'filter-reset': 'Reset all filters',
  'filter-from': 'Request date from',
  'filter-to': 'Request date to',
  'filter-seminar-from': 'Seminar date from',
  'filter-seminar-to': 'Seminar date to',
  'filter-offer-source': 'Offer source',
  'filter-offer-source-all': 'All',
  'filter-status-all': 'All',
  'filter-hotel': 'Hotel',
  'filter-hotel-group': 'Hotel chain',
  'filter-agreement-type': 'Contract type',
  'filter-invoice-interval': 'Invoice interval',
  'filter-contract-valid-from': 'Valid from',
  'filter-contract-valid-to': 'Valid to',
  'filter-show-in-lister': 'Hotel lister',
  'filter-show-in-lister-yes': 'Shown in hotel lister',
  'filter-show-in-lister-no': 'Not shown in hotel lister',
  'filter-state': 'Hotel state',
  'filter-country-hotel': 'Hotel country',
  'main-usermenu-account': 'My account',
  'main-usermenu-logout': 'Log out',
  'sem-version-dialog-title': 'Version information',
  'no-results': 'No results',
  'generic-of': 'of',
  'main-filter-search-placeholder': 'Search hotel or administrative area...',
  'sidebar-bookings': 'Bookings',
  'sidebar-overflows': 'Overflows',
  'offers-overflows-search-label': 'Search',
  'offers-overflows-search-button': 'Search',
  'offers-overflows-header-refcode': 'Booking ID',
  'offers-overflows-header-first-hotel-name': 'Hotel (first reported)',
  'offers-overflows-header-first-hotel-id': 'Hotel ID (first reported)',
  'offers-overflows-header-first-notification': 'Notification timestamp',
  'offers-overflows-header-start': 'Seminar start date',
  'offers-overflows-header-end': 'Seminar end date',
  'offers-overflows-header-status': 'Status',
  'offers-overflows-header-booker': 'Booker',
  'offers-overflows-header-overnights': 'Overnights',
  'offers-overflows-header-actions': 'Actions',
  'offers-overflows-export-download': 'Download Excel report',
  'offers-overflows-export-failed': 'Export failed',
  'sidebar-administration': 'Administration',
  'version-label-branch': 'Branch version',
  'version-label-build-date': 'Build date',
  'version-label-commit-hash': 'Commit hash',
  'main-load-spaces-hotels-error': 'Hotels/administrative areas could not be loaded. Please reload the page. Details in browser console:',
  'filter-status-offer': 'Request status',
  'filter-status-offer-version': 'Detail status',
  'filter-status-booking': 'Booking status',
  'filter-hotel-group-select': 'Select hotel chain(s)',
  'filter-hotel-search-placeholder': 'Search hotel...',
  'filter-company-search-placeholder': 'Search company...',
  'filter-contact-search-placeholder': 'Search contact person...',
  'filter-enter-min-3': 'Enter at least 3 characters',
  'filter-hotels-not-found': 'No hotels found',
  'filter-companies-not-found': 'No companies found',
  'filter-contacts-not-found': 'No contact persons found',
  'filter-customer': 'Customer',
  'filter-date': 'Date',
  'filter-country': 'Country',
  'report-loading': 'Loading report...',
  'report-load-error': 'Failed to load report. Please try again.',
  'report-no-export-data': 'No data to export. Please generate a report first.',
  'report-export-failed': 'Export failed: {{message}}',
  'download-csv': 'Download CSV',
  'download-csv-report': 'Download CSV report',
  'earlybird-select-date': 'Please select a date.',
  'earlybird-report-title': 'Early Bird Report',
  'earlybird-date-day': 'Date (day)',
  'earlybird-date-week': 'Date (week)',
  'earlybird-date-month': 'Date (month)',
  'earlybird-date-year': 'Date (year)',
  'earlybird-metric': 'Metric',
  'earlybird-period-day': 'Day',
  'earlybird-period-week': 'Week',
  'earlybird-period-month': 'Month',
  'earlybird-period-year': 'Year',
  'report-contact-persons-found': 'contact person(s) found',
  'contact-firstname': 'First name',
  'contact-lastname': 'Last name',
  'contact-title': 'Title',
  'contact-street': 'Street',
  'contact-street-number': 'No.',
  'contact-zip': 'ZIP',
  'contact-city': 'City',
  'contact-country': 'Country',
  'contact-state': 'State',
  'contact-supplement': 'Supplement',
  'contact-email': 'Email',
  'contact-phone': 'Phone',
  'contact-mobile': 'Mobile',
  'contact-subjects': 'Subject(s)',
  'contact-net-inquiries': 'Net inquiries',
  'contact-gross-inquiries': 'Gross inquiries',
  'contact-reservations': 'Reservations',
  'contact-overnights': 'Overnight stays',
  'contact-participants': 'Participants',
  'contact-person': 'Contact person',
  'contact-timestamp': 'Timestamp',
  'contact-subject': 'Subject',
  'contact-employee': 'Employee',
  'contact-type': 'Contact type',
  'report-filter-company-min-3': 'Company (min. 3 characters)',
  'report-filter-hotels-min-3': 'Hotel(s) (min. 3 characters)',
  'report-contract-none': 'No contract',
  'statistics-request-date': 'Request date',
  'statistics-booking-date': 'Booking date',
  'statistics-year': 'Year',
  'statistics-month': 'Month of inquiry',
  'statistics-booking-month-range': 'Month of booking',
  'statistics-no-seminar-start': '— (no date)',
  'statistics-hotel': 'Hotel',
  'statistics-source': 'Source',
  'statistics-requests': 'Requests',
  'statistics-reservations': 'Reservations',
  'statistics-rejected-hotel': 'Rejected hotel',
  'statistics-rejected-client': 'Rejected client',
  'statistics-overnights': 'Overnight stays',
  'statistics-participants': 'Participants',
  'statistics-generate': 'Generate statistics',
  'statistics-loading': 'Loading statistics...',
  'statistics-results': 'Statistics results',
  'statistics-entries-found': 'entries found',
  'statistics-total-sum': 'TOTAL SUM',
  'statistics-sum': 'Sum',
  'statistics-rows-per-page': 'Rows per page:',
  'table-pagination-first-page': 'First page',
  'table-pagination-last-page': 'Last page',
  'table-pagination-next-page': 'Next page',
  'table-pagination-previous-page': 'Previous page',
  'reports-page-tabs-aria': 'Report subtabs',
  'selector-price-tabs-aria': 'Price list selector tabs',
  'system-status-redis-heading': 'Redis',
  'system-status-mysql-heading': 'MySQL',
  'system-mysql-buffer-pool': 'Buffer pool',
  'statistics-more-than': 'more than',
  'offers-list-page-title-WIDGET': 'SEM bookings',
  'offers-list-page-title-LISTER': 'seminargo bookings',
  'offers-list-seminargo-confirmation': 'Invoices to be confirmed by seminargo',
  'offers-list-offer-seminargo-confirmation': 'Offers to be confirmed by seminargo',
  'offers-list-offer-seminargo-confirmation-hotel': 'Offer review',
  'offers-list-open': 'To process',
  'offers-list-hotel-editing': 'Under processing by hotels',
  'offers-list-offers': 'Offers',
  'offers-list-reservations': 'Reservations',
  'offers-list-invoice-review': 'Invoice review',
  'offers-list-archive': 'Archive',
  'offers-list-header-createdat': 'Received',
  'offers-list-header-from-to': 'Arrival/Departure',
  'offers-list-header-refcode': 'Booking ID',
  'offers-list-header-title': 'Event title',
  'offers-list-header-hotel': 'Hotel',
  'offers-list-header-client': 'Customer',
  'offers-list-header-status': 'Status',
  'offers-list-header-delete-reason': 'Archive reason',
  'offers-list-create-new': 'Create new offer',
  'offer-duplicate-tooltip': 'Duplicate',
  'offer-duplicate-title': 'Duplicate {{refCode}} - {{company}}',
  'offer-duplicate-confirm-text':
    'Are you sure you want to duplicate booking {{refCode}} for customer {{company}}? A new quote will be created. The customer will not receive a notification yet.',
  'offer-duplicate-startdate-label': 'Start date',
  'offer-duplicate-startdate-warning': 'Please choose the start date for the new offer. The event length and day-before services stay the same.',
  'offer-duplicate-startdate-past-error': 'Start date cannot be in the past',
  'offer-duplicate-confirm': 'Create offer',
  'offer-duplicate-success': 'Offer {{refCode}} created',
  'offers-list-combined-view': 'Show bookings from SEM and seminargo',
  'offers-debug-copied': 'Debug info copied to clipboard',
  'offers-debug-copy-sentry': 'Copy + Sentry',
  'offer-history-email-cc-title': 'Alternative recipients (CC):',
  'offer-history-email-cc-placeholder': 'Add email address',
  'offer-history-email-invalid-address': 'Invalid email address',
  'offer-history-email-max-cc': 'A maximum of 5 additional email addresses is allowed',
  'offer-history-email-max-addresses': 'A maximum of 5 email addresses is allowed',
  'offer-history-email-duplicate-cc': 'This email address has already been added',
  'offer-history-email-unknown-error': 'Unknown error',
  'offer-history-email-recipients-count-preview': '{{count}} recipients: {{recipientType}}: {{emailAddress}} ({{sourceName}}){{suffix}}',
  'offer-history-email-more-recipients': '{{count}} additional recipients',
  'offer-history-email-hotel-with-email': 'Hotel ({{email}})',
  'offer-history-transfer-from-to': 'Booking was transferred from {{oldClientName}}{{oldCompanyName}} to {{newClientName}}{{newCompanyName}}',
  'offer-history-transfer-to': 'Booking was transferred to {{newClientName}}{{newCompanyName}}',
  'offer-history-transfer-to-new-contact': 'Booking was transferred to a new contact person',
  'offer-history-transfer-reason': 'Reason: {{reason}}',
  'offer-history-template-booking-received-client': 'Booking confirmation to customer',
  'offer-history-template-booking-received-hotel': 'Booking request to hotel',
  'offer-history-template-booking-approved-client': 'Booking approved - customer',
  'offer-history-template-booking-approved-hotel': 'Booking approved - hotel',
  'offer-history-template-booking-rejected-client': 'Booking rejected - customer',
  'offer-history-template-booking-rejected-hotel': 'Booking rejected - hotel',
  'offer-history-template-offer-received-client': 'Offer received - customer',
  'offer-history-template-offer-reminder-client': 'Offer reminder - customer',
  'offer-history-template-offer-reminder-hotel': 'Offer reminder - hotel',
  'offer-history-template-client-verification': 'Email verification',
  'offer-history-template-lister-nonbinding-request-hotel': 'Non-binding request - hotel',
  'offer-history-template-lister-booking-direct-client': 'Direct booking - customer',
  'offer-history-template-lister-booking-direct-hotel': 'Direct booking - hotel',
  'offer-history-template-lister-booking-cancellation-hotel': 'Booking cancelled via hotel lister (to hotel)',
  'offer-history-template-lister-reservation-hotel': 'Reservation via hotel lister (to hotel)',
  'offer-history-template-lister-reservation-approved-client': 'Reservation confirmed via hotel lister (to customer)',
  'offer-history-template-lister-booking-reminder-start-soon-hotel': 'Event starts soon reminder via hotel lister (to hotel)',
  'offer-history-template-lister-event-planning-hotel': 'Event planning via hotel lister (to hotel)',
  'offer-history-template-lister-missing-data-hotel': 'Missing data via hotel lister (to hotel)',
  'offer-history-template-lister-missing-reservation-client': 'Missing reservation via hotel lister (to customer)',
  'offer-email-history-no-subject': 'No subject',
  hotel: 'Hotel',
  email: 'Email',
  'offer-input-tab': 'Received customer request',
  'offer-editor-tab': 'Offer view',
  'offer-confirmation-editor-tab': 'Reservation confirmation',
  'offer-integration-tab': 'PMS history',
  'offer-event-tab': 'Event checklist',
  'offer-tasks-tab': 'Tasks',
  'offer-history-tab': 'History',
  'offer-seminargo-contact': 'seminargo Contact',
  'offer-seminargo-contact-office': 'seminargo Office',
  'offer-history-booking-tab': 'Changes',
  'offer-history-email-tab': 'Emails',
  'offer-history-details-title': 'History details',
  'offer-history-details-next': 'Next',
  'offer-history-addon-name': 'Service',
  'offer-history-previous-count': 'Previous count',
  'offer-history-new-count': 'New count',
  'offer-history-change-status': 'Change status',
  'offer-addon-change-removed': 'Removed',
  'offer-addon-change-increased': 'Increased',
  'offer-addon-change-decreased': 'Decreased',
  'offer-addon-change-unchanged': 'Unchanged',
  'offer-history-changed-by': 'Changed by {{user}} on {{date}}',
  'offer-history-unknown-user': 'Unknown user',
  'offer-event-tab-download': 'Download checklist',
  'offer-history-email-sent': 'Email was sent',
  'offer-history-email-failed': 'Email was not sent',
  'offer-email-history-error-details': 'Error details',
  'offer-info-header-commission': 'Commission',
  'offer-info-text-commission-net': 'When a reservation is completed, {{commissionPercentage}} commission is calculated on the net total plus VAT.',
  'offer-info-text-commission-gross':
    'When a reservation is completed, {{commissionPercentage}} commission is calculated on the gross total plus VAT.',
  'offer-info-commission-calculating': 'Calculating estimated commission...',
  'offer-info-commission-error': 'Error calculating commission',
  'offer-info-commission-retry': 'Calculate again',
  'offer-info-commission-calculated': 'Estimated commission: {{amount}}',
  'offer-info-commission-calculate': 'Calculate estimated commission',
  'offer-info-edit-commission': 'Edit here',
  'offer-info-dialog-commission-title': 'Edit commission',
  'offer-info-commission-validation-error': 'Commission is invalid. Please check the percentage and calculation basis.',
  'offer-info-commission-saved': 'Commission was saved',
  'offer-info-header-cancellation': 'Cancellation conditions',
  'offer-info-header-downpayment': 'Down payment',
  'customer-message-for-hotel': 'Customer message',
  'customer-comment-dialog-title': 'Customer comment',
  'customer-comment-dialog-intro': 'The customer left the following comment:',
  'customer-comment-dialog-acknowledge': 'I have taken note of this comment and will take it into account when preparing the offer.',
  'customer-comment-dialog-start-editing': 'Start editing',
  'customer-request-file-download': 'Download',
  'offer-info-text-cancellation': 'The following cancellation conditions apply for this event:',
  'offer-info-text-downpayment-empty': 'You can enter your down payment terms here.',
  'offer-info-edit-cancellation': 'Edit here',
  'offer-info-dialog-cancellation-title': 'Edit cancellation conditions',
  'offer-info-cancellation-rule-add': 'Add cancellation condition',
  'offer-info-cancellation-rule-days': 'Cancellation period (days before event start)',
  'offer-info-cancellation-rule-rate': 'Cancellation rate (in %)',
  'offer-info-cancellation-rule-tolerance': 'Tolerance (in %)',
  'offer-info-cancellation-agreed-waiver': 'Agreed cancellation waiver (in %)',
  'offer-info-cancellation-agreed-waiver-tooltip': 'The agreed cancellation waiver is credited free of charge in case of customer cancellation.',
  'offer-info-cancellation-validation-error':
    'Cancellation conditions are invalid. Each rule must have distinct days and a rate between 0% and 100%.',
  'offer-info-cancellation-validation-hint': 'Each rule must have distinct days and a rate between 0% and 100%.',
  'offer-info-cancellation-rules-saved': 'Cancellation conditions were saved',
  'offer-info-edit-downpayment': 'Edit here',
  'offer-versions-header-user': 'User',
  'offer-versions-header-usertype': 'Type',
  'offer-versions-header-status': 'Status',
  'offer-versions-header-createdat': 'Timestamp',
  'offer-email-history-header-zeitpunkt': 'Time',
  'offer-email-history-header-sender': 'From',
  'offer-email-history-header-recipients': 'Recipients',
  'offer-email-history-header-alternative-recipients': 'Alternative recipients',
  'offer-email-history-header-description': 'Description',
  'offer-email-history-header-status': 'Status',
  'offer-email-history-header-actions': 'Actions',
  'offer-history-email-system': 'System',
  'enums-EOfferVersionHistoryStatus-CANCELLATION_CONFIRMED_BY_HOTEL': 'Cancellation confirmed',
  'enums-EOfferVersionHistoryStatus-CANCELLED_CLIENT': 'Cancelled',
  'enums-EOfferVersionHistoryStatus-PROCESS_FAILURE': 'Process failure',
  'enums-EOfferVersionHistoryStatus-EVENT_STARTING_SOON_HOTEL_SENT': 'Event starts soon sent to hotel',
  'enums-EOfferVersionHistoryStatus-EVENT_PLANNING_HOTEL_SENT': 'Event planning sent to hotel',
  'enums-EOfferVersionHistoryStatus-EVENT_MISSINGDETAILS_CLIENT_SENT': 'Missing event details sent to customer',
  'enums-EOfferVersionHistoryStatus-RESERVATION_HOTEL': 'Hotel confirmed reservation',
  'enums-EOfferVersionHistoryStatus-APPROVED_CLIENT': 'Customer confirmed',
  'enums-EOfferVersionHistoryStatus-APPROVED_HOTEL': 'Hotel confirmed availability',
  'enums-EOfferVersionHistoryStatus-APPROVED_SEMINARGO': 'Approved by seminargo',
  'enums-EOfferVersionHistoryStatus-WAITFORSEMAPPROVAL': 'Waiting for seminargo approval',
  'enums-EOfferVersionHistoryStatus-WAITFORCLIENTAPPROVAL': 'Customer confirmation pending',
  'enums-EOfferVersionHistoryStatus-REQUEST_SUBMITTED': 'Request submitted',
  'offer-input-header-company-data': 'Company data',
  'offer-input-header-billing-data': 'Billing data',
  'offer-input-premium-customer-title': 'Premium customer',
  'offer-input-premium-customer-note': 'After reservation and invoicing, customer support for the hotel is handled by seminargo.',
  'offer-input-classic-customer-title': 'Classic customer',
  'offer-input-classic-customer-note': 'After reservation and invoicing, support is handled directly by the hotel.',
  'offer-input-not-available': 'Not available',
  'offer-input-header-email': 'Email',
  'offer-input-header-phone': 'Phone number',
  'offer-input-header-start': 'Start',
  'offer-input-header-end': 'End',
  'offer-input-header-comment': 'Comment',
  'offer-input-header-preday': 'Previous day',
  'offer-input-header-day': 'Day',
  'offer-input-header-company': 'Company name',
  'offer-input-header-address': 'Address',
  'offer-input-header-addons': 'Additional services',
  'offer-input-no-addons': 'No additional services',
  'offer-input-line-sku': 'SKU',
  'offer-input-line-header': 'Service',
  'offer-input-line-count1': 'Requested',
  'offer-input-line-count2': 'Confirmed',
  'offer-input-line-diff': 'Difference',
  'offertemplate-section-info': 'General information',
  'offertemplate-section-input': 'Received customer request',
  'offertemplate-section-client': 'Customer and offer data',
  'offertemplate-section-client-company': 'Company address',
  'offertemplate-section-client-billing': 'Billing address',
  'offerblock-cancellationitems-header-phase': 'Cancellation phase {{rate}} ({{daysToEvent}} days before start)',
  'offerblock-lineitem-priceitem-header': 'Single service composition',
  'offerblock-lineitem-priceitem-net': 'Single service (net)',
  'offerblock-lineitem-priceitem-gross': 'Single service (gross)',
  'offerblock-lineitem-pricenet': 'Total service (net)',
  'offerblock-lineitem-pricegross': 'Total service (gross)',
  'offertemplate-totals-displaynet': 'Display prices in net',
  'offertemplate-totals-displaygross': 'Display prices in gross',
  'offertemplate-section-totals': 'Price overview',
  'offertemplate-totals-net': 'Net',
  'offertemplate-totals-tax': 'VAT %',
  'offertemplate-totals-gross': 'Gross',
  'offertemplate-totals-taxrate': 'Tax',
  'offer-info-seminargo-included':
    'For the first seminar room, 1x projector and screen/flatscreen, 1x flipchart, and 1x pinboard are included as equipment.',
  'offer-info-text-cancellation-rule': 'From {{daysToEvent}} days before the event start, {{rate}} cancellation costs apply.',
  'offer-info-text-cancellation-rule-waive': 'An agreed cancellation waiver of {{toleranceRate}} is considered.',
  'hotel-cancellation-category-overnight': 'Overnight guests',
  'hotel-cancellation-category-seminar': 'Seminar guests',
  'offer-input-header-duration': 'Period',
  'offer-input-header-guests': 'Guests',
  'offer-input-header-overnightguests': 'Rooms',
  'offer-input-header-overnightguests-ez': 'Single room',
  'offer-input-header-overnightguests-dz': 'Double room',
  'offer-input-header-rooms': 'Rooms',
  'offer-occupancy-header-room': 'Room number',
  'offer-occupancy-header-occupancy': 'Duration',
  'offer-occupancy-header-seating': 'Seating',
  'offer-occupancy-header-participants': 'Participants',
  'offer-occupancy-header-requirements': 'Requirements',
  'offer-occupancy-header-technology': 'Technology',
  'offer-occupancy-air': 'Air conditioning',
  'offer-occupancy-daylight': 'Daylight',
  'offer-room-persons': 'persons',
  'offer-room-guaranteed-size': 'Guaranteed room size',
  'offer-room-min-size-hint': '{{size}}m² minimum required',
  'offer-room-acknowledge': 'I confirm that the offered room meets all the requirements listed above.',
  'offer-room-size-required': 'Guaranteed room size is required',
  'offer-room-size-min': 'At least {{size}} m²',
  'offer-room-confirm-required': 'Please confirm',
  'offer-room-acknowledge-required': 'Please confirm the requirements',
  'offer-addons-header-name': 'Extra',
  'offer-addons-header-count': 'Quantity',
  'customer-message-readonly': 'This message was provided by the customer and cannot be edited.',
  'offer-process-download': 'Download offer',
  'offer-confirmation-process-download': 'Download offer',
  'offer-cancellation-confirmation-process-download': 'Download cancellation confirmation',
  'offer-process-approve-offer': 'Confirm offer',
  'offer-process-approve-reservation': 'Confirm reservation',
  'offer-process-approve-as-client': 'Confirm offer (as customer)',
  'offer-process-reject-offer': 'Decline offer',
  'offer-process-reject-reservation': 'Decline reservation',
  'offer-process-reject-cr': 'Reject Offer',
  'offer-process-reject-cr-reservation': 'Reject change request',
  'offer-process-approve-change': 'Confirm offer',
  'offer-process-approve-reservation-change': 'Confirm request',
  'offer-process-approve-cr-no-changes': 'Change request approved',
  'offer-process-approve-approved-change': 'Save and send updated reservation confirmation',
  'offer-process-approve-as-client-change': 'Confirm updated offer (as customer)',
  'offer-confirm-cancellation-button': 'Confirm cancellation',
  'offerconfirm-approve-offer-title': 'Confirm offer',
  'offerconfirm-approve-offer-change-title': 'Confirm request',
  'offerconfirm-approve-reservation-title': 'Confirm reservation',
  'offerconfirm-approve-offer-question': 'Do you really want to confirm this offer?',
  'offerconfirm-approve-reservation-question': 'Do you really want to confirm this reservation?',
  'offerconfirm-approve-question-change': 'Do you really want to confirm the supplemented offer?',
  'offerconfirm-approve-cr-question': "Do you really want to confirm the customer's change request?",
  'offerconfirm-approve-cr-question-change': "Do you really want to confirm the customer's amended change request?",
  'offerconfirm-approve-approved-question-change': 'Do you really want to amend the reservation confirmation?',
  'offerconfirm-approve-as-client-question':
    'You are about to confirm the offer on behalf of the customer. Only click Continue if this has been agreed with the customer.',
  'offerconfirm-approve-as-client-question-change':
    'You are about to confirm the supplemented offer on behalf of the customer. Only click Continue if this has been agreed with the customer.',
  'offerconfirm-approve-approved-title': 'Amend reservation confirmation',
  'offerconfirm-approve-line-items-overview': 'The following services will be sent to the customer',
  'offerconfirm-approve-offer-save-prices': 'Save prices as reference? You can still adjust prices before each booking.',
  'offerconfirm-reject-title': 'Decline offer',
  'offerconfirm-reject-text': 'Do you really want to decline this offer?',
  'offerconfirm-rejectcr-title': 'Reject change request',
  'offerconfirm-rejectcr-text': 'Do you really want to reject this change request?',
  'offerconfirm-rejectcr-reservation-title': 'Reject change request',
  'offerconfirm-rejectcr-reservation-text': 'Do you really want to reject this change request?',
  'rejection-dialog-title': 'Decline offer',
  'rejection-dialog-subtitle': 'Please select the reason for declining the offer:',
  'rejection-dialog-cancel': 'Cancel',
  'rejection-dialog-confirm': 'Decline',
  'rejection-reason-NOT_ENOUGH_TIME': 'Not enough rooms available',
  'rejection-reason-NOT_ENOUGH_SEMINAR_ROOMS': 'Not enough seminar rooms available',
  'rejection-reason-REQUESTED_TIME_PERIOD_NOT_AVAILABLE': 'Request outside the bookable period',
  'rejection-reason-REQUEST_NOT_FITTING': 'Given terms and conditions / cancellation terms do not fit',
  'rejection-reason-OTHER': 'Other',
  'rejection-reason-custom-label': 'Please state the reason',
  'rejection-reason-custom-min-chars': 'characters minimum',
  'rejection-error-no-reason': 'Please select a reason',
  'rejection-error-no-text': 'Please enter a reason',
  'rejection-error-min-length': 'The reason must be at least 5 characters long',
  'offertemplate-totals-incomplete': 'Please fill out the prices completely.',
  'offerblock-lineitem-addsku': 'Add additional services (optional)',
  'pricelist-lookup-ROOM_EZ': 'Overnight stay (single room)',
  'pricelist-lookup-ROOM_DZ': 'Overnight stay (double room)',
  'pricelist-lookup-VOLLPENSIONSPAUSCHALE_EZ': 'Full board (single room)',
  'pricelist-lookup-VOLLPENSIONSPAUSCHALE_DZ': 'Full board (double room)',
  'pricelist-lookup-ADDON_COFFEE': 'Coffee',
  'pricelist-lookup-ADDON_DINNER': 'Dinner',
  'pricelist-lookup-ADDON_LUNCH': 'Lunch',
  'pricelist-lookup-ADDON_MEALDRINK': 'Meal drinks',
  'pricelist-lookup-ADDON_SEMINARDRINK': 'Seminar drinks',
  'pricelist-lookup-ADDON_COFFEEBREAK': 'Coffee break',
  'pricelist-lookup-DEDUCTION_DINNER': 'Deduction (dinner)',
  'pricelist-lookup-DEDUCTION_LUNCH': 'Deduction (lunch)',
  'pricelist-lookup-DEDUCTION_PAUSE': 'Deduction (break)',
  'pricelist-lookup-DEDUCTION_SEMIDAY': 'Deduction (half day)',
  'pricelist-lookup-HALBTAGESPAUSCHALE': 'Half-day flat rate',
  'pricelist-lookup-SEMINARPAUSCHALE_DZ': 'Seminar flat rate (double room)',
  'pricelist-lookup-SEMINARPAUSCHALE_EZ': 'Seminar flat rate (single room)',
  'pricelist-lookup-SEMINARROOM_FULLDAY': 'Meeting room (full day)',
  'pricelist-lookup-SEMINARROOM_SEMIDAY': 'Meeting room (half day)',
  'pricelist-lookup-TAGESPAUSCHALE': 'Full-day flat rate',
  'pricelist-lookup-VISITOR_TAX': "Visitor's tax",
  'pricelist-lookup-VORTAGSANREISE_DZ': 'Previous-day arrival (double room)',
  'pricelist-lookup-VORTAGSANREISE_EZ': 'Previous-day arrival (single room)',
  'pricelist-lookup-ABREISEPAUSCHALE': 'Departure flat rate',
  'pricelist-facility-SEMINARROOM_FULLDAY': 'Meeting room (full day)',
  'pricelist-facility-SEMINARROOM_SEMIDAY': 'Meeting room (half day)',
  'offerblock-lineitem-count': 'Quantity',
  'offerblock-lineitem-details': 'Description',
  'offerblock-show-special-requests': 'Show special requests',
  'offerblock-no-special-requests': 'This request has no special requests',
  'offerblock-inputdays-header-day': 'Service configuration',
  'offer-personal-message': 'Customer message',
  'offer-confirm-agb': 'I confirm seminargo terms and conditions',
  'offer-confirm-cancellation': 'I confirm the applicable cancellation terms',
  'offer-confirm-commission': 'I confirm the commission agreement',
  'offer-confirm-payment-premium': 'The customer payment term is 30 days after invoice approval.',
  'sync-prices-button': 'Apply prices from the current price list',
  'sync-prices-dialog-title': 'Apply prices from the current price list',
  'sync-prices-dialog-description': 'Choose how prices from the currently valid price list should be applied.',
  'sync-prices-dialog-fill-empty': 'Fill only empty prices (existing prices stay unchanged)',
  'sync-prices-dialog-overwrite': 'Overwrite all prices (including already filled prices)',
  'offer-process-withdraw-approval': 'Withdraw offer',
  'offer-info-dialog-downpayment-title': 'Edit down payment terms',
  'offer-info-dialog-guests': 'Number of participants',
  'offer-info-dialog-rooms': 'Number of rooms',
  'offer-info-dialog-total-amount': 'Total amount',
  'offer-info-downpayment-calculation-type': 'Input mode',
  'offer-info-downpayment-fixed-amount': 'Amount (€)',
  'offer-info-downpayment-percentage-amount': 'Percent (%)',
  'offer-info-downpayment-percentage': 'Down payment value',
  'offer-info-downpayment-amount': 'Down payment amount',
  'offer-info-downpayment-due-date': 'Due date',
  'offer-info-downpayment-remark': 'Additional information about down payment',
  'offer-info-downpayment-validation-error': 'Down payment terms are invalid. Due date and amount are required.',
  'offer-info-downpayment-saved': 'Down payment terms saved',
  'offer-status-approved': 'Booking confirmed',
  'offertemplate-offerdate': 'Offer date',
  'offertemplate-startdate': 'Start date event',
  'offertemplate-client-phone': 'Phone',
  'offertemplate-client-company-firstname': 'First name',
  'offertemplate-client-company-lastname': 'Last name',
  'offertemplate-client-company-company': 'Company',
  'offertemplate-client-company-address': 'Address',
  'offertemplate-client-company-zip': 'ZIP',
  'offertemplate-client-company-city': 'City',
  'offertemplate-client-company-country': 'Country',
  'offertemplate-client-billing-company': 'Company',
  'offertemplate-client-billing-firstname': 'First name',
  'offertemplate-client-billing-lastname': 'Last name',
  'offertemplate-client-billing-address': 'Address',
  'offertemplate-client-billing-zip': 'ZIP',
  'offertemplate-client-billing-city': 'City',
  'offertemplate-client-billing-country': 'Country',
  'offertemplate-additionalinfo1': 'Additional info 1',
  'offertemplate-additionalinfo2': 'Additional info 2',
  'offertemplate-additionalinfo3': 'Additional info 3',
  'offerblock-special-requests-info': 'for example for additional special requests of the booker, such as extra finger food, etc.',
  'offerblock-lineitem-other': 'Special request',
  'invoices-erpconfirm-title': 'Forward invoice to ERP',
  'invoices-erpconfirm-question': 'Do you want to approve this invoice for ERP transfer?',
  'invoices-erpconfirm-question-title': 'Approve invoice for ERP transfer',
  'invoice-status-erpconfirmed': 'The invoice has been approved for ERP transfer',
  'cancellation-invoice-status-approved': 'The cancellation was confirmed by the hotel',
  'cancellation-invoice-confirm-title': 'Confirm cancellation',
  'cancellation-invoice-confirm-question': 'Should the cancellation be confirmed? This action cannot be undone.',
  'cancellation-invoice-confirm-question-title': 'Confirm cancellation',
  'invoices-download-proforma-invoice': 'Proforma customer invoice',
  'invoice-info-lineitems-tab': 'Invoice overview',
  'offerconfirm-withdraw-text': 'Do you really want to withdraw your confirmation? The customer will be informed.',
  'offer-save-and-send-success': 'Offer saved and sent',
  'offer-save-changes-epilo': 'Save draft for later',
  'offer-event-mealtimes': 'Meal times',
  'offer-event-mealtimes-day': 'Day',
  'offer-event-mealtimes-ek': 'Breakfast',
  'offer-event-mealtimes-kp1': 'Coffee break 1',
  'offer-event-mealtimes-me': 'Lunch',
  'offer-event-mealtimes-kp2': 'Coffee break 2',
  'offer-event-mealtimes-ae': 'Dinner',
  'offer-event-mealtimes-ez': 'Single room',
  'offer-event-mealtimes-dz': 'Double room',
  'offer-event-rooms': 'Rooms',
  'offer-event-rooms-duration': 'Duration',
  'offer-event-rooms-seating': 'Seating',
  'offer-event-rooms-guests': 'Guests',
  'offer-event-rooms-label': 'Room label',
  'offer-event-rooms-facilities': 'Facilities',
  'offer-event-attendances': 'Attendances',
  'offer-event-comments': 'Comments',
  'offer-event-comments-accomodation': 'Accommodation',
  'offer-event-comments-payment': 'Payment',
  'offer-event-comments-rooms': 'Rooms',
  'offer-event-comments-other': 'Other',
  'enums-EOfferVersionStatus-WAITFORCLIENTAPPROVAL': 'Offer: awaiting customer approval',
  'enums-EOfferVersionStatus-WAITFORHOTELAPPROVAL': 'Offer: awaiting hotel approval',
  'enums-EOfferVersionStatus-WAITFORHOTELRESERVATION': 'Reservation confirmation: awaiting hotel response',
  'enums-EOfferVersionStatus-CR_WAITFORCLIENTAPPROVAL': 'Changed offer: awaiting customer approval',
  'enums-EOfferVersionStatus-CR_WAITFORHOTELAPPROVAL': 'Changed offer: awaiting hotel approval',
  'enums-EOfferVersionStatus-CR_WAITFORCLIENTAPPROVAL-RESERVATION': 'Customer confirmation pending (after reservation change)',
  'offer-cr-waitforhotelapproval-warning':
    "If any additional costs (e.g. cancellation fees) arise as a result of the customer's change to the booking, please add these via 'Add additional service/Other' in the booking. Invoicing is handled separately.",
  'enums-EOfferVersionStatus-CR_WAITFORHOTELAPPROVAL-RESERVATION': 'Hotel confirmation (after reservation change) pending',
  'enums-EOfferVersionStatus-CR_EDITING': 'Changed offer: in processing',
  'enums-EOfferVersionStatus-CIV_WAITFORHOTEL': 'Invoice with cancellation costs: awaiting hotel',
  'enums-EOfferVersionStatus-IV_WAITFORHOTEL': 'Invoice: awaiting hotel',
  'enums-EOfferVersionStatus-WAITFORSEMAPPROVAL': 'Waiting for seminargo approval',
  'enums-EOfferVersionStatus-IV_WAITFORSEMAPPROVAL': 'Invoice: awaiting seminargo approval',
  'enums-EOfferVersionStatus-IV_WAITFORCLIENTAPPROVAL': 'Invoice: awaiting customer approval',
  'enums-EOfferVersionStatus-IV_GENERATED': 'Invoice generated',
  'enums-EOfferVersionStatus-IV_APPROVED': 'Invoice approved',
  'enums-EOfferVersionStatus-APPROVED': 'Approved',
  'enums-EOfferVersionStatus-REJECTED_CLIENT': 'Rejected by customer',
  'enums-EOfferVersionStatus-REJECTED_HOTEL': 'Rejected by hotel',
  'enums-EOfferVersionStatus-REJECTED_HOTEL_WITHDRAWN': 'Rejected by hotel (withdrawn)',
  'enums-EOfferVersionStatus-CANCELLED_CLIENT': 'Cancelled by customer',
  'enums-EOfferVersionStatus-CANCELLED_CLIENT_PENDING_HOTEL': 'Cancelled by customer (pending hotel)',
  'enums-EOfferVersionStatus-CLOSED': 'Closed',
  'enums-EOfferVersionStatus-DELETED': 'Deleted',
  'enums-EHLBookingStatus-PENDING': 'Open',
  'enums-EHLBookingStatus-APPROVED': 'Reserved',
  'enums-EHLBookingStatus-DELETED': 'Rejected',
  'enums-EHLBookingStatus-CANCELLED': 'Cancelled',
  'booking-offers-header-hotel': 'Hotel',
  'booking-offers-header-status': 'Status',
  'booking-info-assign-to-me': 'Assign booking to me',
  'booking-info-assign-employee': 'Assign Employee',
  'booking-info-change-assignee': 'Change assignee',
  'booking-info-select-employee': 'Select employee',
  'booking-info-assignee-unassigned': 'Unassigned',
  'booking-info-assignee': 'Assignee',
  'booking-page-header': 'Booking',
  'booking-info-status': 'Status',
  'booking-info-bid': 'BID',
  'booking-info-bid-copy': 'Copy BID',
  'booking-info-bid-copied': 'BID copied!',
  'booking-info-title': 'Title',
  'booking-info-company': 'Company',
  'booking-info-contact': 'Contact person',
  'booking-info-login-as-client': 'Log in as customer',
  'booking-hotels-section-title': 'Hotel overview',
  'booking-todos-section-title': 'Tasks',
  'booking-comments-section-title': 'Internal notes',
  'booking-comment-add': 'Add comment',
  'booking-comment-edit': 'Edit comment',
  'booking-comment-delete': 'Delete comment',
  'booking-comment-delete-confirm': 'Do you really want to delete this comment?',
  'booking-comment-delete-success': 'Comment deleted successfully',
  'booking-comment-save-success': 'Comment saved successfully',
  'booking-comment-created-at': 'Created at',
  'booking-comment-created-by': 'Created by',
  'booking-comment-description': 'Description',
  'booking-comment-description-helper': 'At least 10 characters',
  'booking-comment-validation-min': 'Description must be at least 10 characters long',
  'booking-comment-no-comments': 'No notes yet',
  'booking-comments-error-loading': 'Error loading notes',
  'booking-not-found': 'Booking not found',
  'tasks-settings-groups': 'Areas',
  'tasks-settings-categories': 'Categories',
  'tasks-settings-subjects': 'Subjects',
  'tasks-settings-groups-title': 'Task settings / areas',
  'tasks-settings-categories-title': 'Task settings / categories',
  'tasks-settings-subjects-title': 'Task settings / subjects',
  'tasks-table-status': 'Status',
  'tasks-status-disabled': 'Disabled',
  'tasks-status-active': 'Active',
  'tasks-settings-add-group': 'Add area',
  'tasks-settings-add-category': 'Add category',
  'tasks-settings-add-subject': 'Add subject',
  'tasks-filter-title': 'Filter',
  'tasks-filter-saved': 'Filter saved successfully',
  'tasks-filter-apply': 'Apply filter',
  'tasks-field-status': 'Status',
  'tasks-status-open': 'Open',
  'tasks-status-done': 'Done',
  'tasks-field-due-from': 'Due from',
  'tasks-field-due-to': 'Due to',
  'tasks-field-assignee': 'Assigned to',
  'tasks-empty-all': '<All>',
  'tasks-edit-title': 'Edit task',
  'tasks-create-title': 'Create task',
  'tasks-saved': 'Task saved successfully',
  'tasks-marked-done': 'Task marked as done',
  'tasks-reopened': 'Task reopened',
  'tasks-mark-undone': 'Mark as not done',
  'tasks-field-assignment': 'Assignment',
  'tasks-empty-none': '<none>',
  'tasks-field-due-date': 'Due date',
  'tasks-field-subject': 'Subject',
  'tasks-field-description': 'Description',
  'tasks-field-booking': 'Booking',
  'tasks-field-booking-id': 'Booking ID',
  'tasks-current-booking': 'Current booking',
  'tasks-comment': 'Comment',
  'tasks-created-at': 'Created at',
  'tasks-created-by': 'Created by',
  'tasks-comment-updated': 'Comment updated successfully',
  'tasks-comment-delete-question': 'Are you sure you want to delete this comment?',
  'tasks-comment-delete-title': 'Delete comment',
  'tasks-comment-deleted': 'Comment deleted successfully',
  'tasks-new-comment': 'New comment',
  'tasks-comment-saved': 'Comment saved successfully',
  'tasks-table-subject': 'Subject',
  'tasks-table-category': 'Category',
  'tasks-table-due-date': 'Due date',
  'tasks-table-assignee': 'Assigned to',
  'tasks-table-created-by': 'Created by',
  'tasks-table-hotel': 'Hotel',
  'tasks-table-company': 'Company',
  'tasks-table-contact': 'Contact person',
  'tasks-table-booking': 'Booking',
  'tasks-system': 'System',
  'tasks-for-booking': 'Tasks for booking',
  'tasks-create-button': 'Create new task',
  'tasks-quickfilter-mine': 'Only mine',
  'tasks-quickfilter-all': 'All',
  'tasks-quickfilter-unassigned': 'Unassigned',
  'tasks-filter-button': 'Filter',
  'tasks-filter-button-with-count': 'Filter ({{count}})',
  'bericht-tab-earlybird': 'Early bird',
  'bericht-tab-statistics': 'Booking statistics',
  'bericht-tab-requests': 'Requests/bookings',
  'bericht-tab-hotel-contracts': 'Hotel contracts',
  'bericht-tab-contact-overview': 'Contact overview',
  'bericht-tab-contact-persons': 'Contact persons',
  'modal-offers': 'Offers',
  'modal-bookings': 'Bookings',
  'modal-load-error': 'Error loading',
  'modal-offers-found': 'offer(s) found',
  'modal-bookings-found': 'booking(s) found',
  'modal-offer-id': 'Offer ID',
  'modal-booking-id': 'Booking ID',
  'modal-ref-code': 'Ref / Code',
  'modal-company': 'Company',
  'modal-request-date': 'Request date',
  'modal-start-date': 'Start date',
  'modal-end-date': 'End date',
  'modal-backoffice-employee': 'Back-office employee',
  'modal-cancellation': 'Cancellation',
  'modal-canceled-by': 'Canceled by',
  'modal-type': 'Type',
  'modal-type-seminar-overnight': 'Seminar with overnight stay',
  'modal-type-day-seminar': 'Day seminar (without overnight stays)',
  close: 'Close',
  'availability-hotels-list-page-title': 'Availability',
  'availability-header-hotel': 'Hotel',
  'availability-header-product': 'Service',
  'products-list-add': 'Add service',
  'products-list-import': 'Import services list',
  'product-name': 'Name',
  'product-lookupcode': 'SEM reference code',
  'product-extrefcode': 'External reference code',
  'product-recurring': 'Billing',
  'product-issystem': 'seminargo base service',
  'product-isdeduction': 'Deduction billing',
  'product-isaddon': 'Optional add-on service',
  'product-components': 'Tax class(es)',
  'product-save': 'Save service',
  'product-save-text': 'Save text modules',
  'product-delete': 'Delete service',
  'product-copy': 'Duplicate service',
  'product-copy-space': 'Administrative area',
  'productbundles-list-add': 'Add package',
  'productbundle-name': 'Name',
  'productbundle-recurring': 'Billing',
  'productbundle-lookupcode': 'SEM reference code',
  'productbundle-extrefcode': 'External reference code',
  'productbundle-issystem': 'seminargo base service',
  'productbundle-components': 'Tax class(es)',
  'productbundle-item-includedcount': 'Included units',
  'productbundle-item-isoptional': 'Optional',
  'productbundle-item-add': 'Add service item',
  'productbundle-save': 'Save package',
  'productbundle-save-text': 'Save text modules',
  'productbundle-delete': 'Delete package',
  'productbundle-copy': 'Duplicate package',
  'productbundle-copy-space': 'Administrative area',
  'facilities-list-add': 'Add facility',
  'facility-name': 'Name',
  'facility-lookupcode': 'SEM reference code',
  'facility-extrefcode': 'External reference code',
  'facility-recurring': 'Billing',
  'facility-isaddon': 'Optional additional facility',
  'facility-issystem': 'seminargo base facility',
  'facility-components': 'Tax class(es)',
  'facility-save': 'Save facility',
  'facility-save-text': 'Save text modules',
  'facility-delete': 'Delete facility',
  'facility-copy': 'Duplicate facility',
  'facility-copy-space': 'Administrative area',
  'servicetypes-list-add': 'Add service package',
  'servicetype-name': 'Name',
  'servicetype-ispublished': 'Published',
  'servicetype-issystem': 'seminargo base package',
  'servicetype-sequence': 'Sort order',
  'servicetype-save': 'Save service package',
  'servicetype-save-text': 'Save text modules',
  'servicetype-contact-office': 'For adjustments, please contact office@seminargo.com',
  'servicetype-delete': 'Delete service package',
  'servicetype-copy': 'Duplicate service package',
  'servicetype-confirm-copy-title': 'To which administrative area should the service package be duplicated?',
  'servicetype-copy-space': 'Administrative area',
  'servicetype-confirm-delete-title': 'Delete service package',
  'servicetype-confirm-delete': 'Do you really want to delete this service package?',
  'servicetype-deleted': 'Service package deleted',
  'servicetype-saved': 'Service package saved',
  'servicetype-sku': 'SKU',
  'pricelists-list-published': 'Published price lists',
  'pricelists-list-notpublished': 'Unpublished price lists',
  'pricelists-list-add': 'Create price list',
  'pricelists-list-import': 'Import price sheet',
  'pricelist-name': 'Name',
  'pricelist-refcode': 'Reference code',
  'pricelist-version': 'Version',
  'pricelist-selector-name': 'Name',
  'pricelist-selector-baseprice': 'Base price',
  'pricelist-selector-minguestcount': 'Min. guests',
  'pricelist-selector-maxguestcount': 'Max. guests',
  'pricelist-selector-minitemcount': 'Min. items',
  'pricelist-selector-maxitemcount': 'Max. items',
  'pricelist-selector-validity': 'Validity period',
  'pricelist-selector-weekdays': 'Weekdays',
  'pricelist-selector-add': 'Add pricing rule',
  'pricelist-ispublished': 'Published',
  'pricelist-ispricesbrut': 'Prices are gross prices',
  'pricelist-currency': 'Currency',
  'pricelist-hotel': 'Hotel',
  'pricelist-hotel-none': 'No hotel',
  'pricelist-title-for-hotel': 'Price list for hotel {{hotel}}',
  'pricelist-title-for-hotel-with-type': '{{type}} price list for hotel {{hotel}}',
  'pricelist-validity': 'Validity periods',
  'pricelist-validfrom': 'Valid from',
  'pricelist-validto': 'Valid to',
  'pricelist-validity-add': 'Add validity period',
  'pricelist-rules-rule': 'Pricing rule',
  'pricelist-rules-rate': 'Surcharge / percentage',
  'pricelist-rules-rounding': 'Rounding',
  'pricelist-rules-item': 'Valid for product / facility',
  'pricelist-rules-all': 'All prices',
  'enums-EPricingRuleType-FIXED': 'Add fixed amount',
  'enums-EPricingRuleType-OVERWRITE': 'Overwrite price',
  'enums-EPricingRuleType-PERCENTAGE': 'Add percentage',
  'enums-EPricingRoundingType-NONE': 'No rounding',
  'enums-EPricingRoundingType-CEIL': 'Round up',
  'enums-EPricingRoundingType-FLOOR': 'Round down',
  'enums-EPricingWeekDays-MON': 'Monday',
  'enums-EPricingWeekDays-TUE': 'Tuesday',
  'enums-EPricingWeekDays-WED': 'Wednesday',
  'enums-EPricingWeekDays-THU': 'Thursday',
  'enums-EPricingWeekDays-FRI': 'Friday',
  'enums-EPricingWeekDays-SAT': 'Saturday',
  'enums-EPricingWeekDays-SUN': 'Sunday',
  'pricelist-save': 'Save price list',
  'pricelist-delete': 'Delete price list',
  'pricelist-copy': 'Duplicate price list',
  'pricelist-space': 'Administrative area',
  'selector-backlink': 'Back to price list',
  'selector-name': 'Name',
  'selector-name-required': 'Name is required',
  'selector-isbaseprice': 'Base price',
  'selector-minguestcount': 'Min guests',
  'selector-minguestcount-helpertext': 'Refers to the number of booked packages',
  'selector-maxguestcount': 'Max guests',
  'selector-maxguestcount-helpertext': 'Refers to the number of booked packages',
  'selector-minitemcount': 'Min items',
  'selector-minitemcount-helpertext': 'Refers to the number of booked services',
  'selector-maxitemcount': 'Max items',
  'selector-maxitemcount-helpertext': 'Refers to the number of booked services',
  'selector-validfrom': 'Valid from',
  'selector-validfrom-before-validto': 'Must be before or equal to valid to',
  'selector-validto': 'Valid to',
  'selector-validto-after-validfrom': 'Must be after or equal to valid from',
  'selector-customer': 'Customer',
  'selector-customer-all': 'All customers',
  'selector-weekdays': 'Weekdays',
  'selector-multitax-price': 'Tax component',
  'selector-product-price': 'Price',
  'selector-bundle-price': 'Price',
  'selector-facility-price': 'Price',
  'selector-lister-rooms': 'Rooms',
  'selector-lister-board': 'Catering',
  'selector-lister-amenities': 'Amenities',
  'selector-lister-combinations': 'Combinations',
  'selector-lister-beamer': 'Projector & screen / flatscreen',
  'selector-lister-flipchart': 'Flipchart',
  'selector-lister-pinnwand': 'Pin board',
  'selector-lister-moderation': 'Moderation kit',
  'selector-lister-board-small': 'Small package',
  'selector-lister-board-small-subtitle': '1x coffee break',
  'selector-lister-board-medium': 'Half-day package',
  'selector-lister-board-medium-subtitle': '1x coffee break, 1x lunch',
  'selector-lister-board-regular': 'Full-day package',
  'selector-lister-board-regular-subtitle': '2x coffee breaks, 1x lunch',
  'selector-lister-board-welcome-coffee': 'Welcome coffee',
  'selector-lister-board-coffee-break': 'Coffee break',
  'selector-lister-board-lunch': 'Lunch',
  'selector-lister-board-dinner': 'Dinner',
  'selector-lister-combinations-vollpension-ez': 'Single room combination',
  'selector-lister-combinations-vollpension-ez-subtitle': 'Single room + full-day package + dinner',
  'selector-lister-combinations-vollpension-dz': 'Double room combination (per person)',
  'selector-lister-combinations-vollpension-dz-subtitle': 'Double room + full-day package + dinner',
  'selector-lister-combinations-description':
    'These prices are used when single or double rooms are booked together with full-day package and dinner. In this case they replace separate room, package and dinner positions.',
  'selector-lister-combinations-no-products':
    'No full-board products found for this price list. Create products with lookup codes VOLLPENSIONSPAUSCHALE_EZ and VOLLPENSIONSPAUSCHALE_DZ.',
  'selector-lister-combinations-optional': 'Optional',
  'selector-lister-open-product': 'Open product',
  'selector-lister-open-bundle': 'Open package',
  'selector-lister-open-facility': 'Open facility',
  'selector-lister-premium-label-short': 'Premium',
  'pricelist-includemainroominpackage': '(Main) room rent included in packages',
  'pricelist-includemainroominpackage-description': 'The first automatically selected meeting room is priced at €0',
  'selector-lister-room-single': 'Single room',
  'selector-lister-room-double': 'Double room (per person)',
  'selector-lister-bundle-price-from-products': 'Price is calculated from individual components.',
  'selector-lister-single-services': 'Single services',
  'selector-lister-edit-meetingrooms': 'Edit meeting rooms',
  'selector-lister-meetingrooms-included': 'included:',
  'selector-lister-combinations-empty': 'No content available yet.',
  'selector-save': 'Save prices',
  'selector-copy': 'Duplicate pricing rule',
  'selector-delete': 'Delete pricing rule',
  'selector-price-label-premium': 'Premium price',
  'selector-price-label-classic-bar': 'BAR price (classic)',
  'selector-price-total': 'Total',
  'selector-price-net': 'Net',
  'selector-price-gross': 'Gross',
  'selector-price-on-request': 'On request',
  'selector-lister-empty-default': 'No content available yet.',
  'selector-lister-price-not-configured': 'Not configured',
  'selector-lister-room-missing': 'No room price configured.',
  'selector-lister-bundle-missing': 'No package configured.',
  'selector-lister-facility-missing': 'No equipment configured.',
  'selector-baseprice-info':
    'The base price list is always valid when no other price list is defined. It has no restrictions regarding validity period, weekdays, participant count, or customer.',
  'prices-import-page-title': 'File import',
  'prices-import-name': 'Name',
  'prices-import-version': 'Version',
  'prices-import-file': 'Price list file (*.json, *.xlsx)',
  'prices-import-default-name': 'Price list',
  'prices-import-run': 'Start import',
  'prices-import-start': 'Back',
  'prices-export-run': 'Start export',
  'products-import-page-title': 'File import',
  'products-import-mode': 'Import mode',
  'products-import-file': 'Services catalog file (*.json)',
  'products-import-run': 'Start import',
  'products-import-start': 'Back',
  'products-export-run': 'Start export',
  'contracts-title': 'Contracts',
  'contracts-title-single': 'Contract',
  'contracts-create-new': 'Create new contract',
  'contracts-header-type': 'Type',
  'contracts-header-validity': 'Validity',
  'contracts-header-interval': 'Interval',
  'contracts-header-amount': 'Amount',
  'contracts-header-status': 'Status',
  'contracts-header-next-invoice': 'Next invoice',
  'contracts-header-created-at': 'Created at',
  'contracts-header-actions': 'Actions',
  'contracts-empty': 'No contracts available',
  'contracts-delete-title': 'Delete contract',
  'contracts-delete-confirm': 'Do you really want to delete this contract?',
  'contracts-delete-success': 'Contract deleted successfully',
  'contracts-update-success': 'Contract updated successfully',
  'contracts-create-success': 'Contract created successfully',
  'contracts-invoice-create-success': 'Invoice created successfully',
  'contracts-document-upload-success': 'Document uploaded successfully',
  'contracts-document-delete-confirm': 'Do you really want to delete this document?',
  'contracts-document-delete-success': 'Document deleted successfully',
  'contracts-not-found': 'Contract not found',
  'contracts-details': 'Contract details',
  'contracts-field-type': 'Contract type',
  'contracts-field-valid-from': 'Valid from',
  'contracts-field-valid-to': 'Valid to',
  'contracts-field-interval': 'Invoice interval',
  'contracts-field-amount': 'Amount (EUR)',
  'contracts-field-description': 'Description',
  'contracts-status-sap': 'Status & SAP integration',
  'contracts-no-invoice-planned': 'None planned',
  'contracts-sap-status': 'SAP status',
  'contracts-sap-last-sync': 'Last SAP sync',
  'contracts-sap-error': 'SAP error',
  'contracts-upload-pdf': 'Upload PDF',
  'contracts-no-documents': 'No documents available',
  'contracts-invoice-history': 'Invoice history',
  'contracts-create-manual-invoice': 'Create manual invoice',
  'contracts-invoice-date': 'Invoice date',
  'contracts-triggered-at': 'Triggered at',
  'contracts-trigger-auto': 'Automatic',
  'contracts-trigger-manual': 'Manual',
  'contracts-trigger-on-create': 'On creation',
  'contracts-no-invoices': 'No invoices available',
  'tasks-group-updated': 'Area updated',
  'tasks-group-created': 'Area created',
  'tasks-category-updated': 'Category updated',
  'tasks-category-created': 'Category created',
  'tasks-subject-updated': 'Subject updated',
  'tasks-subject-created': 'Subject created',
  'tasks-field-name': 'Name',
  'tasks-field-area': 'Area',
  'tasks-field-category': 'Category',
  comments: 'Comments',
  'tasks-field-description-template': 'Description template',
  'tasks-field-disabled': 'Disabled',
  'tasks-field-system-relevant': 'System relevant',
  'clients-search-placeholder': 'Company name, email, city...',
  'spaces-search-placeholder': 'Name, country, users, license...',
  'users-search-placeholder': 'Name, username, email, area...',
  'search-min-length-error': 'Search term must be at least {{count}} characters',
  'search-companies-contact-person-singular': 'contact person',
  'search-companies-contact-person-plural': 'contact persons',
  'publicclient-status-master': 'Master',
  'publicclient-status-invitation-pending': 'Confirmation pending',
  'publicclient-status-inactive': 'Inactive',
  'space-has-epilo': 'seminargo enabled',
  'spaces-list-page-title': 'Administrative areas',
  'spaces-list-header-name': 'Name',
  'spaces-list-header-country': 'Country',
  'spaces-list-header-users': 'Users',
  'spaces-list-header-license': 'SEM license',
  'spaces-list-add': 'Provision administrative area',
  'space-name': 'Name',
  'space-country': 'Country',
  'space-parentspace': 'Parent administrative area',
  'space-license': 'SEM license',
  'space-update': 'Save administrative area',
  'space-updated': 'Administrative area saved',
  'space-confirm-update': 'Changing the administrative area can affect permission structures.',
  'space-confirm-update-title': 'Warning - change administrative area',
  'space-delete': 'Delete administrative area',
  'space-deleted': 'Administrative area deleted',
  'space-confirm-delete-title': 'Delete administrative area',
  'space-confirm-delete': 'Do you really want to delete the administrative area “{{name}}”? It can only be deleted when it is no longer in use.',
  'space-confirm-delete-double': 'I confirm that the administrative area should be deleted permanently.',
  'graphqlerror-SPACE_DELETE_NOT_POSSIBLE_STILL_ASSIGNED':
    'The administrative area “{{name}}” cannot be deleted while data, users, or child areas are still assigned to it.',
  'graphqlerror-SPACE_DELETE_PUBLIC_NOT_ALLOWED': 'The public administrative area cannot be deleted.',
  'space-importoffertemplates': 'Import default offer templates',
  'space-offertemplatesimported': 'Default offer templates were imported',
  'space-confirm-importoffertemplates': 'Import default offer templates (offer, reservation confirmation, cancellation)?',
  'space-confirm-importoffertemplates-title': 'Import offer templates',
  'space-importemailtemplates': 'Import default email templates',
  'space-emailtemplatesimported': 'Default email templates were imported',
  'space-confirm-importemailtemplates': 'Existing email templates will be overwritten and changes will be lost.',
  'space-confirm-importemailtemplates-title': 'Warning - import default email templates',
  'space-importproducts': 'Import service portfolio',
  'space-productsimported': 'Service portfolio imported',
  'space-confirm-importproducts': 'Import SEM products for this area?',
  'space-confirm-importproducts-title': 'Import service portfolio',
  'space-importpricelists': 'Create price lists',
  'space-pricelistscreated': 'Price lists created',
  'space-confirm-importpricelists': 'Create SEM price lists for all hotels?',
  'space-confirm-importpricelists-title': 'Create price lists',
  'space-users': 'Users',
  'newspace-page-title': 'Provision administrative area',
  'newspace-spaceName': 'Administrative area',
  'newspace-spaceCountry': 'Country',
  'newspace-parentspace': 'Parent administrative area',
  'newspace-hotelName': 'Hotel name',
  'newspace-hotelRefCode': 'Hotel reference code',
  'newspace-import-file': 'Hotel logo (*.png)',
  'newspace-userid': 'Grant admin rights to user',
  'newspace-newuser': 'Create new user',
  'newspace-userName': 'User name',
  'newspace-userUsername': 'Username',
  'newspace-userEmail': 'Email address',
  'newspace-userPassword': 'Set password',
  'newspace-license': 'SEM license',
  'newspace-importDefaultEmailTemplates': 'Set up default email templates',
  'newspace-importDefaultOfferTemplates': 'Set up default offer templates',
  'newspace-importDefaultProducts': 'Set up default service portfolio',
  'newspace-importDefaultPriceslist': 'Set up default price lists',
  'newspace-save': 'Provision administrative area',
  'newspace-ready': 'Administrative area was provisioned and is ready to use',
  'newspace-result-email': 'System login with email address (if used once)',
  'newspace-result-username': 'System login with username',
  'newspace-result-password': 'Login with initial password',
  'newspace-section-management': 'Administrative area',
  'newspace-section-hotel': 'Hotel',
  'newspace-section-user': 'User',
  'content-tabs-header-offertemplates': 'PDF/HTML templates',
  'content-tabs-header-emailtemplates': 'Email templates',
  'contentmedias-list-page-title': 'Media files',
  'contentmedias-list-header-name': 'File name',
  'contentmedias-list-header-mimetype': 'MIME type',
  'contentmedias-list-header-updatedat': 'Last updated',
  'contentmedias-export-run': 'Export',
  'contentmedias-import-run': 'Import',
  'contentmedias-import-ready': 'Media imported successfully',
  'contentmedias-select-area-first': 'Please select a specific administrative area first to view or upload media files.',
  'contentmedia-confirm-delete': 'This media file will be deleted and cannot be used anymore.',
  'contentmedia-confirm-delete-title': 'Delete media file',
  'contentmedia-deleted': 'Media file deleted',
  'emailtemplates-list-page-title': 'Email templates',
  'emailtemplates-list-header-usage': 'Usage',
  'emailtemplates-list-header-subject': 'Subject',
  'emailtemplates-list-header-language': 'Language',
  'emailtemplates-list-header-receiver': 'Recipient',
  'emailtemplates-list-header-updatedat': 'Last updated',
  'emailtemplates-list-add': 'Create email template',
  'emailtemplates-list-warning-new-templates': 'New email templates are available.',
  'emailtemplates-list-warning-missing-templates': 'Missing templates',
  'emailtemplates-list-receiver-hotel': 'Hotel',
  'emailtemplates-list-receiver-client': 'Client',
  'emailtemplates-list-receiver-seminargo': 'seminargo',
  'emailtemplates-list-receiver-user': 'User',
  'emailtemplates-export-run': 'Export',
  'emailtemplates-import-run': 'Import',
  'emailtemplates-import-ready': 'Email templates imported successfully',
  'emailtemplate-usage': 'Usage',
  'emailtemplate-subject': 'Subject',
  'emailtemplate-language': 'Language',
  'emailtemplate-body': 'Content',
  'emailtemplate-save': 'Save email template',
  'emailtemplate-updated': 'Email template saved',
  'emailtemplate-created': 'Email template created',
  'emailtemplate-preview': 'Show preview',
  'emailtemplate-preview-created': 'Preview created',
  'emailtemplate-confirm-delete': 'The email template will be deleted and can no longer be used.',
  'emailtemplate-confirm-delete-title': 'Delete email template',
  'emailtemplate-delete': 'Delete email template',
  'emailtemplate-deleted': 'Email template deleted',
  'emailtemplate-copy': 'Duplicate email template',
  'emailtemplate-copied': 'Email template duplicated',
  'offertemplates-list-page-title': 'PDF/HTML templates',
  'offertemplates-list-header-name': 'Name',
  'offertemplates-list-header-title': 'Document title',
  'offertemplates-list-header-type': 'Template type',
  'offertemplates-list-header-updatedat': 'Last updated',
  'offertemplates-list-header-ispublished': 'Published',
  'offertemplates-list-add': 'Create template',
  'offertemplates-export-run': 'Export',
  'offertemplates-import-run': 'Import',
  'offertemplates-import-ready': 'Templates imported successfully',
  'offertemplate-data-tab': 'Basic settings',
  'offertemplate-template-tab': 'HTML layout',
  'offertemplate-name': 'Name',
  'offertemplate-ispublished': 'Published',
  'offertemplate-includeemptylineitems': 'Include empty offer lines',
  'offertemplate-type': 'Template type',
  'offertemplate-save': 'Save template',
  'offertemplate-updated': 'Template saved',
  'offertemplate-created': 'Template created',
  'offertemplate-delete': 'Delete template',
  'offertemplate-confirm-delete': 'The template will be deleted and can no longer be used.',
  'offertemplate-confirm-delete-title': 'Delete template',
  'offertemplate-deleted': 'Template deleted',
  'offertemplate-copy': 'Duplicate template',
  'offertemplate-copied': 'Template duplicated',
  'contentproducts-list-page-title': 'Text blocks',
  'contentproducts-list-header-sku': 'SKU',
  'contentproducts-list-header-product': 'Service',
  'contentproducts-list-header-position': 'Text position',
  'contentproducts-list-header-hotel': 'Hotel',
  'contentproducts-list-add': 'Add text block',
  'contentproduct-sku': 'SKU',
  'contentproduct-position': 'Text position',
  'contentproduct-hotel': 'Hotel',
  'contentproduct-texts-tab': 'Texts',
  'contentproduct-text-language': 'Language',
  'contentproduct-text-text': 'Text',
  'contentproduct-text-add': 'Add text',
  'contentproduct-save': 'Save text block',
  'contentproduct-saved': 'Text block saved',
  'enums-EContentOfferTemplateType-OFFER': 'Offer template',
  'enums-EContentOfferTemplateType-RESERVATION_CONFIRMATION': 'Reservation confirmation',
  'enums-EContentOfferTemplateType-CANCELLATION_CONFIRMATION': 'Cancellation confirmation',
  'enums-EContentOfferTemplateType-PROFORMAINVOICE': 'Proforma invoice',
  'enums-EContentOfferTemplateType-CANCELLATIONINVOICE': 'Cancellation invoice',
  'enums-EContentOfferTemplateType-COMMISSIONINVOICE': 'Commission invoice',
  'enums-EContentOfferTemplateType-CLIENTINVOICE': 'Client invoice',
  'enums-EContentOfferTemplateType-COSTCENTRE': 'Cost center receipt',
  'enums-EContentProductDetailPosition-HEADER': 'Title',
  'enums-EContentProductDetailPosition-HEADER_SHORT': 'Title (short)',
  'enums-EContentProductDetailPosition-DETAILS': 'Description',
  'enums-EContentProductDetailPosition-GROUP': 'Grouping (white label only)',
  'enums-EContentEmailTemplate-CLIENT_VERIFICATION': 'Customer email verification (to customer)',
  'enums-EContentEmailTemplate-USER_WELCOME': 'Welcome email for new users',
  'enums-EContentEmailTemplate-RESET_PASSWORD': 'Forgot password',
  'enums-EContentEmailTemplate-USER_PASSWORD': 'Password change',
  'enums-EContentEmailTemplate-OFFER_REMINDER_HOTEL': 'Reservation reminder (to hotel)',
  'enums-EContentEmailTemplate-OFFER_CANCELED_CLIENT': 'Booking cancellation (to customer)',
  'enums-EContentEmailTemplate-RESERVATION_REMINDER_HOTEL': 'Reservation confirmation reminder (to hotel)',
  'enums-EContentEmailTemplate-OFFER_REMINDER_CLIENT': 'Reservation reminder (to customer)',
  'enums-EContentEmailTemplate-OFFER_RECEIVED_CLIENT': 'Reservation confirmation (to customer)',
  'enums-EContentEmailTemplate-OFFER_CHANGED_RECEIVED_CLIENT': 'Reservation confirmation for updated offer (to customer)',
  'enums-EContentEmailTemplate-BOOKING_RECEIVED_HOTEL': 'New seminar request (to hotel)',
  'enums-EContentEmailTemplate-BOOKING_RECEIVED_CLIENT': 'Request is being processed',
  'enums-EContentEmailTemplate-BOOKING_REJECTED_OVERFLOW_CLIENT': 'Availability rejected',
  'enums-EContentEmailTemplate-BOOKING_APPROVED_CLIENT': 'Booking approved by customer',
  'enums-EContentEmailTemplate-BOOKING_REJECTED_CLIENT': 'Booking rejected by customer',
  'enums-EContentEmailTemplate-BOOKING_APPROVED_HOTEL': 'Booking approved by customer',
  'enums-EContentEmailTemplate-BOOKING_REJECTED_HOTEL': 'Booking rejected by customer',
  'enums-EContentEmailTemplate-BOOKING_OVERFLOW_APPROVED_CLIENT': 'Handover to seminargo approved',
  'enums-EContentEmailTemplate-BOOKING_OVERFLOW_APPROVED_HOTEL': 'Handover to seminargo approved',
  'enums-EContentEmailTemplate-BOOKING_OVERFLOW_REJECTED_CLIENT': 'Handover to seminargo rejected',
  'enums-EContentEmailTemplate-BOOKING_OVERFLOW_APPROVED_SEMINARGO': 'Handover to seminargo',
  'enums-EContentEmailTemplate-DEPOSIT_REMINDER_HOTEL': 'Deposit invoice reminder',
  'enums-EContentEmailTemplate-DEPOSIT_REMINDER_CHECK_HOTEL': 'Deposit receipt reminder',
  'enums-EContentEmailTemplate-BOOKING_DIRECT_CLIENT': 'Direct booking confirmation via white label (to customer)',
  'enums-EContentEmailTemplate-BOOKING_DIRECT_HOTEL': 'Direct booking confirmation via white label (to hotel)',
  'enums-EContentEmailTemplate-BOOKING_CHANGED_CLIENT': 'Confirmation of changed reservation (to customer)',
  'enums-EContentEmailTemplate-COMPANY_INVITATION': 'Company invitation',
  'enums-EContentEmailTemplate-HOTEL_REGISTRATION_NOTIFICATION': 'Hotel registration notification',
  'enums-EContentEmailTemplate-LISTER_INVOICE_HOTEL': 'Outgoing invoices',
  'enums-EContentEmailTemplate-LISTER_CREDIT_NOTE_HOTEL': 'Credit notes',
  'pricelists-tabs-header-taxtypes': 'Tax classes',
  'taxtypes-list-page-title': 'Tax classes',
  'taxtypes-list-header-name': 'Tax class',
  'taxtypes-list-header-sequence': 'Sort order',
  'taxtypes-list-add': 'Create new tax class',
  'taxtype-name': 'Label',
  'taxtype-sequence': 'Sort order',
  'taxtype-rates-tab': 'Tax rates',
  'taxtype-rates': 'No tax rates configured',
  'taxtype-rate-name': 'Label',
  'taxtype-rate-rate': 'Tax rate',
  'taxtype-rate-country': 'Country',
  'taxtype-rate-isreversecharge': 'Reverse charge',
  'taxtype-rate-validfrom': 'Valid from',
  'taxtype-rate-validto': 'Valid to',
  'taxtype-rate-add': 'Add new tax rate',
  'taxtype-save': 'Save tax class',
  'taxtype-saved': 'Tax class saved',
  'taxtype-saved-bg-refresh': 'Tax class saved. Prices are updated in the background.',
  'taxtype-refresh-progress': 'Updating prices and texts...',
  'taxtype-refresh-cancel': 'Cancel',
  'taxtype-refresh-cancelled': 'Refresh canceled',
  'taxtype-refresh-hotels': 'Hotels',
  'taxtype-refresh-spaces': 'Administrative areas',
  'taxtype-refresh-current-space': 'Current administrative area',
  'taxtype-refresh-starting': 'Refresh is starting...',
  'taxtype-refresh-idle': 'No refresh active.',
  'taxtype-refresh-confirm-title': 'Update prices and texts',
  'taxtype-refresh-confirm-message-global': 'Prices and texts will be recalculated for all administrative areas and hotels. Continue?',
  'taxtype-refresh-confirm-message-space': 'Prices and texts will be recalculated for hotels in this administrative area. Continue?',
  'taxtype-delete': 'Delete tax class',
  'taxtype-deleted': 'Tax class deleted',
  'taxtype-confirm-delete-title': 'Delete tax class',
  'taxtype-confirm-delete': 'Do you really want to delete this tax class including all tax rates?',
  'hotel-agb-title': 'Hotel terms and conditions',
  'hotel-agb-filename': 'Filename',
  'hotel-agb-filesize': 'File size',
  'hotel-agb-uploaded-at': 'Uploaded',
  'hotel-agb-uploaded-by': 'Uploaded by',
  'hotel-agb-download': 'Download terms and conditions',
  'hotel-agb-empty-info': 'No terms file is available yet. Please upload a PDF file.',
  'hotel-document-title': 'Standard offer attachments',
  'hotel-document-subtitle': 'Upload documents here that can be attached to any offer. You can adjust your selection per request.',
  'hotel-document-displayname-placeholder': 'e.g. Additional options',
  'hotel-document-upload-hint': 'PDF files only • Max. 10 MB • Max. 10 documents',
  'hotel-document-pdf-required': 'Please select a PDF file',
  'hotel-document-max-count': 'Maximum number of documents (10) reached',
  'hotel-document-max-size': 'The file exceeds the maximum size of 10 MB',
  'hotel-document-upload-success': 'Document uploaded successfully',
  'hotel-document-upload-failed': 'Failed to upload document',
  'hotel-document-delete-success': 'Document deleted successfully',
  'hotel-document-approved-tooltip': 'Approved by seminargo backoffice',
  'hotel-document-pending-tooltip': 'Waiting for approval by seminargo backoffice',
  'hotel-document-approve-title': 'Approve document?',
  'hotel-document-approve-question': 'Do you really want to approve "{{title}}"?',
  'hotel-document-approve': 'Approve',
  'hotel-document-delete': 'Delete',
  'hotel-document-cancel': 'Cancel',
  'hotel-document-approved-success': 'Document approved',
  'hotel-document-empty-info': 'No standard offer attachments yet.',
  'offer-standard-documents-title': 'Standard offer attachments',
  'offer-standard-documents-empty': 'No approved standard documents for this hotel.',
  'offer-standard-document-size-exceeded':
    'The total attachment size cannot exceed 10 MB. Please deselect a standard attachment or delete an additional document.',
  'offer-document-upload-hint': 'Max. 10 MB total attachment size',
  'validation-facilities-without-tax-rates': 'Individual services without tax classes',
  'validation-products-without-tax-rates': 'Products without tax classes',
  'validation-bundles-without-tax-rates': 'Bundles without tax classes',
  'validation-no-data': 'No validation data',
  filename: 'File name',
  size: 'Size',
  'uploaded-at': 'Uploaded at',
  'updated-at': 'Updated at',
  trigger: 'Trigger',
  error: 'Error',
  documents: 'Documents',
  uploading: 'Uploading...',
  download: 'Download',
  create: 'Create',
  save: 'Save',
  cancel: 'Cancel',
  new: 'New',
  delete: 'Delete',
  active: 'Active',
  inactive: 'Inactive',
  status: 'Status',
  deactivate: 'Deactivate',
  restore: 'Restore',
  loading: 'Loading...',
  'common-search-placeholder': 'Search...',
  'error-boundary-title': 'Something went wrong',
  'error-boundary-description': 'Please reload the page or contact support.',
  'error-boundary-reload': 'Reload page',
  'reports-statistics-section-hotel': 'Hotel',
  'reports-statistics-section-customer': 'Customer',
  'contracts-agreement-type-symposion-hotel': 'Symposion hotel agreement',
  'contracts-agreement-type-partner': 'Partner agreement',
  'contracts-agreement-type-none': 'No agreement',
  'contact-subjects-title': 'Contact subjects',
  'contact-subjects-manage-title': 'Manage contact subjects',
  'contact-subjects-add': 'New subject',
  'contact-subjects-info':
    'Manage available contact subjects for comments. Deactivated subjects are hidden for new comments, but remain visible in existing comments.',
  'contact-subjects-empty': 'No subjects found',
  'contact-subjects-edit': 'Edit subject',
  'contact-subjects-create': 'Create new subject',
  'contact-subjects-name-required': 'Name *',
  'contact-subjects-name-placeholder': 'e.g. Premium contract',
  'contact-subjects-validation-name-required': 'Name is required',
  'contact-subjects-validation-name-min': 'Name must be at least 3 characters long',
  'contact-subjects-validation-name-duplicate': 'A subject with this name already exists',
  'contact-subjects-updated': 'Subject updated successfully',
  'contact-subjects-created': 'Subject created successfully',
  'contact-subjects-deactivate-confirm': 'Do you really want to deactivate subject "{{name}}"?',
  'contact-subjects-deactivated': 'Subject deactivated successfully',
  'comment-dialog-title-edit': 'Edit comment',
  'comment-dialog-created-at': 'Created at',
  'comment-dialog-created-by': 'Created by',
  'comment-dialog-current-user': 'Current user',
  'comment-dialog-unknown-user': 'Unknown',
  'comment-dialog-booking-relevant': 'Comment is booking-relevant',
  'comment-dialog-contact-relevant': 'Comment is contact-relevant',
  'comment-dialog-contact-type': 'Contact type',
  'comment-dialog-subject': 'Subject',
  'comment-dialog-subject-required': 'Subject *',
  'comment-dialog-no-subject': 'No subject',
  'comment-dialog-subjects-refresh': 'Refresh subjects',
  'comment-dialog-subjects-manage': 'Manage subjects',
  'comment-dialog-contact-person': 'Contact person',
  'comment-dialog-no-contact-person': 'No contact person',
  'comment-dialog-description-required': 'Comment *',
  'comment-dialog-description-placeholder': 'Enter your comment here...',
  'comment-dialog-validation-description-required': 'Comment is required',
  'comment-dialog-validation-subject-required': 'Subject is required',
  'comment-contact-type-personal': 'In person',
  'comment-contact-type-email': 'By email',
  'comment-contact-type-phone': 'By phone',
  'comment-contact-type-info': 'Info',
  'comment-edited-at': 'Edited',
  'comment-booking-relevant': 'Booking relevant',
  'comment-booking': 'Booking',
  'comment-contact-relevant': 'Contact relevant',
  'comment-relevance': 'Relevance',
  'booking-comments-booking-relevant-count': '{{count}} booking-relevant comments',
  'booking-comments-company-note-tooltip': 'Company note (booking-relevant)',
  'offer-documents-title': 'Additional documents',
  'offer-documents-upload-pdf': 'Upload PDF document',
  'offer-documents-upload-hint': 'Only PDF files are allowed. Documents are sent with confirmation emails.',
  'offer-documents-uploaded-at': 'Uploaded on',
  'offer-documents-only-pdf': 'Only PDF files are allowed',
  'offer-documents-load-error': 'Failed to load documents',
  'offer-documents-upload-success': 'Document uploaded successfully',
  'offer-documents-delete-success': 'Document deleted successfully',
  'offer-documents-empty-with-upload': 'No documents uploaded yet. Click "Upload PDF document" to add documents.',
  'offer-documents-empty-readonly': 'No additional documents for this offer.',
  'admin-offer-documents-loading': 'Loading documents...',
  'admin-offer-documents-load-error': 'Failed to load documents',
  'admin-offer-documents-empty': 'No documents uploaded by customer.',
  'admin-offer-documents-title': 'Seminar documents',
  'admin-offer-documents-uploaded-at': 'Uploaded on',
  'publicclient-resend-invitation': 'Resend invitation',
  'usergroup-title': 'User groups',
  'usergroup-no-active-contacts': 'No active contacts available for this company.',
  'publicclient-email': 'Email',
  'publicclient-firstname': 'First name',
  'publicclient-lastname': 'Last name',
  'publicclient-responsibility': 'Responsibility',
  'publicclient-button-new': 'New contact person',
  'publicclient-active-contacts': 'Active contacts',
  'publicclient-inactive-contacts': 'Inactive contacts',
  'publicclient-resend-invitation-success': 'Invitation for {{clientName}} was resent',
  'publiccompany-name': 'Company name',
  'publiccompany-main-language': 'Main language',
  'publiccompany-street': 'Street',
  'publiccompany-address-supplement': 'Address supplement',
  'publiccompany-zip': 'ZIP',
  'publiccompany-city': 'City',
  'publiccompany-country': 'Country',
  'publiccompany-state': 'State',
  'publiccompany-language-german': 'German',
  'publiccompany-language-english': 'English',
  'publiccompany-section-basic': 'Basic information',
  'publiccompany-section-address': 'Address',
  'publiccompany-section-contact': 'Contact',
  'publiccompany-section-business': 'Business information',
  'publiccompany-section-comments': 'Internal comments',
  'publiccompany-section-tasks': 'Tasks',
  'publiccompany-section-phone': 'Phone number',
  'publiccompany-phone-country-code': 'Country code',
  'publiccompany-phone-number': 'Number',
  'publiccompany-email': 'Email address',
  'publiccompany-industry': 'Industry',
  'publiccompany-website': 'URL',
  'publiccompany-key-account-manager': 'Key account manager',
  'publiccompany-no-selection': 'No selection',
  'publiccompany-premium': 'Premium',
  'publiccompany-show-prices-net': 'Show prices in net',
  'publiccompany-privacy-policy-accepted': 'Privacy policy accepted',
  'publiccompany-button-save': 'Save',
  'publiccompany-button-back': 'Back to companies',
  'publiccompany-status-active': 'Active',
  'publiccompany-status-inactive': 'Inactive',
  'publiccompany-deactivate-button': 'Deactivate company',
  'publiccompany-deactivate-title': 'Deactivate company',
  'publiccompany-deactivate-question':
    'Deactivating the company will deactivate all associated users, who will no longer be able to log in with their email address. You can reactivate the account at any time.',
  'publiccompany-deactivated-success': 'Company was deactivated successfully.',
  'publiccompany-reactivate-button': 'Reactivate company',
  'publiccompany-reactivate-title': 'Reactivate company',
  'publiccompany-reactivate-question':
    'This step will reactivate the company, all prior client users will stay deactivated and need to be activated manually, if needed.',
  'publiccompany-reactivated-success': 'Company was reactivated successfully.',
  'publiccompany-blocked-dialog-title': 'Company cannot be deactivated',
  'publiccompany-blocked-dialog-message':
    'Contacts in this company still have active bookings and therefore cannot be deactivated. Please delete all bookings first to enable deactivation.',
  'publiccompany-blocked-contact': 'Contact person',
  'publiccompany-inactive-contacts-warning': 'This company is inactive. No contacts can be created or reactivated.',
  'publiccompany-inactive-contact-create-blocked': 'New contacts cannot be created for inactive companies.',
  'publiccompany-inactive-contact-action-blocked': 'Contacts can only be reactivated for active companies.',
  'publiccompany-tab-basic-data': 'Basic data',
  'publiccompany-tab-contracts': 'Contracts',
  'publiccompany-tab-contacts': 'Contacts',
  'publiccompany-tab-billing': 'Billing',
  'publiccompany-billing-price-display': 'Price display',
  'publiccompany-billing-save-settings': 'Save settings',
  'publiccompany-billing-accounting-areas': 'Accounting areas',
  'publiccompany-comment-add': 'Add comment',
  'publiccompany-comment-no-comments': 'No comments available',
  'publiccompany-comments-error-loading': 'Error loading comments',
  'publicclient-blocked-booking-id': 'Booking ID',
  'usergroup-add-button': 'Add user group',
  'usergroup-dialog-title': 'Create user group',
  'usergroup-dialog-edit-title': 'Edit user group',
  'usergroup-name-label': 'User group name',
  'usergroup-name-placeholder': 'Please enter the user group name (max. 30 characters)',
  'usergroup-button-create': 'Create user group',
  'usergroup-button-update': 'Save user group',
  'usergroup-button-cancel': 'Cancel',
  'usergroup-list-name': 'Name',
  'usergroup-list-members-count': 'Members count',
  'usergroup-list-actions': 'Actions',
  'usergroup-delete-confirm-title': 'Delete user group',
  'usergroup-delete-confirm-message': 'Do you really want to delete this user group? This action cannot be undone.',
  'usergroup-created-success': 'User group created successfully',
  'usergroup-updated-success': 'User group updated successfully',
  'usergroup-deleted-success': 'User group deleted successfully',
  'usergroup-members-updated-success': 'User group members updated successfully',
  'usergroup-error-name-required': 'Name is required',
  'usergroup-error-name-too-long': 'Name may be at most 30 characters long',
  'usergroup-error-name-exists': 'A user group with this name already exists',
  'usergroup-members-title': 'Manage members',
  'usergroup-members-description': 'Select contacts that should belong to this user group:',
  'usergroup-no-groups': 'No user groups available',
  'hotel-settings-tab': 'Master data',
  'hotel-contacts-tab': 'Contacts',
  'hotel-notifications-tab': 'Notifications',
  'hotel-lister-tab': 'seminargo settings',
  'hotel-widget-tab': 'SEM settings',
  'hotel-groups-tab': 'Chains',
  'hotel-contracts-tab': 'Agreements',
  'hotel-integrations-tab': 'Integrations',
  'hotel-mp-enable-service': 'Enable service',
  'hotel-mp-description':
    'This service automatically retrieves all available information (hotel name, meeting rooms, mail address) from Meeting Package.',
  'hotel-mp-property-identifier': 'Property Identifier',
  'hotel-mp-property-identifier-hint': 'Enter your unique MeetingPackage ID to start syncing.',
  'hotel-mp-validated-hotel-name': 'Validated Hotel Name',
  'hotel-mp-no-hotel-found': 'No hotel found',
  'hotel-mp-validating': 'Validating...',
  'hotel-mp-enter-valid-identifier': 'Enter a valid property identifier to enable the service.',
  'hotel-mp-enabled': 'Meeting Package integration enabled',
  'hotel-mp-disabled': 'Meeting Package integration disabled',
  'hotel-mp-synced': 'Meeting Package data was synchronized',
  'hotel-mp-sync-now': 'Sync now',
  'hotel-mp-fields-readonly': 'These fields are managed by Meeting Package. Disable the Meeting Package integration to edit them manually.',
  'hotel-mp-open-integrations': 'Open integrations',
  'hotel-mp-active-info': 'Meeting Package is active. Data is automatically synced from Meeting Package.',
  'hotel-mp-need-assistance': 'Need assistance?',
  'hotel-mp-view-documentation': 'Visit FAQ',
  'hotel-mp-contact-support': 'Contact Seminargo',
  'hotel-contact-add': 'Add contact person',
  'hotel-comments-section-title': 'Internal comments',
  'hotel-comment-add': 'Add comment',
  'hotel-comment-no-comments': 'No comments yet',
  'hotel-reminder-receiver-section': 'Primary email address for notifications',
  'hotel-reminder-type-section': 'Additional email recipients per reminder type',
  'hotel-reminder-type-section-helper': 'Define which additional email addresses should receive specific reminder types.',
  'hotel-reminder-type': 'Reminder type',
  'hotel-reminder-description': 'Description',
  'hotel-reminder-receiver': 'Additional recipients',
  'hotel-reminder-edit-header': 'Edit email recipients for "{{type}}"',
  'hotel-reminder-edit-header-helper':
    'Add extra email addresses that should receive this notification. The primary email address {{notificationEmail}} always receives this notification.',
  'hotel-reminder-edit-add-header': 'Add new email address',
  'hotel-reminder-type-LISTER_NONBINDING_REQUEST_HOTEL': 'Request received',
  'hotel-reminder-type-LISTER_REMINDER_REQUEST_HOTEL': 'Reminder for unanswered request',
  'hotel-reminder-type-LISTER_REJECTED_OFFER_HOTEL': 'Request rejected by booker',
  'hotel-reminder-type-LISTER_RESERVATION_HOTEL': 'Reservation',
  'hotel-reminder-type-LISTER_CR_HOTEL': 'Change',
  'hotel-reminder-type-CLASSIC_CR_HOTEL': 'Reservation change (Classic)',
  'hotel-reminder-type-LISTER_TITLE_CHANGE_HOTEL': 'Seminar title changed',
  'hotel-reminder-type-LISTER_BOOKING_INFORMATION_HOTEL': 'Checklist',
  'hotel-reminder-type-LISTER_BOOKING_REMINDER_START_SOON_HOTEL': 'Reminder',
  'hotel-reminder-type-LISTER_BOOKING_REMINDER_END_TODAY_HOTEL': 'Invoicing close',
  'hotel-reminder-type-LISTER_BOOKING_CANCELLATION_HOTEL': 'Cancellation',
  'hotel-reminder-type-LISTER_AVAILABILITY_APPROVED_CLIENT': 'Offer approved by hotel',
  'hotel-reminder-type-LISTER_RESERVATION_APPROVED_CLIENT': 'Reservation reconfirmed by hotel',
  'hotel-reminder-type-LISTER_INVOICE_HOTEL': 'Outgoing invoices',
  'hotel-reminder-type-LISTER_CREDIT_NOTE_HOTEL': 'Credit notes',
  'hotel-reminder-description-LISTER_NONBINDING_REQUEST_HOTEL': 'Sent when a new request is received',
  'hotel-reminder-description-LISTER_REMINDER_REQUEST_HOTEL': 'Sent when a request has not been answered after 5h',
  'hotel-reminder-description-LISTER_REJECTED_OFFER_HOTEL': 'Sent when a request is rejected by the booker',
  'hotel-reminder-description-LISTER_RESERVATION_HOTEL': 'Sent when a reservation is made by the booker',
  'hotel-reminder-description-LISTER_CR_HOTEL': 'Sent when the booker makes changes to the reservation.',
  'hotel-reminder-description-CLASSIC_CR_HOTEL': 'Sent when a Classic customer requests a reservation change.',
  'hotel-reminder-description-LISTER_TITLE_CHANGE_HOTEL': 'Sent when the booker changes the seminar title',
  'hotel-reminder-description-LISTER_BOOKING_INFORMATION_HOTEL': 'Contains the checklist for the seminar',
  'hotel-reminder-description-LISTER_BOOKING_REMINDER_START_SOON_HOTEL': 'Reminds about the upcoming start of the event',
  'hotel-reminder-description-LISTER_BOOKING_REMINDER_END_TODAY_HOTEL': 'Sent when booking ends and should be officially closed.',
  'hotel-reminder-description-LISTER_BOOKING_CANCELLATION_HOTEL': 'Sent when the booking is canceled.',
  'hotel-reminder-description-LISTER_AVAILABILITY_APPROVED_CLIENT': 'Sent to the customer when the hotel has approved the offer.',
  'hotel-reminder-description-LISTER_RESERVATION_APPROVED_CLIENT': 'Sent to the customer when the hotel has reconfirmed the reservation.',
  'hotel-reminder-description-LISTER_INVOICE_HOTEL': 'Used for sending outgoing invoices.',
  'hotel-reminder-description-LISTER_CREDIT_NOTE_HOTEL': 'Used for sending credit notes (invoices on behalf of the hotel).',
  'hotel-notificationemail-helper':
    'This email receives system reminders for this hotel. This setting is mandatory so important notifications are not missed.',
  'hotel-lister-settings': 'seminargo settings',
  'hotel-showinlister': 'Show in the public seminargo directory',
  'hotel-lister-servicetypes': 'Available service packages',
  'hotel-lister-servicetype-add': 'Assign service package',
  'hotel-settings-texts-tab': 'Text modules',
  'hotel-settings-texts-type': 'Type',
  'hotel-settings-texts-details': 'Text',
  'hotel-settings-texts-add': 'Add text module',
  'hotel-settings-texts-language': 'Language',
  'hotel-settings-capacities-tab': 'Capacities',
  'hotel-settings-attributes-tab': 'Attributes',
  'hotel-settings-attributes-parking': 'Parking',
  'hotel-settings-attributes-parking-free': 'Free Parking',
  'hotel-settings-attributes-parking-free-spaces': 'Number of free parking spaces',
  'hotel-settings-attributes-parking-paid': 'Paid Parking',
  'hotel-settings-attributes-parking-paid-spaces': 'Number of paid parking spaces',
  'hotel-settings-cancellation-tab': 'Cancellation terms',
  'hotel-settings-media-tab': 'Media selection',
  'hotel-settings-commission-tab': 'Commission rates',
  'hotel-groups-assignment': 'Chain assignment',
  'hotel-groups-none-available': 'No chains available',
  'hotel-groups-none-assigned': 'No chains assigned',
  'hotels-list-header-refcode': 'Reference code',
  'hotels-list-header-integration': 'PMS integration',
  'hotels-list-header-lister': 'seminargo',
  'hotels-list-header-widget': 'SEM',
  'hotels-list-header-address': 'Address',
  'hotels-list-lister-url': 'Open in seminargo',
  'hotels-list-test-url': 'Test SEM',
  'hotels-list-test-url-no-rooms': 'Without room booking',
  'hotels-list-add': 'Create hotel',
  'hotel-selection-search-placeholder': 'Search hotel or area...',
  'validation-details-title': '{{hotelName}} - validation details',
  'validation-missing-products': 'Missing products',
  'validation-missing-services': 'Missing services',
  'validation-invalid-products': 'Invalid products',
  'validation-invalid-services': 'Invalid services',
  'hotel-name': 'Hotel name',
  'hotel-refcode': 'Reference code',
  'hotel-extrefcode': 'External reference code',
  'hotel-businessemail': 'Business email address',
  'hotel-businessphone': 'Business phone number',
  'hotel-businessurl': 'Business website',
  'hotel-iban': 'IBAN',
  'hotel-uid': 'VAT ID',
  'hotel-bic': 'BIC',
  'hotel-businessname': 'Official company name',
  'hotel-businessaddress1': 'Street',
  'hotel-businessaddress2': 'Address addition 1',
  'hotel-businessaddress3': 'Address addition 2',
  'hotel-businesszip': 'ZIP',
  'hotel-businesscity': 'City',
  'hotel-businesscountry': 'Country',
  'hotel-businessstate': 'State',
  'hotel-location-latitude': 'Coordinates (latitude)',
  'hotel-location-longitude': 'Coordinates (longitude)',
  'hotel-location-fetch-coords': 'Resolve coordinates from address',
  'hotel-location-openmap': 'Open in map',
  'hotel-different-invoice-address': 'Different billing address',
  'hotel-invoiceaddress1': 'Invoice address line 1',
  'hotel-invoiceaddress2': 'Invoice address line 2',
  'hotel-invoiceaddress4': 'Invoice contact',
  'hotel-contact-title': 'Title',
  'hotel-contact-firstname': 'First name',
  'hotel-contact-lastname': 'Last name',
  'hotel-contact-gender': 'Gender',
  'hotel-contact-job': 'Position',
  'hotel-contact-phone': 'Phone',
  'hotel-contact-email': 'Email',
  'hotel-contact-type': 'Contact type',
  'hotel-notificationemail': 'Email address for notifications',
  'hotel-reminder-days-section': 'Time window configuration',
  'hotel-offernotapprovedreminderhotel': 'Send reminder to hotel (after days)',
  'hotel-offernotapprovedreminderclient': 'Send reminder to customer (after days)',
  'hotel-depositreminderhotel': 'Send deposit reminder to hotel (after days)',
  'hotel-depositremindercheckhotel': 'Send deposit check reminder to hotel (after days)',
  'hotel-lister-status-READY': 'Fully configured',
  'hotel-rating': 'Rating',
  'hotel-slug': 'SEO short code (URL)',
  'hotel-offertemplate': 'Offer template',
  'hotel-showinwidget': 'Show in SEM widget',
  'hotel-enableavailabilitycheck': 'Enable availability check',
  'hotel-offerexpirationdays': 'Offer expiration (days)',
  'hotel-widgeturl': 'Widget URL',
  'hotel-widgeturl-helper': 'Generated URL to the hotel SEM widget.',
  'hotel-widget-code': 'Widget embedding code',
  'hotel-widget-advanced-options': 'Advanced options (event tracking)',
  'hotel-widget-event-tracking-title': 'Event tracking for the SEM widget',
  'hotel-widget-event-tracking-description':
    'The widget fires events on key interactions (tab clicks, button clicks, booking form, email verification). These can be subscribed to on the host page to forward them to Google Analytics, GTM, or custom analytics.',
  'hotel-widget-event-tracking-snippet-label': 'Sample integration',
  'hotel-widget-event-tracking-events-label': 'Available events',
  'hotel-widget-event-tracking-copy': 'Copy code',
  'hotel-widget-event-tracking-copied': 'Code copied to clipboard',
  'hotel-widget-color': 'Widget accent color',
  'hotel-widget-color-enabled': 'Enable custom accent color',
  'hotel-widget-color-helper': 'Applies to buttons and highlights in the widget.',
  'hotel-widget-display-mode': 'Widget display mode',
  'hotel-widget-terms-url': 'Terms URL',
  'hotel-widget-privacy-url': 'Privacy URL',
  'hotel-widget-terms-url-helper': 'Link to terms shown in widget checkout.',
  'hotel-widget-privacy-url-helper': 'Link to privacy policy shown in widget checkout.',
  'hotel-widget-no-rooms': 'Allow booking without room',
  'hotel-widget-no-rooms-helper': 'If enabled, users can book events without room reservations.',
  'hotel-widget-preview': 'Widget preview',
  'hotel-widget-status-READY': 'Fully configured',
  'hotel-sem-widget-settings': 'SEM widget settings',
  'hotel-sem-offer-settings': 'SEM offer settings',
  'hotel-servicetypes-widget': 'Available service packages',
  'hotel-settings-meeting-rooms': 'Meeting rooms',
  'hotel-settings-meeting-rooms-add': 'Add meeting room',
  'hotel-settings-meeting-rooms-name': 'Name',
  'hotel-settings-meeting-rooms-daylight': 'Daylight',
  'hotel-settings-meeting-rooms-airconditioning': 'Air conditioning',
  'hotel-settings-meeting-rooms-area': 'Area',
  'hotel-settings-meeting-rooms-capacityUForm': 'U-shape',
  'hotel-settings-meeting-rooms-capacityTheater': 'Theater',
  'hotel-settings-meeting-rooms-capacityParlament': 'Classroom',
  'hotel-settings-meeting-rooms-capacityCircle': 'Circle',
  'hotel-settings-meeting-rooms-capacityBankett': 'Banquet',
  'hotel-settings-meeting-rooms-capacityCocktail': 'Cocktail',
  'hotel-settings-meeting-rooms-capacityBlock': 'Boardroom',
  'hotel-settings-capacities-maxCapacityRooms': 'Maximum number of rooms',
  'hotel-settings-capacities-maxCapacityPeople': 'Maximum number of persons',
  'hotel-settings-medias-name': 'Name',
  'hotel-settings-medias-description': 'Description',
  'hotel-settings-medias-add': 'Add media',
  'hotel-settings-agb-title': 'Terms and conditions (PDF)',
  'hotel-settings-agb-filename': 'File name',
  'hotel-settings-agb-size': 'File size',
  'hotel-settings-agb-upload-date': 'Uploaded at',
  'hotel-settings-agb-uploader': 'Uploaded by',
  'hotel-settings-agb-download': 'Download',
  'hotel-settings-agb-upload': 'Upload terms and conditions',
  'hotel-settings-agb-uploading': 'Uploading...',
  'hotel-settings-agb-no-file': 'No terms and conditions uploaded yet',
  'hotel-settings-agb-pdf-required': 'Please select a PDF file',
  'hotel-settings-agb-download-failed': 'Failed to download terms',
  'hotel-settings-agb-drop-pdf': 'Drag and drop PDF file here',
  'hotel-settings-agb-or': 'or',
  'hotel-settings-agb-upload-select-file': 'Select file',
  'hotel-settings-agb-upload-hint': 'PDF files only + Maximum file size: 10MB',
  'hotel-settings-agb-uploaded': 'Terms uploaded successfully',
  'hotel-settings-agb-upload-failed': 'Failed to upload terms',
  'hotel-settings-agb-file-read-failed': 'Failed to read file',
  'hotel-save': 'Save hotel settings',
  'hotel-validation-error': 'Cannot save. Please fill required fields on these tabs: {{tabs}}',
  'hotel-delete': 'Delete hotel',
  'hotel-deleted': 'Hotel deleted',
  'graphqlerror-HOTEL_SLUG_ALREADY_EXISTS': 'SEO short code {{- slug}} is already used by another hotel',
  'hotel-created': 'Hotel created',
  'hotel-updated': 'Hotel updated',
  'hotel-updated-by-another-user': 'Hotel was updated by another user',
  'hotel-lister-slug-required': 'Please enter a slug first.',
  'hotel-wordpress-url-not-found': 'No WordPress URL was found for this slug.',
  'generic-error-unknown': 'Unknown error',
  'hotel-confirm-delete-title': 'Delete hotel',
  'hotel-confirm-delete': 'Do you really want to delete this hotel?',
  'hotel-commission-note-label': 'Note',
  'hotel-commission-rates-info': 'Commission rates are frozen when offers are created. Changes affect only future offers.',
  'hotel-commission-customer-type': 'Customer type',
  'hotel-commission-base': 'Commission base',
  'hotel-commission-percentage': 'Commission percentage',
  'hotel-commission-valid-from': 'Valid from',
  'hotel-commission-remark': 'Remark',
  'hotel-commission-add': 'Add commission rate',
  'hotel-commission-dialog-title': 'Create commission rate',
  'hotel-commission-save': 'Save commission rate',
  'hotel-commission-saved': 'Commission rate saved',
  'hotel-commission-deleted': 'Commission rate deleted',
  'hotel-commission-error-duplicate': 'A commission rate already exists for this customer type and date.',
  'hotel-commission-validation-past-date': 'Date must be today or in the future',
  'groups-available': 'Available chains',
  'groups-assigned': 'Assigned chains',
  'groups-search': 'Search by chain name',
  'groups-name': 'Chain name',
  'groups-title': 'Chains',
  'groups-create': 'Create new chain',
  'groups-assigned-hotels': 'Assigned hotels',
  'pricelist-validation-products-without-base-price': 'Products without base price',
  'pricelist-validation-products-without-price-components': 'Products without tax components',
  'pricelist-validation-bundles-without-base-price': 'Packages without base price',
  'pricelist-validation-bundles-without-price-components': 'Packages without tax components',
  'pricelist-validation-facilities-without-base-price': 'Facilities without base price',
  'pricelist-validation-facilities-without-price-components': 'Facilities without tax components',
  'enums-EHotelWidgetModeType-EXPANDED': 'Expanded',
  'enums-EHotelWidgetModeType-SLIM': 'Slim',
  'enums-EContentHotelAttribute-CATEGORY_NONE': 'No category',
  'enums-EContentHotelAttribute-CATEGORY_ONE_STARS': '1 star',
  'enums-EContentHotelAttribute-CATEGORY_TWO_STARS': '2 stars',
  'enums-EContentHotelAttribute-CATEGORY_THREE_STARS': '3 stars',
  'enums-EContentHotelAttribute-CATEGORY_THREE_STARS_SUPERIOR': '3 stars superior',
  'enums-EContentHotelAttribute-CATEGORY_FOUR_STARS': '4 stars',
  'enums-EContentHotelAttribute-CATEGORY_FOUR_STARS_SUPERIOR': '4 stars superior',
  'enums-EContentHotelAttribute-CATEGORY_FIVE_STARS': '5 stars',
  'enums-EContentHotelAttribute-CATEGORY_FIVE_STARS_SUPERIOR': '5 stars superior',
  'enums-EContentHotelAttribute-CATEGORY': 'Category',
  'enums-EContentHotelAttribute-LOCATION': 'Location',
  'enums-EContentHotelAttribute-LOCATION_CITY': 'City',
  'enums-EContentHotelAttribute-LOCATION_COUNTRY': 'Countryside',
  'enums-EContentHotelAttribute-ROOM': 'Rooms',
  'enums-EContentHotelAttribute-ROOM_AIRCONDITIONER': 'Air conditioning',
  'enums-EContentHotelAttribute-ROOM_BALCONY': 'Balcony',
  'enums-EContentHotelAttribute-ROOM_BARRIER_FREE': 'Accessible',
  'enums-EContentHotelAttribute-ROOM_HAIRDRYER': 'Hair dryer',
  'enums-EContentHotelAttribute-ROOM_MINIBAR': 'Minibar',
  'enums-EContentHotelAttribute-ROOM_SAFE': 'Safe',
  'enums-EContentHotelAttribute-ROOM_TV': 'TV',
  'enums-EContentHotelAttribute-ROOM_INTERNET_WIFI': 'WiFi',
  'enums-EContentHotelAttribute-ROOM_INTERNET_FIBER': 'Fiber internet',
  'enums-EContentHotelAttribute-ROOM_SMOKING': 'Smoking room',
  'enums-EContentHotelAttribute-ROOM_WORKTABLE': 'Work desk',
  'enums-EContentHotelAttribute-ROOM_TRAINERROOM': 'Trainer room',
  'enums-EContentHotelAttribute-SEMINAR': 'Seminar room',
  'enums-EContentHotelAttribute-SEMINAR_ACCESSABLE_BY_CAR': 'Car access',
  'enums-EContentHotelAttribute-SEMINAR_AIRCONDITIONER': 'Air conditioning',
  'enums-EContentHotelAttribute-SEMINAR_DAYLIGHT': 'Daylight',
  'enums-EContentHotelAttribute-SEMINAR_INTERNET_WIFI': 'WiFi',
  'enums-EContentHotelAttribute-SEMINAR_INTERNET_FIBER': 'Fiber internet',
  'enums-EContentHotelAttribute-SEMINAR_BREAK_AREA': 'Break area',
  'enums-EContentHotelAttribute-SEMINAR_GROUPWORK': 'Group work',
  'enums-EContentHotelAttribute-DESIGN': 'Design',
  'enums-EContentHotelAttribute-DESIGN_MODERN': 'Modern',
  'enums-EContentHotelAttribute-DESIGN_BUSINESS': 'Business',
  'enums-EContentHotelAttribute-DESIGN_TRADITIONAL': 'Traditional',
  'enums-EContentHotelAttribute-DESIGN_RURAL': 'Rural',
  'enums-EContentHotelAttribute-ACTIVITY': 'Activities',
  'enums-EContentHotelAttribute-ACTIVITY_BIKE_RENTAL': 'Bike rental',
  'enums-EContentHotelAttribute-ACTIVITY_GOLF': 'Golf',
  'enums-EContentHotelAttribute-ACTIVITY_MINIGOLF': 'Mini golf',
  'enums-EContentHotelAttribute-ACTIVITY_BOWLING': 'Bowling',
  'enums-EContentHotelAttribute-ACTIVITY_TENNIS': 'Tennis',
  'enums-EContentHotelAttribute-ACTIVITY_RUNNING': 'Running',
  'enums-EContentHotelAttribute-ACTIVITY_BILLIARD': 'Billiards',
  'enums-EContentHotelAttribute-ACTIVITY_FITNESS': 'Fitness center',
  'enums-EContentHotelAttribute-ACTIVITY_HIGH_ROPES_COURSE': 'High ropes course',
  'enums-EContentHotelAttribute-ACTIVITY_TEAM_COOKING': 'Team cooking',
  'enums-EContentHotelAttribute-ACTIVITY_RAFT_BUILDING': 'Raft building',
  'enums-EContentHotelAttribute-ACTIVITY_RAFTING': 'Rafting',
  'enums-EContentHotelAttribute-ACTIVITY_SKI_SLOPE': 'Ski slope',
  'enums-EContentHotelAttribute-ACTIVITY_TOBOGGAN_RUN': 'Toboggan run',
  'enums-EContentHotelAttribute-CATERING': 'Catering',
  'enums-EContentHotelAttribute-CATERING_LUNCH': 'Lunch menu',
  'enums-EContentHotelAttribute-CATERING_DINNER': 'Dinner menu',
  'enums-EContentHotelAttribute-CATERING_BREAKFAST': 'Breakfast',
  'enums-EContentHotelAttribute-CATERING_HALFBOARD': 'Half board',
  'enums-EContentHotelAttribute-CATERING_FULLBOARD': 'Full board',
  'enums-EContentHotelAttribute-CATERING_OUTDOOR': 'Outdoor catering',
  'enums-EContentHotelAttribute-CATERING_RESTAURANT': 'Restaurant',
  'enums-EContentHotelAttribute-WELLNESS': 'Wellness',
  'enums-EContentHotelAttribute-WELLNESS_INDOOR_POOL': 'Indoor pool',
  'enums-EContentHotelAttribute-WELLNESS_OUTDOOR_POOL': 'Outdoor pool',
  'enums-EContentHotelAttribute-WELLNESS_THERMAL': 'Thermal spa',
  'enums-EContentHotelAttribute-WELLNESS_MASSAGE': 'Massage',
  'enums-EContentHotelAttribute-WELLNESS_WHIRLPOOL': 'Whirlpool',
  'enums-EContentHotelAttribute-WELLNESS_INFRARED_CABIN': 'Infrared cabin',
  'enums-EContentHotelAttribute-WELLNESS_SAUNA': 'Sauna',
  'enums-EContentHotelAttribute-WELLNESS_STEAM_BATH': 'Steam bath',
  'enums-EContentHotelAttribute-PARKING_FREE': 'Free Parking',
  'enums-EContentHotelAttribute-PARKING_PAID': 'Paid Parking',
  'enums-EContentHotelAttribute-HOTELFACILITY': 'Hotel facilities',
  'enums-EContentHotelAttribute-HOTELFACILITY_24_ROOMSERVICE': '24h room service',
  'enums-EContentHotelAttribute-HOTELFACILITY_BARRIER_FREE': 'Accessible',
  'enums-EContentHotelAttribute-HOTELFACILITY_CLOTHES_CLEANING': 'Laundry service',
  'enums-EContentHotelAttribute-HOTELFACILITY_COPY_FAX': 'Copy/Fax',
  'enums-EContentHotelAttribute-HOTELFACILITY_ELECTRIC_CHARGING_STATION': 'Electric charging station',
  'enums-EContentHotelAttribute-HOTELFACILITY_ELEVATOR': 'Elevator',
  'enums-EContentHotelAttribute-HOTELFACILITY_GREEN_AREA': 'Green area',
  'enums-EContentHotelAttribute-HOTELFACILITY_INTERNET_WIFI': 'WiFi',
  'enums-EContentHotelAttribute-HOTELFACILITY_IT_ROOM': 'IT room',
  'enums-EContentHotelAttribute-HOTELFACILITY_PRESS_ROOM': 'Press room',
  'enums-EContentHotelAttribute-HOTELFACILITY_PUBLIC_INTERNET': 'Public internet',
  'enums-EContentHotelAttribute-HOTELFACILITY_SECRETARY': 'Secretary service',
  'enums-EContentHotelAttribute-HOTELFACILITY_SHUTTLE': 'Shuttle service',
  'enums-EContentHotelAttribute-HOTELFACILITY_SMOKING_AREA': 'Smoking area',
  'enums-EContentHotelAttribute-HOTELFACILITY_WARDROBE': 'Wardrobe',
  'enums-EContentHotelAttribute-ECOLABEL': 'Eco labels',
  'enums-EContentHotelAttribute-ECOLABEL_AUSTRIAN_ECOLABEL': 'Austrian Ecolabel',
  'enums-EContentHotelAttribute-ECOLABEL_EU_ECOLABEL': 'EU Ecolabel',
  'enums-EContentHotelAttribute-ECOLABEL_GREEN_KEY': 'Green Key',
  'enums-EContentHotelAttribute-ECOLABEL_GREEN_MEETINGS_AND_EVENTS': 'Green Meetings & Events',
  'enums-EClientGender-MALE': 'Male',
  'enums-EClientGender-FEMALE': 'Female',
  'enums-EClientGender-VARIOUS': 'Diverse / no information',
  'enums-EContentHotelContactType-MAIN_CONTACT': 'Main contact',
  'enums-EContentHotelContactType-MANAGEMENT': 'Management',
  'enums-EContentHotelContactType-BANQUET_DEPARTMENT': 'Banquet department',
  'enums-EContentHotelContactType-RESERVATION': 'Reservation',
  'enums-EContentHotelContactType-SALES': 'Sales',
  'enums-EContentHotelContactType-ACCOUNTING': 'Accounting',
  'hotel-cancellation-tab': 'Cancellation terms',
  'hotel-cancellation-details-tab': 'Cancellation terms',
  'hotel-cancellation-details-partner':
    'As a seminargo contract partner, you can define your own cancellation terms when more than 50% room capacity or more than 30 participants are booked. Otherwise, seminargo standard cancellation terms apply.',
  'hotel-cancellation-details-nopartner':
    'You are not a seminargo contract partner and can therefore define your cancellation terms freely. If no custom cancellation terms are defined, seminargo standard cancellation terms apply. These can be adjusted per offer.',
  'hotel-cancellation-details-defaultrules-premiumclient': 'Default cancellation terms for seminargo premium customers',
  'hotel-cancellation-details-defaultrules-defaultclient': 'Default cancellation terms for seminargo broker customers',
  'hotel-cancellation-details-rule': 'Rule',
  'hotel-cancellation-details-cancellation-days': 'Cancellation period (days before start)',
  'hotel-cancellation-details-cancellation-capacity': 'Occupancy (from - to in %)',
  'hotel-cancellation-details-cancellation-overnight': 'Overnight guests (from - to)',
  'hotel-cancellation-details-cancellation-totalguests': 'Seminar guests (from - to)',
  'hotel-cancellation-details-tolerance': 'Tolerance (in %)',
  'hotel-cancellation-details-rate': 'Cancellation rate (in %)',
  'hotel-cancellation-agreed-waiver': 'Agreed cancellation waiver (in %)',
  'hotel-cancellation-agreed-waiver-info': 'The agreed cancellation waiver is credited free of charge in case of customer cancellation.',
  'hotel-cancellation-details-add': 'Add cancellation term',
  'hotel-cancellation-details-add-defaults': 'Apply and adjust default cancellation terms',
  action: 'Action',
  add: 'Add',
  added: 'Added',
  remove: 'Remove',
  apply: 'Apply',
  'contracts-only-pdf-allowed': 'Only PDF files are allowed',
  'contracts-download-url-missing': 'No download URL available',
  'contracts-hotel-id-missing': 'Hotel ID is required to create the contract',
  'contracts-invoice-date-required': 'Please select an invoice date',
  'contracts-interval-ONCE': 'One-time',
  'contracts-interval-M1': 'Monthly',
  'contracts-interval-M3': 'Quarterly',
  'contracts-interval-M6': 'Semi-annually',
  'contracts-interval-M12': 'Yearly',
  'contracts-sap-PENDING': 'Pending',
  'contracts-sap-SENT': 'Sent',
  'contracts-sap-FAILED': 'Failed',
  'contracts-type-SYMPOSION_HOTEL_OFFER': 'Symposion hotel offer',
  'contracts-type-SYMPOSION_HOTEL_CONTRACT': 'Symposion hotel contract',
  'contracts-type-PARTNER_HOTEL_CONTRACT': 'Partner hotel contract',
  'contracts-type-FRIEND_HOTEL_CONTRACT': 'Friend hotel contract',
  'contracts-type-FRIEND_LOCATION_CONTRACT': 'Friend location contract',
  'contracts-type-CATALOG_REGISTRATION': 'Catalog registration',
  'contracts-type-NEWSLETTER': 'Newsletter',
  'contracts-type-HOTEL_OF_THE_WEEK': 'Hotel of the week',
  'contracts-type-DIGITAL_SALES_BOOSTER': 'Digital Sales Booster',
  'contracts-type-DIGITAL_SALES_BOOSTER_SETUP_FEE': 'Digital Sales Booster setup fee',
  'contracts-type-SEM_BUSINESS': 'SEM business',
  'contracts-type-SEM_SETUP_FEE': 'SEM setup fee',
  'contracts-type-SEM_SERVICE_FEE': 'SEM service fee',
  'contracts-type-CATALOG_RIGHT_SIDE': 'Catalog right side',
  'contracts-type-CATALOG_LOGO': 'Catalog logo',
  'contracts-type-CATALOG_TRANSLATION': 'Catalog translation',
  'contracts-type-CATALOG_SECOND_CHANGE': 'Catalog second change',
  'contracts-type-CATALOG_QR': 'Catalog QR',
  'contracts-type-MEETING_PACKAGE_IMPORT': 'Meeting Package Import',
  'contracts-type-BASIC_AGREEMENT': 'Basic agreement',
  'contracts-type-POP_UP': 'Pop-Up',
  'contracts-type-TEASER': 'Teaser',
  'company-contract-type-PREMIUM_CUSTOMER_CONTRACT': 'Premium customer contract',
  'company-contract-type-SPONSORING_CATALOG': 'Catalog sponsoring',
  'company-contract-type-SPONSORING_INDUSTRY_MEETING': 'Industry meeting sponsoring',
  'company-contract-title-new': 'New company contract',
  'company-contract-title-edit': 'Edit company contract',
  'company-contract-title-view': 'View company contract',
  'selector-lister-meetingrooms': 'Meeting rooms',
  'selector-lister-meetingrooms-missing': 'No meeting rooms linked with equipment.',
  'selector-lister-meetingrooms-equipment-beamer': '1x projector & screen / flatscreen',
  'selector-lister-meetingrooms-equipment-flipchart': '1x flipchart',
  'selector-lister-meetingrooms-equipment-pinnwand': '1x pin board',
  'system-cache-flushed-rebuilding': 'Cache cleared, rebuild is running in the background...',
  'system-cache-flushed': 'Cache cleared successfully',
  'system-widget-form-reset': 'Widget form state reset successfully',
  'system-cache-rebuild-cancelled': 'Cache rebuild cancelled',
  'system-seed-products-running': 'Services and price lists are being imported...',
  'system-import-cancelled': 'Import cancelled',
  'system-seed-texts-running': 'Missing product texts are being filled...',
  'system-text-import-cancelled': 'Product text import cancelled',
  'system-debug-hotel-or-ref-required': 'Please enter hotel ID or ref code',
  'system-debug-pattern-required': 'Please enter a pattern',
  'system-debug-key-required': 'Please enter a key',
  'system-widget-reset-title': 'Reset lister widget',
  'system-widget-reset-confirm': 'Do you really want to reset all widget form states?',
  'system-cache-clear-title': 'Clear cache',
  'system-cache-clear-confirm': 'Do you really want to clear the entire cache? This action cannot be undone.',
  'system-cache-rebuild-after-clear': 'Rebuild cache after clearing',
  'system-cache-rebuild-hint': 'Rebuild runs in the background. Progress is shown above.',
  'system-cache-status-title': 'Cache status',
  'system-storage-source': 'Read from',
  'system-storage-in-redis': 'In Redis',
  'system-storage-in-db': 'In DB',
  'system-storage-db-updated': 'DB updated',
  'system-storage-no-ttl': 'no expiry',
  'system-storage-not-db-backed': 'not DB-backed',
  'system-storage-source-REDIS': 'Redis',
  'system-storage-source-DB': 'Database',
  'system-storage-source-NONE': 'Missing',
  'system-cache-rebuilding': 'Cache is being rebuilt...',
  'system-cache-please-wait': 'Please wait until the process is completed.',
  'system-main-data': 'Main data',
  'system-hotels': 'Hotels',
  'system-calculators': 'Price lists (calculators)',
  'system-products': 'Services (products)',
  'system-pricelist-validation': 'Price list validation',
  'system-lister-validation': 'Lister validation',
  'system-widget-validation': 'Widget validation',
  'system-status-title': 'System status',
  'system-unlimited': 'unlimited',
  connections: 'Connections',
  memory: 'Memory',
  type: 'Type',
  default: 'Default',
  saving: 'Saving...',
  'system-admin-title': 'System administration',
  'administration-settings-title': 'Settings',
  'administration-offer-seminargo-approval': 'Offer review by seminargo',
  'administration-offer-seminargo-approval-description':
    'When enabled, hotel offers must be confirmed by seminargo before being forwarded to the customer.',
  'administration-offer-seminargo-approval-disable-warning':
    'All offers with status "Waiting for seminargo approval" will be automatically approved.',
  'administration-offer-seminargo-approval-updated': 'Setting has been updated.',
  'system-redis-cache': 'Redis cache',
  'system-redis-cache-description': 'Deletes all data from Redis cache (FLUSHALL). This action cannot be undone.',
  'system-lister-widget': 'Lister widget',
  'system-lister-widget-description': 'Resets form state for all lister widgets. Users must enter search criteria again.',
  'system-services-pricelists': 'Services & price lists',
  'system-seed-products-description':
    'Imports EPILO services (products, facilities, bundles, service types) and creates EPILO price lists for all hotels. Existing product details are not deleted.',
  'system-import-running': 'Import running...',
  'system-processing-spaces': 'Processing administrative areas ({{count}})',
  'system-spaces-completed': 'spaces completed',
  'system-services-pricelists-import': 'Import services & price lists',
  'system-missing-producttexts': 'Fill product texts (EPILO)',
  'system-seed-texts-description':
    'Adds missing default texts (HEADER/DETAILS) for EPILO products, bundles, facilities and service packages. Existing texts are not overwritten.',
  'system-text-import-running': 'Product text import running...',
  'system-missing-producttexts-fill': 'Fill missing product texts',
  'system-cache-debug-tool': 'Cache debug tool',
  'system-cache-debug-description': 'Shows the stored data of a hotel and where it comes from: Redis or database (DB store). Nothing is recomputed.',
  'system-debug-hotel-or-ref': 'Hotel ID or ref code',
  'system-debug-hotel-or-ref-placeholder': 'e.g. 123 or HOTEL-XYZ',
  'system-debug-max-bytes': 'Max bytes',
  'system-cache-fetch': 'Fetch cache',
  'system-lister-status': 'Lister status',
  'system-widget-status': 'Widget status',
  'system-hotel-data': 'Hotel data',
  'system-redis-key-scan': 'Redis key scan',
  'system-redis-key-scan-description': 'Scans Redis keys by pattern (SCAN). Supports wildcards such as * and ?.',
  'system-pattern': 'Pattern',
  'system-pattern-placeholder': 'e.g. HOTEL_DATA_* or QUICKPRICE_*',
  'system-limit': 'Limit',
  'system-max-keys': 'Max keys',
  'system-cursor-optional': 'Cursor (optional)',
  'system-cursor-start-placeholder': '0 to start',
  'system-scan-keys': 'Scan keys',
  'system-scan-results': 'Scan results',
  'system-found-keys': 'Found: {{count}} keys',
  'system-cursor': 'Cursor',
  'system-complete': 'complete',
  'system-no-keys-found': 'No keys found',
  'system-continue-scan': 'Continue scan',
  'system-redis-key-lookup': 'Redis key lookup',
  'system-redis-key-lookup-description': 'Shows the value of a Redis key (only string types are fully shown).',
  'system-key': 'Key',
  'system-key-placeholder': 'e.g. HOTEL_DATA_123',
  'system-lookup-key': 'Lookup key',
  'system-key-exists-no-value': 'Key exists, but value could not be loaded',
  'system-key-not-found': 'Key not found',
  'system-seed-products-confirm': 'Do you really want to import EPILO services and price lists for all spaces?',
  'system-action-imports': 'This action imports:',
  'system-import-item-products': 'EPILO products',
  'system-import-item-facilities': 'EPILO facilities',
  'system-import-item-bundles': 'EPILO bundles',
  'system-import-item-servicetypes': 'EPILO service packages',
  'system-import-item-pricelists': 'EPILO price lists for all hotels',
  'system-import-item-assignments': 'Service type assignments (hotel ↔ service package)',
  'system-seed-products-hint': 'Existing product details are not deleted. Progress is shown during import.',
  'system-restart-ignore-processed-spaces': 'Start from scratch (ignores already processed spaces)',
  'system-seed-texts-confirm': 'Do you really want to fill missing EPILO product texts (HEADER/DETAILS) for all spaces?',
  'system-action-fills-for': 'This action only fills missing texts for:',
  'system-seed-texts-hint': 'Existing product texts remain unchanged.',
  'offer-editor-change-servicetype-title': 'Change service package',
  'integration-log-sync-property': 'Property',
  'integration-log-sync-availability': 'Availability',
  'integration-log-sync-products': 'Products',
  'integration-log-sync-prices': 'Prices',
  'integration-log-sync-booking': 'Booking',
  'integration-log-sync-delete-booking': 'Delete booking',
  'integration-log-sync-confirm-booking': 'Confirm booking',

  'integration-tabs-header-secrets': 'API keys',
  'integration-tabs-header-hotels': 'PMS connections',
  'integration-secrets-list-page-title': 'Integrations',
  'integration-secrets-list-header-name': 'Name',
  'integration-secrets-list-header-system': 'External system',
  'integration-secrets-list-header-key': 'API key',
  'integration-secrets-list-add': 'New API key',

  'integration-secret-name': 'Name',
  'integration-secret-system': 'External system',
  'integration-apaleo-key': 'Apaleo Client Id',
  'integration-apaleo-secretchange': 'Change Apaleo Client Secret',
  'integration-apaleo-secret': 'Apaleo Client Secret',
  'integration-elitefo-secret': 'Authentication key (from Elitesoftware)',
  'integration-elitefo-secretchange': 'Change authentication key',
  'integration-elitefo-htuser': 'HTTP Basic Auth user (set)',
  'integration-elitefo-htpasswd': 'HTTP Basic Auth password (set)',
  'integration-protelids-pushurl': 'Push URL',
  'integration-protelids-user': 'Auth user (set)',
  'integration-protelids-passwd': 'Auth password (set)',
  'integration-meetingpackage-key': 'Meeting Package API Key',
  'integration-meetingpackage-keychange': 'Meeting Package API Key (change)',

  'integration-secret-save': 'Save API key',
  'integration-secret-saved': 'API key saved',
  'integration-secret-confirm-delete-title': 'Delete API key',
  'integration-secret-confirm-delete': 'Really delete this API key? The PMS link will be removed.',
  'integration-secret-deleted': 'API key deleted',
  'integration-secret-delete': 'Delete API key',

  'integration-secret-test': 'Connection test',
  'integration-secret-test-title': 'Connection test',
  'integration-secret-test-empty': 'The connection works technically, but the list of hotel locations is empty.',
  'integration-secret-test-result': 'The connection works technically. The following hotel locations were read:',

  'integration-hotels-list-header-hotel': 'Hotel',
  'integration-hotels-list-header-system': 'External system',
  'integration-hotels-list-header-name': 'API key',
  'integration-hotels-list-add': 'New PMS connection',

  'integration-hotels-list-page-title': 'PMS connections',
  'integration-hotel-secret': 'API key',
  'integration-hotel-pmsproperty': 'PMS hotel',
  'integration-hotel-new': 'Create new SEM hotel and link',
  'integration-hotel-hotel': 'Linked hotel',
  'integration-hotel-extrefcode': 'External reference code',
  'integration-hotel-hotel-select': 'Select existing SEM hotel',
  'integration-hotel-hotel-help': 'Any existing link to another SEM hotel in the same administrative area will be removed',
  'integration-hotel-space-help': 'Any existing link to another SEM hotel in the same administrative area will be removed',
  'integration-hotel-pms-product-name': 'PMS product',
  'integration-hotel-pms-facility-name': 'PMS facility',
  'integration-hotel-pms-pricelist': 'PMS price list',
  'integration-hotel-pms-include': '',

  'integration-hotel-syncproperties': 'Sync master hotel data',
  'integration-hotel-syncproducts': 'Sync product catalog',
  'integration-hotel-syncprices': 'Sync price lists',
  'integration-hotel-syncpricesdays': 'Number of days',
  'integration-hotel-syncavailability': 'Sync availability',
  'integration-hotel-syncavailabilitydays': 'Number of days',
  'integration-hotel-directbooking': 'Direct bookings',

  'integration-hotel-log-tab': 'Data sync',
  'integration-hotel-log-status': 'Status',
  'integration-hotel-log-action': 'Action(s)',
  'integration-hotel-log-trigger': 'Trigger',
  'integration-hotel-log-offer': 'Booking',
  'integration-hotel-log-startdate': 'Start',
  'integration-hotel-log-enddate': 'End',
  'integration-hotel-log-showresult': 'Show log',
  'integration-hotel-log-showresult-title': 'Log',

  'integration-hotel-create': 'Create PMS connection',
  'integration-hotel-created': 'PMS connection was created',
  'integration-hotel-save': 'Save PMS connection',
  'integration-hotel-saved': 'PMS connection saved',
  'integration-hotel-sync': 'Start data sync',
  'integration-hotel-sync-triggered': 'Data sync started',

  'integration-hotel-confirm-delete-title': 'Delete PMS connection',
  'integration-hotel-confirm-delete': 'Really delete the PMS connection for this hotel?',
  'integration-hotel-deleted': 'PMS connection deleted',
  'integration-hotel-delete': 'Delete PMS connection',

  'enums-EIntSystem-DUMMY': 'Test data (sample hotel)',
  'enums-EIntSystem-APALEO': 'Apaleo',
  'enums-EIntSystem-ELITEFO': 'Elite Hotel Software',
  'enums-EIntSystem-PROTELIDS': 'Protel IDS',
  'enums-EIntSystem-MEETINGPACKAGE': 'Meeting Package',

  'enums-EIntBookingMode-SEM': 'No direct booking (editing in SEM)',
  'enums-EIntBookingMode-DIRECT_OFFER': 'Direct booking as reservation (editing in PMS)',
  'enums-EIntBookingMode-DIRECT_CONFIRM': 'Direct booking with automatic confirmation',

  'enums-EIntegrationLogStatus-PENDING': 'Pending',
  'enums-EIntegrationLogStatus-RUNNING': 'Running',
  'enums-EIntegrationLogStatus-SUCCESS': 'Success',
  'enums-EIntegrationLogStatus-FAILED': 'Failed',
  'enums-EIntegrationLogStatus-SKIPPED': 'Skipped',

  'enums-EIntegrationLogTrigger-INIT': 'Initial sync',
  'enums-EIntegrationLogTrigger-SCHEDULED': 'Scheduled',
  'enums-EIntegrationLogTrigger-USER': 'User',
  'enums-EIntegrationLogTrigger-PMS': 'PMS',
  'enums-EIntegrationLogTrigger-PROCESS': 'Automated',

  'hotel-meetingroom-capacity-max-tooltip': 'max. number of persons',
  'accounting-area-new': 'New accounting area',
  'accounting-area-edit': 'Edit accounting area',
  'accounting-area-basic-data': 'Basic data',
  'accounting-area-company-name-required': 'Company name *',
  'accounting-area-attention': 'Attention',
  'accounting-area-vat-id-required': 'VAT ID *',
  'accounting-area-address': 'Address',
  'accounting-area-country-required': 'Country *',
  'accounting-area-zip-required': 'ZIP *',
  'accounting-area-city-required': 'City *',
  'accounting-area-street-required': 'Street *',
  'accounting-area-address-addition': 'Address addition',
  'accounting-area-invoice-email-required': 'Invoice email *',
  'accounting-area-email-recipients': 'Email recipients',
  'accounting-area-email-recipient-add': 'Add email recipient',
  'accounting-area-set-default': 'Set as default',
  'accounting-area-created': 'Accounting area created.',
  'accounting-area-saved': 'Accounting area saved.',
  'accounting-area-save-error': 'Error saving: {{message}}',
  'accounting-area-default-set': 'Default accounting area set.',
  'accounting-area-default-error': 'Error: {{message}}',
  'accounting-area-cannot-delete-default': 'The default accounting area cannot be deleted.',
  'accounting-area-delete-confirm': 'Really delete this accounting area?',
  'accounting-area-deleted': 'Accounting area deleted.',
  'accounting-area-delete-error': 'Error deleting: {{message}}',
  'system-service-backoffice-api': 'Backoffice API',
  'system-service-public-api': 'Public API',
  'system-service-scheduler': 'Scheduler',
  offline: 'offline',
  'system-heap-max': 'Heap / max',
  cpu: 'CPU',
  uptime: 'Uptime',
  bytes: 'bytes',
  'system-pricelist-lister': 'Pricelist (LISTER)',
  'system-pricelist-widget': 'Pricelist (WIDGET)',
  'system-cached': 'CACHED',
  'system-not-cached': 'NOT CACHED',
  'system-truncated': 'TRUNCATED',
  'system-not-in-cache': 'Not in cache',
  'system-exists': 'EXISTS',
  'system-not-found': 'NOT FOUND',
  'system-ttl': 'TTL',
  unknown: 'unknown',
  'content-product-header-position': 'Position',
  'content-product-header-hotel': 'Administrative area',
  'content-product-header-language': 'Language',
  'content-product-header-text': 'Text',
  'content-product-add': 'Add new text module',
  'list-header-space': 'Administrative area',
  'field-space': 'Administrative area',
  'security-tabs-header-users': 'Users',
  'security-tabs-header-spaces': 'Administrative areas',
  'users-list-page-title': 'Users',
  'users-list-header-name': 'Name',
  'users-list-header-username': 'Username',
  'users-list-header-email': 'Email address',
  'users-list-header-isadmin': 'Admin access',
  'users-list-header-islocked': 'Locked',
  'users-list-header-lastseen': 'Last seen',
  'users-list-header-memberships': 'Additional administrative areas',
  'users-list-add': 'Create user',
  'user-name': 'Name',
  'user-username': 'Username',
  'user-email': 'Email address',
  'user-isadmin': 'Admin access',
  'user-islocked': 'User is locked',
  'user-memberships-tab': 'Additional administrative areas',
  'user-membership-add': 'Add another administrative area',
  'user-membership-isadmin': 'Admin access',
  'user-membership-space': 'Administrative area',
  'user-create': 'Create user and send password',
  'user-created': 'User was created and password was sent to the email address',
  'user-update': 'Save user',
  'user-updated': 'User saved',
  'user-confirm-islocked': 'The user will no longer be able to log in - are you sure?',
  'user-confirm-islocked-title': 'Lock user',
  'user-reset': 'Reset password',
  'user-resetted': 'Password was reset and sent to the email address',
  'user-confirm-reset': 'The password will be set to a random value and sent to the email address.',
  'user-confirm-reset-title': 'Reset password',
  'user-reset-password-description': 'Set a new password directly for this user. This bypasses the reset email flow.',
  'user-new-password': 'New password',
  'user-generate-new-password': 'Generate new password',
  'user-reset-password-confirm': 'Set password',
  'user-password-reset-email-sent': 'Email with password reset link was sent',
  'user-set-password': 'Set password',
  'user-set-password-title': 'Set password directly',
  'user-password-set-success': 'Password was set successfully',
  'user-password-changed': 'Password changed successfully',
  'user-sudo': 'Sign in as this user',
  'user-confirm-sudo': 'You are about to sign in to seminargo as this user. Continue?',
  'user-confirm-sudo-title': 'Sign in as another user',
  'user-sudo-failed': 'Sign in as another user failed',
  'pricelists-list-header-name': 'Price list',
  'pricelists-list-header-refcode': 'Reference code',
  'pricelists-list-header-version': 'Version',
  'pricelists-list-header-validity': 'Validity period',
  'pricelists-list-header-ispublished': 'Published',
  'pricelists-list-header-isdefault': 'Default',
  'pricelists-list-header-isvalid': 'Complete',
  'pricelists-list-header-hotel': 'Hotel',
  'pricelist-selector-page-title': 'Pricing rule',
  'pricelist-selectors-tab': 'Pricing rules',
  'productprices-pricelist': 'Price list',
  'productprices-selector': 'Pricing rule',
  'productprices-ispublished': 'Published',
  'productprices-price': 'Price',
  'invoices-list-page-title-LISTER': 'Invoice approval',
  'invoices-list-page-title-WIDGET': 'Invoice approval',
  'invoices-confirm-title': 'Approve invoice for seminargo',
  'invoice-media-tab': 'Invoice closing',
  'invoice-booked-services-readonly-title': 'Originally requested and booked services',
  'invoice-booked-services-readonly-subtitle': 'Read-only: compare captured services with invoice line items.',
  'invoice-booked-services-current-title': 'Current requested and booked services',
  'invoice-extras-tab': 'Additional Services',
  'invoice-extras-info': 'Services that were additionally consumed during the event and were not paid for on site',
  'invoice-extras-selectsku': '+ Add',
  'invoice-extras-selectsku-products': 'Hotel services',
  'invoice-extras-selectsku-bundles': 'Packages',
  'invoice-extras-selectsku-facilities': 'Equipment & facilities',
  'invoice-extras-upload-receipt': 'Upload receipt',
  'invoice-info-tab': 'Invoice information',
  'invoice-tasks-tab': 'Tasks',
  'invoice-noshows-tab': 'No shows',
  'invoice-noshows-participant': 'Participant',
  'invoice-noshows-empty': 'No participants recorded for this event.',
  'invoice-noshow-present': 'Present',
  'invoice-noshow-noshow': 'No-show',
  'invoice-media-upload-header': 'Upload documents',
  'invoice-media-upload-description': 'Please upload your final invoice for the event and the related supporting documents.',
  'invoice-media-upload-header-seminargo': 'Documents for the event',
  'invoice-media-upload-invoice': 'Upload final invoice',
  'invoice-media-upload-extra': 'Upload related supporting documents',
  'invoice-media-upload-helper': 'Supported file formats: image files (PNG, JPG, JPEG), PDF files. Maximum 10MB per file.',
  'invoice-media-upload-helper-size': 'The file is too large. Maximum 10MB per file.',
  'invoice-media-type-HOTEL_INVOICE': 'Final invoice',
  'invoice-media-type-HOTEL_EXTRA': 'Supporting document',
  'invoice-commission-base': 'Commission basis',
  'invoice-commission-percentage': 'Commission rate',
  'invoice-commission-base-amount': 'Calculation basis',
  'invoice-commission-base-amount-net': 'Net amount excl. VAT',
  'invoice-commission-base-amount-gross': 'Gross amount incl. VAT',
  'enums-EOfferCommissionBase-GROSS': 'Gross amount',
  'enums-EOfferCommissionBase-NET': 'Net amount',
  'invoice-commmission-info-text-net':
    'As agreed in the contract, we charge a commission of {{commissionPercentage}} ({{commissionAmountNet}}) on the agreed net amount ({{commissionBaseAmount}}), plus statutory VAT.',
  'invoice-commmission-info-text-gross':
    'As agreed in the contract, we charge a commission of {{commissionPercentage}} ({{commissionAmountNet}}) on the agreed gross amount ({{commissionBaseAmount}}), plus statutory VAT.',
  'invoice-sender-name': 'Company name',
  'invoice-sender-address1': 'Address line 1',
  'invoice-sender-address2': 'Address line 2',
  'invoice-sender-address3': 'Address line 3',
  'invoice-sender-address4': 'Address line 4',
  'invoice-sender-zip': 'ZIP',
  'invoice-sender-city': 'City',
  'invoice-sender-country': 'Country',
  'invoice-sender-billing-uid': 'VAT ID',
  'invoice-sender-email': 'Email address',
  'invoice-sender-phone': 'Phone number',
  'invoice-sender-contact': 'Contact name',
  'invoice-sender-comment': 'Invoice comment',
  'invoice-sender-accepted': 'I confirm the billing issuer address',
  'invoice-sender-accepted-commission-net':
    'I hereby confirm that all information provided is accurate and complete. Upon approval, the invoice will be forwarded to seminargo for further processing and calculation of the commission in accordance with the contract, in the amount of {{commissionPercentage}} ({{commissionAmount}}) of the agreed net total amount ({{baseAmount}}) plus applicable VAT.',
  'invoice-sender-accepted-commission-gross':
    'I hereby confirm that all information provided is accurate and complete. Upon approval, the invoice will be forwarded to seminargo for further processing and calculation of the commission in accordance with the contract, in the amount of {{commissionPercentage}} ({{commissionAmount}}) of the agreed gross total amount ({{baseAmount}}) plus applicable VAT.',
  'invoice-sender-accepted-commission-agb': 'Terms and Conditions',
  'invoice-totals': 'Price overview',
  'invoice-totals-net': 'Net total',
  'invoice-totals-taxes': 'Tax total',
  'invoice-totals-gross': 'Gross total',
  'invoice-cancellation-agreedwaive': 'Agreed cancellation waiver (excl. VAT)',
  'invoice-cancellation-manualwaive': 'Manual cancellation waiver (excl. VAT)',
  'invoice-cancellation-manualwaive-min-error': 'The amount must not be negative',
  'invoice-cancellation-manualwaive-max-error': 'The amount must not exceed the invoice total',
  'invoice-cancellation-full-waive-button': 'Apply full cancellation waiver',
  'invoice-cancellation-fees-and-waiver': 'Cancellation fees & Cancellation Waiver',
  'invoice-cancellation-fees': 'Cancellation fees',
  'invoice-cancellation-waiver': 'Cancellation waiver',
  'invoice-cancellation-waiver-sum': 'Sum',
  'invoice-cancellation-waiver-price-on-request': 'Price on request',
  'invoice-cancellation-waiver-fees-empty': 'No cancellation fee breakdown available.',
  'invoice-cancellation-waiver-phase': 'Cancellation phase · until {{days}} days ({{rate}})',
  'invoice-cancellation-waiver-cancelled': 'Cancelled services',
  'invoice-cancellation-waiver-added': 'Added services',
  'invoice-cancellation-waiver-basis': 'Cancellation basis',
  'invoice-cancellation-waiver-phase-costs': 'Cancellation costs',
  'invoice-cancellation-waiver-free-period': 'Changes outside cancellation phases',
  'invoice-cancellation-waiver-tooltip-date': 'First confirmed on {{date}}',
  'invoice-cancellation-waiver-tooltip-basis': 'Highest confirmed net total: {{amount}}',
  'invoice-cancellation-waiver-tooltip-rate': '{{rate}} of that: {{amount}}',
  'invoice-cancellation-waiver-ask': 'Will you waive the cancellation costs for the customer?',
  'invoice-cancellation-waiver-yes-complete': 'Yes, waive completely',
  'invoice-cancellation-waiver-partial': 'Partially waive',
  'invoice-cancellation-waiver-no-full': 'No, charge full amount',
  'invoice-cancellation-waiver-charged-amount': 'Charged amount',
  'invoice-cancellation-waiver-amount-to-invoice': 'Amount to invoice',
  'invoice-cancellation-waiver-charged-max-error': 'The amount must not exceed the sum',
  'integration-log-result-empty': 'No result',
  'integration-sync-col-name': 'Name',
  'integration-sync-col-ref-code': 'Ref code',
  'integration-sync-col-sku': 'SKU',
  'integration-sync-col-external-ref-code': 'External ref code',
  'integration-sync-col-added': 'Added',
  'integration-sync-col-updated': 'Updated',
  'integration-sync-col-success': 'Success',
  'integration-sync-col-error': 'Error',
  'integration-sync-section-property': 'Property',
  'integration-sync-section-product': 'Product',
  'integration-sync-section-facility': 'Facility',
  'integration-sync-section-availability': 'Availability',
  'integration-sync-section-price': 'Price',
  'integration-pms-elitefo-setup-title': 'Setup in Elite Hotel Software',
  'integration-pms-protelids-setup-title': 'Setup in Protel IDS',
  'integration-pms-sem-endpoint-label': 'SEM endpoint:',
  'integration-pms-elitefo-http-basic-point': 'HTTP Basic authentication as configured on the API key',
  'integration-pms-protel-auth-point': 'Authentication as configured on the API key',
  'integration-pms-requestor-id-label': 'Requestor ID:',
  'integration-pms-hotel-code-label': 'Hotel code:',
  'integration-pms-protel-property-code-label': 'Property code:',
  'integration-pms-term-reference-code': 'Reference code',
  'integration-pms-elitefo-category-before': 'Category: matches the ',
  'integration-pms-elitefo-category-after': ' of each SEM service',
  'integration-pms-elitefo-board-before': 'Board / F&B: matches the ',
  'integration-pms-elitefo-board-after': ' of each SEM service',
  'integration-pms-elitefo-arracode-before': 'Arracode: matches the ',
  'integration-pms-elitefo-arracode-after': ' of the SEM price list',
  'integration-pms-protel-roomcode-before': 'Room code: matches the ',
  'integration-pms-protel-roomcode-after': ' of each SEM service',
  'integration-pms-protel-ratecode-before': 'Rate code: matches the ',
  'integration-pms-protel-ratecode-after': ' of the SEM price list',
  'offer-create-pricelist-date-hint': 'Only dates with a valid assigned price list can be selected.',
  'offer-create-guests-summary': 'Guests ({{overnightGuests}} overnight guests / {{dayGuests}} day guests)',
  'offer-create-rooms-header': 'Rooms: {{count}}',
  'space-settings-ci-inherit-title': 'Settings for the administrative area "{{spaceName}}"',
  'space-settings-ci-inherit-description': 'Settings are inherited from the parent administrative area. They can be overridden here.',
  // PDF import
  'graphqlerror-PDF_IMPORT_DISABLED': 'The PDF import is not enabled.',
  'pdf-import-target-hotel': 'Hotel of the existing booking.',
  'pdf-import-case-both-version': 'result in two new versions: first the offer, then the reservation.',
  'pdf-import-case-confirmation-version': 'is added as a new reservation version; the booking becomes confirmed.',
  'pdf-import-case-offer-version': 'is added to the booking as a new offer version.',
  'graphqlerror-PDF_IMPORT_ALREADY_IMPORTED': 'This PDF has already been imported into booking {{refCode}}.',
  'graphqlerror-PDF_IMPORT_GROUP_TARGET': 'All documents must belong to the same booking.',
  'graphqlerror-PDF_IMPORT_TARGET_CLOSED': 'Booking {{refCode}} is closed and does not accept a new version.',
  'graphqlerror-PDF_IMPORT_TARGET_NOT_FOUND': 'The booking was not found or is not accessible.',
  'pdf-import-create-version': 'Create as new version',
  'pdf-import-create-version-reservation': 'Create reservation as new version',
  'pdf-import-create-versions': 'Create {{count}} new versions',
  'pdf-import-info-version': 'A new offer version is added to booking {{refCode}}. No messages are sent to the hotel or the customer.',
  'pdf-import-info-version-reservation':
    'A new version is added to booking {{refCode}}; the last new version is the confirmed reservation and the booking becomes confirmed. No messages are sent to the hotel or the customer.',
  'pdf-import-list-target': 'for {{refCode}}',
  'pdf-import-offer-button': 'Import PDF',
  'pdf-import-step-version': 'Create version',
  'pdf-import-success-version': 'The new version was added to booking {{refCode}}.',
  'pdf-import-success-version_other': 'The {{count}} new versions were added to booking {{refCode}}.',
  'pdf-import-target-closed': 'The booking is closed and does not accept a new version.',
  'pdf-import-target-customer': 'The customer comes from the booking and is not changed when the new version is created.',
  'pdf-import-target-number': 'Booking number of the existing booking.',
  'pdf-import-target-open': 'Open booking',
  'pdf-import-target-review-text':
    'Once created, this document is added as a new version after the {{count}} existing versions. Hotel, customer and booking number come from the booking; existing versions stay unchanged.',
  'pdf-import-target-title': 'New version for booking {{refCode}}',
  'pdf-import-target-upload-text':
    'Uploaded PDFs are added as a new version of this booking after review: an offer as an offer version, a booking confirmation as a reservation. Hotel, customer and booking number are kept.',
  'graphqlerror-PDF_IMPORT_AUTH': 'The PDF import requires a signed-in back-office session.',
  'graphqlerror-PDF_IMPORT_BOOKING_TAKEN':
    'Booking {{refCode}} already exists. This PDF cannot be imported; delete the existing booking first if it should be replaced.',
  'graphqlerror-PDF_IMPORT_CHANGED': 'The import has changed in the meantime. Please reload.',
  'graphqlerror-PDF_IMPORT_COMPANY_NOT_FOUND': 'Company not found.',
  'graphqlerror-PDF_IMPORT_FILE_INVALID': 'Please upload a valid PDF file of up to 20 MB.',
  'graphqlerror-PDF_IMPORT_FILE_SIZE': 'A PDF may be at most 20 MB.',
  'graphqlerror-PDF_IMPORT_GROUP_HOTEL': 'All documents of a booking must be assigned to the same hotel.',
  'graphqlerror-PDF_IMPORT_GROUP_IMPORTED':
    'A document of this booking has already been imported. Please remove the document from the combined booking.',
  'graphqlerror-PDF_IMPORT_GROUP_INVALID': 'Invalid upload group.',
  'graphqlerror-PDF_IMPORT_GROUP_LEAVE': 'Imported documents can no longer be removed from the booking.',
  'graphqlerror-PDF_IMPORT_GROUP_NUMBERS': 'The documents of the booking have different booking numbers ({{numbers}}).',
  'graphqlerror-PDF_IMPORT_GROUP_PENDING': 'All documents of the booking must be reviewed: {{- files}}',
  'graphqlerror-PDF_IMPORT_GROUP_SIZE': 'At most {{max}} PDFs can be imported together as one booking.',
  'graphqlerror-PDF_IMPORT_HOTEL_FIRST': 'Assign a hotel first.',
  'graphqlerror-PDF_IMPORT_HOTEL_NOT_FOUND': 'Hotel not found or not accessible.',
  'graphqlerror-PDF_IMPORT_INVALID': '{{- detailsEn}}',
  'graphqlerror-PDF_IMPORT_NOT_FOUND': 'PDF import not found.',
  'graphqlerror-PDF_IMPORT_PREVIEW_LIMIT': 'The PDF exceeds the preview limit.',
  'graphqlerror-PDF_IMPORT_RETRY_STATE': 'Only failed or not yet imported reviews can be processed again.',
  'graphqlerror-PDF_IMPORT_TEMPLATE_OFFER': 'The offer template for the import is missing.',
  'graphqlerror-PDF_IMPORT_TEMPLATE_RESERVATION': 'The reservation confirmation template is missing.',
  'graphqlerror-PDF_IMPORT_TOTAL_DIFFERENCE':
    'The total ({{offer}} €) differs from the total in the PDF ({{printed}} €). Please check the items or confirm the difference.',
  'graphqlerror-PDF_IMPORT_TOTAL_DIFFERENCE_GROUP':
    'The total in {{- files}} differs from the total in the PDF. Please check the items or confirm the difference.',
  'pdf-import-additionalTerms': 'Further agreements',
  'pdf-import-addon-ADDON_COFFEE': 'Coffee',
  'pdf-import-addon-ADDON_COFFEEBREAK': 'Coffee break',
  'pdf-import-addon-ADDON_DINNER': 'Dinner',
  'pdf-import-addon-ADDON_LUNCH': 'Lunch',
  'pdf-import-addon-ADDON_MEALDRINK': 'Drinks with meals',
  'pdf-import-addon-ADDON_SEMINARDRINK': 'Seminar drinks',
  'pdf-import-addon-VISITOR_TAX': 'City tax',
  'pdf-import-addon-add': 'Add extra service',
  'pdf-import-addon-description': 'Extra service as stated in the PDF',
  'pdf-import-addon-remove': 'Remove extra service',
  'pdf-import-addon-type': 'Type of extra service',
  'pdf-import-addons': 'Extra services',
  'pdf-import-all-uploads': 'All uploads',
  'pdf-import-apply': 'Apply',
  'pdf-import-basis-gross': 'gross',
  'pdf-import-basis-net': 'net',
  'pdf-import-billing': 'Billing details',
  'pdf-import-billing-address': 'Billing address',
  'pdf-import-billing-city': 'Billing city',
  'pdf-import-billing-company': 'Billing company',
  'pdf-import-billing-country': 'Billing country',
  'pdf-import-billing-firstname': 'Billing first name',
  'pdf-import-billing-hint': 'Only add if the PDF states separate billing details.',
  'pdf-import-billing-lastname': 'Billing last name',
  'pdf-import-billing-zip': 'Billing postcode',
  'pdf-import-booking-all-ready': 'all documents ready for review',
  'pdf-import-booking-created': 'created',
  'pdf-import-booking-documents': 'Booking with {{count}} documents',
  'pdf-import-booking-number': 'Booking number',
  'pdf-import-booking-number-empty': 'Leave empty if the PDF does not state a booking number.',
  'pdf-import-booking-number-placeholder': 'e.g. BWTBXB',
  'pdf-import-booking-number-taken': 'This booking number is already taken.',
  'pdf-import-booking-number-taken-bar': 'Booking number {{refCode}} is already taken.',
  'pdf-import-booking-number-used': 'The booking receives this number.',
  'pdf-import-booking-taken-chip': 'Booking already exists',
  'pdf-import-busy': 'Working …',
  'pdf-import-button': 'Import PDF',
  'pdf-import-cancellationTerms': 'Cancellation terms',
  'pdf-import-card-deposit': 'Deposit {{amount}}',
  'pdf-import-card-gross': '{{amount}} gross',
  'pdf-import-card-no-customer': 'No customer recognised',
  'pdf-import-card-no-deposit': 'No deposit',
  'pdf-import-card-no-period': 'Period missing',
  'pdf-import-card-no-tiers': "No cancellation scale – the hotel's rules apply",
  'pdf-import-card-no-title': 'Untitled',
  'pdf-import-card-no-total': 'Total missing',
  'pdf-import-card-participants': '{{count}} participants',
  'pdf-import-card-participants-unknown': 'Number of participants unknown',
  'pdf-import-card-printed': 'Total in the PDF: {{amount}}',
  'pdf-import-card-services': '{{count}} services',
  'pdf-import-card-services_one': '{{count}} service',
  'pdf-import-card-tiers': '{{count}} cancellation tiers',
  'pdf-import-card-tiers_one': '{{count}} cancellation tier',
  'pdf-import-card-valid-until': 'Valid until {{date}}',
  'pdf-import-case-both-text': 'result in one booking with an offer and a reservation version.',
  'pdf-import-case-both-title': 'Offer and booking confirmation together',
  'pdf-import-case-confirmation-text': 'is created as a confirmed reservation.',
  'pdf-import-case-confirmation-title': 'A booking confirmation',
  'pdf-import-case-offer-text': 'is created as an offer draft.',
  'pdf-import-case-offer-title': 'An offer',
  'pdf-import-category-ABREISEPAUSCHALE': 'Departure-day package',
  'pdf-import-category-ADDON_COFFEE': 'Coffee',
  'pdf-import-category-ADDON_COFFEEBREAK': 'Coffee break',
  'pdf-import-category-ADDON_DINNER': 'Dinner',
  'pdf-import-category-ADDON_LUNCH': 'Lunch',
  'pdf-import-category-ADDON_MEALDRINK': 'Drinks with meals',
  'pdf-import-category-ADDON_SEMINARDRINK': 'Seminar drinks',
  'pdf-import-category-BEAMER': 'Projector',
  'pdf-import-category-CAM': 'Camera',
  'pdf-import-category-CD': 'CD/audio',
  'pdf-import-category-FLIPCHART': 'Flipchart',
  'pdf-import-category-GROUPROOM': 'Breakout room',
  'pdf-import-category-HALBTAGESPAUSCHALE': 'Half-day package',
  'pdf-import-category-LEINWAND': 'Screen',
  'pdf-import-category-MODERATION': 'Facilitation kit',
  'pdf-import-category-PINNWAND': 'Pinboard',
  'pdf-import-category-ROOM_DZ': 'Overnight stay double room',
  'pdf-import-category-ROOM_EZ': 'Overnight stay single room',
  'pdf-import-category-SEMINARPAUSCHALE_DZ': 'Seminar package double room',
  'pdf-import-category-SEMINARPAUSCHALE_EZ': 'Seminar package single room',
  'pdf-import-category-SEMINARROOM_FULLDAY': 'Meeting room full day',
  'pdf-import-category-SEMINARROOM_SEMIDAY': 'Meeting room half day',
  'pdf-import-category-TAGESPAUSCHALE': 'Day package',
  'pdf-import-category-TV': 'TV',
  'pdf-import-category-VISITOR_TAX': 'City tax',
  'pdf-import-category-VOLLPENSIONSPAUSCHALE_DZ': 'Full board package double room',
  'pdf-import-category-VOLLPENSIONSPAUSCHALE_EZ': 'Full board package single room',
  'pdf-import-category-VORTAGSANREISE_DZ': 'Arrival the day before, double room',
  'pdf-import-category-VORTAGSANREISE_EZ': 'Arrival the day before, single room',
  'pdf-import-change': 'Change',
  'pdf-import-check-details': 'Review details',
  'pdf-import-checks-text': 'These details are needed before anything can be created. A click takes you to the matching area.',
  'pdf-import-checks-title': 'Still to check ({{count}})',
  'pdf-import-chip-blocked': 'Cannot be imported',
  'pdf-import-chip-draft': 'Created as a draft',
  'pdf-import-chip-ready': 'Ready to create',
  'pdf-import-chip-reservation': 'Created as a reservation',
  'pdf-import-chip-version': 'Created as a version of the booking',
  'pdf-import-choose-files': 'Choose PDFs',
  'pdf-import-client-address': 'Address',
  'pdf-import-client-city': 'City',
  'pdf-import-client-company': 'Company',
  'pdf-import-client-country': 'Country',
  'pdf-import-client-email': 'E-mail',
  'pdf-import-client-firstname': 'First name',
  'pdf-import-client-lastname': 'Last name',
  'pdf-import-client-phone': 'Phone',
  'pdf-import-client-zip': 'Postcode',
  'pdf-import-col-customer': 'Customer',
  'pdf-import-col-document': 'Document',
  'pdf-import-col-hotel': 'Hotel',
  'pdf-import-col-period': 'Period',
  'pdf-import-col-status': 'Status',
  'pdf-import-col-total': 'Total',
  'pdf-import-conflict-deleted-text':
    'When created, the new booking receives the number {{refCode}}; the deleted booking is renamed to {{refCode}}-{{offerId}}.',
  'pdf-import-conflict-deleted-title': 'Deleted booking {{refCode}} exists',
  'pdf-import-conflict-open': 'Open existing booking',
  'pdf-import-conflict-text':
    'This PDF cannot be imported. If the existing booking should be replaced, delete it first and then open this import again.',
  'pdf-import-conflict-title': 'Booking {{refCode}} already exists – import not possible',
  'pdf-import-country-placeholder': 'Select country',
  'pdf-import-create-booking': 'Create booking with {{count}} versions',
  'pdf-import-create-offer': 'Create offer as draft',
  'pdf-import-create-reservation': 'Create reservation',
  'pdf-import-currency': 'Currency',
  'pdf-import-customer-company': 'Customer in the company directory (optional)',
  'pdf-import-customer-company-helper': 'Search for an existing company or check the details below.',
  'pdf-import-customer-existing': 'Use existing customer',
  'pdf-import-customer-existing-helper': 'Takes over contact, e-mail, address and billing details. Suggestions match the company from the PDF.',
  'pdf-import-customers-none': 'No matching customers found',
  'pdf-import-day-outside': 'This day is outside the event period. Correct the period or remove the day explicitly.',
  'pdf-import-day-remove': 'Remove day',
  'pdf-import-day-summary': 'Guests: {{guests}} · Single rooms: {{single}} · Double rooms: {{double}}',
  'pdf-import-deposit': 'Deposit',
  'pdf-import-deposit-due': 'Deposit due on',
  'pdf-import-details': 'Details',
  'pdf-import-difference-confirm': 'I have checked the items and accept the differing total.',
  'pdf-import-difference-others': 'The total also differs from the PDF in other documents of the booking: {{- files}}.',
  'pdf-import-difference-text':
    'The offer total ({{offer}} €) differs from the total in the PDF ({{printed}} €). Common causes: missing nights or items, surcharges such as the city tax that the PDF states as not included.',
  'pdf-import-disabled': 'The PDF import is not available here yet.',
  'pdf-import-document-BOOKING_CONFIRMATION': 'Reservation',
  'pdf-import-document-OFFER': 'Offer',
  'pdf-import-document-UNKNOWN': 'Document type open',
  'pdf-import-document-type': 'Document type',
  'pdf-import-document-type-confirmation': 'Booking/reservation confirmation (created as a reservation)',
  'pdf-import-document-type-offer': 'Hotel offer',
  'pdf-import-document-type-unknown': 'Please confirm',
  'pdf-import-documentDate': 'Date of the PDF',
  'pdf-import-double-rooms': 'Double rooms',
  'pdf-import-download-raw': 'Download recognition data (JSON)',
  'pdf-import-download-review': 'Download reviewed data (JSON)',
  'pdf-import-drop-here': 'Drag PDFs here',
  'pdf-import-drop-limits':
    'Up to {{max}} PDFs at once, at most 20 MB each. You review the recognised details before anything is created; no messages are sent to the hotel or the customer.',
  'pdf-import-empty-imported': 'Nothing created yet.',
  'pdf-import-empty-none': 'No PDFs uploaded yet.',
  'pdf-import-empty-open': 'All done – no open uploads.',
  'pdf-import-error-action': 'Action failed.',
  'pdf-import-error-too-large': 'The PDF is too large for automatic recognition. Please split it into smaller files.',
  'pdf-import-error-unavailable': 'Automatic processing is currently unavailable. Please try again or contact support.',
  'pdf-import-event-requirements': 'Rooms, meeting rooms and further requests',
  'pdf-import-event-title': 'Title',
  'pdf-import-external-reference': 'Offer or booking number',
  'pdf-import-failed-default': 'The details could not be read.',
  'pdf-import-failed-hint': 'If needed, upload the file again.',
  'pdf-import-filter-all': 'All ({{count}})',
  'pdf-import-filter-imported': 'Created ({{count}})',
  'pdf-import-filter-open': 'Open ({{count}})',
  'pdf-import-from-pdf': 'Details from the PDF',
  'pdf-import-gross': 'Gross',
  'pdf-import-gross-prices': 'Gross prices',
  'pdf-import-group-hotel': 'All documents of the booking must be assigned to the same hotel.',
  'pdf-import-group-leave': 'Import this document on its own',
  'pdf-import-group-leave-hint': 'Removes this document from the combined booking; it is then created as its own offer or reservation.',
  'pdf-import-group-numbers': 'The documents of the booking have different booking numbers.',
  'pdf-import-group-open': '{{count}} details open',
  'pdf-import-group-open-checks': 'Other documents of the booking still have details to check.',
  'pdf-import-group-pending': 'Not all documents of the booking have been read or reviewed yet.',
  'pdf-import-group-text':
    'The documents are created together as versions of one booking, in this order. Review each document; the booking can then be created from any of them.',
  'pdf-import-group-title': 'Combined booking ({{count}} documents)',
  'pdf-import-guests': 'Guests',
  'pdf-import-hotel-conflicts': 'Differences from the PDF: {{- conflicts}}. Please check the hotel assignment.',
  'pdf-import-hotel-differs': 'Details differ from the PDF – please check',
  'pdf-import-hotel-helper': 'The recognised hotel is preselected. You can search for another hotel or city.',
  'pdf-import-hotel-label': 'Which hotel does this offer belong to?',
  'pdf-import-hotel-matches': 'Matches the details in the PDF',
  'pdf-import-hotels-loading': 'Loading hotels …',
  'pdf-import-hotels-matching': 'Matching hotels from the PDF',
  'pdf-import-hotels-none': 'No matching hotels found',
  'pdf-import-hotels-other': 'Other hotels',
  'pdf-import-imported-details': 'Imported details',
  'pdf-import-info-booking': 'A booking with {{count}} versions is created. No messages are sent to the hotel or the customer.',
  'pdf-import-info-booking-reservation':
    'A booking with {{count}} versions is created; the last version is the confirmed reservation. No messages are sent to the hotel or the customer.',
  'pdf-import-info-offer': 'A draft is created. No messages are sent and no bookings are triggered.',
  'pdf-import-info-reservation': 'A confirmed reservation is created (reservation confirmation). No messages are sent to the hotel or the customer.',
  'pdf-import-issue-country': "Please select the customer's country.",
  'pdf-import-issue-email': "Please add the contact person's e-mail address.",
  'pdf-import-issue-hotelMissing': 'Please select the matching hotel.',
  'pdf-import-issue-priceListMissing': 'This hotel is not yet set up for creating offers. Please contact support.',
  'pdf-import-issue-taxMissing': 'Please check the tax rate. Missing amounts are calculated afterwards.',
  'pdf-import-item': 'Item {{n}}',
  'pdf-import-language-de': 'German',
  'pdf-import-language-en': 'English',
  'pdf-import-line-add': 'Add item',
  'pdf-import-line-category': 'Service type',
  'pdf-import-line-category-helper': 'Helps with matching the hotel catalogue and the tax.',
  'pdf-import-line-date': 'Service date',
  'pdf-import-line-description': 'Service',
  'pdf-import-line-notes': 'Details / included services',
  'pdf-import-line-remove': 'Remove item',
  'pdf-import-line-tax': 'Assign tax rate',
  'pdf-import-line-taxAmount': 'Tax amount',
  'pdf-import-line-totalGross': 'Gross total',
  'pdf-import-line-totalNet': 'Net total',
  'pdf-import-line-unit': 'Unit',
  'pdf-import-line-unitPrice': 'Unit price',
  'pdf-import-lines-details': 'Edit individual services in detail',
  'pdf-import-mixed-catalog': 'Mixed according to the hotel catalogue',
  'pdf-import-mixed-summary': '{{- item}}: mixed tax {{- shares}} according to the hotel catalogue.',
  'pdf-import-net': 'Net',
  'pdf-import-net-prices': 'Net prices',
  'pdf-import-no': 'No',
  'pdf-import-no-addons': 'No extra services (confirmed)',
  'pdf-import-no-rooms': 'No rooms (confirmed)',
  'pdf-import-no-services': 'No services recognised yet.',
  'pdf-import-not-assigned': 'Not assigned',
  'pdf-import-not-in-pdf': 'Not stated in the PDF',
  'pdf-import-offer-language': 'Offer language',
  'pdf-import-open': 'Open',
  'pdf-import-open-booking': 'Open booking',
  'pdf-import-open-offer': 'Open offer',
  'pdf-import-open-pdf-first': 'Please open a PDF first.',
  'pdf-import-open-points': '{{count}} points open',
  'pdf-import-open-points-show': '{{count}} points open – show',
  'pdf-import-open-points-show_one': '{{count}} point open – show',
  'pdf-import-open-points_one': '{{count}} point open',
  'pdf-import-opened': 'open',
  'pdf-import-paymentTerms': 'Payment terms',
  'pdf-import-please-choose': 'Please choose',
  'pdf-import-preview-download': 'Download original',
  'pdf-import-preview-draw-failed': 'The PDF preview could not be drawn.',
  'pdf-import-preview-fit-width': 'Fit to page width',
  'pdf-import-preview-fullscreen': 'Full screen',
  'pdf-import-preview-fullscreen-close': 'Close full screen',
  'pdf-import-preview-fullscreen-close-hint': 'Close full screen (Esc)',
  'pdf-import-preview-fullscreen-label': 'PDF full screen: {{- filename}}',
  'pdf-import-preview-hint': 'Compare the recognised details with the original. You can also open the PDF in full screen from the preview.',
  'pdf-import-preview-load-failed': 'The PDF could not be loaded.',
  'pdf-import-preview-loading': 'Loading PDF',
  'pdf-import-preview-page': 'Page {{page}} / {{pages}}',
  'pdf-import-preview-page-drawing': 'Drawing page {{page}}',
  'pdf-import-preview-page-failed': 'The PDF page could not be displayed.',
  'pdf-import-preview-page-label': 'PDF, page {{page}} of {{pages}}',
  'pdf-import-preview-preparing': 'Preparing PDF',
  'pdf-import-preview-reload': 'Reload',
  'pdf-import-preview-scroll-hint': 'Scroll to move through the PDF',
  'pdf-import-preview-zoom-in': 'Zoom in',
  'pdf-import-preview-zoom-out': 'Zoom out',
  'pdf-import-price-basis-all': 'Price basis for all items',
  'pdf-import-price-basis-hint': 'Gross includes tax, net does not. If the PDF does not say, gross is assumed. Switching recalculates the totals.',
  'pdf-import-price-basis-line': 'Price basis item {{n}}',
  'pdf-import-prices-are': 'The prices in the PDF are',
  'pdf-import-prices-kept': 'The prices from your PDF are kept. Matching tax rates are added automatically.',
  'pdf-import-processing': 'Processing runs in the background. You can come back to this page later.',
  'pdf-import-quantity': 'Quantity',
  'pdf-import-raw-hide': 'Hide recognition data',
  'pdf-import-raw-show': 'Show recognition data',
  'pdf-import-reading': 'Being read',
  'pdf-import-recognition-notes': 'Recognition notes',
  'pdf-import-remove': 'Remove',
  'pdf-import-request-comment': 'Comment / further requirements',
  'pdf-import-reread': 'Read details from the PDF again',
  'pdf-import-reread-hint': 'Reads the details again and replaces the reviewed data so far.',
  'pdf-import-review': 'Review',
  'pdf-import-room': 'Room {{n}}',
  'pdf-import-room-add': 'Add room',
  'pdf-import-room-airConditioning': 'Air conditioning',
  'pdf-import-room-beamers': 'Projectors',
  'pdf-import-room-daylight': 'Daylight',
  'pdf-import-room-flipcharts': 'Flipcharts',
  'pdf-import-room-from': 'From',
  'pdf-import-room-minArea': 'Minimum area (m²)',
  'pdf-import-room-name': 'Room name',
  'pdf-import-room-number': 'Room number',
  'pdf-import-room-participants': 'Participants',
  'pdf-import-room-pinboards': 'Pinboards',
  'pdf-import-room-remove': 'Remove room',
  'pdf-import-room-to': 'To',
  'pdf-import-rooms': 'Rooms',
  'pdf-import-save': 'Save changes',
  'pdf-import-save-failed': 'The changes could not be saved. Please try again.',
  'pdf-import-seating': 'Seating',
  'pdf-import-seating-BANKETT': 'Banquet',
  'pdf-import-seating-BLOCK': 'Boardroom',
  'pdf-import-seating-CIRCLE': 'Circle of chairs',
  'pdf-import-seating-COCKTAIL': 'Cocktail',
  'pdf-import-seating-EMPTY': 'No seating',
  'pdf-import-seating-PARLAMENT': 'Classroom',
  'pdf-import-seating-THEATER': 'Theatre',
  'pdf-import-seating-UFORM': 'U-shape',
  'pdf-import-section-customer': 'Customer',
  'pdf-import-section-event': 'Event',
  'pdf-import-section-header': 'Hotel & document',
  'pdf-import-section-overview': 'Overview',
  'pdf-import-section-services': 'Services',
  'pdf-import-section-terms': 'Terms',
  'pdf-import-single-rooms': 'Single rooms',
  'pdf-import-split-apply-all': 'Apply to all {{count}} items "{{- item}}"',
  'pdf-import-split-check-first': 'Check quantity and unit price first.',
  'pdf-import-split-intro': '{{- item}}: mixed tax according to the hotel catalogue. Enter the share per unit ({{basis}})',
  'pdf-import-split-rest': 'Remainder {{rate}} %:',
  'pdf-import-split-share': 'of which {{rate}} % in €',
  'pdf-import-split-single-instead': 'Choose a single tax rate instead',
  'pdf-import-split-too-high': 'Together the shares must not exceed the unit price.',
  'pdf-import-split-unit': ', unit price {{price}} €',
  'pdf-import-status-FAILED': 'Processing failed',
  'pdf-import-status-IMPORTED': 'Offer created',
  'pdf-import-status-IMPORTING': 'Creating offer',
  'pdf-import-status-PROCESSING': 'Reading PDF',
  'pdf-import-status-QUEUED': 'Queued for reading',
  'pdf-import-status-REVIEW': 'Ready for review',
  'pdf-import-step-booking': 'Create booking',
  'pdf-import-step-offer': 'Create offer',
  'pdf-import-step-review': 'Review details',
  'pdf-import-step-upload': 'Upload PDF',
  'pdf-import-subtitle': 'Import hotel offers and booking confirmations from PDFs.',
  'pdf-import-success-booking': 'The booking was created with {{count}} versions ({{- documents}}).',
  'pdf-import-success-offer': 'Your offer was created as a draft. You can open it now and continue editing.',
  'pdf-import-success-reservation': 'The reservation was created as a confirmed reservation. You can open it now.',
  'pdf-import-sum-totalGross': 'Gross total',
  'pdf-import-sum-totalNet': 'Net total',
  'pdf-import-sum-totalTax': 'Tax total',
  'pdf-import-tax-change-hint': 'You can change the tax rate if needed.',
  'pdf-import-tax-choose': 'Choose tax rate',
  'pdf-import-tax-choose-hint': 'Please choose the tax rate. The amounts are calculated afterwards.',
  'pdf-import-tax-for': 'Tax: {{- item}}',
  'pdf-import-tax-mixed-option': 'Mixed: {{low}} % + {{high}} %',
  'pdf-import-tax-open': 'Tax rate open',
  'pdf-import-tax-recheck': 'Check tax rates again',
  'pdf-import-tax-summary': '{{- item}}: tax rate {{rate}} %.',
  'pdf-import-tax-unclear': 'For some services the tax is not yet clear. Please choose the matching tax rate there.',
  'pdf-import-technical': 'More options and technical details',
  'pdf-import-terms-title': 'Cancellation, payment and further agreements',
  'pdf-import-tier-add': 'Add cancellation tier',
  'pdf-import-tier-days': 'From days before the event',
  'pdf-import-tier-percent': 'Fee %',
  'pdf-import-tier-wording': 'Wording in the PDF',
  'pdf-import-tiers': 'Cancellation scale',
  'pdf-import-tiers-hint':
    "Saved as the offer's cancellation rule and replaces the hotel's default rules there. Without a scale, the hotel's rules apply.",
  'pdf-import-title': 'PDF import',
  'pdf-import-to-offers': 'To the offer list',
  'pdf-import-totals': 'Totals',
  'pdf-import-unit-price-is': 'The unit price is',
  'pdf-import-unknown': 'Unknown',
  'pdf-import-upload-only-pdf': 'Please upload PDF files.',
  'pdf-import-upload-read-failed': 'The file could not be read.',
  'pdf-import-uploads': 'Uploads',
  'pdf-import-validUntil': 'Valid until according to the PDF',
  'pdf-import-version': 'Version {{n}}',
  'pdf-import-yes': 'Yes',
};

const deTranslation = languages.resources.de.translation as Record<string, any>;
(languages.resources as Record<string, { translation: Record<string, any> }>).en = {
  translation: {
    ...deTranslation,
    ...enTranslationOverrides,
  },
};
